import usePlannerReceiptRecovery from "../hooks/usePlannerReceiptRecovery";
import PlannerSaveRecoveryNotice from "../components/PlannerSaveRecoveryNotice";
import PlannerSessionBoundary from "../components/PlannerSessionBoundary";
import { createPlannerBrowserStorage } from "../utils/plannerBrowserStorage";
import { isYahooPlatform, placeYahooAfterAmazon } from "../utils/platformPresentationOrder";
import { providerPlannerKey, providerSurfaceLabel } from "../utils/providerSurfaceRegistry";
// client/src/pages/SherlocMediaPlanner.js (CLIENT)
//
// ✅ Matches SUPERADMIN structure + advertiser/campaign/draft UX
// ✅ CLIENT is READ-ONLY for loaded campaigns (no "Save Changes")
// ✅ Drafts (multi-draft, filtered by selected advertiser)
// ✅ If navigated from Media Campaigns with a campaignId:
//    fetch /api/media-campaigns/:id and hydrate fields + strategy.
// ✅ goalType saved in DB as objective, strategy saved as strategy_text.
// ✅ Auto-load latest campaign ONLY if there are ZERO drafts for the advertiser.
// ✅ Guards against stale fetches overwriting state after advertiser switches.
//
// ✅ Advertiser switching inside page
// - Uses AdvertiserContext canonical API: selectAdvertiser({id,name})
// - Supports STRING advertiser IDs (UUID/slug/etc.) without Number() coercion
//
// ✅ FIXES (per your request):
// 1) Output HERO logic is now STRICT (no accidental triggers mid-paragraph).
// 2) If BOTH Facebook + Instagram are selected, output HERO shows META with meta.jpg.
// 3) Programmatic Video (OLV) hero will NOT mistakenly trigger above Google Search
//    (strict start-of-line platform header detection + ordered matching).

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./Dashboard.css";
import "./SherlocMediaPlanner.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import useStrategyCurrency from "../hooks/useStrategyCurrency";
import { buildStrategyNextActionPath, normalizeStrategyNextAction, resolveStrategyNextActionNotice } from "../utils/strategyNextAction";
import { canRequestFullStrategy, resolveClientStrategyNotice, resolveFullStrategyRequest, STRATEGY_REQUEST_UNAVAILABLE } from "./sherlocStrategyGenerationRequest";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  EDEN_PLANNER_DRAFT_EVENT,
  mapPlannerDraftToForm,
} from "../eden/planner/plannerIntakeAssist";
import EdenPlannerDraftReview from "../components/EdenPlannerDraftReview";
import PredictiveFilmIntelligence, { isFilmIntelligenceScopeReady } from "../components/PredictiveFilmIntelligence";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import { useAdvertiser } from "../contexts/AdvertiserContext";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import {
  normalizePlatformAllocationKey,
  normalizeSherlocPlatformDisplayLabel,
  parseSherlocBudgetAllocationItems,
  normalizeSherlocBudgetAllocationText,
  resolvePlatformBudgetAmount,
  computeGrossAllocationCentsByPercent,
  formatGrossAllocationCentsAmount,
  sliceBudgetAllocationSectionText,
} from "../utils/mediaPlanAllocations";
import { normalizeSherlocOutputSectionHeading } from "../utils/sherlocOutputHeading";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";
import { getVerticalIntelligenceConfig } from "../config/verticalIntelligenceConfig";
import { formatSupportReference, readTraceHeadersFromResponse } from "../utils/requestCorrelation";
import {
  buildPlannerDraftOptions,
  choosePlannerResume,
  formatPlannerDraftOptionLabel,
  localPlannerIdForServerDraft,
  normalizeServerDraftId,
  plannerAccountKey,
  plannerChoiceOfOption,
  plannerDraftOptionKey,
  resolveLocalPlannerIdForServerDraft,
  sameAdvertiserId,
  serializePlannerChoice,
  serverDraftIdOf,
  shouldKeepUnsavedLocalCopy,
  unmarkedCopiesOfListedDrafts,
  upsertServerDraft,
} from "../utils/plannerDraftIdentity";

// Platform logos
import metaLogo from "../assets/meta.jpg"; // ✅ NEW: Meta combined hero image
import facebookLogo from "../assets/facebook.jpg";
import instagramLogo from "../assets/instagram.jpg";
import tiktokLogo from "../assets/tiktok.jpg";
import xLogo from "../assets/x.jpg";
import googleLogo from "../assets/google.jpg";
import openaiLogo from "../assets/openAI.png";
import youtubeLogo from "../assets/youtube.jpg";
import truthSocialLogo from "../assets/truth-social-logo.jpg";
import rumbleLogo from "../assets/rumble-logo.jpg";

// ✅ NEW (make sure these files exist in /assets; rename imports if needed)
import redditLogo from "../assets/reddit.jpg";
import amazonLogo from "../assets/amazon.jpg";
import shopifyLogo from "../assets/shopify.png";
import linkedinLogo from "../assets/linkedin.png";

import audioLogo from "../assets/audio.jpg";
import ingameLogo from "../assets/ingame.jpg";
import ctvLogo from "../assets/ctv.jpg";
import videoLogo from "../assets/video.jpg";
import mobileLogo from "../assets/mobile.jpg";
import displayLogo from "../assets/display.jpg";
import viantLogo from "../assets/viant.png";
import yahooLogo from "../assets/yahoo.png";
import doohLogo from "../assets/dooh.gif";
import emailLogo from "../assets/email.jpg";

import aiOptimizationsImage from "../assets/aioptimizations.gif";
import CphereAnalysisWriteup from "../components/CphereAnalysisWriteup";
import SherlocPlatformBudgetSummary from "../components/SherlocPlatformBudgetSummary";
// Shared governed platform artwork resolvers — the ONLY hero/brand image
// source for the canonical strategy presentation (no local asset maps, no
// raw URLs, no name-based inference).
import {
  resolvePlatformHeroImage,
  resolveSherlocPlatformBranding,
} from "../utils/platformBranding";
import { buildSherlocRiskDependencyFindings } from "../utils/sherlocRiskDependencyFindings";
import {
  SHERLOC_GENERATION_ACTIVE_STATUSES,
  SHERLOC_GENERATION_BACKGROUND_NOTE,
  SHERLOC_GENERATION_OPERATIONS_PATH,
  SHERLOC_GENERATION_OPERATION_STATUSES,
  SHERLOC_GENERATION_POLL_INTERVAL_MS,
  createSherlocGenerationOperation,
  formatSherlocGenerationElapsedSeconds,
  formatSherlocGenerationRelativeTime,
  getSherlocGenerationOperation,
  getSherlocGenerationOperationElapsedMs,
  getSherlocGenerationOperationForCampaign,
  getSherlocGenerationOperationLastUpdateAt,
  getSherlocGenerationOperationStageLabel,
  getSherlocGenerationOperationStatus,
  isSherlocGenerationOperationActive,
  resolveSherlocGenerationPollOutcome,
  retrySherlocGenerationOperation,
  shouldPauseSherlocGenerationPolling,
  startSherlocDurableGeneration,
} from "./sherlocGenerationOperations";
import { readCampaignStrategyMicrositeAddress } from "../utils/campaignStrategyMicrositeAddress";
import { applyApprovedAllocationToRefreshRequest } from "../utils/approvedAllocationRefreshRequest";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const SHERLOC_MICROSITE_RETURN_SOURCE = "sherloc-media-planner";
const SHERLOC_MICROSITE_RETURN_STATE_KEY = "cphere:sherloc:microsite-return-state";

export const normalizeCampaignStrategyMicrositeSlug = (value = "") =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

export const buildCampaignStrategyMicrositeSlug = ({
  advertiserName = "",
  campaignName = "",
} = {}) =>
  normalizeCampaignStrategyMicrositeSlug(
    [advertiserName, campaignName].filter(Boolean).join(" ")
  );

const PLANNER_ACTION_ROW_ORDER = ["utility", "creative", "strategy", "microsite"];
const PLANNER_ACTION_ROW_BY_KEY = {
  "creative-variations": "creative",
  "primary-strategy": "strategy",
  "strategy-microsite": "microsite",
};

const GENERATION_LOADING_TYPES = Object.freeze({
  STRATEGY: "strategy",
  REFRESH_STRATEGY: "refreshStrategy",
  CREATIVE_VARIATIONS: "creativeVariations",
});
const GENERATION_LOADING_SECONDARY_COPY =
  "This can take a moment. Your planner inputs will stay on this page.";
const GENERATION_LOADING_CONTENT = Object.freeze({
  [GENERATION_LOADING_TYPES.STRATEGY]: {
    title: "Sherloc is building the media strategy…",
  },
  [GENERATION_LOADING_TYPES.REFRESH_STRATEGY]: {
    title: "Sherloc is refreshing the strategy…",
  },
  [GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS]: {
    title: "Sherloc is generating creative variations…",
    // The copy is written and then checked by an independent reviewer before
    // it is shown, so the wait is minutes; editing pauses until it answers.
    subtitle:
      "Sherloc writes the variations, then an independent reviewer checks them. This can take a few minutes. Editing is paused until it finishes; your planner inputs stay on this page.",
  },
});

// Internal accounting form keys are never Eden-draft-writable on Cortex: the
// client form carries them as hidden model state only, so exposing them to the
// draft mapper would let a chat draft mutate internal accounting values AND
// surface their internal accounting labels in the client-facing draft-review
// card. Hide them from the mapper, then carry the current values through
// unchanged.
const CORTEX_INTERNAL_ACCOUNTING_FORM_KEYS = Object.freeze([
  "netMediaBudget",
  "targetMarginPercent",
]);

export const mapPlannerDraftToCortexForm = (draft, currentForm = {}, options = {}) => {
  const visibleForm = { ...currentForm };
  CORTEX_INTERNAL_ACCOUNTING_FORM_KEYS.forEach((key) => delete visibleForm[key]);
  const selectableDraft = Array.isArray(draft?.selectedPlatforms)
    ? { ...draft, selectedPlatforms: withoutClientUnselectablePlatforms(draft.selectedPlatforms) }
    : draft;
  const result = mapPlannerDraftToForm(selectableDraft, visibleForm, options);
  return {
    ...result,
    nextForm: {
      ...result.nextForm,
      ...Object.fromEntries(
        CORTEX_INTERNAL_ACCOUNTING_FORM_KEYS.filter((key) => key in currentForm).map(
          (key) => [key, currentForm[key]]
        )
      ),
    },
  };
};

export const SherlocPlannerActionButtons = ({ actions }) => {
  const actionRows = PLANNER_ACTION_ROW_ORDER.map((rowKey) => ({
    rowKey,
    actions: actions.filter(
      (action) => (action.row || PLANNER_ACTION_ROW_BY_KEY[action.key] || "utility") === rowKey
    ),
  })).filter((row) => row.actions.length > 0);

  return (
    <div
      className="sherloc-planner-primary-actions sherloc-planner-action-buttons"
      data-testid="sherloc-planner-action-buttons"
    >
      {actionRows.map(({ rowKey, actions: rowActions }) => (
        <div
          key={rowKey}
          className={`sherloc-planner-action-row sherloc-planner-action-row--${rowKey}`}
          data-sherloc-planner-action-row={rowKey}
        >
          {rowActions.map(({ key, className, fullWidth, onClick, disabled, title, label }) => {
            const isStandalone = rowKey === "creative" || rowKey === "strategy";
            const buttonClassName = [
              className,
              fullWidth ? "sherloc-planner-action-btn--full" : "",
              isStandalone ? "sherloc-planner-action-btn--standalone" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={key}
                type="button"
                className={buttonClassName}
                onClick={onClick}
                disabled={disabled}
                title={title}
                data-sherloc-planner-action={key}
              >
                {label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export const SherlocGenerationLoadingCard = ({ type, compact = false }) => {
  const content = GENERATION_LOADING_CONTENT[type];
  if (!content) return null;

  return (
    <div
      className={[
        "sherloc-generation-loading-card",
        compact ? "sherloc-generation-loading-card--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="status"
      aria-live="polite"
    >
      <span className="sherloc-generation-loading-orb" aria-hidden="true" />
      <span className="sherloc-generation-loading-copy">
        <span className="sherloc-generation-loading-title">{content.title}</span>
        <span className="sherloc-generation-loading-subtitle">
          {content.subtitle || GENERATION_LOADING_SECONDARY_COPY}
        </span>
      </span>
    </div>
  );
};

// CLIENT storage keys (avoid collisions with superadmin)

// Logical draft keys. The verified storage adapter gives every key an owner and advertiser partition.
const PLANNER_INDEX_KEY = "sherlocMediaPlanner_client_planner_index_v2";
const ACTIVE_PLANNER_ID_KEY_BASE = "sherlocMediaPlanner_client_active_planner_id_v2"; // per-advertiser active pointer
// The plan the operator last chose, per verified account and advertiser.
const CHOSEN_PLANNER_KEY_BASE = "sherlocMediaPlanner_client_chosen_plan_v1";
const PLANNER_STATE_PREFIX = "sherlocMediaPlanner_client_planner_state_v2_";
const makePlannerStorageKey = (id) => `${PLANNER_STATE_PREFIX}${id}`;

// Campaign endpoints
const CAMPAIGNS_BASE_PATH = "/api/media-campaigns";

// A campaign's readable strategy microsite address, from the campaign read
// (which assigns it); "" when it cannot be read. For a campaign the planner
// adopted from a response that does not carry it — the first generation
// creates the campaign.
export const fetchStrategyMicrositeAddress = async (
  campaignId,
  { token, baseUrl = API_BASE_URL } = {}
) => {
  const id = String(campaignId ?? "").trim();
  if (!id || !token || !baseUrl) return "";
  try {
    const res = await fetch(`${safeBase(baseUrl)}${CAMPAIGNS_BASE_PATH}/${encodeURIComponent(id)}`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return "";
    const data = await res.json().catch(() => ({}));
    const campaign = data?.campaign || data;
    return String(campaign?.id ?? "") === id ? readCampaignStrategyMicrositeAddress(campaign) : "";
  } catch {
    return "";
  }
};
const PLANNER_SAVE_PATH = "/api/sherloc/media-planner/save-receipts/v1";
// The advertiser's saved drafts (newest first): entering the planner and Load
// Draft resolve the plan to open from this list, never by minting a new one.
const PLANNER_DRAFTS_LIST_PATH = "/api/sherloc/media-planner/drafts";

// Campaign list endpoints (tolerant)
const CAMPAIGNS_LIST_PATH_CANDIDATES = [
  "/api/media-campaigns",
  "/api/mediacampaigns",
  "/api/campaigns",
  "/api/sherloc-media-campaigns",
  "/api/sherlocMediaCampaigns",
];

const safeBase = (base) => String(base || "").replace(/\/+$/, "");

const safeJsonParse = (raw, fallback) => {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

export const getClientInitialPlannerSessionMode = (campaignIdFromNav = null) =>
  campaignIdFromNav ? "campaign" : "new";

// Pure planner-index/draft helpers (module scope so hooks that use them do
// not need them as dependencies).
const getPlannerTitle = (fd, fallback = "Untitled Planner") => {
  const adv = (fd?.advertiser || "").trim();
  const name = (fd?.campaignName || "").trim();
  if (adv && name) return `${adv} — ${name}`;
  if (name) return name;
  if (adv) return adv;
  return fallback;
};



const getDraftMeta = (fd) => {
  const advId = fd?.advertiserId != null ? String(fd.advertiserId) : "";
  const advName = String(fd?.advertiser || "").trim();
  const k = getAdvertiserKey(advId, advName);
  return { advertiserKey: k, advertiserId: advId, advertiserName: advName };
};

// Planner inputs that make a plan worth keeping (the advertiser alone is not).
const PLANNER_CONTENT_TEXT_FIELDS = [
  "campaignName",
  "budget",
  "goalType",
  "primaryKPI",
  "notes",
  "destinationUrl",
  "imdbUrl",
  "rottenTomatoesUrl",
  "ageMin",
  "ageMax",
  "interests",
  "customTargeting",
  "geoTargetingNotes",
  "languageTargeting",
  "deviceTargetingNotes",
  "flightStart",
  "flightEnd",
];

// Blank defaults are not input. Selecting gender deliberately, including
// "all", is input; that intent travels with the owned browser copy.
const plannerFormHasContent = (fd) =>
  Boolean(String(fd?.releaseDate || "").trim()) ||
  (Array.isArray(fd?.earnedMediaProfileUrls) && fd.earnedMediaProfileUrls.some((url) => String(url || "").trim())) ||
  (Boolean(String(fd?.gender || "").trim()) &&
    (String(fd.gender).toLowerCase() !== "all" || fd?.plannerInputIntent?.gender === true)) ||
  PLANNER_CONTENT_TEXT_FIELDS.some((field) => String(fd?.[field] || "").trim()) ||
  (Array.isArray(fd?.platforms) && fd.platforms.length > 0);

// What a stored local planner copy says about itself: the saved draft it
// holds, whether it carries edits never confirmed saved (null when the copy
// never recorded it: the previous planner did not), the saved version it was
// based on, and the saved plan whose changes it keeps apart.




// The inputs a copy the previous planner left is compared on with its saved
// draft (it never recorded whether it holds unsaved edits).
const PLANNER_CONTENT_COMPARE_TEXT_FIELDS = [
  ...PLANNER_CONTENT_TEXT_FIELDS.filter((field) => field !== "budget"),
  "releaseDate",
  "gender",
];

// A plan's content as a save would send it, in one representation whatever
// form it is read from (the open page, a stored copy, a saved draft), so "does
// this copy still hold what was saved" is an exact comparison.
const plannerSaveContentFingerprint = (content, { advertiserId = null, advertiser = "", verticalConfig } = {}) =>
  JSON.stringify({
    formData: normalizeSherlocPlannerFormData(
      gatePlannerReleaseDateForVertical(
        normalizeSherlocPlannerFormData({
          ...normalizeSherlocPlannerFormData(content?.formData || {}),
          advertiserId,
          advertiser,
        }),
        verticalConfig
      )
    ),
    strategy: content?.strategy || "",
    creativeRecommendations: Array.isArray(content?.creatives) ? content.creatives : [],
    creativeGenerationMeta: content?.creativeMeta || null,
  });

// Whether a copy's inputs are exactly its saved draft's (the same fields as
// above, blanks included): such a copy holds nothing unsaved, whatever its
// marker says (a save that committed after the page was closed, say).
const plannerFormsMatch = (copyFormData, draftFormData, { verticalConfig, ignoreFields = [] } = {}) => {
  const view = (fd) => {
    const form = gatePlannerReleaseDateForVertical(
      normalizeSherlocPlannerFormData(fd && typeof fd === "object" ? fd : {}),
      verticalConfig
    );
    const text = (value) => String(value ?? "").trim();
    const parsedBudget = Number(text(form.budget).replace(/[^0-9.-]/g, ""));
    return JSON.stringify({
      fields: PLANNER_CONTENT_COMPARE_TEXT_FIELDS.filter((field) => !ignoreFields.includes(field)).map((field) =>
        text(form[field])
      ),
      budget: text(form.budget) && Number.isFinite(parsedBudget) ? parsedBudget : null,
      platforms: [...new Set((Array.isArray(form.platforms) ? form.platforms : []).map((p) => text(p).toLowerCase()))]
        .filter(Boolean)
        .sort(),
      urls: (Array.isArray(form.earnedMediaProfileUrls) ? form.earnedMediaProfileUrls : []).map(text).filter(Boolean),
    });
  };
  return view(copyFormData) === view(draftFormData);
};


// Saves of a plan whose answer has not arrived, by the content each sends.
const trackSaveInFlight = (savesInFlight, plannerId, content) => {
  const sends = savesInFlight.get(plannerId) || [];
  sends.push(content);
  savesInFlight.set(plannerId, sends);
  return () => {
    const remaining = savesInFlight.get(plannerId) || [];
    const at = remaining.indexOf(content);
    if (at >= 0) remaining.splice(at, 1);
    if (!remaining.length) savesInFlight.delete(plannerId);
  };
};

// Whether a save of other content than this is still writing for the plan:
// until it answers, the plan cannot be called saved.
const otherContentInFlight = (savesInFlight, plannerId, content) =>
  (savesInFlight.get(plannerId) || []).some((sent) => sent !== content);

// Merge-write a local planner copy: fields a write does not name (the saved
// draft it holds, the saved version it was based on) are kept.


export const getSherlocMediaPlannerAuthToken = () => {
  if (typeof window === "undefined" || !window.localStorage) return "";
  return (
    window.localStorage.getItem("clientAuthToken") ||
    window.localStorage.getItem("authToken") ||
    window.localStorage.getItem("token") ||
    window.localStorage.getItem("accessToken") ||
    ""
  );
};

// The save route refuses a plan id it cannot save under (one shared by two
// saved drafts, or one that is not a valid id) with its own sentence saying
// what to do; that sentence is shown instead of "please try again".
const PLANNER_DRAFT_ID_REFUSAL_CODES = new Set([
  "planner_local_draft_id_ambiguous",
  "planner_local_draft_id_invalid",
]);



const logSherlocPlannerDraftSaveFailure = (context = {}) => {
  if (process.env.NODE_ENV !== "development") return;
  console.warn("[SherlocMediaPlanner] Draft save failed", {
    route: PLANNER_SAVE_PATH,
    page: "SherlocMediaPlanner",
    advertiserId: context.advertiserId ?? null,
    localDraftId: context.localDraftId ?? null,
    serverDraftId: context.serverDraftId ?? null,
    status: context.status || undefined,
    message: context.message || undefined,
  });
};

// Aligned to sit just above the backend planner timeout (default 60s) so the
// backend's own calm 504 governs instead of a premature client-side abort.
// The durable operations path has NO client-side generation timeout — this
// constant (and its message) remain for the creative-variations sync path and
// for source-lock parity only.
const STRATEGY_REFRESH_TIMEOUT_MS = 65000;
const STRATEGY_REFRESH_TIMEOUT_MESSAGE =
  "Sherloc is taking longer than expected. Your planner inputs are still on this page — please try again. If this continues, reduce the prompt detail or try generating the strategy first, then creative variations.";
// Durable-path server timeout copy: generation runs on the server, so this
// never suggests trimming inputs ("reduce the prompt detail" is sync-only).
const STRATEGY_DURABLE_TIMEOUT_MESSAGE =
  "Sherloc's generation service took longer than expected on the server. Your planner inputs are still on this page — retry when ready.";
const STRATEGY_REFRESH_RECOVERY_MESSAGE =
  "Sherloc could not refresh the strategy yet. Your planner inputs are still on this page — please try again.";
const STRATEGY_REFRESH_REQUIRED_FIELDS_MESSAGE =
  "Add a campaign name, budget, goal type, and primary KPI before refreshing strategy.";
const CREATIVE_GENERATION_RECOVERY_MESSAGE =
  "Sherloc could not generate creative variations yet. Your planner inputs are still on this page — please try again.";
// Generate Creative Variations is one synchronous governed request: the
// generator (about 20s), then Sherloc's independent reviewer (about 70s per
// cycle; the creative tier allows one revision, so up to two reviews, each
// bounded by the reviewer's own 300s ceiling). On Oct 5 2026 two production
// runs needed 85s and 158s. This action used to borrow the strategy
// refresh's 65s ceiling, which predates that review, so the page stopped
// waiting while the backend kept working.
const CREATIVE_GENERATION_TIMEOUT_MS = 6 * 60 * 1000;
const CREATIVE_GENERATION_WAIT_LABEL = `${CREATIVE_GENERATION_TIMEOUT_MS / 60000} minutes`;
// Each creative failure says which stage stopped and what was not saved. A
// server reference is shown only when a response arrived; a timeout or a
// network failure has none, and none is invented.
const CREATIVE_CONFIGURATION_MESSAGE =
  "This page has no planner service address configured, so creative variations can’t be generated here. Retrying won’t help; the page’s configuration needs fixing.";
const CREATIVE_DRAFT_NOT_SAVED_MESSAGE =
  "Draft could not be saved — creative generation not started.";
const CREATIVE_TIMEOUT_MESSAGE = `Sherloc didn’t answer within ${CREATIVE_GENERATION_WAIT_LABEL}, so this page stopped waiting. Nothing on this page was changed — please try again.`;
const CREATIVE_TIMEOUT_MAY_STILL_SAVE_MESSAGE = `Sherloc didn’t answer within ${CREATIVE_GENERATION_WAIT_LABEL}, so this page stopped waiting. The server may still finish and save creative variations to this plan — reload the planner before generating again, so a second run isn’t started. Your planner inputs are still on this page.`;
const CREATIVE_NETWORK_FAILURE_MESSAGE =
  "Sherloc couldn’t reach the creative service — no response came back (it may be updating). Nothing on this page was changed; please try again in a moment.";
const CREATIVE_NETWORK_MAY_STILL_SAVE_MESSAGE =
  "The connection to Sherloc was lost before an answer came back. If the request reached Sherloc, it may still finish and save creative variations to this plan — reload the planner before generating again, so a second run isn’t started. Your planner inputs are still on this page.";
const CREATIVE_SAVED_UNUSABLE_MESSAGE =
  "Sherloc answered and saved to this plan, but its answer had no creative variations this page can show. Reload the planner to see what was saved.";
const CREATIVE_SESSION_EXPIRED_MESSAGE =
  "Your session has expired, so creative variations were not generated. Sign in again — your planner inputs are still on this page.";
const CREATIVE_FORBIDDEN_MESSAGE =
  "This account isn’t permitted to generate creative variations for this advertiser. Nothing was generated or saved.";
const CREATIVE_TARGET_MISSING_MESSAGE =
  "This plan’s saved record could not be found, so nothing was generated. Reload the planner and try again.";
const CREATIVE_PLATFORMS_REQUIRED_MESSAGE =
  "Select at least one platform for this plan before generating creative variations. Nothing was generated.";
const CREATIVE_INPUT_REQUIRED_MESSAGE =
  "Sherloc needs more planner context before writing creative variations — add a campaign name, goal, primary KPI, website or notes. Nothing was generated.";
const CREATIVE_URL_INVALID_MESSAGE =
  "Enter a valid http or https website URL before generating creative variations. Nothing was generated.";
const CREATIVE_PLAN_CHANGED_NOT_SAVED_MESSAGE =
  "Sherloc generated creative variations, but this plan changed while it was working, so they were not saved. The saved creative variations are unchanged — reload the planner, then generate again.";
const CREATIVE_GENERATED_NOT_SAVED_BY_SERVER_MESSAGE =
  "Sherloc generated creative variations but could not save them to this plan, so nothing was changed. Please try again.";
const CREATIVE_SAVE_UNCONFIRMED_MESSAGE =
  "Sherloc generated creative variations, but could not confirm whether they were saved to this plan. Reload the planner to check before generating again.";
const CREATIVE_SAVED_BUT_UNREADABLE_MESSAGE =
  "Sherloc saved creative variations to this plan, but its answer could not be read. Reload the planner to see them.";
const CREATIVE_SERVICE_FAILURE_MESSAGE =
  "Sherloc’s creative service had a problem, so no creative variations were saved. Please try again in a moment.";
const CREATIVE_EMPTY_RESPONSE_MESSAGE =
  "Sherloc’s answer contained no usable creative variations, so nothing on this page was changed. Please try again.";
const CREATIVE_SAVED_TO_DRAFT_NOTICE = "Generated and saved to this draft.";
const CREATIVE_SAVED_TO_CAMPAIGN_NOTICE = "Generated and saved to this campaign.";
const CREATIVE_NOT_SAVED_NOTICE =
  "Generated, not saved — these creative variations are only on this page until the draft saves.";
// The backend substitutes rule-based template copy when the AI path fails or
// Sherloc's independent review rejects the AI copy. That copy is never
// reviewed, so it is labeled wherever it is shown.
const CREATIVE_RULE_BASED_REASONS = Object.freeze({
  governance_rejected:
    "Sherloc’s independent review did not approve the AI-written copy for this brief",
  governance_review_failed: "Sherloc’s independent review could not be completed",
  platform_scope_violation: "the AI-written copy referenced a platform outside this plan",
  generation_unavailable: "AI-written copy was unavailable",
});
const STRATEGY_REFRESH_REQUIRED_FIELDS = Object.freeze([
  { key: "campaignName", label: "campaign name" },
  { key: "budget", label: "budget" },
  { key: "goalType", label: "goal type" },
  { key: "primaryKPI", label: "primary KPI" },
]);

const getMissingStrategyRefreshFields = (data = {}) =>
  STRATEGY_REFRESH_REQUIRED_FIELDS.filter(
    ({ key }) => !String(data?.[key] ?? "").trim()
  ).map(({ label }) => label);

// Short request-correlation id sent with each generation request (echoed by the
// backend in logs + the X-Request-Id header) so a failure can be traced.
const makePlannerRequestId = () =>
  `smp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// Specific, calm failure categories (no raw provider/technical detail).
const STRATEGY_VALIDATION_MESSAGE_PREFIX =
  "Sherloc needs a little more information before building the media plan:";
const STRATEGY_PROVIDER_FAILURE_MESSAGE =
  "Sherloc could not complete the media plan because the AI providers are temporarily unavailable. Your planner inputs are still on this page — please try again.";
// Fine provider categories from the backend (provider misconfig / bad model).
const STRATEGY_PROVIDER_NOT_CONFIGURED_MESSAGE =
  "Sherloc’s AI provider is not configured correctly. Your planner inputs are still on this page.";
const STRATEGY_MODEL_UNAVAILABLE_MESSAGE =
  "Sherloc’s selected AI model is unavailable. Your planner inputs are still on this page.";
const STRATEGY_INCOMPLETE_OUTPUT_MESSAGE =
  "Sherloc returned an incomplete media plan. Please try again.";
// The provider answered, but its answer could not be used, or it declined —
// never "the AI providers are temporarily unavailable" (campaign 53, ops
// b04677f2 / 3e24e17e).
const STRATEGY_PROVIDER_OUTPUT_MALFORMED_MESSAGE =
  "The AI provider returned a malformed structured response that could not be repaired, so nothing was saved. This was not an AI-provider availability failure. Your planner inputs are still on this page.";
const STRATEGY_STRUCTURE_INVALID_MESSAGE =
  "Sherloc received model output that did not satisfy the governed strategy structure, so nothing was saved. This was not an AI-provider availability failure. Your planner inputs are still on this page.";
const STRATEGY_PROVIDER_REFUSED_MESSAGE =
  "The AI provider declined to produce this strategy, so nothing was saved. This was not an AI-provider availability failure. Review the brief before starting a new generation.";
const STRATEGY_SAVE_WARNING_MESSAGE =
  "Sherloc generated a media plan, but it could not be saved yet. Please try again.";
// Shown when the user opens the hosted microsite with unsaved planner edits — the
// microsite renders the saved campaign, so unsaved changes would not appear.
const STRATEGY_MICROSITE_UNSAVED_CHANGES_MESSAGE =
  "Save or refresh the strategy before opening the microsite so it reflects the latest planner changes.";
// Shown when a loaded campaign has no readable strategy address yet (the
// backend assigns it when the campaign is read).
const STRATEGY_MICROSITE_ADDRESS_UNAVAILABLE_MESSAGE =
  "This campaign's strategy page address is not available yet. Reload the campaign and try again.";

const buildStrategyValidationMessage = (fields = []) => {
  const list = (Array.isArray(fields) ? fields : []).filter(Boolean);
  if (!list.length) return STRATEGY_REFRESH_REQUIRED_FIELDS_MESSAGE;
  return `${STRATEGY_VALIDATION_MESSAGE_PREFIX} ${list.join(", ")}.`;
};

// Unknown/unexpected category: never the bare generic line. Include a safe
// reference id (matches backend logs) so it can be traced.
const STRATEGY_UNKNOWN_FAILURE_PREFIX = "Sherloc could not complete the refresh yet.";
const buildUnknownFailureMessage = (requestId) => {
  const ref = String(requestId || "").trim();
  return ref
    ? `${STRATEGY_UNKNOWN_FAILURE_PREFIX} Reference: ${ref}. Your planner inputs are still on this page.`
    : `${STRATEGY_UNKNOWN_FAILURE_PREFIX} Your planner inputs are still on this page — please try again.`;
};

// A network/no-response failure: the fetch never received an HTTP response (e.g.
// the backend is restarting/deploying, offline, or blocked). This is distinct
// from a backend failure — there is NO server-side log/requestId for it, so the
// copy must not imply one. Detected because the errors we raise from a received
// response always carry a numeric `status`, while a failed fetch throws a
// TypeError ("Failed to fetch" / "Load failed" / "NetworkError").
const STRATEGY_NETWORK_FAILURE_MESSAGE =
  "Sherloc couldn’t reach the planner service just now — it may be updating. Your planner inputs are still on this page; please try again in a moment.";
const isPlannerNetworkError = (err) => {
  if (!err || isSherlocStrategyTimeoutError(err)) return false;
  if (typeof err.status === "number") return false;
  return (
    err instanceof TypeError ||
    /failed to fetch|networkerror|load failed|network request failed/i.test(
      String(err?.message || "")
    )
  );
};

const readSherlocStrategyResponse = async (res) => {
  const raw = await res.text().catch(() => "");
  if (!raw) return {};
  const payload = safeJsonParse(raw, null);
  return payload === null ? { __malformed: true } : payload;
};

const withSupportReference = (message, requestId) => {
  const reference = formatSupportReference(requestId);
  return reference ? `${message} ${reference}` : message;
};

// One failed Generate Creative Variations request, named by its stage. Built
// only from the status, the backend's machine-readable code and stage, and
// the server request id — never from a raw backend or provider message.
export const mapCreativeFailureToMessage = ({
  kind,
  status,
  code,
  stage,
  persisted,
  backendError,
  requestId,
  targetSent,
} = {}) => {
  if (kind === "timeout") {
    return targetSent ? CREATIVE_TIMEOUT_MAY_STILL_SAVE_MESSAGE : CREATIVE_TIMEOUT_MESSAGE;
  }
  // A dropped connection after the request was sent is not proof that the
  // backend stopped; it may still save to the plan the request named.
  if (kind === "network") {
    return targetSent ? CREATIVE_NETWORK_MAY_STILL_SAVE_MESSAGE : CREATIVE_NETWORK_FAILURE_MESSAGE;
  }
  if (kind === "saved_unreadable") {
    return withSupportReference(CREATIVE_SAVED_UNUSABLE_MESSAGE, requestId);
  }
  if (kind === "empty_response") {
    return withSupportReference(CREATIVE_EMPTY_RESPONSE_MESSAGE, requestId);
  }
  if (status === 401 || (status === 400 && /invalid user id/i.test(String(backendError || "")))) {
    return CREATIVE_SESSION_EXPIRED_MESSAGE;
  }
  if (status === 403) return CREATIVE_FORBIDDEN_MESSAGE;
  if (code === "selected_platforms_required" || code === "creative_platform_scope_required") {
    return CREATIVE_PLATFORMS_REQUIRED_MESSAGE;
  }
  if (code === "sherloc_stale_plan_response") return CREATIVE_PLAN_CHANGED_NOT_SAVED_MESSAGE;
  if (stage === "response" && persisted === true) {
    return withSupportReference(CREATIVE_SAVED_BUT_UNREADABLE_MESSAGE, requestId);
  }
  // A COMMIT lost in flight: the write may or may not have landed.
  if ((stage === "persistence" || stage === "response") && persisted === null) {
    return withSupportReference(CREATIVE_SAVE_UNCONFIRMED_MESSAGE, requestId);
  }
  if (stage === "persistence" || stage === "response") {
    return withSupportReference(CREATIVE_GENERATED_NOT_SAVED_BY_SERVER_MESSAGE, requestId);
  }
  if (status === 404 || code === "creative_campaign_not_found") return CREATIVE_TARGET_MISSING_MESSAGE;
  if (
    code === "creative_destination_url_invalid" ||
    (status === 400 && /destinationurl/i.test(String(backendError || "")))
  ) {
    return CREATIVE_URL_INVALID_MESSAGE;
  }
  if (status === 400 || code === "creative_input_required") return CREATIVE_INPUT_REQUIRED_MESSAGE;
  if (typeof status === "number" && status >= 500) {
    return withSupportReference(CREATIVE_SERVICE_FAILURE_MESSAGE, requestId);
  }
  return withSupportReference(CREATIVE_GENERATION_RECOVERY_MESSAGE, requestId);
};

// The label rule-based creative copy carries wherever it is shown: all of
// it when the AI path failed or was rejected, the padded tail otherwise.
export const describeRuleBasedCreativeVariations = (meta, count) => {
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return "";
  if (String(meta.providerUsed || "").toLowerCase() === "rule_based") {
    const reason =
      CREATIVE_RULE_BASED_REASONS[meta.fallbackReason] ||
      CREATIVE_RULE_BASED_REASONS.generation_unavailable;
    return `Rule-based starter copy, not AI-written: ${reason}. Review and edit before use.`;
  }
  const padded = Number(meta.ruleBasedVariantCount) || 0;
  const total = Number(count) || 0;
  if (padded <= 0 || total <= 0) return "";
  const which =
    padded === 1
      ? `Variant ${total} is`
      : `Variants ${Math.max(total - padded + 1, 1)}–${total} are`;
  return `${which} rule-based starter copy, not AI-written: Sherloc returned fewer AI-written variations than requested. Review and edit before use.`;
};

// Development-only, safe diagnostics for a failed refresh. No raw payload/intake/
// secrets — just the request + failure shape. Especially useful for "no HTTP
// response" failures (CORS/preflight/offline) where there is no backend log.
const PLANNER_GENERATION_ROUTE = "/api/sherloc/media-planner";
const logPlannerFailureDebug = (context = {}) => {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.warn("[SherlocMediaPlanner] refresh failure", {
    url: context.url ?? PLANNER_GENERATION_ROUTE,
    method: context.method ?? "POST",
    status: context.status ?? null,
    code: context.code ?? null,
    aborted: Boolean(context.aborted),
    errorName: context.errorName ?? null,
    errorMessage: context.errorMessage ?? null,
    elapsedMs: typeof context.elapsedMs === "number" ? context.elapsedMs : null,
    requestId: context.requestId ?? null,
    missingFields: Array.isArray(context.missingFields) ? context.missingFields : [],
  });
};

// Fields required before Sherloc can build a media plan. Goes beyond save-required
// fields to include platforms + at least one audience/geo note.
const getMissingPlannerGenerationFields = (data = {}) => {
  const missing = getMissingStrategyRefreshFields(data);
  const platforms = Array.isArray(data?.platforms) ? data.platforms.filter(Boolean) : [];
  if (!platforms.length) missing.push("selected platforms");
  const hasAudienceNote = ["geoTargetingNotes", "interests", "customTargeting"].some(
    (key) => String(data?.[key] ?? "").trim()
  );
  if (!hasAudienceNote) missing.push("an audience, geo, or targeting note");
  return missing;
};

// Map a backend failure to a specific calm message. Codes come from the backend's
// canonical category set; legacy codes + status fallbacks are also handled. The
// default no longer collapses to a generic line — it surfaces a safe reference id.
// Deterministic pre-generation scope refusal (422
// planner_prompt_scope_review_required): the backend's preflight found an
// instruction about a media platform outside the campaign's selected scope
// and refused BEFORE creating an operation row or calling a provider. The
// copy names what the refusal proves: nothing ran, nothing was billed, the
// brief is preserved, and which platform reference to look at. Byte-identical
// with Quantum's presentation of the same refusal.
//
// Platform names come from the backend's finding keys (the guard's own
// vocabulary). A key this surface cannot name keeps the generic wording rather
// than inventing a label.
const STRATEGY_SCOPE_REVIEW_PLATFORM_LABELS = Object.freeze({
  meta: "Meta",
  tiktok: "TikTok",
  snapchat: "Snapchat",
  reddit: "Reddit",
  linkedin: "LinkedIn",
  amazon: "Amazon",
  shopify: "Shopify",
  x: "X",
  dooh: "DOOH",
  cinema: "Cinema",
  audio: "Audio",
  ingame: "In-Game",
  email: "Email",
  youtube: "YouTube",
  ctv: "CTV",
  video: "Video",
  display: "Display",
  mobile: "Mobile",
  search: "Search",
});
const STRATEGY_SCOPE_REVIEW_PREFIX =
  "Sherloc paused before generation because the brief appears to reference a media platform outside this campaign's selected scope";
const STRATEGY_SCOPE_REVIEW_NOTHING_RAN = "No media plan was generated and no provider call was made.";
// The same refusal reached through a durable operation row (a worker-side
// preflight): terminal, so the way forward is a corrected new generation.
const STRATEGY_SCOPE_REVIEW_DURABLE_MESSAGE =
  `${STRATEGY_SCOPE_REVIEW_PREFIX}. ${STRATEGY_SCOPE_REVIEW_NOTHING_RAN} Review the flagged platform reference and start a new generation.`;

// One brief line reported on two surfaces (the composed prompt and the
// additionalNotes field) is ONE reference to review. Prefer the backend's
// de-duplicated view; fold the raw findings the same way when it is absent.
const uniqueScopeReviewFindings = (preflight) => {
  const unique = Array.isArray(preflight?.uniqueFindings) ? preflight.uniqueFindings : null;
  const raw = Array.isArray(preflight?.findings) ? preflight.findings : [];
  const source = unique || raw;
  const byKey = new Map();
  for (const finding of source) {
    if (!finding || typeof finding !== "object") continue;
    const platforms = Array.isArray(finding.detectedPlatforms)
      ? finding.detectedPlatforms.map((p) => String(p || "").toLowerCase()).filter(Boolean)
      : [];
    const key = `${String(finding.excerpt || "")}\u0000${[...platforms].sort().join(",")}`;
    const fields = [
      ...(Array.isArray(finding.fields) ? finding.fields : []),
      ...(finding.field ? [finding.field] : []),
    ].map(String);
    const existing = byKey.get(key);
    if (existing) {
      for (const field of fields) if (!existing.fields.includes(field)) existing.fields.push(field);
      continue;
    }
    byKey.set(key, { platforms, fields: [...new Set(fields)] });
  }
  return [...byKey.values()];
};

const buildScopeReviewMessage = (preflight) => {
  const findings = uniqueScopeReviewFindings(preflight);
  const keys = [...new Set(findings.flatMap((f) => f.platforms))];
  const labels = keys.map((key) => STRATEGY_SCOPE_REVIEW_PLATFORM_LABELS[key] || null);
  const named = labels.length > 0 && labels.every(Boolean) ? labels : [];
  const inNotes = findings.length > 0 && findings.every((f) => f.fields.includes("additionalNotes"));
  const location = inNotes ? "Additional Notes" : "the planner instructions";
  const scope = named.length ? `${STRATEGY_SCOPE_REVIEW_PREFIX} (${named.join(", ")}).` : `${STRATEGY_SCOPE_REVIEW_PREFIX}.`;
  const action = named.length
    ? `Review the flagged ${named.join(" and ")} reference${named.length > 1 ? "s" : ""} in ${location} and try again.`
    : "Review the flagged platform reference and try again.";
  return `${scope} ${STRATEGY_SCOPE_REVIEW_NOTHING_RAN} ${action}`;
};

const mapStrategyFailureToMessage = ({ status, code, category, missingFields, requestId, preflight } = {}) => {
  // Fine provider categories take precedence over the generic provider message.
  if (category === "anthropic_not_configured") return STRATEGY_PROVIDER_NOT_CONFIGURED_MESSAGE;
  if (category === "model_unavailable") return STRATEGY_MODEL_UNAVAILABLE_MESSAGE;
  // Deterministic pre-generation scope refusal: typed, provider-free, and
  // about the brief — never the generic reference line.
  if (code === "planner_prompt_scope_review_required") {
    return buildScopeReviewMessage(preflight);
  }
  // Governed revision refusals: each category is distinct and actionable —
  // a refusal must never read as "nothing happened".
  if (code === "revision_stale_version") {
    return "This plan changed since it was loaded (another session may have revised it). Reload the latest plan, review it, then re-apply your revision.";
  }
  if (code === "revision_reconciliation_failed") {
    return "The revision's budget figures don't reconcile (channel amounts, totals, and the agency structure must agree exactly). Nothing was changed — adjust the amounts in the revision text and try again.";
  }
  if (code === "revision_delta_invalid") {
    return "Sherloc couldn't interpret this revision into concrete plan changes. Nothing was changed — restate the channels, amounts, or dates more explicitly and try again.";
  }
  if (code === "revision_delta_unavailable" || code === "revision_delta_timeout") {
    return "The revision interpreter is temporarily unavailable. Nothing was changed — your revision text is preserved; try again shortly.";
  }
  if (code === "planner_service_timeout" || code === "strategy_refresh_timeout" || status === 504) {
    return STRATEGY_REFRESH_TIMEOUT_MESSAGE;
  }
  if (code === "missing_required_fields" || code === "invalid_budget" || status === 400) {
    return buildStrategyValidationMessage(missingFields);
  }
  // An answer that could not be used is not an unavailable provider: these
  // arrive as 502s and must be named before the generic provider rule.
  if (
    code === "provider_output_malformed" ||
    category === "provider_output_malformed" ||
    code === "ai_response_parse_failed"
  ) {
    return STRATEGY_PROVIDER_OUTPUT_MALFORMED_MESSAGE;
  }
  if (code === "sherloc_strategy_structure_invalid") {
    return STRATEGY_STRUCTURE_INVALID_MESSAGE;
  }
  if (code === "provider_output_refused" || category === "provider_output_refused") {
    return STRATEGY_PROVIDER_REFUSED_MESSAGE;
  }
  if (
    code === "provider_error" ||
    code === "planner_service_unavailable" ||
    status === 502 ||
    status === 503
  ) {
    return STRATEGY_PROVIDER_FAILURE_MESSAGE;
  }
  if (code === "incomplete_strategy" || code === "ai_response_empty" || code === "malformed_response") {
    return STRATEGY_INCOMPLETE_OUTPUT_MESSAGE;
  }
  if (code === "persistence_failed") {
    return STRATEGY_SAVE_WARNING_MESSAGE;
  }
  return buildUnknownFailureMessage(requestId);
};

// Campaign-neutral refine-plan placeholder. Example text must never suggest a
// specific platform (Meta/Facebook/Instagram/…) that may not be in the
// campaign's selection. Placeholder text is presentation-only: requests send
// only the controlled revisionPrompt state value, never this string.
export const SHERLOC_REVISION_REFINE_PLACEHOLDER =
  "Describe the budget, targeting, pacing, or channel changes you want Sherloc to evaluate.";

// Labeled backend-refusal state: clients may be refused regeneration past
// DRAFT (403/409). The backend's typed refusal message is surfaced verbatim
// under an explicit label instead of collapsing into a generic error.
const STRATEGY_GENERATION_REFUSED_PREFIX = "Sherloc declined to start this generation:";
const buildGenerationRefusalMessage = (message) => {
  const text = String(message || "").trim();
  return text
    ? `${STRATEGY_GENERATION_REFUSED_PREFIX} ${text}`
    : `${STRATEGY_GENERATION_REFUSED_PREFIX} the backend refused the request. Your planner inputs are still on this page.`;
};

// Durable-operation failure copy, keyed by the operation's failureClass. The
// sync timeout sentence ("reduce the prompt detail") is NEVER used here — a
// durable failure is server-side and retry-shaped, not input-size-shaped.
const mapSherlocOperationFailureToMessage = (operation = {}) => {
  const failureClass = String(operation.failureClass || "").trim();
  if (
    failureClass === "planner_service_timeout" ||
    failureClass === "strategy_refresh_timeout" ||
    failureClass === "generation_timeout"
  ) {
    return STRATEGY_DURABLE_TIMEOUT_MESSAGE;
  }
  const mapped = mapStrategyFailureToMessage({
    code: failureClass || null,
    missingFields: null,
    requestId: operation.id || null,
  });
  if (!mapped.startsWith(STRATEGY_UNKNOWN_FAILURE_PREFIX)) return mapped;
  // Unmapped class: surface the backend's typed failure message (governed
  // refusals become FAILED_TERMINAL with a calm, typed message) before the
  // generic reference copy.
  const failureMessage = String(operation.failureMessage || "").trim();
  return failureMessage || mapped;
};

// ---------------------------------------------------------------------------
// Fine-grained durable failure categories (additive backend contract).
//
// The serialized operation carries `failureCategory` (same sanitized value as
// `failureClass`), a governed retry budget `maxAttempts`, and
// `retryAfterSeconds` (> 0 while a governed backoff refuses retries). The
// failure card selects its copy from the category; retry AVAILABILITY truth
// comes ONLY from the operation fields — never from the copy map.
// ---------------------------------------------------------------------------

const SHERLOC_OPERATION_FAILURE_CATEGORY_COPY = Object.freeze({
  provider_rate_limited:
    "The AI provider's rate limit was reached. Retry after a short wait.",
  provider_quota_exceeded:
    "The AI provider's usage quota was reached. Retry after a governed delay.",
  provider_output_truncated:
    "The strategy exceeded the provider's configured output limit. Engineering remediation is required before retrying.",
  anthropic_not_configured:
    "Sherloc's AI provider authentication is unavailable. Operator action required.",
  provider_not_configured:
    "Sherloc's AI provider authentication is unavailable. Operator action required.",
  provider_not_allowed:
    "Sherloc's AI provider is not permitted in this environment. Operator action required.",
  provider_authentication_failed:
    "Sherloc's AI provider access requires configuration. Retrying the same request will not succeed.",
  provider_invalid_request:
    "Sherloc's provider request configuration is invalid. Engineering remediation is required before retrying.",
  model_unavailable:
    "Sherloc's selected AI model is unavailable. Engineering remediation required.",
  governance_reviewer_malformed:
    "The independent governance reviewer returned an invalid result. No strategy was published.",
  governance_rejected:
    "The independent governance review did not approve this strategy. No strategy was published.",
  ai_governance_rejected:
    "The independent governance review did not approve this strategy. No strategy was published.",
  operation_superseded:
    "This generation was superseded before its result could be committed. No strategy was published.",
  planner_service_timeout:
    "Sherloc took longer than expected. A safe retry is available.",
  ai_response_parse_failed:
    "The AI provider returned an invalid structured response and automatic repair was exhausted. A safe retry is available.",
  // The provider ANSWERED, with structured text that did not parse even after
  // the bounded syntax-only repair (and after any healthy fallback).
  provider_output_malformed:
    "The AI provider returned a malformed structured response that could not be repaired, so nothing was saved. This was not an AI-provider availability failure; a fresh generation may be started manually.",
  // Sherloc's deterministic raw-structure gate refused the generated strategy.
  sherloc_strategy_structure_invalid:
    "Sherloc received model output that did not satisfy the governed strategy structure. Nothing was saved. This was not an AI-provider availability failure, and any fresh attempt requires manual authorization.",
  // The provider declined to answer: a policy outcome.
  provider_output_refused:
    "The AI provider declined to produce this strategy, so nothing was saved. This was not an AI-provider availability failure; review the brief before starting a new generation.",
  incomplete_strategy:
    "The AI provider returned an incomplete strategy. A safe retry is available.",
  // Terminal classes (FAILED_TERMINAL): the words below never gate retry —
  // availability truth stays with the operation fields.
  // The worker's deterministic prompt-scope preflight refused before any
  // provider call (the route normally refuses this earlier, with 422, before a
  // row exists). Same copy as the page-level refusal; the way forward is a
  // corrected new generation.
  planner_prompt_scope_review_required: STRATEGY_SCOPE_REVIEW_DURABLE_MESSAGE,
  assembly_contract_violation:
    "The strategy was not published. The assembled strategy artifact was structurally incomplete — it was missing the required per-platform detail envelope. Your campaign inputs are correct and preserved; engineering remediation is required before this generation can run again.",
  operation_attempt_budget_exhausted:
    "This run was interrupted repeatedly and the governed attempt budget is exhausted. No strategy was published. Your planner inputs are preserved; engineering review is required.",
});

const SHERLOC_OPERATION_FAILURE_DEFAULT_RETRYABLE_COPY =
  "The AI provider is temporarily unavailable. A safe retry is available.";
const SHERLOC_OPERATION_FAILURE_DEFAULT_TERMINAL_COPY =
  "The AI provider returned an error.";
// Reassurance line kept alongside the category copy on every failure card.
const SHERLOC_OPERATION_FAILURE_INPUTS_NOTE =
  "Your planner inputs are still on this page.";
// POST /:id/retry refusal (409 operation_stale_dependencies): campaign inputs
// changed since the failed attempt, so the same operation may not resume.
const SHERLOC_OPERATION_STALE_DEPENDENCIES_MESSAGE =
  "Campaign inputs changed since this attempt — start a new generation.";

export const getSherlocOperationFailureCategory = (operation = {}) =>
  String(operation?.failureCategory || operation?.failureClass || "").trim();

// Retry-shaped: this operation may still be retried at some point — status is
// FAILED_RETRYABLE, the backend did not mark it non-retryable, and the
// governed attempt budget (maxAttempts, when present) is not exhausted.
export const isSherlocOperationRetryEligible = (operation = {}) => {
  if (
    getSherlocGenerationOperationStatus(operation) !==
    SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE
  ) {
    return false;
  }
  if (operation.retryable === false) return false;
  const maxAttempts = operation.maxAttempts;
  if (
    maxAttempts != null &&
    Number(operation.attemptCount ?? 0) >= Number(maxAttempts)
  ) {
    return false;
  }
  return true;
};

export const getSherlocOperationRetryBackoffSeconds = (operation = {}) => {
  const seconds = Number(operation?.retryAfterSeconds);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
};

// Retry available NOW: eligible AND no governed backoff window is active.
export const isSherlocOperationRetryAvailable = (operation = {}) =>
  isSherlocOperationRetryEligible(operation) &&
  getSherlocOperationRetryBackoffSeconds(operation) === 0;

// Card copy resolution order:
//   1. fine failureCategory/failureClass copy map (own properties only);
//   2. legacy class-specific copy (governed revision refusals, durable timeout
//      classes, typed backend failureMessage passthrough) — preserved so those
//      labeled states do not regress;
//   3. the generic default, split by retry eligibility (operation truth).
export const resolveSherlocOperationFailureCategoryCopy = (operation = {}) => {
  const category = getSherlocOperationFailureCategory(operation);
  if (
    category &&
    Object.prototype.hasOwnProperty.call(
      SHERLOC_OPERATION_FAILURE_CATEGORY_COPY,
      category
    )
  ) {
    return SHERLOC_OPERATION_FAILURE_CATEGORY_COPY[category];
  }
  if (
    String(operation?.generation?.failure?.stage || "").toUpperCase() === "REVIEW" &&
    ["provider_error", "provider_timeout", "planner_service_unavailable", "http_503"].includes(
      category
    )
  ) {
    return "The independent governance reviewer is temporarily unavailable. A safe retry is available.";
  }
  if (category) {
    const legacy = mapSherlocOperationFailureToMessage(operation);
    if (
      legacy &&
      legacy !== STRATEGY_PROVIDER_FAILURE_MESSAGE &&
      !legacy.startsWith(STRATEGY_UNKNOWN_FAILURE_PREFIX)
    ) {
      return legacy;
    }
  }
  return isSherlocOperationRetryEligible(operation)
    ? SHERLOC_OPERATION_FAILURE_DEFAULT_RETRYABLE_COPY
    : SHERLOC_OPERATION_FAILURE_DEFAULT_TERMINAL_COPY;
};

// Canonical durable-operation disposition. The backend now serializes a
// `disposition` on both the polled operation payload and the campaign GET's
// strategy_provenance.latestOperation; older payloads lack it, so the same
// verdict is derived from status + failureClass. One lifecycle, shared by the
// operation card and the stale-strategy banner — the banner must never say
// GENERATING after the tracked operation terminally failed.
export const resolveSherlocOperationDisposition = (operation = {}) => {
  const explicit = String(operation?.disposition || "").trim().toUpperCase();
  if (explicit) return explicit;
  const status = getSherlocGenerationOperationStatus(operation);
  if (SHERLOC_GENERATION_ACTIVE_STATUSES.includes(status)) return "IN_PROGRESS";
  if (status === SHERLOC_GENERATION_OPERATION_STATUSES.COMPLETED) return "COMPLETED";
  if (status === SHERLOC_GENERATION_OPERATION_STATUSES.CANCELLED) return "CANCELLED";
  if (status === SHERLOC_GENERATION_OPERATION_STATUSES.SUPERSEDED) return "SUPERSEDED";
  if (status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_TERMINAL) {
    const failureClass = String(operation?.failureClass || "").trim();
    return failureClass === "ai_governance_rejected" ||
      failureClass === "governance_rejected"
      ? "REJECTED_REGENERATION_REQUIRED"
      : "FAILED_TERMINAL";
  }
  if (status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE) {
    return "FAILED_RETRYABLE";
  }
  return null;
};

// Provenance "Latest generation attempt" label, keyed by the canonical
// disposition (which already folds failureClass into the governance-rejected
// verdict — callers must not append the failure-class parenthetical again).
export const formatSherlocProvenanceAttemptStatus = (operation = {}) => {
  const failureClass = String(operation?.failureClass || "").trim();
  const failureSuffix = failureClass ? ` (${failureClass})` : "";
  switch (resolveSherlocOperationDisposition(operation)) {
    case "REJECTED_REGENERATION_REQUIRED":
      return "REJECTED — REGENERATION REQUIRED";
    case "FAILED_RETRYABLE":
      return `FAILED — RETRY AVAILABLE${failureSuffix}`;
    case "FAILED_TERMINAL":
      return `FAILED${failureSuffix}`;
    case "IN_PROGRESS":
      return "GENERATING";
    case "COMPLETED":
      return "COMPLETED";
    case "CANCELLED":
      return "CANCELLED";
    case "SUPERSEDED":
      return "SUPERSEDED";
    default:
      return String(operation?.status || "UNKNOWN");
  }
};

const createStrategyRefreshTimeoutError = () => {
  const err = new Error(STRATEGY_REFRESH_TIMEOUT_MESSAGE);
  err.name = "SherlocStrategyRefreshTimeoutError";
  err.code = "strategy_refresh_timeout";
  err.status = 504;
  return err;
};

const runWithSherlocStrategyRefreshTimeout = (
  work,
  { timeoutMs = STRATEGY_REFRESH_TIMEOUT_MS, onTimeout } = {}
) =>
  new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      onTimeout?.();
      reject(createStrategyRefreshTimeoutError());
    }, timeoutMs);

    const settle = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      callback(value);
    };

    Promise.resolve()
      .then(work)
      .then((value) => settle(resolve, value))
      .catch((err) => settle(reject, err));
  });

// The legacy sync-response readers were removed with the durable operations
// migration: the operations module parses acknowledgement/poll payloads, and
// COMPLETED results reuse getUsableSherlocStrategyText below.

const getUsableSherlocStrategyText = (payload = {}) => {
  const candidates = [
    payload?.strategy,
    payload?.strategy_text,
    payload?.strategyText,
    payload?.plan,
    payload?.media_plan,
    payload?.mediaPlan,
    payload?.overview,
    payload?.mediaPlanOverview,
    payload?.media_plan_overview,
    payload?.result?.strategy,
    payload?.result?.strategy_text,
    payload?.result?.overview,
    payload?.data?.strategy,
    payload?.data?.strategy_text,
    payload?.data?.overview,
    payload?.campaign?.strategy,
    payload?.campaign?.strategy_text,
    payload?.campaign?.strategyText,
    payload?.campaign?.overview,
  ];

  for (const candidate of candidates) {
    const text = typeof candidate === "string" ? candidate.trim() : "";
    if (text) return text;
  }

  // Sectioned responses: join section bodies into a single overview string.
  const sections = Array.isArray(payload?.sections)
    ? payload.sections
    : Array.isArray(payload?.mediaPlanOverview?.sections)
    ? payload.mediaPlanOverview.sections
    : null;
  if (sections && sections.length) {
    const joined = sections
      .map((section) => {
        if (typeof section === "string") return section.trim();
        const heading = String(section?.title || section?.heading || "").trim();
        const bodyText = String(section?.body || section?.content || section?.text || "").trim();
        return [heading, bodyText].filter(Boolean).join("\n");
      })
      .filter(Boolean)
      .join("\n\n")
      .trim();
    if (joined) return joined;
  }

  return "";
};

const normalizePlannerGoalTypeValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const compact = raw
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[_-]+/g, " ")
    .replace(/\s*\/\s*/g, " / ")
    .replace(/\s+/g, " ")
    .trim();

  const aliasMap = new Map([
    ["awareness", "awareness"],
    ["brand awareness", "awareness"],
    ["reach", "awareness"],
    ["reach awareness", "awareness"],
    ["reach / awareness", "awareness"],
    ["consideration", "consideration"],
    ["engagement", "consideration"],
    ["consideration engagement", "consideration"],
    ["consideration / engagement", "consideration"],
    ["traffic", "traffic"],
    ["conversion", "conversions"],
    ["conversions", "conversions"],
    ["sales", "conversions"],
    ["conversion sales", "conversions"],
    ["conversions / sales", "conversions"],
    ["ticket sales", "ticket_sales"],
    ["mixed", "mixed"],
    ["full funnel", "mixed"],
    ["mixed full funnel", "mixed"],
    ["mixed / full funnel", "mixed"],
    ["qualified traffic", "qualified_traffic"],
    ["lead generation", "lead_generation"],
    ["inventory demand", "inventory_demand"],
    ["offer engagement", "offer_engagement"],
    ["service campaign", "service_campaign"],
    ["model launch", "model_launch"],
    ["retargeting", "retargeting"],
    ["purchase efficiency", "purchase_efficiency"],
    ["roas", "roas"],
    ["cart recovery", "cart_recovery"],
    ["new customer acquisition", "new_customer_acquisition"],
    ["retention", "retention"],
    ["retention repeat purchase", "retention"],
    ["retention / repeat purchase", "retention"],
    ["promo lift", "promo_lift"],
    ["booking consideration", "booking_consideration"],
    ["reservation inquiries", "reservation_inquiries"],
    ["destination demand", "destination_demand"],
    ["seasonal lift", "seasonal_lift"],
    ["loyalty", "loyalty"],
    ["loyalty returning guests", "loyalty"],
    ["loyalty / returning guests", "loyalty"],
  ]);

  return aliasMap.get(compact) || raw;
};

// Explicit labeled generic failure: when an error does not match any known
// category, the panel still labels the failure instead of rendering the
// default "fill out the campaign inputs" placeholder over a real error.
const STRATEGY_PANEL_GENERIC_FAILURE_MESSAGE =
  "Sherloc hit an unexpected error while preparing the strategy. Your planner inputs are still on this page — please try again.";

const getStrategyRefreshPanelMessage = (message) => {
  const text = String(message || "").trim();
  if (!text) return "";
  if (text === "Draft save is not confirmed. Check its outcome before generating.") return text;
  // Dynamic validation copy ("Sherloc needs a little more information: …").
  if (text.startsWith(STRATEGY_VALIDATION_MESSAGE_PREFIX)) return text;
  // Dynamic unknown-failure copy (carries a reference id).
  if (text.startsWith(STRATEGY_UNKNOWN_FAILURE_PREFIX)) return text;
  // Labeled backend-refusal states pass through verbatim.
  if (text.startsWith(STRATEGY_GENERATION_REFUSED_PREFIX)) return text;
  return [
    STRATEGY_REFRESH_TIMEOUT_MESSAGE,
    STRATEGY_DURABLE_TIMEOUT_MESSAGE,
    STRATEGY_REFRESH_RECOVERY_MESSAGE,
    STRATEGY_REFRESH_REQUIRED_FIELDS_MESSAGE,
    STRATEGY_PROVIDER_FAILURE_MESSAGE,
    STRATEGY_PROVIDER_NOT_CONFIGURED_MESSAGE,
    STRATEGY_MODEL_UNAVAILABLE_MESSAGE,
    STRATEGY_INCOMPLETE_OUTPUT_MESSAGE,
    STRATEGY_PROVIDER_OUTPUT_MALFORMED_MESSAGE,
    STRATEGY_STRUCTURE_INVALID_MESSAGE,
    STRATEGY_PROVIDER_REFUSED_MESSAGE,
    STRATEGY_SAVE_WARNING_MESSAGE,
    STRATEGY_NETWORK_FAILURE_MESSAGE,
  ].includes(text)
    ? text
    : // Unknown errors render a labeled generic failure — never an
      // unlabeled-empty panel.
      STRATEGY_PANEL_GENERIC_FAILURE_MESSAGE;
};

const isSherlocStrategyTimeoutError = (err) =>
  err?.code === "strategy_refresh_timeout" ||
  err?.name === "SherlocStrategyRefreshTimeoutError" ||
  err?.name === "AbortError";

// Test seam for the pure failure-classification logic (no React/DOM needed).
export const __sherlocPlannerFailureTesting = {
  isPlannerNetworkError,
  isSherlocStrategyTimeoutError,
  mapStrategyFailureToMessage,
  buildScopeReviewMessage,
  STRATEGY_SCOPE_REVIEW_DURABLE_MESSAGE,
  mapSherlocOperationFailureToMessage,
  getSherlocOperationFailureCategory,
  isSherlocOperationRetryEligible,
  isSherlocOperationRetryAvailable,
  getSherlocOperationRetryBackoffSeconds,
  resolveSherlocOperationFailureCategoryCopy,
  SHERLOC_OPERATION_FAILURE_CATEGORY_COPY,
  SHERLOC_OPERATION_FAILURE_DEFAULT_RETRYABLE_COPY,
  SHERLOC_OPERATION_FAILURE_DEFAULT_TERMINAL_COPY,
  SHERLOC_OPERATION_FAILURE_INPUTS_NOTE,
  SHERLOC_OPERATION_STALE_DEPENDENCIES_MESSAGE,
  buildGenerationRefusalMessage,
  getStrategyRefreshPanelMessage,
  STRATEGY_PROVIDER_OUTPUT_MALFORMED_MESSAGE,
  STRATEGY_STRUCTURE_INVALID_MESSAGE,
  STRATEGY_PROVIDER_REFUSED_MESSAGE,
  buildUnknownFailureMessage,
  buildStrategyValidationMessage,
  getMissingPlannerGenerationFields,
  STRATEGY_DURABLE_TIMEOUT_MESSAGE,
  STRATEGY_PANEL_GENERIC_FAILURE_MESSAGE,
  STRATEGY_GENERATION_REFUSED_PREFIX,
  STRATEGY_NETWORK_FAILURE_MESSAGE,
  STRATEGY_REFRESH_TIMEOUT_MESSAGE,
  STRATEGY_PROVIDER_FAILURE_MESSAGE,
  STRATEGY_PROVIDER_NOT_CONFIGURED_MESSAGE,
  STRATEGY_MODEL_UNAVAILABLE_MESSAGE,
  STRATEGY_INCOMPLETE_OUTPUT_MESSAGE,
  STRATEGY_SAVE_WARNING_MESSAGE,
  STRATEGY_MICROSITE_UNSAVED_CHANGES_MESSAGE,
};

// Durable-operation status card: one explicit labeled state per operation
// status (Queued / Generating / Validating / Saving / failure / cancelled /
// superseded). Active stages show elapsed seconds, the last server update,
// and the leave-safe note; FAILED_RETRYABLE offers a Retry that resumes the
// SAME server-side operation (no new generation is started client-side).
export const SherlocDurableGenerationStatusCard = ({
  operation,
  nowMs = Date.now(),
  onRetry,
  retryPending = false,
}) => {
  if (!operation) return null;
  const status = getSherlocGenerationOperationStatus(operation);
  const active = isSherlocGenerationOperationActive(operation);
  const stageLabel = getSherlocGenerationOperationStageLabel(operation);
  const elapsedLabel = formatSherlocGenerationElapsedSeconds(
    getSherlocGenerationOperationElapsedMs(operation, {
      nowMs,
      receivedAtMs: operation.__receivedAtMs ?? null,
    })
  );
  const lastUpdateLabel = formatSherlocGenerationRelativeTime(
    getSherlocGenerationOperationLastUpdateAt(operation),
    nowMs
  );
  const failed =
    status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE ||
    status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_TERMINAL;
  // Fine failure-category copy (failureCategory || failureClass). Copy is
  // presentation-only: retry availability is decided solely by the operation
  // fields below.
  const failureCopy = failed
    ? resolveSherlocOperationFailureCategoryCopy(operation)
    : "";
  const retryEligible = failed && isSherlocOperationRetryEligible(operation);
  const retryBackoffSeconds = failed
    ? getSherlocOperationRetryBackoffSeconds(operation)
    : 0;
  const retryable =
    failed &&
    isSherlocOperationRetryAvailable(operation) &&
    typeof onRetry === "function";
  const attemptCount = Number(operation.attemptCount);
  const maxAttempts = Number(operation.maxAttempts);
  // Truthful operation-attempt copy. Historical rows can carry an
  // attemptCount above the governed budget (interrupted-run lease reclaims
  // bumped it before the backend bounded claims); the backend serializes
  // `attemptBudgetExceeded` for those rows, with count > budget as the
  // fallback for older snapshots. Such a row must never render
  // "Attempt 9 of 3" — it gets explicit exceeds-budget copy instead.
  const attemptBudgetExceeded =
    operation.attemptBudgetExceeded === true ||
    (Number.isFinite(maxAttempts) &&
      maxAttempts > 0 &&
      Number.isFinite(attemptCount) &&
      attemptCount > maxAttempts);
  const attemptLabel =
    Number.isFinite(attemptCount) && attemptCount > 0
      ? Number.isFinite(maxAttempts) && maxAttempts > 0
        ? attemptBudgetExceeded
          ? `Operation attempt ${attemptCount} — exceeds the ${maxAttempts}-attempt budget (interrupted-run reclaims)`
          : `Operation attempt ${attemptCount} of ${maxAttempts}`
        : `Operation attempt ${attemptCount}`
      : "";
  const failedAtLabel = formatSherlocGenerationRelativeTime(
    operation.failedAt || getSherlocGenerationOperationLastUpdateAt(operation),
    nowMs
  );
  // Abbreviated support reference: first 8 chars of the operation id.
  const supportReference = String(
    operation.supportReference || operation.id || ""
  ).slice(0, 8);
  const failureCategory = getSherlocOperationFailureCategory(operation);
  const providerConfigurationFailure = [
    "anthropic_not_configured",
    "provider_not_configured",
    "provider_not_allowed",
    "provider_authentication_failed",
    "provider_invalid_request",
    "model_unavailable",
    "provider_output_truncated",
  ].includes(failureCategory);
  const retryBudgetExhausted =
    status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE &&
    Number.isFinite(maxAttempts) &&
    maxAttempts > 0 &&
    Number.isFinite(attemptCount) &&
    attemptCount >= maxAttempts;

  // Adaptive-capacity staged progress (sherloc_generation_capacity_v1).
  // Client card shows ONLY the friendly progress label + a stage counter —
  // never tier/mode/escalation and never anything from generation.diagnostics
  // (token budgets are internal; the backend strips diagnostics for client
  // viewers, and this card would not render them regardless).
  const generationProgress = operation.generation?.progress || null;
  const generationProgressLabel = String(generationProgress?.label || "").trim();
  const progressCompletedStages = Number(generationProgress?.completedStages);
  const progressTotalStages = Number(generationProgress?.totalStages);
  const stageCounterLabel =
    Number.isFinite(progressCompletedStages) &&
    Number.isFinite(progressTotalStages) &&
    progressTotalStages > 1
      ? `Stage ${Math.min(progressCompletedStages + 1, progressTotalStages)} of ${progressTotalStages}`
      : "";

  return (
    <div
      className={`sherloc-durable-generation-card is-${String(status || "unknown").toLowerCase()}`}
      role="status"
      aria-live="polite"
      data-generation-operation-status={status || "UNKNOWN"}
    >
      <div className="sherloc-durable-generation-stage">
        {active ? (
          <span className="sherloc-generation-loading-orb" aria-hidden="true" />
        ) : null}
        <span className="sherloc-durable-generation-stage-label">{stageLabel}</span>
      </div>
      {active ? (
        <>
          <div className="sherloc-durable-generation-meta">
            <span className="sherloc-durable-generation-elapsed">
              Elapsed {elapsedLabel}
            </span>
            <span className="sherloc-durable-generation-updated">
              Last update {lastUpdateLabel}
            </span>
          </div>
          <div className="sherloc-durable-generation-note">
            {SHERLOC_GENERATION_BACKGROUND_NOTE}
          </div>
          {generationProgressLabel ? (
            <div
              className="sherloc-durable-generation-note"
              data-sherloc-generation-progress
            >
              {generationProgressLabel}
              {stageCounterLabel ? ` · ${stageCounterLabel}` : ""}
            </div>
          ) : null}
        </>
      ) : null}
      {failureCopy ? (
        <div className="sherloc-durable-generation-failure">{failureCopy}</div>
      ) : null}
      {failed ? (
        <>
          <div className="sherloc-durable-generation-note">
            {SHERLOC_OPERATION_FAILURE_INPUTS_NOTE}
          </div>
          <div className="sherloc-durable-generation-meta sherloc-durable-generation-failure-meta">
            {attemptLabel ? (
              <span className="sherloc-durable-generation-attempts">
                {attemptLabel}
              </span>
            ) : null}
            <span className="sherloc-durable-generation-updated">
              Last update {failedAtLabel}
            </span>
            {supportReference ? (
              <span className="sherloc-durable-generation-support-ref">
                Support reference {supportReference}
              </span>
            ) : null}
          </div>
        </>
      ) : null}
      {status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_TERMINAL ? (
        <div className="sherloc-durable-generation-terminal-note">
          {providerConfigurationFailure
            ? "This generation cannot be retried as-is. Provider configuration must be corrected before starting again."
            : "This generation cannot be retried as-is. Review the governed inputs, then start a new generation."}
        </div>
      ) : null}
      {status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE &&
      !retryEligible ? (
        <div className="sherloc-durable-generation-terminal-note">
          {retryBudgetExhausted
            ? "Automatic retries are exhausted for this generation. Adjust the plan inputs, then generate again."
            : "This failure requires operator action before another attempt can run."}
        </div>
      ) : null}
      {retryEligible && retryBackoffSeconds > 0 ? (
        <div className="sherloc-durable-generation-terminal-note sherloc-durable-generation-backoff-note">
          Retry available in ~{retryBackoffSeconds}s
        </div>
      ) : null}
      {status === SHERLOC_GENERATION_OPERATION_STATUSES.SUPERSEDED ? (
        <div className="sherloc-durable-generation-terminal-note">
          A newer generation replaced this one. The latest saved plan governs.
        </div>
      ) : null}
      {status === SHERLOC_GENERATION_OPERATION_STATUSES.CANCELLED ? (
        <div className="sherloc-durable-generation-terminal-note">
          This generation was cancelled before it completed. Nothing was saved.
        </div>
      ) : null}
      {retryable ? (
        <button
          type="button"
          className="mediaplanner-save-btn sherloc-durable-generation-retry-btn"
          onClick={onRetry}
          disabled={retryPending}
        >
          {retryPending ? "Retrying…" : "Retry Generation"}
        </button>
      ) : null}
    </div>
  );
};

const logSherlocStrategyRefreshFailure = (category, context = {}) => {
  console.warn("[SherlocMediaPlanner] Strategy refresh failed", {
    route: "/api/sherloc/media-planner",
    page: "SherlocMediaPlanner",
    category,
    advertiserId: context.advertiserId ?? null,
    campaignId: context.campaignId ?? null,
    missingRequiredFields: context.missingFields || undefined,
    status: context.status || undefined,
    providerFailureCategory: context.providerFailureCategory || undefined,
  });
};

const newPlannerId = () =>
  `smp_client_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;

const normalizeName = (s) => String(s || "").trim().toLowerCase();

/**
 * ✅ Stable advertiser key used ONLY for:
 * - Filtering draft dropdown
 * - Storing "active draft id" per advertiser
 */
const getAdvertiserKey = (advertiserId, advertiserName) => {
  const id = advertiserId != null && String(advertiserId).trim() ? String(advertiserId).trim() : "";
  if (id) return `id_${id}`;
  const name = String(advertiserName || "").trim();
  if (name) return `name_${normalizeName(name)}`;
  return "unknown";
};

export const hasSelectedPlannerAdvertiser = (advKey) => {
  const normalized = String(advKey || "").trim();
  return Boolean(normalized) && normalized !== "unknown";
};

export const isPlannerFlowActionDisabled = ({ plannerUiLocked = false, advKey = "" } = {}) =>
  Boolean(plannerUiLocked) || !hasSelectedPlannerAdvertiser(advKey);

const makeActiveKeyForAdvertiser = (advKey) => `${ACTIVE_PLANNER_ID_KEY_BASE}_${advKey}`;
const makeChosenPlanKey = (accountKey, advKey) =>
  accountKey && advKey && advKey !== "unknown" ? `${CHOSEN_PLANNER_KEY_BASE}_${accountKey}_${advKey}` : "";

/**
 * ✅ UPDATED ORDER:
 * Facebook, Instagram, TikTok, X, YouTube, Google, Rumble, Truth Social, Reddit,
 * Amazon, Shopify, LinkedIn
 * then Programmatic stack (CTV → Video → Display → Mobile → Audio → In-Game → DOOH → Email).
 */
const PLATFORM_OPTIONS = [
  // Social / Walled Gardens
  { key: "facebook", label: "Facebook", logo: facebookLogo },
  { key: "instagram", label: "Instagram", logo: instagramLogo },
  { key: "tiktok", label: "TikTok", logo: tiktokLogo },
  { key: "x", label: "X", logo: xLogo },
  { key: "youtube", label: "YouTube", logo: youtubeLogo },
  { key: "google_search", label: "Google Search", logo: googleLogo },
  { key: "chatgpt_search", label: "ChatGPT Search", logo: openaiLogo },
  { key: "rumble", label: "Rumble", logo: rumbleLogo },
  { key: "truth_social", label: "Truth Social", logo: truthSocialLogo },
  { key: "reddit", label: "Reddit", logo: redditLogo },
  { key: "amazon", label: "Amazon", logo: amazonLogo },
  { key: "yahoo", label: "Yahoo DSP", logo: yahooLogo },
  // Legacy-only in Cortex (see CLIENT_UNSELECTABLE_PLATFORM_KEYS). Plans that
  // stored "Viant DSP (Display)" still resolve to this key below.
  { key: "viant", label: "Viant", logo: viantLogo, logoShape: "wordmark" },
  { key: "shopify", label: "Shopify", logo: shopifyLogo },
  { key: "linkedin", label: "LinkedIn", logo: linkedinLogo },

  // Programmatic / Cphere Programmatic downstream
  { key: "ctv", label: "CTV", logo: ctvLogo },
  { key: "video", label: "Video", logo: videoLogo },
  { key: "display", label: "Display", logo: displayLogo },
  { key: "mobile", label: "Mobile", logo: mobileLogo },
  { key: "audio", label: "Audio", logo: audioLogo },
  { key: "ingame", label: "In-Game", logo: ingameLogo },
  { key: "dooh", label: "DOOH", logo: doohLogo },
  { key: "email", label: "Email", logo: emailLogo },
];

// Viant is a Quantum-only preparation capability. It stays in PLATFORM_OPTIONS
// so saved plans that already contain it keep their key, label and order, but
// a Cortex user can never add it to a new or existing plan.
const CLIENT_UNSELECTABLE_PLATFORM_KEYS = new Set(["viant"]);
export const isClientSelectablePlannerPlatform = (platform) =>
  !CLIENT_UNSELECTABLE_PLATFORM_KEYS.has(normalizePlannerPlatformOrderKey(platform));
export const clientVisiblePlannerPlatformOptions = (selectedPlatforms = []) => {
  const selected = new Set(
    (Array.isArray(selectedPlatforms) ? selectedPlatforms : []).map(normalizePlannerPlatformOrderKey)
  );
  return PLATFORM_OPTIONS.filter(
    (platform) => isClientSelectablePlannerPlatform(platform.key) || selected.has(platform.key)
  );
};
const withoutClientUnselectablePlatforms = (platforms = []) =>
  (Array.isArray(platforms) ? platforms : []).filter(isClientSelectablePlannerPlatform);

// Yahoo is selectable for governed display-CPC planning; execution remains restricted.
const PLANNER_PLATFORM_ORDER = placeYahooAfterAmazon([
  ...PLATFORM_OPTIONS.map((platform) => platform.key),
]);
const PLANNER_PLATFORM_ORDER_INDEX = new Map(
  PLANNER_PLATFORM_ORDER.map((key, index) => [key, index])
);
const PLANNER_PLATFORM_LABEL_TO_KEY = new Map(
  PLATFORM_OPTIONS.map((platform) => [platform.label.toLowerCase(), platform.key])
);
const PLANNER_PLATFORM_KEY_TO_LABEL = new Map(
  PLATFORM_OPTIONS.map((platform) => [platform.key, platform.label])
);

const normalizePlannerPlatformOrderKey = (platform) => {
  const raw = String(platform || "").trim();
  if (!raw) return "";
  if (PLANNER_PLATFORM_ORDER_INDEX.has(raw)) return raw;
  const lower = raw.toLowerCase().replace(/\s+/g, " ");
  if (PLANNER_PLATFORM_LABEL_TO_KEY.has(lower)) {
    return PLANNER_PLATFORM_LABEL_TO_KEY.get(lower);
  }
  // DSP identity comes from Backend's generated provider registry, so every
  // stored spelling ("Yahoo! DSP", "yahoo_dsp", "Viant DSP (Display)", ...)
  // resolves to the one canonical planner key. No portal keeps its own list.
  const providerKey = providerPlannerKey(raw);
  if (providerKey) return providerKey;
  const compact = lower.replace(/[^a-z0-9]/g, "");
  if (compact.includes("chatgpt") || compact.includes("openai")) return "chatgpt_search";
  if (compact === "googlesearch" || compact === "paidsearch") return "google_search";
  if (compact === "truthsocial") return "truth_social";
  if (compact === "ingame" || compact === "ingames") return "ingame";
  if (compact === "amazonads" || compact === "amazondsp") return "amazon";
  if (compact === "shopifycommerce" || compact === "shopifystore") return "shopify";
  if (compact === "linkedinads" || compact === "linkedinadvertising") return "linkedin";
  return raw;
};

const orderPlannerPlatformKeys = (platforms = []) => {
  const seen = new Set();
  return (Array.isArray(platforms) ? platforms : [])
    .map((platform, index) => ({ key: normalizePlannerPlatformOrderKey(platform), index }))
    .filter(({ key }) => key)
    .filter(({ key }) => {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const aIndex = PLANNER_PLATFORM_ORDER_INDEX.has(isYahooPlatform(a.key) ? "yahoo" : a.key)
        ? PLANNER_PLATFORM_ORDER_INDEX.get(isYahooPlatform(a.key) ? "yahoo" : a.key)
        : Number.MAX_SAFE_INTEGER;
      const bIndex = PLANNER_PLATFORM_ORDER_INDEX.has(isYahooPlatform(b.key) ? "yahoo" : b.key)
        ? PLANNER_PLATFORM_ORDER_INDEX.get(isYahooPlatform(b.key) ? "yahoo" : b.key)
        : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex || a.index - b.index;
    })
    .map(({ key }) => key);
};

const orderPlannerPlatformLabels = (platforms = []) => {
  const seen = new Set();
  return (Array.isArray(platforms) ? platforms : [])
    .map((platform, index) => ({
      label: String(platform || "").trim(),
      key: normalizePlannerPlatformOrderKey(platform),
      index,
    }))
    .filter(({ label }) => label)
    .filter(({ label }) => {
      const dedupeKey = label.toLowerCase();
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    })
    .sort((a, b) => {
      const aIndex = PLANNER_PLATFORM_ORDER_INDEX.has(isYahooPlatform(a.key) ? "yahoo" : a.key)
        ? PLANNER_PLATFORM_ORDER_INDEX.get(isYahooPlatform(a.key) ? "yahoo" : a.key)
        : Number.MAX_SAFE_INTEGER;
      const bIndex = PLANNER_PLATFORM_ORDER_INDEX.has(isYahooPlatform(b.key) ? "yahoo" : b.key)
        ? PLANNER_PLATFORM_ORDER_INDEX.get(isYahooPlatform(b.key) ? "yahoo" : b.key)
        : Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex || a.index - b.index;
    })
    .map(({ key, label }) =>
      PLANNER_PLATFORM_ORDER_INDEX.has(key)
        ? PLANNER_PLATFORM_KEY_TO_LABEL.get(key) || label
        : label
    );
};

// Maps a generated creative recommendation (new ad-copy shape OR a legacy
// object with strategy fields OR a bare string) into a clean, client-ready ad
// creative: variant label, headline, primary text, description, CTA, platform
// fit. Internal strategy fields (uniqueDescription / designDirection /
// audienceAngle / platformHints / rationale) are grouped under internalNotes so
// they can be shown only in an internal-only view, never on the main card.
export const mapCreativeVariationForDisplay = (recommendation, index = 0) => {
  const firstText = (...values) => {
    for (const value of values) {
      const text = String(value ?? "").trim();
      if (text) return text;
    }
    return "";
  };
  if (typeof recommendation === "string") {
    return {
      variantLabel: `Variant ${index + 1}`,
      headline: recommendation.trim(),
      primaryText: "",
      description: "",
      cta: "",
      platformFit: "",
      internalNotes: null,
    };
  }
  const rec = recommendation && typeof recommendation === "object" ? recommendation : {};
  const internal =
    rec.internalNotes && typeof rec.internalNotes === "object" ? rec.internalNotes : {};

  const headline = firstText(rec.headline, rec.title, rec.label);
  const primaryText = firstText(
    rec.primaryText,
    rec.primary_text,
    rec.description,
    rec.body,
    rec.copy,
    rec.message,
    rec.hook
  );
  let description = firstText(rec.description);
  if (description && description.toLowerCase() === primaryText.toLowerCase()) {
    description = "";
  }
  const platformHints = Array.isArray(rec.platformHints)
    ? rec.platformHints
    : Array.isArray(internal.platformHints)
      ? internal.platformHints
      : [];
  const internalNotes = {
    uniqueDescription: firstText(rec.uniqueDescription, internal.uniqueDescription),
    designDirection: firstText(rec.designDirection, internal.designDirection),
    audienceAngle: firstText(rec.audienceAngle, internal.audienceAngle),
    rationale: firstText(rec.rationale, internal.rationale),
    platformHints,
  };
  const hasInternal = Boolean(
    internalNotes.uniqueDescription ||
      internalNotes.designDirection ||
      internalNotes.audienceAngle ||
      internalNotes.rationale ||
      platformHints.length
  );
  return {
    variantLabel: firstText(rec.variantLabel) || `Variant ${index + 1}`,
    headline,
    primaryText,
    description,
    cta: firstText(rec.cta, rec.callToAction, rec.call_to_action),
    platformFit: firstText(rec.platformFit, rec.platform_fit),
    internalNotes: hasInternal ? internalNotes : null,
  };
};

const PLANNER_VERTICAL_CONFIG = Object.freeze({
  film: {
    vertical: "film",
    label: "Film / Entertainment",
    campaignLanguage: "film and entertainment media campaign",
    // Entertainment-only: enables the Earned Media social profile URL intake.
    earnedMediaProfilesEnabled: true,
    // Entertainment-only: enables the theatrical Release Date intake.
    releaseDateEnabled: true,
    campaignNamePlaceholder: "e.g. Digital Release Launch",
    primaryKpiPlaceholder: "eCPM, CTR, ROAS, CPA, Views, etc.",
    notesPlaceholder: "Audiences, markets, release beats, constraints...",
    interestsPlaceholder: "Cinema fans, thrillers, horror culture",
    customTargetingPlaceholder: "Retarget site visitors, CRM audiences, affinity clusters",
    geoTargetingPlaceholder: "Top DMAs, radius targeting, regional exclusions",
    deviceTargetingPlaceholder: "Lean mobile-first, prioritize CTV, exclude desktop, etc.",
    referenceUrlFields: [
      {
        name: "destinationUrl",
        label: "Official Website URL",
        placeholder: "https://www.example.com/title-site",
      },
      {
        name: "imdbUrl",
        label: "IMDb URL",
        placeholder: "https://www.imdb.com/title/tt...",
      },
      {
        name: "rottenTomatoesUrl",
        label: "Rotten Tomatoes URL",
        placeholder: "https://www.rottentomatoes.com/m/...",
      },
    ],
    goalOptions: [
      { value: "awareness", label: "Reach / Awareness" },
      { value: "consideration", label: "Consideration / Engagement" },
      { value: "traffic", label: "Traffic" },
      { value: "conversions", label: "Conversions / Sales" },
      { value: "ticket_sales", label: "Ticket Sales" },
      { value: "mixed", label: "Mixed / Full Funnel" },
    ],
    phasingGuidance:
      '- If the goal type is "ticket_sales" or "conversions", emphasize heavier spend closer to key conversion windows.',
    goalResponseGuidance: `
- "awareness": favor reach, impressions, and video views.
- "consideration" or "traffic": favor CTR, clicks, and mid-funnel engagement.
- "conversions" or "ticket_sales": favor ROAS/CPA and aggressive retargeting.
- "mixed": show how the funnel tiers work together.
`.trim(),
    promptGuidance: `
VERTICAL CONTEXT
- Treat this as a film/entertainment campaign.
- It is appropriate to consider theatrical awareness, ticketing intent, release timing, audience interest, and title positioning when the brief supports it.

REFERENCE URL GUIDANCE
- Use the official website as the primary source for positioning, campaign framing, and title messaging.
- Use IMDb as a film-reference source for title identity, metadata context, and audience-facing framing when useful.
- Use Rotten Tomatoes as a film-reference source for critic/audience sentiment context when useful.
- Do not invent or state exact IMDb ratings or Rotten Tomatoes scores unless those values are explicitly provided in structured input.
- If only the URLs are provided, discuss audience or critic sentiment carefully and conditionally rather than as a confirmed fact.
- When relevant, let these sources inform audience strategy, creative angles, and messaging without overwhelming the media plan.
`.trim(),
  },
  automotive: {
    vertical: "automotive",
    label: "Automotive",
    campaignLanguage: "automotive media campaign",
    campaignNamePlaceholder: "e.g. Spring Sales Event",
    primaryKpiPlaceholder: "CPC, CPM, CTR, Leads, VDP Views, Site Visits, Qualified Traffic",
    notesPlaceholder: "Audiences, markets, inventory priorities, offers, constraints...",
    interestsPlaceholder: "In-market auto shoppers, SUV buyers, EV intenders, luxury vehicle prospects",
    customTargetingPlaceholder:
      "Retarget site visitors, lease-end audiences, lookalike shoppers, first-party audience segments",
    geoTargetingPlaceholder:
      "Top DMAs, dealership radius targeting, priority markets, regional exclusions",
    deviceTargetingPlaceholder:
      "Mobile-first, prioritize CTV, retarget display, exclude low-quality placements",
    referenceUrlFields: [
      {
        name: "destinationUrl",
        label: "Official Website URL",
        placeholder: "https://www.exampledealer.com",
      },
      {
        name: "imdbUrl",
        label: "Inventory / Model Page URL",
        placeholder: "https://www.exampledealer.com/new-inventory",
      },
      {
        name: "rottenTomatoesUrl",
        label: "Offers / Specials URL",
        placeholder: "https://www.exampledealer.com/specials",
      },
    ],
    goalOptions: [
      { value: "awareness", label: "Reach / Awareness" },
      { value: "qualified_traffic", label: "Qualified Traffic" },
      { value: "lead_generation", label: "Lead Generation" },
      { value: "inventory_demand", label: "Inventory Demand" },
      { value: "offer_engagement", label: "Offer Engagement" },
      { value: "service_campaign", label: "Service Campaign" },
      { value: "model_launch", label: "Model Launch" },
      { value: "retargeting", label: "Retargeting" },
    ],
    phasingGuidance:
      '- If the goal type is "lead_generation", "offer_engagement", "inventory_demand", or "retargeting", connect spend weight to high-intent shopper windows and market response without inventing sales data.',
    goalResponseGuidance: `
- "awareness": favor reach, impressions, and video exposure.
- "qualified_traffic": favor CPC, CTR, engaged sessions, and high-intent site traffic.
- "lead_generation": favor qualified traffic, lead activity, and retargeting efficiency when those signals are provided.
- "inventory_demand", "offer_engagement", "service_campaign", or "model_launch": tie budget and creative to the stated automotive objective without inventing inventory, lead-form, or sales data.
- "retargeting": favor lower-funnel audience pools, site return paths, and qualified traffic quality.
`.trim(),
    promptGuidance: `
VERTICAL CONTEXT
- Treat this as an automotive campaign for in-market shoppers, vehicle interest, qualified traffic, offer engagement, dealer/geo performance, and lead activity when data supports it.
- Focus recommendations on channel performance, market response, audience intent, creative/offer messaging, retargeting, and demand generation.
- Avoid entertainment-release terminology, critic-score sources, and event-launch assumptions.

REFERENCE URL GUIDANCE
- Use the official website as the primary source for advertiser positioning and campaign destination context.
- Use the inventory/model page as a planning reference for vehicle interest, inventory demand, and model or segment priorities.
- Use the offers/specials page as a planning reference for offer messaging and promotional urgency.
- Do not claim access to CRM, VIN-level inventory, sales, or lead-form data unless those details are explicitly provided in the brief.
`.trim(),
  },
  retail: {
    vertical: "retail",
    label: "Retail / E-Commerce",
    campaignLanguage: "retail and e-commerce media campaign",
    campaignNamePlaceholder: "e.g. Seasonal Promo Launch",
    primaryKpiPlaceholder: "ROAS, CPA, Conversion Rate, Purchases, AOV, Cart Activity",
    notesPlaceholder: "Audiences, product/category priorities, promo cadence, seasonality, constraints...",
    interestsPlaceholder: "Category shoppers, deal seekers, loyal customers, competitor buyers",
    customTargetingPlaceholder:
      "Retarget site visitors, cart abandoners, product viewers, CRM audiences, high-value shopper lookalikes",
    geoTargetingPlaceholder:
      "Priority markets, store/service areas, shipping regions, seasonal or promo-heavy markets",
    deviceTargetingPlaceholder:
      "Mobile-first shopping paths, retargeting display, CTV for seasonal awareness, exclude low-quality placements",
    referenceUrlFields: [
      {
        name: "destinationUrl",
        label: "Official Website URL",
        placeholder: "https://www.examplestore.com",
      },
      {
        name: "imdbUrl",
        label: "Product / Category Page URL",
        placeholder: "https://www.examplestore.com/category",
      },
      {
        name: "rottenTomatoesUrl",
        label: "Promo / Offer Page URL",
        placeholder: "https://www.examplestore.com/sale",
      },
    ],
    goalOptions: [
      { value: "awareness", label: "Reach / Awareness" },
      { value: "qualified_traffic", label: "Qualified Traffic" },
      { value: "purchase_efficiency", label: "Purchase Efficiency" },
      { value: "roas", label: "ROAS" },
      { value: "cart_recovery", label: "Cart Recovery" },
      { value: "new_customer_acquisition", label: "New Customer Acquisition" },
      { value: "retention", label: "Retention / Repeat Purchase" },
      { value: "promo_lift", label: "Promo Lift" },
    ],
    phasingGuidance:
      '- If the goal type is "roas", "purchase_efficiency", "cart_recovery", or "promo_lift", connect spend weight to shopping windows, promo cadence, and retargeting depth without inventing purchase or cart data.',
    goalResponseGuidance: `
- "awareness": favor reach, impressions, and product discovery.
- "qualified_traffic": favor CPC, CTR, engaged sessions, and product-page traffic.
- "purchase_efficiency" or "roas": favor ROAS, CPA, conversion rate, purchase quality, and retargeting efficiency when those signals are provided.
- "cart_recovery": prioritize cart abandoners, product viewers, checkout reminders, and offer sequencing when those pools are provided.
- "new_customer_acquisition", "retention", or "promo_lift": tie budget and creative to the stated retail objective without inventing purchases or product-level results.
`.trim(),
    promptGuidance: `
VERTICAL CONTEXT
- Treat this as a retail/e-commerce campaign for product discovery, purchase intent, cart recovery, promo responsiveness, and conversion efficiency when data supports it.
- Include prospecting versus retargeting balance, product/category segmentation, promo cadence, seasonal shopping behavior, cart recovery, new customer acquisition, repeat purchase strategy, and offer/product storytelling.
- Do not claim purchases, carts, checkout behavior, ROAS, or product-level performance unless those details are explicitly provided in the brief.

REFERENCE URL GUIDANCE
- Use the official website as the primary source for brand and shopping destination context.
- Use the product/category page as a planning reference for merchandise focus and product storytelling.
- Use the promo/offer page as a planning reference for urgency, offer cadence, and conversion framing.
`.trim(),
  },
  travel_hospitality: {
    vertical: "travel_hospitality",
    label: "Travel / Hospitality",
    campaignLanguage: "travel and hospitality media campaign",
    campaignNamePlaceholder: "e.g. Seasonal Destination Launch",
    primaryKpiPlaceholder: "Bookings, CPA, CTR, CPC, Site Visits, Reservation Inquiries",
    notesPlaceholder:
      "Audiences, origin markets, destinations/properties, packages, seasonality, constraints...",
    interestsPlaceholder:
      "Frequent travelers, weekend getaway planners, family vacationers, luxury resort seekers",
    customTargetingPlaceholder:
      "Retarget site visitors, loyalty audiences, lookalike travelers, destination intenders",
    geoTargetingPlaceholder:
      "Origin markets, feeder DMAs, drive markets, flight markets, destination-market exclusions",
    deviceTargetingPlaceholder:
      "Mobile-first trip planning, CTV for destination inspiration, retargeting display near booking windows",
    referenceUrlFields: [
      {
        name: "destinationUrl",
        label: "Official Website URL",
        placeholder: "https://www.examplehotel.com",
      },
      {
        name: "imdbUrl",
        label: "Destination / Property Page URL",
        placeholder: "https://www.examplehotel.com/destination",
      },
      {
        name: "rottenTomatoesUrl",
        label: "Offer / Package Page URL",
        placeholder: "https://www.examplehotel.com/offers",
      },
    ],
    goalOptions: [
      { value: "awareness", label: "Reach / Awareness" },
      { value: "qualified_traffic", label: "Qualified Traffic" },
      { value: "booking_consideration", label: "Booking Consideration" },
      { value: "reservation_inquiries", label: "Reservation Inquiries" },
      { value: "destination_demand", label: "Destination Demand" },
      { value: "seasonal_lift", label: "Seasonal Lift" },
      { value: "loyalty", label: "Loyalty / Returning Guests" },
      { value: "retargeting", label: "Retargeting" },
    ],
    phasingGuidance:
      '- If the goal type is "booking_consideration", "reservation_inquiries", "destination_demand", or "retargeting", connect spend weight to trip-planning windows and booking-path retargeting without inventing bookings or occupancy data.',
    goalResponseGuidance: `
- "awareness": favor reach, impressions, and destination or property inspiration.
- "qualified_traffic": favor CPC, CTR, engaged sessions, and trip-planning traffic quality.
- "booking_consideration" or "reservation_inquiries": favor qualified traffic, booking-path engagement, and retargeting efficiency when those signals are provided.
- "destination_demand", "seasonal_lift", or "loyalty": tie budget and creative to the stated travel objective without inventing bookings, occupancy, room nights, route sales, or loyalty activity.
- "retargeting": favor site visitor, itinerary, offer, and booking-path audience pools.
`.trim(),
    promptGuidance: `
VERTICAL CONTEXT
- Treat this as a travel/hospitality campaign for destination demand, traveler intent, booking consideration, guest engagement, origin-market response, and seasonal travel planning when data supports it.
- Include origin-market weighting, destination or property storytelling, seasonal travel windows, trip-planning intent, package or offer cadence, booking-path retargeting, loyalty audiences, and guest-experience messaging.
- Do not claim bookings, reservations, occupancy, room nights, route sales, or loyalty activity unless those details are explicitly provided in the brief.

REFERENCE URL GUIDANCE
- Use the official website as the primary source for brand, property, route, or destination context.
- Use the destination/property page as a planning reference for travel intent, guest experience, destination storytelling, amenities, and property or route priorities.
- Use the offer/package page as a planning reference for urgency, seasonal timing, bundled value, and booking consideration.
`.trim(),
  },
  general: {
    vertical: "general",
    label: "General",
    campaignLanguage: "cross-platform media campaign",
    campaignNamePlaceholder: "e.g. Brand Awareness Campaign",
    primaryKpiPlaceholder: "CPM, CTR, CPC, CPA, Leads, Site Visits, Qualified Traffic",
    notesPlaceholder: "Audiences, markets, offers, objectives, constraints...",
    interestsPlaceholder: "Audience segments, category intenders, affinity groups",
    customTargetingPlaceholder:
      "Retarget site visitors, first-party audiences, lookalikes, contextual segments",
    geoTargetingPlaceholder: "Priority markets, radius targeting, regional exclusions",
    deviceTargetingPlaceholder:
      "Mobile-first, prioritize video, retarget display, exclude low-quality placements",
    referenceUrlFields: [
      {
        name: "destinationUrl",
        label: "Official Website URL",
        placeholder: "https://www.example.com",
      },
      {
        name: "imdbUrl",
        label: "Landing Page URL",
        placeholder: "https://www.example.com/landing-page",
      },
      {
        name: "rottenTomatoesUrl",
        label: "Conversion Page URL",
        placeholder: "https://www.example.com/convert",
      },
    ],
    goalOptions: [
      { value: "awareness", label: "Reach / Awareness" },
      { value: "consideration", label: "Consideration / Engagement" },
      { value: "traffic", label: "Traffic" },
      { value: "conversions", label: "Conversions / Sales" },
      { value: "mixed", label: "Mixed / Full Funnel" },
    ],
    phasingGuidance:
      '- If the goal type is "conversions", emphasize efficient retargeting and high-intent conversion windows.',
    goalResponseGuidance: `
- "awareness": favor reach, impressions, and video exposure where relevant.
- "consideration" or "traffic": favor CTR, clicks, and mid-funnel engagement.
- "conversions": favor CPA, conversion quality, and efficient retargeting.
- "mixed": show how the funnel tiers work together.
`.trim(),
    promptGuidance: `
VERTICAL CONTEXT
- Treat this as a general cross-platform campaign.
- Use neutral campaign intelligence language around audience response, channel performance, campaign optimization, qualified traffic, and conversion signals.
- Do not assume industry-specific context unless explicitly provided in the brief.

REFERENCE URL GUIDANCE
- Use the official website as the primary source for advertiser positioning and campaign framing.
- Use the landing page and conversion page as planning context for audience journey, traffic quality, and conversion intent.
- Do not invent page performance, sales, or conversion outcomes unless those details are explicitly provided.
`.trim(),
  },
});

const getPlannerVerticalConfig = (vertical) =>
  PLANNER_VERTICAL_CONFIG[normalizeAdvertiserVertical(vertical)] || PLANNER_VERTICAL_CONFIG.general;

const getPlannerUrlValidationError = (formData, config) => {
  const fields = Array.isArray(config?.referenceUrlFields) ? config.referenceUrlFields : [];
  for (const field of fields) {
    const value = formData?.[field.name];
    if (value && !normalizeHttpUrl(value)) {
      return `${field.label} must be a valid http or https URL.`;
    }
  }
  if (config?.earnedMediaProfilesEnabled) {
    const profileUrls = Array.isArray(formData?.earnedMediaProfileUrls)
      ? formData.earnedMediaProfileUrls
      : [];
    for (let i = 0; i < profileUrls.length; i += 1) {
      // Empty fields never block submission; only malformed URLs do.
      if (!isValidEarnedMediaProfileUrl(profileUrls[i])) {
        return `Earned Media Profile URL ${i + 1} must be a valid http or https URL.`;
      }
    }
  }
  return "";
};

const formatBudgetForPrompt = (budget) => {
  if (!budget) return "N/A";
  const num = Number(budget);
  if (Number.isNaN(num)) return "N/A";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatBudgetForDisplay = (budget) => {
  if (budget === null || budget === undefined || budget === "") return "";
  const num = Number(budget);
  if (Number.isNaN(num)) return String(budget);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const normalizeBudgetInputValue = (value) => {
  const compact = String(value || "").replace(/[,$\s]/g, "").toLowerCase();
  const magnitudeMatch = compact.match(/^(\d+(?:\.\d+)?)(k|m)$/);
  if (magnitudeMatch) {
    const multiplier = magnitudeMatch[2] === "m" ? 1000000 : 1000;
    const parsed = Number(magnitudeMatch[1]) * multiplier;
    return Number.isFinite(parsed) ? String(Math.round(parsed)) : "";
  }

  const numericOnly = String(value || "").replace(/[^0-9.]/g, "");
  const parts = numericOnly.split(".");
  return parts.length <= 1 ? numericOnly : `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const normalizeBudgetDisplayValue = (value) => {
  const normalized = normalizeBudgetInputValue(value);
  if (!normalized) return "";
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : "";
};

const firstBudgetDisplayValue = (...values) => {
  for (const value of values) {
    const normalized = normalizeBudgetDisplayValue(value);
    if (normalized) return normalized;
  }
  return "";
};

const normalizeTargetMarginDisplayValue = (value) => {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const parsed = Number(raw.replace(/[%,$,\s]/g, ""));
  if (!Number.isFinite(parsed)) return "";
  // Margin is a percent of gross (18 = 18%). Values strictly below 1 are the
  // legacy fractional form (0.18) — exactly 1 means 1%, never 100%.
  const pct = parsed > 0 && parsed < 1 ? parsed * 100 : parsed;
  if (pct < 0 || pct > 100) return "";
  return String(Number(pct.toFixed(2))).replace(/\.00$/, "");
};

const firstTargetMarginDisplayValue = (...values) => {
  for (const value of values) {
    const normalized = normalizeTargetMarginDisplayValue(value);
    if (normalized) return normalized;
    if (value === 0 || value === "0") return "0";
  }
  return "";
};

const deriveNetMediaBudgetFromGrossAndMargin = (grossBudget, targetMarginPercent) => {
  const gross = Number(normalizeBudgetDisplayValue(grossBudget));
  const margin = Number(normalizeTargetMarginDisplayValue(targetMarginPercent));
  if (!Number.isFinite(gross) || gross <= 0 || !Number.isFinite(margin)) return "";
  if (margin < 0 || margin > 100) return "";
  const net = gross * (1 - margin / 100);
  if (!Number.isFinite(net) || net < 0) return "";
  return String(Math.round(net * 100) / 100);
};

const resolveNetMediaBudgetValue = ({ grossBudget, targetMarginPercent } = {}, ...fallbacks) => {
  const derived = deriveNetMediaBudgetFromGrossAndMargin(grossBudget, targetMarginPercent);
  return derived !== "" ? derived : firstBudgetDisplayValue(...fallbacks);
};

const normalizeCommaSeparatedList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || "").trim()).filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\r?\n|,/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

const toCommaSeparatedValue = (value) => normalizeCommaSeparatedList(value).join(", ");

const normalizeHttpUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw);
    if (!/^https?:$/i.test(parsed.protocol)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
};

// Earned Media social profile URL intake (entertainment / film only). Renders
// 3 URL fields by default; the array model supports "Add another profile"
// without a refactor. Empty fields are always allowed.
const EARNED_MEDIA_DEFAULT_PROFILE_FIELDS = 3;
const EARNED_MEDIA_MAX_PROFILE_FIELDS = 25;
// Broad social/media examples (not Instagram-only). Cycled across the fields so
// the intake reads as multi-platform.
const EARNED_MEDIA_PROFILE_PLACEHOLDERS = Object.freeze([
  "https://www.instagram.com/example",
  "https://www.youtube.com/@example",
  "https://www.facebook.com/example",
]);
const getEarnedMediaProfilePlaceholder = (index) =>
  EARNED_MEDIA_PROFILE_PLACEHOLDERS[index % EARNED_MEDIA_PROFILE_PLACEHOLDERS.length];

const normalizeEarnedMediaProfileUrlList = (value) => {
  let list = [];
  if (Array.isArray(value)) {
    list = value.map((entry) => (entry == null ? "" : String(entry)));
  } else if (typeof value === "string") {
    // Tolerate a single pasted, delimiter-separated string.
    list = value.split(/[\n,]+/).map((part) => part.trim());
  }
  list = list.slice(0, EARNED_MEDIA_MAX_PROFILE_FIELDS);
  while (list.length < EARNED_MEDIA_DEFAULT_PROFILE_FIELDS) list.push("");
  return list;
};

// Empty is allowed; a non-empty value must resolve to an http/https URL. A bare
// host like "instagram.com/example" is accepted (https assumed), matching the
// lenient backend normalizer.
const isValidEarnedMediaProfileUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  return Boolean(normalizeHttpUrl(withScheme));
};

// Test surface for the entertainment-only Earned Media profile URL intake.
export const __earnedMediaProfileTesting = {
  getPlannerVerticalConfig,
  normalizeEarnedMediaProfileUrlList,
  isValidEarnedMediaProfileUrl,
  getPlannerUrlValidationError,
  EARNED_MEDIA_DEFAULT_PROFILE_FIELDS,
  EARNED_MEDIA_MAX_PROFILE_FIELDS,
  isEarnedMediaProfilesEnabled: (vertical) =>
    Boolean(getPlannerVerticalConfig(vertical)?.earnedMediaProfilesEnabled),
};

const normalizePlannerGender = (value) => {
  const candidate = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "_");

  if (candidate === "female") return "female";
  if (candidate === "male") return "male";
  return "all";
};

const PLANNER_FORM_FIELD_ALIASES = Object.freeze({
  budget: ["budget", "totalBudget", "total_budget", "grossBudget", "gross_budget"],
  netMediaBudget: [
    "netMediaBudget",
    "net_media_budget",
    "netBudget",
    "mediaNetBudget",
    "approvedNetMediaBudget",
  ],
  targetMarginPercent: [
    "targetMarginPercent",
    "target_margin_percent",
    "targetMargin",
    "target_margin",
    "targetMarginPct",
    "target_margin_pct",
  ],
  flightStart: ["flightStart", "flight_start", "flight_start_date", "flightStartDate"],
  flightEnd: ["flightEnd", "flight_end", "flight_end_date", "flightEndDate"],
  releaseDate: ["releaseDate", "release_date"],
  interests: ["interests"],
  customTargeting: ["customTargeting", "custom_targeting"],
  geoTargetingNotes: [
    "geoTargetingNotes",
    "geo_targeting_notes",
    "geoTargeting",
    "geo_targeting",
    "locationTargeting",
    "location_targeting",
  ],
  deviceTargetingNotes: [
    "deviceTargetingNotes",
    "device_targeting_notes",
    "devicePlatformNotes",
    "device_platform_notes",
  ],
  languageTargeting: ["languageTargeting", "language_targeting"],
  ageMin: ["ageMin", "age_min"],
  ageMax: ["ageMax", "age_max"],
  gender: ["gender"],
  primaryKPI: ["primaryKPI", "primaryKpi", "primary_kpi"],
  notes: ["notes", "additionalNotes", "additional_notes"],
  platforms: ["platforms", "targetPlatforms", "target_platforms"],
});

const PLANNER_STRUCTURED_FIELD_KEYS = Object.freeze(Object.keys(PLANNER_FORM_FIELD_ALIASES));

const hasOwnPlannerField = (source, key) =>
  Boolean(source && Object.prototype.hasOwnProperty.call(source, key));

const isEmptyPlannerValue = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === "";
};

const getPlannerMergeSources = (incoming = {}) => {
  const strategyPayload = incoming?.strategy_payload || incoming?.strategyPayload || {};
  return [
    incoming?.formData,
    incoming?.plannerDraft?.formData,
    strategyPayload?.plannerDraft?.formData,
    strategyPayload?.formData,
    strategyPayload?.plannerFormData,
    incoming?.targeting,
    strategyPayload?.targeting,
    strategyPayload?.financials,
    incoming,
  ].filter((source) => source && typeof source === "object" && !Array.isArray(source));
};

const readAliasedPlannerValue = (sources = [], fieldKey) => {
  const aliases = PLANNER_FORM_FIELD_ALIASES[fieldKey] || [fieldKey];
  for (const source of sources) {
    for (const alias of aliases) {
      if (hasOwnPlannerField(source, alias)) return { found: true, value: source[alias] };
    }
  }
  return { found: false, value: undefined };
};

const normalizePlannerMergeValue = (fieldKey, value) => {
  if (fieldKey === "platforms") return normalizePlatformArray(value);
  if (fieldKey === "flightStart") return normalizeDateInputValue(value);
  if (fieldKey === "flightEnd") return normalizeDateInputValue(value);
  if (fieldKey === "releaseDate") return normalizeDateInputValue(value);
  if (fieldKey === "budget" || fieldKey === "netMediaBudget") {
    return firstBudgetDisplayValue(value);
  }
  if (fieldKey === "targetMarginPercent") return firstTargetMarginDisplayValue(value);
  if (fieldKey === "interests" || fieldKey === "customTargeting") {
    return toCommaSeparatedValue(value);
  }
  if (fieldKey === "gender") return normalizePlannerGender(value);
  return value === null || value === undefined ? "" : String(value);
};

export const mergePlannerStatePreservingUserFields = (
  currentFormState = {},
  incomingRecommendationOrRevision = {},
  options = {}
) => {
  const preserveCurrentNonEmpty = options.preserveCurrentNonEmpty !== false;
  const allowExplicitClears = Boolean(options.allowExplicitClears);
  const preferIncomingNonEmpty = Boolean(options.preferIncomingNonEmpty);
  const incomingSources = getPlannerMergeSources(incomingRecommendationOrRevision);
  const next = { ...(currentFormState || {}) };

  PLANNER_STRUCTURED_FIELD_KEYS.forEach((fieldKey) => {
    const incoming = readAliasedPlannerValue(incomingSources, fieldKey);
    if (!incoming.found) return;

    const currentValue = normalizePlannerMergeValue(fieldKey, currentFormState?.[fieldKey]);
    const incomingValue = normalizePlannerMergeValue(fieldKey, incoming.value);

    if (preserveCurrentNonEmpty && !isEmptyPlannerValue(currentValue)) {
      if (isEmptyPlannerValue(incomingValue)) {
        next[fieldKey] = allowExplicitClears ? incomingValue : currentValue;
        return;
      }
      next[fieldKey] = preferIncomingNonEmpty ? incomingValue : currentValue;
      return;
    }

    next[fieldKey] = incomingValue;
  });

  return next;
};

const buildPlannerTargetingPayload = (fd = {}) => {
  const interests = normalizeCommaSeparatedList(fd.interests);
  const customTargeting = normalizeCommaSeparatedList(fd.customTargeting);
  const geoTargetingNotes = String(fd.geoTargetingNotes || fd.geoTargeting || "").trim();
  const deviceTargetingNotes = String(
    fd.deviceTargetingNotes || fd.devicePlatformNotes || ""
  ).trim();
  const languageTargeting = String(fd.languageTargeting || "").trim();

  return {
    ageMin: fd.ageMin || null,
    age_min: fd.ageMin || null,
    ageMax: fd.ageMax || null,
    age_max: fd.ageMax || null,
    gender: fd.gender || null,
    interests,
    customTargeting,
    custom_targeting: customTargeting,
    geoTargetingNotes: geoTargetingNotes || null,
    geo_targeting_notes: geoTargetingNotes || null,
    geoTargeting: geoTargetingNotes || null,
    geo_targeting: geoTargetingNotes || null,
    locationTargeting: geoTargetingNotes || null,
    location_targeting: geoTargetingNotes || null,
    languageTargeting: languageTargeting || null,
    language_targeting: languageTargeting || null,
    deviceTargetingNotes: deviceTargetingNotes || null,
    device_targeting_notes: deviceTargetingNotes || null,
    devicePlatformNotes: deviceTargetingNotes || null,
    device_platform_notes: deviceTargetingNotes || null,
  };
};

const getPlannerTargetingFromCampaign = (campaign) => {
  const strategyPayload = campaign?.strategy_payload || campaign?.strategyPayload || {};
  const draftFormData =
    strategyPayload?.plannerDraft &&
    typeof strategyPayload.plannerDraft === "object" &&
    !Array.isArray(strategyPayload.plannerDraft)
      ? strategyPayload.plannerDraft.formData || {}
      : {};
  const targeting = campaign?.targeting || strategyPayload?.targeting || {};

  return {
    ageMin:
      targeting?.ageMin != null && targeting?.ageMin !== ""
        ? String(targeting.ageMin)
        : draftFormData?.ageMin != null && draftFormData?.ageMin !== ""
        ? String(draftFormData.ageMin)
        : "",
    ageMax:
      targeting?.ageMax != null && targeting?.ageMax !== ""
        ? String(targeting.ageMax)
        : draftFormData?.ageMax != null && draftFormData?.ageMax !== ""
        ? String(draftFormData.ageMax)
        : "",
    gender: normalizePlannerGender(targeting?.gender ?? draftFormData?.gender),
    interests: toCommaSeparatedValue(targeting?.interests ?? draftFormData?.interests),
    customTargeting: toCommaSeparatedValue(
      targeting?.customTargeting ?? targeting?.custom_targeting ?? draftFormData?.customTargeting
    ),
    geoTargetingNotes: String(
      targeting?.geoTargetingNotes ||
        targeting?.geo_targeting_notes ||
        targeting?.geoTargeting ||
        targeting?.locationTargeting ||
        draftFormData?.geoTargetingNotes ||
        draftFormData?.geoTargeting ||
        draftFormData?.locationTargeting ||
        ""
    ),
    languageTargeting: String(
      targeting?.languageTargeting ||
        targeting?.language_targeting ||
        draftFormData?.languageTargeting ||
        ""
    ),
    deviceTargetingNotes: String(
      targeting?.deviceTargetingNotes ||
        targeting?.device_targeting_notes ||
        targeting?.devicePlatformNotes ||
        draftFormData?.deviceTargetingNotes ||
        draftFormData?.devicePlatformNotes ||
        ""
    ),
  };
};

const getDestinationUrlFromCampaign = (campaign) =>
  String(
    campaign?.destination_url ||
      campaign?.destinationUrl ||
      campaign?.strategy_payload?.plannerDraft?.formData?.destinationUrl ||
      campaign?.strategyPayload?.plannerDraft?.formData?.destinationUrl ||
      campaign?.strategy_payload?.destinationUrl ||
      campaign?.strategyPayload?.destinationUrl ||
      ""
  );

const getImdbUrlFromCampaign = (campaign) =>
  String(
    campaign?.imdb_url ||
      campaign?.imdbUrl ||
      campaign?.strategy_payload?.plannerDraft?.formData?.imdbUrl ||
      campaign?.strategyPayload?.plannerDraft?.formData?.imdbUrl ||
      campaign?.strategy_payload?.imdbUrl ||
      campaign?.strategyPayload?.imdbUrl ||
      ""
  );

const getRottenTomatoesUrlFromCampaign = (campaign) =>
  String(
    campaign?.rotten_tomatoes_url ||
      campaign?.rottenTomatoesUrl ||
      campaign?.strategy_payload?.plannerDraft?.formData?.rottenTomatoesUrl ||
      campaign?.strategyPayload?.plannerDraft?.formData?.rottenTomatoesUrl ||
      campaign?.strategy_payload?.rottenTomatoesUrl ||
      campaign?.strategyPayload?.rottenTomatoesUrl ||
      ""
  );

const getCreativeRecommendationsFromCampaign = (campaign) => {
  const recommendations =
    campaign?.creative_recommendations ||
    campaign?.creativeRecommendations ||
    campaign?.creative_variations ||
    campaign?.creativeVariations ||
    campaign?.strategy_payload?.creativeRecommendations ||
    campaign?.strategy_payload?.creativeVariations ||
    campaign?.strategyPayload?.creativeRecommendations ||
    campaign?.strategyPayload?.creativeVariations;

  return Array.isArray(recommendations) ? recommendations : [];
};

const getCreativeGenerationMetaFromCampaign = (campaign) => {
  const meta =
    campaign?.creative_generation_meta ||
    campaign?.creativeGenerationMeta ||
    campaign?.strategy_payload?.creativeGenerationMeta ||
    campaign?.strategyPayload?.creativeGenerationMeta;

  return meta && typeof meta === "object" && !Array.isArray(meta) ? meta : null;
};

export const buildMediaPlannerPrompt = (data) => {
  const {
    advertiser,
    campaignName,
    budget,
    goalType,
    primaryKPI,
    notes,
    flightStart,
    flightEnd,
    releaseDate,
    platforms,
    ageMin,
    ageMax,
    gender,
    interests,
    customTargeting,
    geoTargetingNotes,
    languageTargeting,
    deviceTargetingNotes,
    advertiserVertical,
  } = data;
  const plannerVerticalConfig = getPlannerVerticalConfig(advertiserVertical);
  const verticalIntelligence = getVerticalIntelligenceConfig(advertiserVertical);

  const selectedPlatformLabels = orderPlannerPlatformLabels(platforms || []).join(", ");

  const platformListText = selectedPlatformLabels || "No specific platforms were selected.";
  // Platform guidance is compiled to the GOVERNED SELECTION, never emitted as
  // an all-platform glossary.
  //
  // Campaign 50 (YouTube + CTV) sent this template verbatim: an Amazon /
  // Shopify / LinkedIn ordering rule, an "Audio / In-Game / CTV / Video /
  // Mobile / Display / DOOH / Email" routing line, and a six-entry channel-role
  // list naming Meta, Instagram, TikTok, Display, DOOH and Cinema. The model
  // read a channel menu and wrote about channels the campaign had not bought;
  // the backend scanner then refused the whole plan fail-closed
  // (planner_platform_scope_violation, operation 98d69a89) after 56 seconds of
  // generation. Naming a channel we did not select is an invitation to use it.
  //
  // The backend compiler and preflight remain the authoritative defense — this
  // is not a substitute for them. It stops us composing the problem.
  const selectedPlatformKeySet = new Set(
    (platforms || []).map((key) => String(key || "").trim().toLowerCase()).filter(Boolean)
  );
  const scopedLine = (line, ...channelKeys) =>
    channelKeys.some((key) => selectedPlatformKeySet.has(key)) ? line : "";
  const commercePlatformRules = [
    scopedLine(
      "   - When Amazon, Shopify, and LinkedIn are selected, present them in this exact order: Amazon, Shopify, LinkedIn.",
      "amazon",
      "shopify",
      "linkedin"
    ),
    scopedLine(
      "   - Treat Shopify as a commerce intelligence, measurement, and conversion optimization layer unless the brief explicitly says otherwise.",
      "shopify"
    ),
    scopedLine(
      "   - Treat LinkedIn as professional audience and B2B paid media when the campaign objective supports it.",
      "linkedin"
    ),
  ]
    .filter(Boolean)
    .join("\n");
  const PROGRAMMATIC_ROUTED_CHANNELS = [
    ["audio", "Audio"],
    ["ingame", "In-Game"],
    ["ctv", "CTV"],
    ["video", "Video"],
    ["mobile", "Mobile"],
    ["display", "Display"],
    ["dooh", "DOOH"],
    ["email", "Email"],
  ];
  const selectedProgrammaticLabels = PROGRAMMATIC_ROUTED_CHANNELS.filter(([key]) =>
    selectedPlatformKeySet.has(key)
  ).map(([, label]) => label);
  const programmaticRoutingSection = selectedProgrammaticLabels.length
    ? `5. Assume downstream execution will route ${selectedProgrammaticLabels.join(
        " / "
      )} into Cphere Programmatic when pushes are enabled.`
    : "";
  const scopedPlatformRoleLines = Object.entries(
    verticalIntelligence.platformRoleGuidance || {}
  )
    .filter(([platform]) => selectedPlatformKeySet.has(String(platform).toLowerCase()))
    .map(([platform, role]) => `- ${platform}: ${role}`)
    .join("\n");
  const platformRoleSection = scopedPlatformRoleLines
    ? `7. Apply these platform roles:\n${scopedPlatformRoleLines}`
    : "";
  const budgetDisplay = formatBudgetForPrompt(budget);
  const referenceUrlText = plannerVerticalConfig.referenceUrlFields
    .map((field) => `- ${field.label}: ${data[field.name] || "Not provided"}`)
    .join("\n");

  return `
You are Sherloc, an AI cross platform media planner inside the Cphere ecosystem.

Your job is to design a clear, implementable ${plannerVerticalConfig.campaignLanguage} that can be handed directly to a media buyer and trafficked across platforms, with a focus on discipline, efficiency, and performance.

CAMPAIGN CONTEXT
- Advertiser: ${advertiser || "N/A"}
- Advertiser Vertical: ${plannerVerticalConfig.vertical}
- Campaign Name: ${campaignName || "N/A"}
- Total Budget: ${budgetDisplay}
- Goal Type: ${goalType || "N/A"}
- Primary KPI: ${primaryKPI || "N/A"}
- Flight Dates: ${flightStart || "TBD"} to ${flightEnd || "TBD"}${
    plannerVerticalConfig.releaseDateEnabled && releaseDate
      ? `\n- Release Date: ${releaseDate}`
      : ""
  }
- Target Platforms: ${platformListText}
${referenceUrlText}
- Audience Age Range: ${ageMin || "?"}${ageMax ? `-${ageMax}` : ""}
- Gender Targeting: ${normalizePlannerGender(gender)}
- Interests: ${interests || "None provided"}
- Custom Targeting: ${customTargeting || "None provided"}
- Geo / Location Notes: ${geoTargetingNotes || "None provided"}
- Language Targeting: ${languageTargeting || "None provided"}
- Device / Platform Notes: ${deviceTargetingNotes || "None provided"}
- Additional Notes / Brief Details: ${notes || "None provided."}
- Budget Authority: The Total Budget field above is the authoritative gross client budget. Do not infer, replace, or normalize it from budget amounts mentioned in notes.

REQUIREMENTS FOR YOUR RESPONSE
1. Start with a tight 2–4 sentence overview of the recommended cross platform strategy.
2. Then break the plan into these sections with clear headings:

   A. Budget Allocation by Platform
   - Allocate the total budget across the selected platforms.
   - Include every selected platform by its exact selected name: ${platformListText}.
${commercePlatformRules}
   - Do not introduce or substitute unselected platforms; Programmatic Video / OLV
     may appear only when Video is selected.
   - Show approximate % splits that add up to 100%.
   - Briefly justify why each platform gets that share in 1–2 bullets.

   B. Flighting & Phasing
   - Recommend how to phase spend over the flight dates
     (e.g., ramp-up, sustain, heavy-up around key beats).
   ${plannerVerticalConfig.phasingGuidance}

   C. Audience & Targeting
   - Outline 3–6 audience concepts (e.g., core demo, intent/affinity,
     retargeting pools, lookalikes, geo, contextual, etc.).
   - Note which platforms are best for each audience idea.

   D. Creative Guidance
   - Provide 4–8 bullets of creative guidance tailored to the goal type and KPI.
   - Include suggestions for video vs. static, hooks, CTAs, and format mix
     (e.g., short-form vertical, long-form video, carousels, etc.).

   E. Optimization Logic & Guardrails
   - Describe how to manage pacing, bid strategies, and optimization.
   - Explain how to shift budget between platforms or tactics week to week
     based on performance versus the primary KPI.
   - Include any guardrails (e.g., frequency caps, minimum spend thresholds).

3. Use concise bullets and subheadings, not giant walls of text.
4. When the goal type is:
${plannerVerticalConfig.goalResponseGuidance}

${programmaticRoutingSection}

6. Apply this vertical-specific planning context:
${plannerVerticalConfig.promptGuidance}

${platformRoleSection}

8. Guardrails:
- ${verticalIntelligence.unsupportedClaimGuards.join("\n- ")}
- Use cross-platform language only.
- Do not expose internal terms: canonical, fallback, resolver, raw spend, snapshot, cache, rollup.

Now generate the full media plan in this structured, implementation-ready format.
`.trim();
};

const normalizePlatformArray = (value) => {
  if (Array.isArray(value)) return orderPlannerPlatformKeys(value.filter(Boolean).map(String));

  if (typeof value === "string" && value.trim()) {
    return orderPlannerPlatformKeys(
      value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }

  if (value && typeof value === "object") {
    const maybe = value.platforms || value.targetPlatforms || value.target_platforms || [];
    if (Array.isArray(maybe)) return orderPlannerPlatformKeys(maybe.filter(Boolean).map(String));
  }

  return [];
};

const getGoalTypeFromCampaign = (campaign) => {
  if (!campaign) return "";
  return normalizePlannerGoalTypeValue(
    campaign.goal_type ||
    campaign.goalType ||
    campaign.objective ||
    campaign.strategy_payload?.goalType ||
    campaign.strategy_payload?.goal_type ||
    ""
  );
};

const getStrategyFromCampaign = (campaign) => {
  if (!campaign) return "";
  return (
    campaign.strategy ||
    campaign.strategy_text ||
    campaign.plan ||
    campaign.media_plan ||
    campaign.strategy_payload?.strategyResult?.strategy ||
    campaign.strategy_payload?.mediaPlanResult?.strategy ||
    campaign.strategyPayload?.strategyResult?.strategy ||
    campaign.strategyPayload?.mediaPlanResult?.strategy ||
    ""
  );
};

// ── Canonical structured strategy presentation (single financial authority) ──
// When the backend has written strategy_payload.strategyPresentation with this
// exact schema version, it is the ONLY strategy renderer input: every legacy
// text-parsed surface (allocation tiles, rich-text platform sections, repair
// bullet blocks, mediaPlanResult strategy text) is suppressed. Campaigns
// without a presentation keep the legacy renderers untouched.
export const SHERLOC_STRATEGY_PRESENTATION_SCHEMA_VERSION =
  "sherloc_strategy_presentation_v1";

export const getSherlocStrategyPresentationFromCampaign = (campaign) => {
  const payload =
    campaign?.strategy_payload || campaign?.strategyPayload || {};
  const presentation = payload?.strategyPresentation;
  if (
    presentation &&
    typeof presentation === "object" &&
    !Array.isArray(presentation) &&
    presentation.schemaVersion === SHERLOC_STRATEGY_PRESENTATION_SCHEMA_VERSION
  ) {
    return presentation;
  }
  return null;
};

export const isCanonicalSherlocStrategyPresentation = (presentation) =>
  Boolean(
    presentation &&
      typeof presentation === "object" &&
      !Array.isArray(presentation) &&
      presentation.schemaVersion === SHERLOC_STRATEGY_PRESENTATION_SCHEMA_VERSION
  );

const normalizeDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const trimmed = value.trim();
    const ymdMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
    const ymdSlashMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (ymdSlashMatch) {
      return `${ymdSlashMatch[1]}-${String(ymdSlashMatch[2]).padStart(2, "0")}-${String(
        ymdSlashMatch[3]
      ).padStart(2, "0")}`;
    }
    const mdYMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdYMatch) {
      return `${mdYMatch[3]}-${String(mdYMatch[1]).padStart(2, "0")}-${String(
        mdYMatch[2]
      ).padStart(2, "0")}`;
    }
  }

  const parsed = parseDateLike(value);
  if (!parsed) return "";

  const yyyy = String(parsed.getFullYear()).padStart(4, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getPlannerDraftFlightDateValue = (formData, type) => {
  if (!formData || typeof formData !== "object") return "";

  const isStart = type === "start";
  const directFieldCandidates = isStart
    ? [
        "flightStart",
        "flight_start_date",
        "flightStartDate",
        "flight_start",
        "startDate",
        "start_date",
        "campaignStartDate",
        "campaign_start_date",
        "campaignStart",
        "campaign_start",
      ]
    : [
        "flightEnd",
        "flight_end_date",
        "flightEndDate",
        "flight_end",
        "endDate",
        "end_date",
        "campaignEndDate",
        "campaign_end_date",
        "campaignEnd",
        "campaign_end",
      ];

  for (const key of directFieldCandidates) {
    const normalized = normalizeDateInputValue(formData?.[key]);
    if (normalized) return normalized;
  }

  const nestedSources = [
    formData.flightDates,
    formData.flight_dates,
    formData.flightWindow,
    formData.flight_window,
    formData.flight,
    formData.dateRange,
    formData.date_range,
    formData.schedule,
    formData.timeframe,
    formData.dates,
  ].filter(Boolean);

  const nestedFieldCandidates = isStart
    ? ["start", "startDate", "start_date", "flightStart", "flight_start_date"]
    : ["end", "endDate", "end_date", "flightEnd", "flight_end_date"];

  for (const source of nestedSources) {
    for (const key of nestedFieldCandidates) {
      const normalized = normalizeDateInputValue(source?.[key]);
      if (normalized) return normalized;
    }
  }

  return "";
};

export const DEFAULT_SHERLOC_PLANNER_FORM_DATA = Object.freeze({
  advertiserId: null,
  advertiser: "",
  campaignName: "",
  budget: "",
  netMediaBudget: "",
  targetMarginPercent: "",
  goalType: "",
  primaryKPI: "",
  primaryKpi: "",
  notes: "",
  additionalNotes: "",
  destinationUrl: "",
  imdbUrl: "",
  rottenTomatoesUrl: "",
  earnedMediaProfileUrls: ["", "", ""],
  ageMin: "",
  ageMax: "",
  gender: "all",
  interests: "",
  customTargeting: "",
  geoTargetingNotes: "",
  geoTargeting: "",
  locationTargeting: "",
  languageTargeting: "",
  deviceTargetingNotes: "",
  devicePlatformNotes: "",
  flightStart: "",
  flightEnd: "",
  releaseDate: "",
  platforms: [],
});

// Alias spellings that hydration payloads may carry for the financial fields.
// Once a form snapshot is canonicalized these must NOT survive on live form
// state: a retained stale alias (e.g. total_budget from a cloned source
// campaign) would silently resurrect the old value the next time the field is
// cleared and re-normalized.
const PLANNER_CANONICALIZED_FINANCIAL_ALIAS_KEYS = Object.freeze([
  "totalBudget",
  "total_budget",
  "grossBudget",
  "gross_budget",
  "net_media_budget",
  "netBudget",
  "mediaNetBudget",
  "approvedNetMediaBudget",
  "target_margin_percent",
  "targetMargin",
  "target_margin",
  "targetMarginPct",
  "target_margin_pct",
]);

export const normalizeSherlocPlannerFormData = (fd = {}) => {
  const normalizedBudget = firstBudgetDisplayValue(
    fd?.budget,
    fd?.totalBudget,
    fd?.total_budget,
    fd?.grossBudget,
    fd?.gross_budget
  );
  const normalizedTargetMarginPercent = firstTargetMarginDisplayValue(
    fd?.targetMarginPercent,
    fd?.target_margin_percent,
    fd?.targetMargin,
    fd?.target_margin
  );
  const normalizedNetMediaBudget = resolveNetMediaBudgetValue(
    { grossBudget: normalizedBudget, targetMarginPercent: normalizedTargetMarginPercent },
    fd?.netMediaBudget,
    fd?.net_media_budget,
    fd?.netBudget,
    fd?.mediaNetBudget,
    fd?.approvedNetMediaBudget
  );

  const normalized = {
    ...DEFAULT_SHERLOC_PLANNER_FORM_DATA,
    ...fd,
    advertiserId:
      fd?.advertiserId != null && String(fd.advertiserId).trim()
        ? String(fd.advertiserId).trim()
        : null,
    advertiser: String(fd?.advertiser || "").trim(),
    campaignName: String(fd?.campaignName || ""),
    budget: normalizedBudget,
    netMediaBudget: normalizedNetMediaBudget,
    targetMarginPercent: normalizedTargetMarginPercent,
    goalType: normalizePlannerGoalTypeValue(fd?.goalType),
    primaryKPI: String(fd?.primaryKPI || fd?.primaryKpi || fd?.primary_kpi || ""),
    primaryKpi: String(fd?.primaryKpi || fd?.primaryKPI || fd?.primary_kpi || ""),
    notes: String(fd?.notes ?? fd?.additionalNotes ?? fd?.additional_notes ?? ""),
    additionalNotes: String(fd?.additionalNotes ?? fd?.additional_notes ?? fd?.notes ?? ""),
    destinationUrl: String(fd?.destinationUrl || fd?.destination_url || ""),
    imdbUrl: String(fd?.imdbUrl || fd?.imdb_url || ""),
    rottenTomatoesUrl: String(fd?.rottenTomatoesUrl || fd?.rotten_tomatoes_url || ""),
    earnedMediaProfileUrls: normalizeEarnedMediaProfileUrlList(
      fd?.earnedMediaProfileUrls ?? fd?.earned_media_profile_urls
    ),
    ageMin: String(fd?.ageMin || ""),
    ageMax: String(fd?.ageMax || ""),
    gender: normalizePlannerGender(fd?.gender),
    interests: toCommaSeparatedValue(fd?.interests),
    customTargeting: toCommaSeparatedValue(fd?.customTargeting ?? fd?.custom_targeting),
    geoTargetingNotes: String(
      fd?.geoTargetingNotes ||
        fd?.geo_targeting_notes ||
        fd?.geoTargeting ||
        fd?.geo_targeting ||
        fd?.locationTargeting ||
        fd?.location_targeting ||
        ""
    ),
    geoTargeting: String(
      fd?.geoTargeting ||
        fd?.geoTargetingNotes ||
        fd?.geo_targeting_notes ||
        fd?.locationTargeting ||
        ""
    ),
    locationTargeting: String(
      fd?.locationTargeting ||
        fd?.geoTargeting ||
        fd?.geoTargetingNotes ||
        fd?.geo_targeting_notes ||
        ""
    ),
    languageTargeting: String(fd?.languageTargeting || fd?.language_targeting || ""),
    deviceTargetingNotes: String(
      fd?.deviceTargetingNotes ||
        fd?.device_targeting_notes ||
        fd?.devicePlatformNotes ||
        fd?.device_platform_notes ||
        ""
    ),
    devicePlatformNotes: String(
      fd?.devicePlatformNotes ||
        fd?.deviceTargetingNotes ||
        fd?.device_targeting_notes ||
        ""
    ),
    flightStart: getPlannerDraftFlightDateValue(fd, "start"),
    flightEnd: getPlannerDraftFlightDateValue(fd, "end"),
    releaseDate: normalizeDateInputValue(fd?.releaseDate ?? fd?.release_date),
    platforms: normalizePlatformArray(fd?.platforms),
  };
  // The aliases were consumed above; live form state keeps only the
  // canonical keys so a stale alias can never overwrite an operator edit.
  PLANNER_CANONICALIZED_FINANCIAL_ALIAS_KEYS.forEach((aliasKey) => {
    delete normalized[aliasKey];
  });
  return normalized;
};

const getCampaignFlightDateValue = (campaign, type) => {
  if (!campaign || typeof campaign !== "object") return "";

  const isStart = type === "start";
    const fieldCandidates = isStart
    ? [
        "start",
        "flight_start_date",
        "flightStartDate",
        "flight_start",
        "flightStart",
        "start_date",
        "startDate",
        "campaign_start_date",
        "campaignStartDate",
        "campaign_start",
        "campaignStart",
      ]
    : [
        "end",
        "flight_end_date",
        "flightEndDate",
        "flight_end",
        "flightEnd",
        "end_date",
        "endDate",
        "campaign_end_date",
        "campaignEndDate",
        "campaign_end",
        "campaignEnd",
      ];

  const nestedSources = [
    campaign,
    campaign.flightWindow,
    campaign.flight_window,
    campaign.flightDates,
    campaign.flight_dates,
    campaign.planner,
    campaign.plannerPayload,
    campaign.planner_payload,
    campaign.strategyInputs,
    campaign.strategy_inputs,
    campaign.selectedCampaign,
    campaign.campaign,
    campaign.campaign?.flightWindow,
    campaign.campaign?.flight_window,
    campaign.campaign?.planner,
    campaign.campaign?.plannerPayload,
    campaign.campaign?.planner_payload,
    campaign.campaign?.strategyInputs,
    campaign.campaign?.strategy_inputs,
    campaign.strategy_payload,
    campaign.strategy_payload?.plannerDraft,
    campaign.strategy_payload?.plannerDraft?.formData,
    campaign.strategy_payload?.formData,
    campaign.strategy_payload?.plannerFormData,
    campaign.strategy_payload?.mediaPlan,
    campaign.strategy_payload?.flightWindow,
    campaign.strategy_payload?.flight_window,
    campaign.strategy_payload?.planner,
    campaign.strategy_payload?.plannerPayload,
    campaign.strategy_payload?.planner_payload,
    campaign.strategy_payload?.strategyInputs,
    campaign.strategy_payload?.strategy_inputs,
    campaign.strategyPayload,
    campaign.strategyPayload?.plannerDraft,
    campaign.strategyPayload?.plannerDraft?.formData,
    campaign.strategyPayload?.formData,
    campaign.strategyPayload?.plannerFormData,
    campaign.strategyPayload?.mediaPlan,
    campaign.strategyPayload?.flightWindow,
    campaign.strategyPayload?.flight_window,
    campaign.strategyPayload?.planner,
    campaign.strategyPayload?.plannerPayload,
    campaign.strategyPayload?.planner_payload,
    campaign.strategyPayload?.strategyInputs,
    campaign.strategyPayload?.strategy_inputs,
    campaign.strategy_payload_contract,
    campaign.strategy_payload_contract?.plannerDraft,
    campaign.strategy_payload_contract?.plannerDraft?.formData,
    campaign.strategy_payload_contract?.formData,
    campaign.strategy_payload_contract?.plannerFormData,
    campaign.strategy_payload_contract?.mediaPlan,
    campaign.strategy_payload_contract?.flightWindow,
    campaign.strategy_payload_contract?.flight_window,
    campaign.strategy_payload_contract?.planner,
    campaign.strategy_payload_contract?.plannerPayload,
    campaign.strategy_payload_contract?.planner_payload,
    campaign.strategy_payload_contract?.strategyInputs,
    campaign.strategy_payload_contract?.strategy_inputs,
    campaign.strategyPayloadContract,
    campaign.strategyPayloadContract?.plannerDraft,
    campaign.strategyPayloadContract?.plannerDraft?.formData,
    campaign.strategyPayloadContract?.formData,
    campaign.strategyPayloadContract?.plannerFormData,
    campaign.strategyPayloadContract?.mediaPlan,
    campaign.strategyPayloadContract?.flightWindow,
    campaign.strategyPayloadContract?.flight_window,
    campaign.strategyPayloadContract?.planner,
    campaign.strategyPayloadContract?.plannerPayload,
    campaign.strategyPayloadContract?.planner_payload,
    campaign.strategyPayloadContract?.strategyInputs,
    campaign.strategyPayloadContract?.strategy_inputs,
    campaign.ai_plan,
    campaign.ai_plan?.plannerDraft,
    campaign.ai_plan?.plannerDraft?.formData,
    campaign.ai_plan?.formData,
    campaign.ai_plan?.plannerFormData,
    campaign.ai_plan?.flightWindow,
    campaign.ai_plan?.flight_window,
    campaign.ai_plan?.planner,
    campaign.ai_plan?.plannerPayload,
    campaign.ai_plan?.planner_payload,
    campaign.ai_plan?.strategyInputs,
    campaign.ai_plan?.strategy_inputs,
    campaign.aiPlan,
    campaign.aiPlan?.plannerDraft,
    campaign.aiPlan?.plannerDraft?.formData,
    campaign.aiPlan?.formData,
    campaign.aiPlan?.plannerFormData,
    campaign.aiPlan?.flightWindow,
    campaign.aiPlan?.flight_window,
    campaign.aiPlan?.planner,
    campaign.aiPlan?.plannerPayload,
    campaign.aiPlan?.planner_payload,
    campaign.aiPlan?.strategyInputs,
    campaign.aiPlan?.strategy_inputs,
  ].filter(Boolean);

  for (const source of nestedSources) {
    for (const key of fieldCandidates) {
      const value = source?.[key];
      const normalized = normalizeDateInputValue(value);
      if (normalized) return normalized;
    }
  }

  return "";
};

const resolveLoadedCampaignFlightDates = (campaign, fallbackFormData = {}) => {
  const loadedFlightStart = getCampaignFlightDateValue(campaign, "start");
  const loadedFlightEnd = getCampaignFlightDateValue(campaign, "end");
  const fallbackFlightStart = getPlannerDraftFlightDateValue(fallbackFormData, "start");
  const fallbackFlightEnd = getPlannerDraftFlightDateValue(fallbackFormData, "end");

  return {
    flightStart: loadedFlightStart || fallbackFlightStart,
    flightEnd: loadedFlightEnd || fallbackFlightEnd,
  };
};

// Canonical read-normalization for the entertainment-only release date. The
// planner always WRITES formData.releaseDate (YYYY-MM-DD); reads tolerate the
// snake_case alias and the known strategy-payload nesting shapes.
//
// EDITOR PRECEDENCE: the value the planner itself saved (formData and its
// mirrors) must outrank the canonical top-level hoist, and both must outrank
// any campaign-level legacy/display carrier. This keeps a stale top-level or
// generated value from ever reverting a planner-entered date. Candidates are
// normalized individually, so one invalid higher-priority value can never
// mask a valid lower-priority value. eSign contract containers are never
// release-date sources. There is deliberately NO current-date fallback: with
// no saved value anywhere this resolves to "".
const RELEASE_DATE_FIELD_CANDIDATES = Object.freeze(["releaseDate", "release_date"]);

const getCampaignReleaseDateValue = (campaign) => {
  if (!campaign || typeof campaign !== "object") return "";

  const nestedSources = [
    // 1) Planner-saved editable value (authoritative).
    campaign.strategy_payload?.formData,
    campaign.strategyPayload?.formData,
    campaign.strategy_payload?.plannerDraft?.formData,
    campaign.strategyPayload?.plannerDraft?.formData,
    campaign.strategy_payload?.plannerFormData,
    campaign.strategyPayload?.plannerFormData,
    // 2) Canonical top-level hoist.
    campaign.strategy_payload,
    campaign.strategyPayload,
    // 3) Compatible legacy display carriers, only when no planner value exists.
    campaign,
    campaign.campaign,
  ].filter(Boolean);

  for (const source of nestedSources) {
    for (const key of RELEASE_DATE_FIELD_CANDIDATES) {
      const normalized = normalizeDateInputValue(source?.[key]);
      if (normalized) return normalized;
    }
  }

  return "";
};

// Entertainment-only: blank the release date whenever the resolved vertical
// does not enable it, so switching an advertiser away from entertainment never
// submits a hidden stale value.
const gatePlannerReleaseDateForVertical = (formData, config) =>
  config?.releaseDateEnabled ? formData : { ...formData, releaseDate: "" };

// Test surface for the entertainment-only Release Date intake.
export const __releaseDateTesting = {
  getPlannerVerticalConfig,
  isReleaseDateEnabled: (vertical) =>
    Boolean(getPlannerVerticalConfig(vertical)?.releaseDateEnabled),
  normalizeDateInputValue,
  getCampaignReleaseDateValue,
  gatePlannerReleaseDateForVertical,
};

const buildPlannerFormDataFromCampaign = (campaign, advertiserMeta = {}) => {
  const strategyPayload = campaign?.strategy_payload || campaign?.strategyPayload || {};
  const savedFormData =
    strategyPayload?.plannerDraft &&
    typeof strategyPayload.plannerDraft === "object" &&
    !Array.isArray(strategyPayload.plannerDraft)
      ? strategyPayload.plannerDraft.formData || {}
      : strategyPayload?.formData && typeof strategyPayload.formData === "object"
      ? strategyPayload.formData
      : strategyPayload?.plannerFormData && typeof strategyPayload.plannerFormData === "object"
      ? strategyPayload.plannerFormData
      : {};
  const campAdvId =
    advertiserMeta.advertiserId ??
    savedFormData?.advertiserId ??
    savedFormData?.advertiser_id ??
    campaign?.advertiser_id ??
    campaign?.advertiserId ??
    campaign?.advertiserID ??
    null;
  const campAdvName = String(
    advertiserMeta.advertiserName ??
      savedFormData?.advertiser ??
      savedFormData?.advertiserName ??
      savedFormData?.advertiser_name ??
      campaign?.advertiser_name ??
      campaign?.advertiser ??
      ""
  ).trim();
  const campaignBudget =
    campaign?.total_budget != null
      ? String(campaign.total_budget)
      : campaign?.budget != null
      ? String(campaign.budget)
      : savedFormData?.budget ?? "";
  const campaignTargetMarginPercent = firstTargetMarginDisplayValue(
    campaign?.target_margin_percent,
    campaign?.targetMarginPercent,
    campaign?.target_margin,
    campaign?.targetMargin,
    campaign?.strategy_payload_contract?.target_margin_percent,
    campaign?.strategy_payload_contract?.targetMarginPercent,
    campaign?.strategyPayloadContract?.target_margin_percent,
    campaign?.strategyPayloadContract?.targetMarginPercent,
    strategyPayload?.targetMarginPercent,
    strategyPayload?.target_margin_percent,
    strategyPayload?.targetMargin,
    strategyPayload?.target_margin,
    strategyPayload?.financials?.targetMarginPercent,
    strategyPayload?.financials?.target_margin_percent,
    savedFormData?.targetMarginPercent,
    savedFormData?.target_margin_percent,
    savedFormData?.targetMargin,
    savedFormData?.target_margin,
    campaign?.financial_summary?.target_margin_percent,
    campaign?.financial_summary?.targetMarginPercent,
    campaign?.financialSummary?.target_margin_percent,
    campaign?.financialSummary?.targetMarginPercent
  );
  const campaignNetMediaBudget = resolveNetMediaBudgetValue(
    { grossBudget: campaignBudget, targetMarginPercent: campaignTargetMarginPercent },
    campaign?.netMediaBudget,
    campaign?.net_media_budget,
    campaign?.netBudget,
    campaign?.mediaNetBudget,
    campaign?.approvedNetMediaBudget,
    campaign?.financial_summary?.net_media_budget,
    campaign?.financial_summary?.netMediaBudget,
    campaign?.financialSummary?.net_media_budget,
    campaign?.financialSummary?.netMediaBudget,
    savedFormData?.netMediaBudget,
    savedFormData?.net_media_budget,
    strategyPayload?.netMediaBudget,
    strategyPayload?.net_media_budget,
    strategyPayload?.financials?.netMediaBudget,
    strategyPayload?.financials?.net_media_budget
  );

  return {
    ...savedFormData,
    advertiserId: campAdvId != null ? String(campAdvId) : null,
    advertiser: campAdvName,
    campaignName:
      campaign?.campaign_name ??
      campaign?.campaignName ??
      savedFormData?.campaignName ??
      savedFormData?.campaign_name ??
      "",
    budget: campaignBudget,
    netMediaBudget: campaignNetMediaBudget,
    targetMarginPercent: campaignTargetMarginPercent,
    goalType: getGoalTypeFromCampaign(campaign) || savedFormData?.goalType || "",
    primaryKPI: campaign?.primary_kpi ?? campaign?.primaryKPI ?? savedFormData?.primaryKPI ?? savedFormData?.primaryKpi ?? "",
    notes: campaign?.notes ?? savedFormData?.notes ?? savedFormData?.additionalNotes ?? "",
    destinationUrl: getDestinationUrlFromCampaign(campaign),
    imdbUrl: getImdbUrlFromCampaign(campaign),
    rottenTomatoesUrl: getRottenTomatoesUrlFromCampaign(campaign),
    ...getPlannerTargetingFromCampaign(campaign),
    flightStart: getCampaignFlightDateValue(campaign, "start"),
    flightEnd: getCampaignFlightDateValue(campaign, "end"),
    releaseDate: getCampaignReleaseDateValue(campaign),
    platforms: normalizePlatformArray(
      campaign?.target_platforms ??
        campaign?.targetPlatforms ??
        campaign?.platforms ??
        savedFormData?.platforms ??
        savedFormData?.targetPlatforms ??
        strategyPayload?.platforms
    ),
  };
};

const formatDraftTimestamp = (iso) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const parseDateLike = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const campaignUpdatedAt = (c) => {
  return (
    c?.updatedAt ||
    c?.updated_at ||
    c?.last_updated ||
    c?.modifiedAt ||
    c?.modified_at ||
    c?.createdAt ||
    c?.created_at ||
    c?.created ||
    null
  );
};

const campaignTitle = (c) => {
  return (
    c?.campaign_name ||
    c?.campaignName ||
    c?.name ||
    c?.title ||
    `Campaign ${(c?.id) ?? ""}`.trim()
  );
};

const normalizeCampaignStatusValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getCampaignStatusCandidates = (campaign) => {
  const sources = [
    campaign,
    campaign?.selectedCampaign,
    campaign?.campaign,
    campaign?.strategy_payload,
    campaign?.strategyPayload,
    campaign?.strategy_payload_contract,
    campaign?.strategyPayloadContract,
  ].filter(Boolean);
  const keys = [
    "status",
    "campaign_status",
    "campaignStatus",
    "contract_status",
    "contractStatus",
    "authorization_status",
    "authorizationStatus",
    "approval_status",
    "approvalStatus",
  ];

  return sources
    .flatMap((source) => keys.map((key) => normalizeCampaignStatusValue(source?.[key])))
    .filter(Boolean);
};

const isBlockedCampaignStatus = (statusValues) =>
  statusValues.some((status) =>
    /(^|_)(draft|voided|declined|expired|cancelled|canceled|archived|deleted|rejected)(_|$)/.test(
      status
    )
  );

const isApprovedCampaignStatus = (statusValues) =>
  statusValues.some((status) =>
    /(^|_)(live|active|in_flight|inflight|approved|authorized|signed|countersigned|executed|complete|completed)(_|$)/.test(
      status
    )
  );

const selectBestLoadableCampaign = (campaigns = [], now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayTs = today.getTime();

  const ranked = (Array.isArray(campaigns) ? campaigns : [])
    .map((campaign, index) => {
      const id = campaign?.id ?? campaign?._id ?? campaign?.campaign_id ?? campaign?.campaignId;
      if (!id) return null;

      const statuses = getCampaignStatusCandidates(campaign);
      if (isBlockedCampaignStatus(statuses)) return null;

      const start = parseDateLike(getCampaignFlightDateValue(campaign, "start"));
      const end = parseDateLike(getCampaignFlightDateValue(campaign, "end"));
      const startTs = start ? new Date(start).setHours(0, 0, 0, 0) : null;
      const endTs = end ? new Date(end).setHours(23, 59, 59, 999) : null;
      const hasWindow = startTs != null || endTs != null;
      const inWindow =
        (!startTs || startTs <= todayTs) && (!endTs || todayTs <= endTs);
      const explicitLive = statuses.some((status) =>
        /(^|_)(live|active|in_flight|inflight)(_|$)/.test(status)
      );
      const approved = isApprovedCampaignStatus(statuses);
      const updated = parseDateLike(campaignUpdatedAt(campaign));

      let rank = 0;
      if ((explicitLive || approved) && hasWindow && inWindow) rank = 400;
      else if (explicitLive) rank = 350;
      else if (approved) rank = 300;
      else if (hasWindow && inWindow) rank = 200;
      else rank = 100;

      return {
        campaign,
        rank,
        updatedTs: updated ? updated.getTime() : 0,
        startTs: startTs || 0,
        index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.rank !== a.rank) return b.rank - a.rank;
      if (b.updatedTs !== a.updatedTs) return b.updatedTs - a.updatedTs;
      if (b.startTs !== a.startTs) return b.startTs - a.startTs;
      return a.index - b.index;
    });

  return ranked[0]?.campaign || null;
};

const getCampaignIdValue = (campaign) =>
  campaign?.id ?? campaign?._id ?? campaign?.campaign_id ?? campaign?.campaignId ?? null;

/**
 * Decide which campaign (if any) to auto-select after a Load Campaign fetch.
 * - exactly one campaign → that campaign (autoLoadedSole: true), even if it
 *   wouldn't rank as the "best loadable" pick (e.g. a single draft);
 * - multiple campaigns → the best loadable one (existing behavior, sole: false);
 * - zero campaigns → none.
 */
export const resolvePlannerCampaignAutoSelection = (campaigns = []) => {
  const list = Array.isArray(campaigns) ? campaigns : [];
  const soleCampaign = list.length === 1 ? list[0] : null;
  const targetCampaign =
    soleCampaign || (list.length > 1 ? selectBestLoadableCampaign(list) : null);
  const rawId = getCampaignIdValue(targetCampaign);
  const campaignId = rawId != null ? String(rawId) : null;
  return {
    campaignId,
    autoLoadedSole: Boolean(soleCampaign && campaignId),
  };
};

/**
 * ✅ Ultra-tolerant campaign array extractor (list endpoints)
 */
const extractCampaignArray = (data) => {
  if (!data) return [];

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return extractCampaignArray(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(data)) return data;

  const candidates = [
    data?.campaigns,
    data?.campaigns?.rows,
    data?.campaigns?.data,
    data?.mediaCampaigns,
    data?.media_campaigns,
    data?.rows,
    data?.data,
    data?.data?.campaigns,
    data?.data?.rows,
    data?.result,
    data?.results,
    data?.items,
    data?.payload,
    data?.payload?.rows,
  ].filter(Boolean);

  const firstArray = candidates.find((x) => Array.isArray(x));
  if (firstArray) return firstArray;

  if (data && typeof data === "object") {
    const keys = Object.keys(data);
    const looksNumericKeyed =
      keys.length > 0 &&
      keys.every((k) => /^\d+$/.test(k)) &&
      keys.some((k) => data[k] && typeof data[k] === "object");
    if (looksNumericKeyed) return Object.values(data);
  }

  return [];
};

function SherlocRichOutputWithHeroes({
  text,
  emptyMessage = "No output yet.",
  showChannelHeroes = true,
  selectedPlatforms = [],
  budget = "",
  netMediaBudget = "",
  notes = "",
  canonicalAllocation = null,
  allocationAuthority = null,
  useStructuredLayout = true,
}) {
  if (useStructuredLayout) {
    return (
      <SherlocStructuredStrategyOutput
        text={text}
        emptyMessage={emptyMessage}
        selectedPlatforms={selectedPlatforms}
        budget={budget}
        netMediaBudget={netMediaBudget}
        notes={notes}
        canonicalAllocation={canonicalAllocation}
        allocationAuthority={allocationAuthority}
      />
    );
  }

  const allowedHeroKeys = getSelectedPlanPlatformKeys(selectedPlatforms);

  return (
    <CphereAnalysisWriteup
      text={text}
      emptyMessage={emptyMessage}
      prefix="smp-analysis"
      resolveHero={(line) =>
        showChannelHeroes ? resolveHeroFromLine(line, selectedPlatforms) : null
      }
      allowedHeroKeys={allowedHeroKeys}
      heroOnPlainHeaders={true}
    />
  );
}


const SHERLOC_STRATEGY_SECTION_DEFINITIONS = Object.freeze([
  { key: "overview", label: "Overview" },
  { key: "objective", label: "Objective" },
  { key: "channelStrategy", label: "Channel Strategy" },
  { key: "budgetAllocation", label: "Budget Allocation" },
  { key: "audienceStrategy", label: "Audience Strategy" },
  { key: "flighting", label: "Flighting / Sequencing" },
  { key: "geography", label: "Geography" },
  { key: "creativeApproach", label: "Creative Approach" },
  { key: "measurementKpi", label: "Measurement / KPI Framework" },
  { key: "optimizationGuidance", label: "Optimization Guidance" },
  { key: "risksDependencies", label: "Risks / Dependencies" },
  { key: "nextActions", label: "Recommended Next Actions" },
]);

const SHERLOC_STRATEGY_SECTION_LABELS = SHERLOC_STRATEGY_SECTION_DEFINITIONS.reduce(
  (acc, section) => ({ ...acc, [section.key]: section.label }),
  {}
);

const SHERLOC_STRATEGY_LOGO_PLACEHOLDER_PATTERN =
  /^(?:meta|facebook|instagram|youtube|tik\s*tok|tiktok|connected\s*tv|ctv|display|retargeting|google|search|mobile|video|dooh|audio|email|amazon|shopify|linkedin|x|reddit)\s+(?:logo|icon)$/i;

const SHERLOC_ALLOCATION_LABELS = Object.freeze({
  meta: "Meta (Facebook + Instagram)",
  youtube: "YouTube",
  ctv: "CTV",
  tiktok: "TikTok",
  viant: "Viant",
  display: "Display / Retargeting",
});

const SHERLOC_ALLOCATION_PATTERN =
  /(meta(?:\s*\([^)]*\))?|facebook\s*(?:\+|&|and)\s*instagram|fb\s*(?:\+|&|and|\/)\s*ig|youtube|you\s*tube|connected\s*(?:tv|television)|ctv|tik\s*tok|tiktok|display\s*(?:\/|and|&|\+)\s*retargeting|display|retargeting)[^\n;|]{0,90}?(\d+(?:\.\d+)?)\s*%/gi;

const cleanSherlocStrategyLine = (value) =>
  String(value || "")
    .replace(/^\s{0,3}#{1,6}\s+/, "")
    .replace(/^\s*>+\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/\x60/g, "")
    .trim();

const normalizeSherlocHeadingText = (value) =>
  normalizeSherlocOutputSectionHeading(cleanSherlocStrategyLine(value))
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\s+/g, " ");

const isSherlocDuplicateOverviewHeading = (value) => {
  const heading = normalizeSherlocHeadingText(value);
  return (
    heading === "sherloc media plan overview" ||
    heading === "media plan overview" ||
    heading === "sherloc recommendations"
  );
};

const isSherlocLogoPlaceholderLine = (value) =>
  SHERLOC_STRATEGY_LOGO_PLACEHOLDER_PATTERN.test(cleanSherlocStrategyLine(value));

const SHERLOC_CLIENT_INTERNAL_ECONOMICS_PATTERN =
  /\b(?:internal\s+)?(?:net\s+(?:working\s+)?media(?:\s+(?:budget|after\s+margin|investment|allocation))?|working\s+media\s+budget|target\s+margin|margin\s*%|margin\s+(?:applied|rate|percent|percentage)|net\s+figures?|net\s+values?|actual\s+media\s+investment)\b/i;

const isClientInternalSherlocEconomicsText = (value) =>
  SHERLOC_CLIENT_INTERNAL_ECONOMICS_PATTERN.test(String(value || ""));

const sanitizeClientVisibleSherlocStrategyText = (value) =>
  String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => {
      const text = String(line || "").trim();
      if (!text) return "";
      if (text.includes("|")) {
        return text
          .split(/\s*\|\s*/)
          .filter((segment) => !isClientInternalSherlocEconomicsText(segment))
          .join(" | ")
          .trim();
      }
      return isClientInternalSherlocEconomicsText(text) ? "" : text;
    })
    .filter(Boolean)
    .join("\n")
    .trim();


const getSherlocStrategySectionKey = (value) => {
  const heading = normalizeSherlocHeadingText(value);
  if (!heading || isSherlocDuplicateOverviewHeading(heading)) return "";
  if (/^(?:executive\s+|strategy\s+)?(?:overview|summary)$/.test(heading)) return "overview";
  if (/^(?:campaign\s+)?objectives?$|^(?:campaign\s+)?goals?$/.test(heading)) return "objective";
  if (/^(?:budget allocation|media mix)(?: by platform)?$/.test(heading)) return "budgetAllocation";
  if (
    /^channel strategy$|^platform strategy$|^platform recommendations$|^platform detail/.test(
      heading
    )
  ) {
    return "channelStrategy";
  }
  if (/^(?:flighting|phasing|flight strategy|sequencing)(?:\b|\s*[/&-])/.test(heading)) {
    return "flighting";
  }
  if (/^(?:creative guidance|creative approach|creative strategy|creative recommendations?)$/.test(heading)) {
    return "creativeApproach";
  }
  if (
    /^(?:audience|targeting)(?:\b|\s*[/&-])/.test(heading) ||
    /^(?:audience and geo strategy|audience geo strategy|audience targeting)$/.test(heading)
  ) {
    return "audienceStrategy";
  }
  if (/^(?:geography|geographic strategy|geo strategy|markets?|market strategy)$/.test(heading)) {
    return "geography";
  }
  if (
    /^(?:kpi expectations|kpi framework|measurement|measurement framework|measurement and kpi framework|projected impressions|performance expectations)$/.test(
      heading
    )
  ) {
    return "measurementKpi";
  }
  if (/^(?:optimization|optimization guidance|optimization logic|optimization strategy)$/.test(heading)) {
    return "optimizationGuidance";
  }
  if (
    /^(?:assumptions|watchouts|watch outs|guardrails|risks?|dependencies|launch requirements?)(?:\b|\s*[/&-])/.test(
      heading
    )
  ) {
    return "risksDependencies";
  }
  if (/^(?:recommended )?(?:next actions?|next steps?|actions?)$/.test(heading)) {
    return "nextActions";
  }
  return "";
};

const getSherlocFallbackSection = (rawValue) => {
  const raw = String(rawValue || "").trim();
  const cleanedLabel = cleanSherlocStrategyLine(raw);
  const qualificationLabel = cleanedLabel.replace(/[:：]\s*$/, "").trim();
  const isMarkdownHeading = /^\s{0,3}#{1,6}\s+\S/.test(raw);
  const isNamedPlainHeading =
    /[:：]\s*$/.test(raw) &&
    qualificationLabel.split(/\s+/).length >= 2 &&
    !/[.!?]$/.test(qualificationLabel);
  if (!isMarkdownHeading && !isNamedPlainHeading) return null;

  const label = normalizeSherlocOutputSectionHeading(cleanedLabel);
  if (!label || label.length > 72) return null;
  if (getSherlocStrategySectionKey(label) || isSherlocDuplicateOverviewHeading(label)) {
    return null;
  }
  if (
    isSherlocLogoPlaceholderLine(label) ||
    isSherlocBareAllocationPlatformLine(label) ||
    /^(?:gross|net)$/i.test(label)
  ) {
    return null;
  }

  const slug =
    normalizeSherlocHeadingText(label)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 52) || "section";
  return {
    key: "fallback-" + slug,
    label,
    isFallback: true,
  };
};

const parseSherlocCurrencyNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value || "");
  const match = text.match(/\$?\s*([\d,]+(?:\.\d+)?)\s*(k)?/i);
  if (!match) return null;
  const numeric = Number(match[1].replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return null;
  return match[2] ? numeric * 1000 : numeric;
};

const collectSherlocCurrencyNumbers = (value) => {
  const text = String(value || "");
  const matches = [...text.matchAll(/\$\s*([\d,]+(?:\.\d+)?)\s*(k)?\b/gi)];
  return matches
    .map((match) => {
      const numeric = Number(String(match[1] || "").replace(/,/g, ""));
      if (!Number.isFinite(numeric)) return null;
      return match[2] ? numeric * 1000 : numeric;
    })
    .filter((amount) => Number.isFinite(amount));
};

const isSherlocNetMediaNarrativeLine = (value) =>
  /\b(?:internal\s+)?net\s+(?:working\s+)?media(?:\s+budget)?\b/i.test(String(value || ""));

const formatSherlocCurrencyAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return "$" + numeric.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const getSherlocAllocationKey = (label) => {
  const text = String(label || "");
  const key = normalizePlatformAllocationKey(text, { allowUnknown: false });
  if (key) return key;
  return /retarget/i.test(text) ? "display" : "";
};

// Every user-facing platform label flows through the shared display-label
// normalizer so ctv-family platforms always render exactly "CTV" (persisted
// legacy labels like "Connected TV (CTV)" are display-normalized, never
// mutated). Presentation-only — keys, alias parsing, and allocation values
// are untouched.
const getSherlocAllocationLabel = (key, fallback = "") =>
  normalizeSherlocPlatformDisplayLabel(
    key,
    SHERLOC_ALLOCATION_LABELS[key] || providerSurfaceLabel(key) || String(fallback || "").trim()
  ) || "Platform";

const getSherlocExplicitBudgetText = (...values) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => String(value || "").trim())
    .find((value) => /\$\s*[\d,]+(?:\.\d+)?/.test(value)) || "";

// ── Shared descending-gross ordering for allocation ROW models ──
// Same governed contract as orderSherlocCanonicalEntriesByGrossDesc /
// orderSherlocAllocationAuthorityRows: the ONLY sort key is the authoritative
// gross allocation in INTEGER CENTS, descending; ties keep the persisted
// relative order (explicit stable index tie-break — the governed stable-order
// rule); if ANY row lacks a finite cents amount the incoming order is
// preserved unchanged (fail closed — never guess an order from partial
// financials). Pure: returns a copy; amounts, percents, and labels are never
// modified — this is a presentation ORDER change only.
export const orderSherlocAllocationRowsByGrossCentsDesc = (
  rows,
  getCents = (row) => row?.grossAllocationCents
) => {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const decorated = list.map((row, index) => {
    // Number(null) === 0: nullish/empty cents are rejected BEFORE coercion so
    // an absent amount fails closed instead of sorting as $0.
    const raw = getCents(row);
    const cents =
      raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
    return { row, index, cents };
  });
  if (!decorated.length || decorated.some((item) => !Number.isFinite(item.cents))) {
    return list;
  }
  decorated.sort((a, b) =>
    b.cents !== a.cents ? b.cents - a.cents : a.index - b.index
  );
  return decorated.map((item) => item.row);
};

// Canonical allocation rows written by the governed revision contract
// (media_plan_revision_v1): exact client dollar amounts with unrounded
// percentages, persisted in strategy_payload.requestedChannelAllocation.
// When present they are the single source of truth for the allocation cards —
// stale percentages embedded in intake notes or older narrative text must not
// resurrect removed channels or superseded splits. Items qualify when they
// carry the client-facing exact-amount contract (key + gross amount, with a
// bounded percent when one is stated); rows are ALSO accepted in the
// budget_breakdown shapes ({platform, grossAmount} or legacy {key, budget})
// so a persisted current allocation renders even without the newer field
// names. netAmount is OPTIONAL — the client portal renders the GROSS figures
// only (net media stays internal), so a missing net figure must never
// disqualify an otherwise complete canonical set.
const buildSherlocCanonicalAllocationRows = (canonicalAllocation) => {
  const items = Array.isArray(canonicalAllocation) ? canonicalAllocation : [];
  if (!items.length) return [];
  const rows = items.map((item, index) => {
    const key = getSherlocAllocationKey(
      item?.key || item?.platform || item?.channel
    );
    // A stated percent must be bounded; an ABSENT percent (legacy
    // {key, budget} budget_breakdown rows) is allowed — the row renders its
    // exact gross amount with no percent badge, never a fabricated share.
    const percentRaw = item?.percent ?? item?.share;
    const percentAbsent =
      percentRaw === null || percentRaw === undefined || percentRaw === "";
    const percent = percentAbsent ? null : Number(percentRaw);
    // Number(null) === 0: nullish amounts are rejected BEFORE coercion so a
    // missing amount reads as absent, never as $0.
    const grossAmountRaw = item?.grossAmount ?? item?.budget;
    const grossAmount =
      grossAmountRaw === null || grossAmountRaw === undefined || grossAmountRaw === ""
        ? NaN
        : Number(grossAmountRaw);
    if (!key) return null;
    if (percent !== null && (!Number.isFinite(percent) || percent <= 0 || percent > 100)) {
      return null;
    }
    if (!Number.isFinite(grossAmount) || grossAmount <= 0) return null;
    const grossAllocationCents = Math.round(grossAmount * 100);
    const roundedPercent = percent === null ? null : Math.round(percent * 10) / 10;
    return {
      key,
      label: getSherlocAllocationLabel(key, item?.channel),
      percent,
      percentLabel:
        roundedPercent === null
          ? ""
          : Number.isInteger(roundedPercent)
            ? roundedPercent + "%"
            : roundedPercent.toFixed(1) + "%",
      grossAmount,
      grossAllocationCents,
      grossAmountLabel: formatGrossAllocationCentsAmount(grossAllocationCents),
      firstIndex: index,
    };
  });
  // All-or-nothing: one malformed row disqualifies the canonical set (fail
  // closed to the text-derived rows rather than rendering a partial plan).
  // Qualified sets render in authoritative gross-cents DESCENDING order.
  return rows.every(Boolean)
    ? orderSherlocAllocationRowsByGrossCentsDesc(rows)
    : [];
};

const buildSherlocRequestedAllocationRows = ({
  text = "",
  notes = "",
  budget = "",
  canonicalAllocation = null,
} = {}) => {
  const canonicalRows = buildSherlocCanonicalAllocationRows(canonicalAllocation);
  if (canonicalRows.length) return canonicalRows;
  const source = [notes, text].filter(Boolean).join("\n");
  const totalBudget = parseSherlocCurrencyNumber(budget);
  const rowsByKey = new Map();

  const addAllocationRow = ({ rawLabel, key, percent, explicitBudgetText = "", firstIndex = 0 }) => {
    if (!key || !Number.isFinite(percent) || percent <= 0 || percent > 100) return;
    if (rowsByKey.has(key)) return;

    const allocationForBudget = explicitBudgetText && totalBudget == null
      ? { grossAmount: explicitBudgetText }
      : { percent, share: percent };
    const grossAmount = resolvePlatformBudgetAmount(allocationForBudget, {
      budgetBasis: totalBudget,
      grossBudget: totalBudget,
      totalBudget,
      percent,
    });

    rowsByKey.set(key, {
      key,
      label: getSherlocAllocationLabel(key, rawLabel),
      percent,
      percentLabel: Number.isInteger(percent) ? percent + "%" : percent.toFixed(1) + "%",
      grossAmount,
      grossAmountLabel: grossAmount != null ? formatSherlocCurrencyAmount(grossAmount) : "",
      firstIndex,
    });
  };

  // Precedence (first write wins per platform):
  //   1) Allocations stated in the intake notes — the client's requested
  //      split stays authoritative over a stale equal-split strategy text.
  //   2) The strategy text's structured Budget Allocation blocks
  //      ("Platform\n18%\n- Role…") — each platform's own section entry.
  //   3) Inline platform-percent mentions anywhere else in the source, as a
  //      fallback only. Running this pass first let a narrative sentence
  //      ("increase retargeting budget to 20–25% of mix") beat a platform's
  //      structured section entry and corrupt its percentage, while
  //      incidental overview mentions scrambled the card order.
  const notesText = String(notes || "");
  let match;
  SHERLOC_ALLOCATION_PATTERN.lastIndex = 0;
  while ((match = SHERLOC_ALLOCATION_PATTERN.exec(notesText))) {
    addAllocationRow({
      rawLabel: match[1],
      key: getSherlocAllocationKey(match[1]),
      percent: Number(match[2]),
      explicitBudgetText: getSherlocExplicitBudgetText(match[0]),
      firstIndex: match.index,
    });
  }

  const structuredItems = parseSherlocBudgetAllocationItems(source);
  structuredItems.forEach((item, index) => {
    const rawLabel = item?.label || item?.platform || item?.key;
    const key = getSherlocAllocationKey(rawLabel || item?.key);
    const percent = Number(item?.percent ?? item?.share ?? item?.allocation ?? item?.value);
    addAllocationRow({
      rawLabel,
      key,
      percent,
      explicitBudgetText: getSherlocExplicitBudgetText(
        item?.grossAmount,
        item?.grossAmountLabel,
        item?.budgetLabel,
        item?.budget,
        item?.phases
      ),
      firstIndex: notesText.length + index,
    });
  });

  // Last-resort line scan, scoped to the Budget Allocation section ONLY. The
  // full-source scan harvested KPI stats elsewhere in the strategy ("YouTube …
  // target 95%+", "Display … CTR benchmark 0.08–0.15%") as budget allocation
  // percentages and fabricated impossible dollar figures from them.
  const allocationSectionText = sliceBudgetAllocationSectionText(source);
  SHERLOC_ALLOCATION_PATTERN.lastIndex = 0;
  while ((match = SHERLOC_ALLOCATION_PATTERN.exec(allocationSectionText))) {
    addAllocationRow({
      rawLabel: match[1],
      key: getSherlocAllocationKey(match[1]),
      percent: Number(match[2]),
      explicitBudgetText: getSherlocExplicitBudgetText(match[0]),
      firstIndex: notesText.length + structuredItems.length + match.index,
    });
  }

  const rows = Array.from(rowsByKey.values()).sort((a, b) => a.firstIndex - b.firstIndex);

  // Integrity gate: allocation percentages summing above 100% (105 tolerance)
  // are internally impossible — rendering them would fabricate dollar amounts
  // (the production defect summed 190%). Fail closed to NO financial rows.
  // An under-100 sum is arithmetically consistent partial data (e.g. a legacy
  // single-platform block) and still renders as parsed.
  if (rows.length) {
    const percentTotal = rows.reduce((total, row) => total + Number(row.percent || 0), 0);
    if (percentTotal > 105) return [];
  }

  // Display contract: each row's gross allocation is recomputed in integer
  // cents from the canonical gross campaign budget (deterministic rounding;
  // reconciles to the exact gross total when the approved percentages sum to
  // 100%). Rows whose gross came from an explicit stated dollar amount (only
  // possible when no campaign budget is available) keep that stated amount.
  const grossAllocationCentsByRow = computeGrossAllocationCentsByPercent(
    totalBudget,
    rows.map((row) => row.percent)
  );
  const resolvedRows = rows.map((row, index) => {
    // Fail closed: a missing gross budget yields NO amount (never a $0 coerced
    // from null) — the card renders the governed unavailable state instead.
    const explicitGrossAmount = Number(row.grossAmount);
    const grossAllocationCents =
      grossAllocationCentsByRow[index] != null
        ? grossAllocationCentsByRow[index]
        : row.grossAmount != null &&
            Number.isFinite(explicitGrossAmount) &&
            explicitGrossAmount > 0
          ? Math.round(explicitGrossAmount * 100)
          : null;
    return {
      ...row,
      grossAllocationCents,
      grossAmountLabel:
        grossAllocationCents != null
          ? formatGrossAllocationCentsAmount(grossAllocationCents)
          : "",
    };
  });
  // Descending authoritative gross-cents presentation order (fail closed to
  // the parsed document order whenever any row lacks a resolved cents amount).
  return orderSherlocAllocationRowsByGrossCentsDesc(resolvedRows);
};

const isSherlocAllocationLikeLine = (line) =>
  /\d+(?:\.\d+)?\s*%/.test(String(line || "")) &&
  Boolean(getSherlocAllocationKey(line));

// Matches a line that is ONLY an allocation value: "22% (~$4,024)",
// "~$4,024 / 22%", or a bare percentage ("18%", "7%"). The bare-percent
// alternative is required for multi-line allocation blocks
// ("Platform\n18%\n- Role…"): the platform heading is consumed by the card
// renderer, so its value line must always be suppressed. Without it,
// two-digit percents were coincidentally consumed (the amount-then-percent
// alternative digit-split "18%" into "1"+"8%") while single-digit percents
// leaked into the section body as orphaned percentage-only paragraphs.
const SHERLOC_STANDALONE_ALLOCATION_VALUE_PATTERN =
  /^\s*(?:\d+(?:\.\d+)?\s*%\s*(?:[-–—/]\s*)?(?:\(\s*)?(?:~|≈|approx\.?\s*)?\$?\s*[\d,]+(?:\.\d+)?\s*(?:\))?|(?:~|≈|approx\.?\s*)?\$?\s*[\d,]+(?:\.\d+)?\s*(?:[-–—/]\s*)?\d+(?:\.\d+)?\s*%|\d+(?:\.\d+)?\s*%)\s*(?:gross|net)?\s*\)?\s*$/i;

const isSherlocStandaloneAllocationValueLine = (line) =>
  SHERLOC_STANDALONE_ALLOCATION_VALUE_PATTERN.test(String(line || "").trim());

const stripSherlocAllocationBulletPrefix = (line) =>
  cleanSherlocStrategyLine(line).replace(/^(?:[-*•]|[0-9]+[.)])\s+/, "").trim();

const compactSherlocAllocationLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "");

const SHERLOC_BARE_ALLOCATION_LABELS = Object.freeze({
  meta: ["meta", "meta facebook instagram", "facebook instagram", "facebook and instagram", "fb ig"],
  youtube: ["youtube", "you tube"],
  ctv: ["ctv", "connected tv", "connected television"],
  tiktok: ["tiktok", "tik tok"],
  viant: ["viant", "viant dsp", "viant dsp display"],
  display: ["display", "retargeting", "display retargeting", "display and retargeting"],
});

// Platform headings may carry qualifiers that do not change platform identity:
// parenthetical restatements ("Connected TV (CTV)"), a buying-path clause
// ("… via Cphere Programmatic"), or a spaced em/en-dash descriptor
// ("CTV — Premium Video"). Strip them before compact comparison so such
// headings still match their bare platform label.
const stripSherlocAllocationLabelQualifiers = (value) =>
  String(value || "")
    .replace(/\s+[–—]\s+.*$/, "")
    .replace(/\s+via\s+.*$/i, "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isSherlocBareAllocationPlatformLine = (line) => {
  const text = stripSherlocAllocationLabelQualifiers(
    stripSherlocAllocationBulletPrefix(line)
  )
    .replace(/[:：\-–—]+\s*$/, "")
    .trim();
  if (!text) return false;
  const key = getSherlocAllocationKey(text);
  if (!key) return false;
  const compact = compactSherlocAllocationLabel(text);
  const labels = [key, SHERLOC_ALLOCATION_LABELS[key], ...(SHERLOC_BARE_ALLOCATION_LABELS[key] || [])];
  return labels.some((label) => compact === compactSherlocAllocationLabel(label));
};

const isSherlocConflictingBudgetAllocationLine = (line, allocationRows = []) => {
  const text = stripSherlocAllocationBulletPrefix(line);
  if (!text) return false;
  if (isSherlocStandaloneAllocationValueLine(text)) return true;
  if (isSherlocAllocationLikeLine(text)) return true;
  if (isSherlocBareAllocationPlatformLine(text)) return true;

  const key = getSherlocAllocationKey(text);
  const row = (Array.isArray(allocationRows) ? allocationRows : []).find((allocation) => allocation?.key === key);
  const hasCurrency = /\$\s*[\d,]+(?:\.\d+)?/.test(text);
  const amount = hasCurrency ? parseSherlocCurrencyNumber(text) : null;
  if (key && row?.grossAmount != null && amount != null) {
    return Math.round(amount) !== Math.round(row.grossAmount);
  }

  return false;
};

const isSherlocStaleBudgetNarrativeLine = (line, budget = "", allocationRows = [], options = {}) => {
  const totalBudget = parseSherlocCurrencyNumber(budget);
  if (!(Number(totalBudget) > 0)) return false;

  const text = stripSherlocAllocationBulletPrefix(line);
  if (!text || isSherlocAllocationLikeLine(text)) return false;
  if (!/\b(?:budget|spend|allocation|gross|net media|media budget|high-tier)\b/i.test(text)) {
    return false;
  }

  const amounts = collectSherlocCurrencyNumbers(text);
  if (!amounts.length) return false;

  const netMediaBudget = parseSherlocCurrencyNumber(options.netMediaBudget);
  const isNetMediaLine = isSherlocNetMediaNarrativeLine(text);
  const acceptedAmounts = [
    totalBudget,
    ...(isNetMediaLine && Number(netMediaBudget) > 0 ? [netMediaBudget] : []),
    ...(Array.isArray(allocationRows) ? allocationRows : [])
      .map((row) => row?.grossAmount)
      .filter((amount) => Number(amount) > 0),
  ];

  if (isNetMediaLine && !(Number(netMediaBudget) > 0)) return false;

  return amounts.some(
    (amount) => !acceptedAmounts.some((accepted) => Math.round(Number(accepted)) === Math.round(amount))
  );
};

const getSherlocHeroAllocationKey = (hero) => {
  const rawKey = String(hero?.key || hero?.label || "");
  const allocationKey = normalizePlatformAllocationKey(rawKey, { allowUnknown: false });
  if (allocationKey) return allocationKey;
  const planKey = normalizePlanPlatformKey(rawKey);
  if (planKey === "facebook" || planKey === "instagram") return "meta";
  return planKey;
};

const getSherlocAllocationRowForHero = (hero, allocationRows = []) => {
  const heroKey = getSherlocHeroAllocationKey(hero);
  if (!heroKey) return null;
  return (Array.isArray(allocationRows) ? allocationRows : []).find((row) => row?.key === heroKey) || null;
};

// Heading value badges: gross allocated dollars + allocation percentage. The
// amount is the platform's share of the campaign's TOTAL/GROSS budget
// (client-facing presentation value — never net media, which stays structurally
// absent from Cortex output). Client-facing copy uses the approved Cortex
// terminology ("Allocated budget" / "Allocation unavailable") — accounting
// terms like gross/net never render; internal identifiers keep gross naming.
// Fail closed: a percentage must never render without either its valid gross
// allocation amount or the governed unavailable state — never "$0", never a
// silent omission.
const renderSherlocHeadingValueBadges = (allocation) => {
  const percentLabel = allocation?.percentLabel || "";
  const grossAmountLabel = allocation?.grossAmountLabel || "";
  if (!percentLabel && !grossAmountLabel) return null;

  return (
    <span className="smp-platform-hero-heading-values">
      {grossAmountLabel ? (
        <span
          className="smp-platform-hero-gross"
          aria-label={"Allocated budget: " + grossAmountLabel}
        >
          {grossAmountLabel}
        </span>
      ) : percentLabel ? (
        <span
          className="smp-platform-hero-gross smp-platform-hero-gross--unavailable"
          role="note"
          data-sherloc-gross-allocation-unavailable="true"
        >
          Allocation unavailable
        </span>
      ) : null}
      {percentLabel ? (
        <span
          className="smp-platform-hero-percent"
          aria-label={"Allocation percentage: " + percentLabel}
        >
          {percentLabel}
        </span>
      ) : null}
    </span>
  );
};

const renderSherlocPlatformHeroCard = (hero, allocation, key, options = {}) => {
  const image = hero?.logo || hero?.img;
  if (!hero || !image) return null;
  const heroKey = getSherlocHeroAllocationKey(hero) || normalizePlanPlatformKey(hero?.key || hero?.label);
  const label = allocation?.label || hero.label || "Platform";
  const className = ["smp-platform-hero", options.className].filter(Boolean).join(" ");

  return (
    <div
      key={key}
      className={className}
      data-sherloc-platform-hero={heroKey}
      data-sherloc-platform-card="true"
      {...(options.dataAttributes || {})}
    >
      <div className="smp-platform-hero-inner">
        <span className="smp-platform-hero-icon" aria-hidden="true">
          <img src={image} alt="" />
        </span>
        <span className="smp-platform-hero-text">
          <span className="smp-platform-hero-heading">
            <span className="smp-platform-hero-label">{label}</span>
            {renderSherlocHeadingValueBadges(allocation)}
          </span>
        </span>
      </div>
    </div>
  );
};

const getSherlocHeroForAllocationRow = (row, selectedPlatforms = []) => {
  if (!row) return null;
  const selected = new Set(
    (Array.isArray(selectedPlatforms) ? selectedPlatforms : [])
      .map((platform) => normalizePlanPlatformKey(platform))
      .filter(Boolean)
  );
  const rowKey = normalizePlatformAllocationKey(row.key || row.label, { allowUnknown: false });
  if (rowKey) selected.add(rowKey);
  if (rowKey === "meta") {
    selected.add("facebook");
    selected.add("instagram");
  }
  const selectedList = Array.from(selected);

  return (
    resolveHeroFromLine(row.label || row.key, selectedList) ||
    resolveHeroFromLine(getSherlocAllocationLabel(row.key, row.label), selectedList)
  );
};

const buildSherlocBudgetSummaryRows = (allocationRows = [], selectedPlatforms = []) =>
  (Array.isArray(allocationRows) ? allocationRows : []).map((row) => {
    const hero = getSherlocHeroForAllocationRow(row, selectedPlatforms);
    return {
      ...row,
      image: hero?.logo || hero?.img || "",
    };
  });

// Governed fail-closed state for an allocation row whose platform card cannot
// be fully rendered (e.g. no resolvable platform icon). The row's identity,
// gross allocation, and percentage stay visible with an explicit unavailable
// note — a platform allocation must never silently degrade into a detached
// copy fragment or a percentage-only row.
const renderSherlocUnavailableAllocationCard = (row, key, cardKey) => (
  <div
    key={key}
    className="smp-platform-hero smp-platform-hero--unavailable"
    role="note"
    data-sherloc-platform-card-unavailable={cardKey}
  >
    <span className="smp-platform-hero-heading">
      <span className="smp-platform-hero-label">{row?.label || "Platform"}</span>
      {renderSherlocHeadingValueBadges(row)}
    </span>
    <span className="smp-platform-hero-unavailable-note">
      Platform card details are unavailable for this allocation.
    </span>
  </div>
);

const renderSherlocBudgetAllocationCard = (row, selectedPlatforms = []) => {
  const hero = getSherlocHeroForAllocationRow(row, selectedPlatforms);
  const cardKey = row?.key || getSherlocHeroAllocationKey(hero) || row?.label || "platform";
  const reactKey = "budget-allocation-platform-hero-" + cardKey;

  return (
    renderSherlocPlatformHeroCard(hero, row, reactKey, {
      className: "smp-platform-hero--allocation",
      dataAttributes: {
        "data-sherloc-budget-allocation-card": cardKey,
      },
    }) || renderSherlocUnavailableAllocationCard(row, reactKey, cardKey)
  );
};

const renderSherlocChannelStrategyCard = (row, selectedPlatforms = []) => {
  const hero = getSherlocHeroForAllocationRow(row, selectedPlatforms);
  const cardKey = row?.key || getSherlocHeroAllocationKey(hero) || row?.label || "platform";
  const reactKey = "channel-strategy-platform-hero-" + cardKey;

  return (
    renderSherlocPlatformHeroCard(hero, row, reactKey, {
      className: "smp-platform-hero--channel",
      dataAttributes: {
        "data-sherloc-channel-strategy-card": cardKey,
      },
    }) || renderSherlocUnavailableAllocationCard(row, reactKey, cardKey)
  );
};

const normalizeSherlocStrategySections = (text = "") => {
  const definitionMap = new Map(
    SHERLOC_STRATEGY_SECTION_DEFINITIONS.map((section) => [section.key, section])
  );
  const sectionMap = new Map(
    SHERLOC_STRATEGY_SECTION_DEFINITIONS.map((section) => [
      section.key,
      { ...section, lines: [] },
    ])
  );
  const fallbackSections = [];
  const ensureSection = (definition) => {
    if (!sectionMap.has(definition.key)) {
      const section = { ...definition, lines: [] };
      sectionMap.set(definition.key, section);
      fallbackSections.push(section);
    }
    return sectionMap.get(definition.key);
  };
  let activeSection = ensureSection(definitionMap.get("overview"));

  normalizeSherlocBudgetAllocationText(text)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .forEach((rawLine) => {
      const line = cleanSherlocStrategyLine(rawLine);
      if (!line || isSherlocLogoPlaceholderLine(line)) return;

      if (isSherlocDuplicateOverviewHeading(line)) {
        activeSection = ensureSection(definitionMap.get("overview"));
        return;
      }

      const sectionKey = getSherlocStrategySectionKey(line);
      if (sectionKey) {
        activeSection = ensureSection(definitionMap.get(sectionKey));
        return;
      }

      const fallbackSection = getSherlocFallbackSection(rawLine);
      if (fallbackSection) {
        activeSection = ensureSection(fallbackSection);
        return;
      }

      activeSection.lines.push(line);
    });

  return [
    ...SHERLOC_STRATEGY_SECTION_DEFINITIONS.map((section) => sectionMap.get(section.key)),
    ...fallbackSections,
  ]
    .filter((section) => section.lines.some((line) => String(line || "").trim()))
    .map((section) => ({
      ...section,
      lines: section.lines.filter(
        (line, index, lines) =>
          String(line || "").trim() &&
          (index === 0 || line !== lines[index - 1]) &&
          !getSherlocStrategySectionKey(line) &&
          !isSherlocDuplicateOverviewHeading(line) &&
          !isSherlocLogoPlaceholderLine(line)
      ),
    }))
    .filter((section) => section.lines.length > 0);
};

// Group budget-allocation body lines by platform so each platform's strategy copy
// (Primary Role, Rationale, Funnel Stage, Estimated Delivery, Execution …) renders
// directly beneath its allocation hero card instead of as a loose block under the
// whole card list. Bare platform headings and stale/conflicting equal-split lines
// are dropped (the card already shows the platform name + correct allocation).
const groupSherlocPlatformStrategyCopy = (lines = [], allocationRows = []) => {
  const rowKeys = new Set(
    (Array.isArray(allocationRows) ? allocationRows : [])
      .map((row) => row?.key)
      .filter(Boolean)
  );
  const groups = new Map();
  const preamble = [];
  let activeKey = null;

  (Array.isArray(lines) ? lines : []).forEach((rawLine) => {
    const line = String(rawLine || "").trim();
    if (!line) return;

    // A bare platform heading switches the active platform. The heading itself is
    // dropped because the hero card already renders the platform name.
    if (isSherlocBareAllocationPlatformLine(line)) {
      const headingKey = getSherlocAllocationKey(line);
      if (headingKey && rowKeys.has(headingKey)) {
        activeKey = headingKey;
        if (!groups.has(activeKey)) groups.set(activeKey, []);
        return;
      }
    }

    // Never carry stale/conflicting equal-split allocation lines into the copy.
    if (
      isSherlocStandaloneAllocationValueLine(line) ||
      isSherlocConflictingBudgetAllocationLine(line, allocationRows)
    ) {
      return;
    }

    // Platform copy is bulleted ("- Primary Role…", "- Rationale…") or a
    // non-bulleted "Label: value" detail line ("Primary Role: …",
    // "Estimated Delivery / Impression Logic: …"). A plain sentence (e.g.
    // "Scale-down tiers preserve requested percentages…") is a general budget
    // note, not platform-specific copy, so it stays as loose section text.
    const isPlatformCopyLine =
      /^(?:[-*•]|[0-9]+[.)])\s+/.test(line) || /^[^:：]{2,52}[:：]\s+\S/.test(line);
    if (activeKey && isPlatformCopyLine) {
      groups.get(activeKey).push(rawLine);
    } else {
      preamble.push(rawLine);
    }
  });

  return { groups, preamble };
};

const buildSherlocStructuredStrategyModel = ({
  text = "",
  emptyMessage = "No output yet.",
  budget = "",
  netMediaBudget = "",
  notes = "",
  canonicalAllocation = null,
} = {}) => {
  const publicText = sanitizeClientVisibleSherlocStrategyText(text);
  const allocationRows = buildSherlocRequestedAllocationRows({
    text: publicText,
    notes,
    budget,
    canonicalAllocation,
  });
  const hasExplicitAllocation = allocationRows.length >= 2;
  const parsedSections = normalizeSherlocStrategySections(publicText)
    .map((section) => ({
      ...section,
      lines: section.lines.filter(
        (line) =>
          !isSherlocStaleBudgetNarrativeLine(line, budget, allocationRows, {
            netMediaBudget,
          })
      ),
    }))
    .filter((section) => section.lines.length > 0);

  // Group platform copy from the RAW budget-allocation lines (before conflicting
  // lines are filtered out) so each platform's copy can be attached under its card.
  const rawBudgetLines =
    parsedSections.find((section) => section.key === "budgetAllocation")?.lines || [];
  const rawChannelStrategyLines =
    parsedSections.find((section) => section.key === "channelStrategy")?.lines || [];
  const platformStrategyCopy = hasExplicitAllocation
    ? groupSherlocPlatformStrategyCopy(rawBudgetLines, allocationRows)
    : { groups: new Map(), preamble: rawBudgetLines };
  const channelStrategyCopy = hasExplicitAllocation
    ? groupSherlocPlatformStrategyCopy(rawChannelStrategyLines, allocationRows)
    : { groups: new Map(), preamble: rawChannelStrategyLines };

  const sections = parsedSections.map((section) => {
    if (section.key !== "budgetAllocation" || !hasExplicitAllocation) return section;
    // Platform-attributed copy is rendered beneath each allocation card via
    // platformStrategyCopy.groups, so the budget section's loose lines are exactly the
    // non-platform "preamble" — keeping grouped/fallback copy and stale allocation values
    // out of the section body.
    return { ...section, lines: platformStrategyCopy.preamble };
  });

  if (hasExplicitAllocation && !sections.some((section) => section.key === "budgetAllocation")) {
    const channelIndex = sections.findIndex((section) => section.key === "channelStrategy");
    const insertAt = channelIndex >= 0 ? channelIndex + 1 : Math.min(2, sections.length);
    sections.splice(insertAt, 0, {
      key: "budgetAllocation",
      label: SHERLOC_STRATEGY_SECTION_LABELS.budgetAllocation,
      lines: [],
    });
  }

  return {
    emptyMessage,
    allocationRows,
    sections,
    platformStrategyCopy,
    channelStrategyCopy,
    hasContent: sections.length > 0 || allocationRows.length > 0,
  };
};

const buildSherlocRenderableItems = (lines = []) => {
  const items = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    items.push({ type: "bullets", lines: bullets });
    bullets = [];
  };

  lines.forEach((line) => {
    const bullet = String(line || "").match(/^(?:[-*•]|[0-9]+[.)])\s+(.+)$/);
    if (bullet) {
      bullets.push(bullet[1].trim());
      return;
    }

    flushBullets();
    const text = String(line || "").trim();
    const detail = text.match(/^([^:：]{2,52})[:：]\s+(.+)$/);
    if (detail && !/^https?$/i.test(detail[1].trim())) {
      items.push({
        type: "detail",
        label: detail[1].trim(),
        value: detail[2].trim(),
      });
    } else if (text) {
      items.push({ type: "paragraph", text });
    }
  });

  flushBullets();
  return items;
};

// Earned-media truth state (deterministic, presentation-only). Persisted
// strategy text can falsely claim that no earned-media inputs were provided;
// when the planner form DID capture profile URLs, the false line is replaced
// at RENDER time (the source text is never mutated) with an honest
// awaiting-analysis state. No AI, no regeneration, no network.
const SHERLOC_EARNED_MEDIA_FALSE_ABSENCE_PATTERN =
  /no\s+(?:cast|filmmaker|talent)?[^.]{0,60}earned[- ]media\s+inputs?[^.]{0,80}(?:provided|supplied)/i;
const SHERLOC_EARNED_MEDIA_PENDING_LINE =
  "Earned-media profile URLs were supplied and are awaiting analysis. Verified follower counts and amplification estimates were not available when this strategy was generated.";
const SHERLOC_EARNED_MEDIA_PENDING_STATE = "PROFILES_SUPPLIED_ANALYSIS_PENDING";

const hasSuppliedSherlocEarnedMediaProfileUrls = (earnedMediaProfileUrls) =>
  (Array.isArray(earnedMediaProfileUrls) ? earnedMediaProfileUrls : []).some((url) =>
    String(url || "").trim()
  );

const isSherlocEarnedMediaFalseAbsenceLine = (line) =>
  SHERLOC_EARNED_MEDIA_FALSE_ABSENCE_PATTERN.test(String(line || ""));

const renderSherlocStrategyItems = (
  lines = [],
  sectionKey = "section",
  { selectedPlatforms = [], allocationRows = [], earnedMediaProfileUrls = null } = {}
) => {
  const isChannelStrategy = sectionKey === "channelStrategy";
  const hasExplicitAllocation = allocationRows.length >= 2;
  const renderedHeroKeys = new Set();
  const correctEarnedMediaAbsenceClaims =
    hasSuppliedSherlocEarnedMediaProfileUrls(earnedMediaProfileUrls);

  return buildSherlocRenderableItems(lines).map((item, index) => {
    if (item.type === "bullets") {
      const bulletLines =
        hasExplicitAllocation && (isChannelStrategy || sectionKey === "budgetAllocation")
          ? item.lines.filter(
              (line) =>
                !isSherlocStandaloneAllocationValueLine(line) &&
                !isSherlocConflictingBudgetAllocationLine(line, allocationRows)
            )
          : item.lines;

      if (!bulletLines.length) return null;

      return (
        <ul key={sectionKey + "-bullets-" + index} className="smp-strategy-structured-list">
          {bulletLines.map((line, bulletIndex) => {
            const replaceFalseAbsence =
              correctEarnedMediaAbsenceClaims && isSherlocEarnedMediaFalseAbsenceLine(line);
            return (
              <li
                key={sectionKey + "-bullet-" + bulletIndex}
                {...(replaceFalseAbsence
                  ? { "data-sherloc-earned-media-state": SHERLOC_EARNED_MEDIA_PENDING_STATE }
                  : {})}
              >
                {replaceFalseAbsence ? SHERLOC_EARNED_MEDIA_PENDING_LINE : line}
              </li>
            );
          })}
        </ul>
      );
    }

    if (item.type === "detail") {
      return (
        <dl key={sectionKey + "-detail-" + index} className="smp-strategy-structured-detail">
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </dl>
      );
    }

    const text = String(item.text || "").trim();

    if (
      sectionKey === "budgetAllocation" &&
      hasExplicitAllocation &&
      isSherlocConflictingBudgetAllocationLine(text, allocationRows)
    ) {
      return null;
    }

    if (isChannelStrategy) {
      const hero = resolveHeroFromLine(text, selectedPlatforms);
      const heroKey = getSherlocHeroAllocationKey(hero);
      if (hero && heroKey) {
        if (renderedHeroKeys.has(heroKey)) return null;
        renderedHeroKeys.add(heroKey);
        return renderSherlocPlatformHeroCard(
          hero,
          getSherlocAllocationRowForHero(hero, allocationRows),
          sectionKey + "-platform-hero-" + heroKey + "-" + index
        );
      }

      if (hasExplicitAllocation && isSherlocStandaloneAllocationValueLine(text)) {
        return null;
      }
    }

    const replaceFalseAbsence =
      correctEarnedMediaAbsenceClaims && isSherlocEarnedMediaFalseAbsenceLine(text);

    return (
      <p
        key={sectionKey + "-paragraph-" + index}
        className="smp-strategy-structured-paragraph"
        {...(replaceFalseAbsence
          ? { "data-sherloc-earned-media-state": SHERLOC_EARNED_MEDIA_PENDING_STATE }
          : {})}
      >
        {replaceFalseAbsence ? SHERLOC_EARNED_MEDIA_PENDING_LINE : text}
      </p>
    );
  });
};

export function SherlocStructuredStrategyOutput({
  text,
  emptyMessage = "No output yet.",
  selectedPlatforms = [],
  budget = "",
  netMediaBudget = "",
  notes = "",
  canonicalAllocation = null,
  allocationAuthority = null,
}) {
  const model = useMemo(
    () =>
      buildSherlocStructuredStrategyModel({
        text,
        emptyMessage,
        selectedPlatforms,
        budget,
        netMediaBudget,
        notes,
        canonicalAllocation,
      }),
    [budget, canonicalAllocation, emptyMessage, netMediaBudget, notes, selectedPlatforms, text]
  );
  const budgetSummaryRows = buildSherlocBudgetSummaryRows(
    model.allocationRows,
    selectedPlatforms
  );
  const approvedAllocation =
    getSherlocAllocationApprovalStatus(allocationAuthority) === "APPROVED";

  if (!model.hasContent) {
    return <p className="smp-strategy-structured-empty">{emptyMessage}</p>;
  }

  // One card hierarchy, never two: when a Channel Strategy section exists it
  // is the single home of the per-platform card list; the Budget Allocation
  // section keeps only the compact summary (plus its narrative items). The
  // Budget Allocation section renders the card list only as a legacy fallback
  // when the strategy has no Channel Strategy section at all.
  const hasChannelStrategyCards =
    model.allocationRows.length > 0 &&
    model.sections.some((section) => section.key === "channelStrategy");
  // Copy grouped from the Budget Allocation body must survive the removal of
  // that section's duplicate card list — it migrates to the surviving cards.
  const getMergedPlatformCopyLines = (rowKey) => {
    const budgetCopy = model.platformStrategyCopy.groups.get(rowKey) || [];
    const channelCopy = model.channelStrategyCopy.groups.get(rowKey) || [];
    const merged = hasChannelStrategyCards ? [...channelCopy, ...budgetCopy] : budgetCopy;
    const seen = new Set();
    return merged.filter((line) => {
      const key = String(line || "").trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  return (
    <div className="smp-strategy-structured-output">
      {model.sections.map((section) => {
        const isAllocationSection =
          section.key === "budgetAllocation" && model.allocationRows.length > 0;
        const isChannelStrategySection =
          section.key === "channelStrategy" && model.allocationRows.length > 0;
        // For the allocation section, attach each platform's copy beneath its card
        // (grouped from the raw body in the model) and keep only non-platform
        // "preamble" text as the loose body.
        const platformCopy = isAllocationSection
          ? model.platformStrategyCopy
          : isChannelStrategySection
            ? model.channelStrategyCopy
            : null;
        const looseLines =
          isAllocationSection || isChannelStrategySection
            ? platformCopy.preamble
            : section.lines;

        return (
          <section
            key={section.key}
            className={[
              "smp-strategy-structured-section",
              section.isFallback ? "smp-strategy-structured-section--fallback" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-sherloc-strategy-section={section.key}
          >
            {isAllocationSection && approvedAllocation ? (
              <div className="smp-alloc-header">
                <h3 className="smp-strategy-structured-title">{section.label}</h3>
                <span
                  className="smp-alloc-chip smp-alloc-chip--approved"
                  data-testid="sherloc-legacy-allocation-approved-chip"
                >
                  APPROVED GOVERNED ALLOCATION
                </span>
              </div>
            ) : (
              <h3 className="smp-strategy-structured-title">{section.label}</h3>
            )}
            {isChannelStrategySection ? (
              <div
                className="smp-strategy-channel-list smp-strategy-allocation-card-list"
                aria-label="Channel strategy allocation"
              >
                {model.allocationRows.map((row) => {
                  const copyLines = getMergedPlatformCopyLines(row.key);
                  return (
                    <div
                      key={"smp-channel-unit-" + (row.key || row.label)}
                      className="smp-strategy-channel-unit"
                      data-sherloc-channel-unit={row.key || row.label}
                    >
                      {renderSherlocChannelStrategyCard(row, selectedPlatforms)}
                      {copyLines.length > 0 ? (
                        <div className="smp-platform-strategy-copy">
                          {renderSherlocStrategyItems(copyLines, "channelStrategy", {
                            selectedPlatforms,
                            allocationRows: model.allocationRows,
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {isAllocationSection ? (
              <div
                className="smp-strategy-allocation-list smp-strategy-allocation-card-list"
                aria-label="Platform allocation"
              >
                <div className="smp-strategy-allocation-eyebrow">Platform allocation</div>
                <SherlocPlatformBudgetSummary
                  rows={budgetSummaryRows}
                  financialMode="client"
                />
                {model.allocationRows.map((row) => {
                  // Legacy fallback only: when a Channel Strategy section
                  // exists, the per-platform card list renders there — this
                  // section keeps only the compact summary, never a second
                  // copy of the card list.
                  if (hasChannelStrategyCards) return null;
                  const copyLines = getMergedPlatformCopyLines(row.key);
                  return (
                    <div
                      key={"smp-allocation-unit-" + (row.key || row.label)}
                      className="smp-strategy-allocation-unit"
                      data-sherloc-allocation-unit={row.key || row.label}
                    >
                      {renderSherlocBudgetAllocationCard(row, selectedPlatforms)}
                      {copyLines.length > 0 ? (
                        <div className="smp-platform-strategy-copy">
                          {renderSherlocStrategyItems(copyLines, "budgetAllocation", {
                            selectedPlatforms,
                            allocationRows: model.allocationRows,
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {renderSherlocStrategyItems(looseLines, section.key, {
              selectedPlatforms,
              allocationRows: model.allocationRows,
            })}
          </section>
        );
      })}
    </div>
  );
}

export const __sherlocStrategyOutputTesting = {
  SHERLOC_STRATEGY_SECTION_DEFINITIONS,
  buildSherlocRequestedAllocationRows,
  buildSherlocCanonicalAllocationRows,
  buildSherlocStructuredStrategyModel,
  buildSherlocRenderableItems,
  getSherlocFallbackSection,
  groupSherlocPlatformStrategyCopy,
  cleanSherlocStrategyLine,
  getSherlocStrategySectionKey,
  isSherlocDuplicateOverviewHeading,
  isSherlocLogoPlaceholderLine,
  normalizeSherlocStrategySections,
  sanitizeClientVisibleSherlocStrategyText,
};

// ─────────────────────────────────────────────────────────────────────────
// Canonical strategy presentation renderer (sherloc_strategy_presentation_v1)
//
// Renders EXACTLY TWO primary strategy sections — Budget Allocation and
// Channel Strategy — from the backend's structured presentation payload.
// Client (Cortex) financial-visibility convention: gross figures render under
// the approved "Allocated budget" terminology; net media, target margin, and
// the accounting words gross/net NEVER render for client users (matches
// SherlocPlatformBudgetSummary financialMode="client" and
// sanitizeClientVisibleSherlocStrategyText). Fail closed: absent financials
// render a governed absent-state line with zero numbers.
// ─────────────────────────────────────────────────────────────────────────

// ABSENT financials mean no governed allocation has been SUPPLIED for this
// plan — the model never invents dollars, so regenerating without an
// allocation input yields the same absent state (proven in production,
// 2026-08-02, Dark Horse campaign 13). The truthful guidance is to provide
// the approved allocation, not to regenerate.
const SHERLOC_CANONICAL_FINANCIALS_ABSENT_NOTE =
  "No approved budget allocation has been provided for this plan. Add the approved channel allocation to the planner inputs (or apply a revision that specifies it) to populate these figures.";

const SHERLOC_CANONICAL_CHANNEL_ABSENT_NOTE =
  "Channel guidance is unavailable. Follow the campaign's current governed next action; missing guidance does not authorize regeneration.";

const formatSherlocCanonicalPercentLabel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0 || numeric > 100) return "";
  const rounded = Math.round(numeric * 10) / 10;
  return Number.isInteger(rounded) ? rounded + "%" : rounded.toFixed(1) + "%";
};

const formatSherlocCanonicalAmountLabel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return formatSherlocCurrencyAmount(numeric);
};

const isSherlocCanonicalAbsentEntry = (entry) =>
  String(entry?.contentSource || "").toLowerCase() === "absent";

// ── Deterministic descending-budget presentation order ──
// The persisted presentation arrays arrive in platform-SELECTION order; both
// canonical sections must render in descending budget order instead. The only
// sort key is the authoritative gross allocation converted to INTEGER CENTS —
// grossAmount is read internally for ordering even though the Cortex client
// surface never renders it as such (the client financial-visibility convention
// keeps restricted values unrendered); rounded allocationPercent values and
// display labels NEVER participate in ordering. Ties keep the persisted
// canonical relative order (explicit stable index tie-break). If ANY entry
// lacks a finite positive gross amount (ABSENT financials), the persisted
// canonical order is preserved unchanged — fail closed, never guess an order
// from partial data. Pure presentation-model functions: they return copies and
// never mutate the persisted presentation object or its arrays.
export const getSherlocCanonicalGrossCents = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 100);
};

export const orderSherlocCanonicalEntriesByGrossDesc = (entries) => {
  const list = Array.isArray(entries) ? entries.slice() : [];
  const decorated = list.map((entry, index) => ({
    entry,
    index,
    grossCents: getSherlocCanonicalGrossCents(entry?.grossAmount),
  }));
  if (!decorated.length || decorated.some((row) => row.grossCents === null)) {
    return list;
  }
  decorated.sort((a, b) =>
    b.grossCents !== a.grossCents ? b.grossCents - a.grossCents : a.index - b.index
  );
  return decorated.map((row) => row.entry);
};

// Shared by BOTH canonical sections: Budget Allocation summary rows and
// Channel Strategy cards render from the same ordered copy.
export const buildSherlocOrderedCanonicalPresentation = (presentation) => {
  if (!isCanonicalSherlocStrategyPresentation(presentation)) return presentation;
  return {
    ...presentation,
    budgetAllocation: orderSherlocCanonicalEntriesByGrossDesc(
      presentation.budgetAllocation
    ),
    channelStrategy: orderSherlocCanonicalEntriesByGrossDesc(
      presentation.channelStrategy
    ),
  };
};

// Labeled channel-card blocks, in governed order. Each renders only when the
// backend supplied non-empty copy — never fabricated. The narrative field is
// not a labeled block: it renders as a golden paragraph beneath the blocks.
const SHERLOC_CANONICAL_CHANNEL_FIELDS = Object.freeze([
  // `publishedKey` names a backend display projection that supersedes the
  // approved field for rendering only. The approved value stays on the card as
  // the raw audit record; an immutable artifact does not oblige a client
  // surface to quote prose the platform can no longer stand behind. Campaign
  // 50's approved role text ends "in theater ZIP markets", which reads as a
  // claim about how the buy was executed that no governed record supports.
  { key: "primaryRole", label: "Primary role", publishedKey: "primaryRolePublished" },
  { key: "rationale", label: "Rationale" },
  { key: "funnelStage", label: "Funnel stage" },
  { key: "deliveryLogic", label: "Delivery / impression logic" },
  { key: "executionGuidance", label: "Execution / creative guidance" },
]);

const buildSherlocCanonicalSummaryRows = (presentation) => {
  const entries = Array.isArray(presentation?.budgetAllocation)
    ? presentation.budgetAllocation
    : [];
  return entries
    .map((entry, index) => {
      const platformKey = String(entry?.platformKey || "").trim();
      const heroAssetKey = String(entry?.heroAssetKey || platformKey).trim();
      if (!platformKey && !heroAssetKey) return null;
      const branding = resolveSherlocPlatformBranding(heroAssetKey || platformKey);
      return {
        key: platformKey || heroAssetKey || "platform-" + index,
        // Display-normalized by platform identity (ctv-family → "CTV"), never
        // the stored displayName verbatim — see normalizeSherlocPlatformDisplayLabel.
        label:
          normalizeSherlocPlatformDisplayLabel(
            platformKey || heroAssetKey,
            entry?.displayName
          ) || String(branding?.label || "Platform"),
        percentLabel: formatSherlocCanonicalPercentLabel(entry?.allocationPercent),
        grossAmountLabel: formatSherlocCanonicalAmountLabel(entry?.grossAmount),
        image: branding?.image || resolvePlatformHeroImage(heroAssetKey || platformKey),
      };
    })
    .filter(Boolean);
};

const SherlocCanonicalChannelCard = ({ entry, index }) => {
  const platformKey = String(entry?.platformKey || "").trim() || "platform-" + index;
  const heroAssetKey = String(entry?.heroAssetKey || entry?.platformKey || "").trim();
  // Governed hero artwork: shared resolver only (canonical platform key in,
  // bundled local asset out — unknown keys fail closed to the neutral hero).
  const heroImage = resolvePlatformHeroImage(heroAssetKey);
  // Display-normalized by platform identity (ctv-family → "CTV") — historical
  // persisted displayName values like "Connected TV (CTV)" must never render.
  const displayName =
    normalizeSherlocPlatformDisplayLabel(entry?.platformKey, entry?.displayName) ||
    "Platform";
  const percentLabel = formatSherlocCanonicalPercentLabel(entry?.allocationPercent);
  const grossAmountLabel = formatSherlocCanonicalAmountLabel(entry?.grossAmount);
  const isAbsent = isSherlocCanonicalAbsentEntry(entry);
  const bullets = !isAbsent && Array.isArray(entry?.bullets)
    ? entry.bullets.map((line) => String(line || "").trim()).filter(Boolean)
    : [];

  return (
    <article
      className="smp-canonical-channel-card"
      data-sherloc-canonical-channel-card={platformKey}
      data-sherloc-canonical-channel-source={isAbsent ? "absent" : "generated"}
      aria-label={displayName + " channel strategy"}
    >
      <div className="smp-canonical-channel-hero" aria-hidden="true">
        <img
          className="smp-canonical-channel-hero-image"
          src={heroImage}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="smp-canonical-channel-body">
        <h4 className="smp-canonical-channel-title">
          <span className="smp-canonical-channel-name">{displayName}</span>
          {percentLabel ? (
            <span
              className="smp-canonical-channel-percent"
              aria-label={"Allocation percentage: " + percentLabel}
            >
              {percentLabel}
            </span>
          ) : null}
        </h4>
        {grossAmountLabel ? (
          <div
            className="smp-canonical-channel-financial"
            data-sherloc-canonical-allocated={platformKey}
            aria-label={"Allocated budget: " + grossAmountLabel}
          >
            <span className="smp-canonical-financial-label">Allocated budget</span>
            <span className="smp-canonical-financial-value">{grossAmountLabel}</span>
          </div>
        ) : null}
        {isAbsent ? (
          <>
            <p
              className="smp-canonical-channel-absent-note"
              role="note"
              data-sherloc-canonical-channel-absent={platformKey}
            >
              {SHERLOC_CANONICAL_CHANNEL_ABSENT_NOTE}
            </p>
          </>
        ) : (
          <>
            {SHERLOC_CANONICAL_CHANNEL_FIELDS.map((field) => {
              // Read straight off `entry`, the card currently being rendered
              // — never from state that outlives a campaign — so switching
              // campaigns cannot carry one campaign's projected copy onto
              // another's card.
              const value = String(
                (field.publishedKey ? entry?.[field.publishedKey] : null) ||
                  entry?.[field.key] ||
                  ""
              ).trim();
              if (!value) return null;
              // Campaign-Inputs-style subtitle contract: each field label
              // renders as a noninteractive branded subtitle
              // (.smp-canonical-channel-subtitle) over the surfaced golden
              // paragraph rail — never the section pill and never a dt/dd
              // label column.
              return (
                <div
                  key={platformKey + "-" + field.key}
                  className="smp-canonical-channel-detail"
                  data-sherloc-canonical-channel-field={field.key}
                  data-sherloc-canonical-block={field.key}
                >
                  <h5 className="smp-canonical-channel-subtitle">{field.label}</h5>
                  <p className="smp-strategy-structured-paragraph smp-canonical-channel-detail-copy">
                    {value}
                  </p>
                </div>
              );
            })}
            {String(entry?.narrative || "").trim() ? (
              <p className="smp-strategy-structured-paragraph smp-canonical-channel-narrative">
                {String(entry.narrative).trim()}
              </p>
            ) : null}
            {bullets.length ? (
              <ul
                className="smp-strategy-structured-list smp-canonical-channel-bullet-list"
                data-sherloc-canonical-bullets={platformKey}
              >
                {bullets.map((line, bulletIndex) => (
                  <li key={platformKey + "-bullet-" + bulletIndex}>{line}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};

// Canonical Budget Allocation section (client financial-visibility convention:
// gross-only "Allocated budget" figures). The optional `looseItems` argument
// lets the golden composed renderer append the strategy text's non-platform
// Budget Allocation narrative after the canonical summary. The optional
// `financialsAbsentAffordance` node replaces the static supply-the-allocation
// note for ABSENT financials: when a persisted campaign can create an
// allocation_recommendation operation, the truthful guidance is the governed
// "Develop Recommended Allocation" affordance — the static note stays only
// for paths without one (unsaved drafts / legacy presentation-less plans).
const renderSherlocCanonicalBudgetAllocationSection = (
  presentation,
  looseItems = null,
  financialsAbsentAffordance = null
) => {
  const financialsAbsent = presentation.financialsSource === "ABSENT";
  const summaryRows = financialsAbsent
    ? []
    : buildSherlocCanonicalSummaryRows(presentation);
  const totalGrossLabel = financialsAbsent
    ? ""
    : formatSherlocCanonicalAmountLabel(presentation?.totals?.grossBudget);

  return (
      <section
        key="smp-canonical-budget-allocation"
        className="smp-strategy-structured-section smp-canonical-section"
        data-sherloc-canonical-section="budgetAllocation"
        aria-label="Budget allocation"
      >
        <h3 className="smp-strategy-structured-title">Budget Allocation</h3>
        {financialsAbsent || !summaryRows.length ? (
          financialsAbsentAffordance || (
            <p
              className="smp-canonical-financials-absent"
              role="note"
              data-sherloc-canonical-financials="absent"
            >
              {SHERLOC_CANONICAL_FINANCIALS_ABSENT_NOTE}
            </p>
          )
        ) : (
          <>
            <SherlocPlatformBudgetSummary rows={summaryRows} financialMode="client" />
            {totalGrossLabel ? (
              <div
                className="smp-canonical-allocation-total"
                role="note"
                data-sherloc-canonical-allocation-total="true"
                aria-label={"Total allocated budget: " + totalGrossLabel}
              >
                <span className="smp-canonical-financial-label">
                  Total allocated budget
                </span>
                <span className="smp-canonical-financial-value">{totalGrossLabel}</span>
              </div>
            ) : null}
          </>
        )}
        {looseItems}
      </section>
  );
};

const renderSherlocCanonicalChannelStrategySection = (presentation) => {
  const channelEntries = Array.isArray(presentation.channelStrategy)
    ? presentation.channelStrategy
    : [];
  return (
      <section
        key="smp-canonical-channel-strategy"
        className="smp-strategy-structured-section smp-canonical-section"
        data-sherloc-canonical-section="channelStrategy"
        aria-label="Channel strategy"
      >
        <h3 className="smp-strategy-structured-title">Channel Strategy</h3>
        <div className="smp-canonical-channel-list">
          {channelEntries.map((entry, index) => (
            <SherlocCanonicalChannelCard
              key={String(entry?.platformKey || "channel-" + index)}
              entry={entry}
              index={index}
            />
          ))}
        </div>
      </section>
  );
};

export function SherlocCanonicalStrategyPresentation({ presentation }) {
  if (!isCanonicalSherlocStrategyPresentation(presentation)) return null;

  // Deterministic descending-budget order for both sections (ordered COPY —
  // the persisted presentation object is never mutated).
  const orderedPresentation = buildSherlocOrderedCanonicalPresentation(presentation);

  return (
    <div
      className="smp-canonical-strategy"
      data-sherloc-canonical-strategy={presentation.schemaVersion}
    >
      {renderSherlocCanonicalBudgetAllocationSection(orderedPresentation)}
      {renderSherlocCanonicalChannelStrategySection(orderedPresentation)}
    </div>
  );
}

// The presentation's budgetAllocation mapped to the canonical-allocation input
// shape consumed by buildSherlocRequestedAllocationRows (used when the page
// does not already supply strategy_payload.requestedChannelAllocation).
const buildSherlocPresentationAllocationInput = (presentation) =>
  (Array.isArray(presentation?.budgetAllocation) ? presentation.budgetAllocation : []).map(
    (row) => ({
      key: row?.platformKey,
      channel: row?.displayName,
      percent: row?.allocationPercent,
      grossAmount: row?.grossAmount,
      netAmount: row?.netAmount,
    })
  );

// ─────────────────────────────────────────────────────────────────────────
// Golden composed canonical presentation (client portal).
//
// The canonical presentation stays the ONLY financial authority (gross-only
// client summary + channel cards render exclusively from the governed
// payload), but the strategy TEXT's narrative sections are restored around it
// through the existing structured model — whose client pipeline applies
// sanitizeClientVisibleSherlocStrategyText FIRST, so internal net/margin
// economics never reach this surface. Per-platform text-grouped copy is never
// re-rendered (the canonical channel cards carry that content) and no legacy
// allocation card, tile, or text-resolved platform hero ever mounts here.
// ─────────────────────────────────────────────────────────────────────────
export function SherlocGoldenCanonicalPresentation({
  presentation,
  text = "",
  emptyMessage = "No output yet.",
  selectedPlatforms = [],
  budget = "",
  netMediaBudget = "",
  notes = "",
  canonicalAllocation = null,
  earnedMediaProfileUrls = [],
  financialsAbsentAffordance = null,
}) {
  if (!isCanonicalSherlocStrategyPresentation(presentation)) return null;

  // Deterministic descending-budget order for both canonical sections
  // (ordered COPY — the persisted presentation object is never mutated).
  const orderedPresentation = buildSherlocOrderedCanonicalPresentation(presentation);

  const financialsPresent = presentation.financialsSource !== "ABSENT";
  const resolvedCanonicalAllocation =
    Array.isArray(canonicalAllocation) && canonicalAllocation.length
      ? canonicalAllocation
      : financialsPresent
        ? buildSherlocPresentationAllocationInput(orderedPresentation)
        : null;

  const model = buildSherlocStructuredStrategyModel({
    text,
    emptyMessage,
    budget,
    netMediaBudget,
    notes,
    canonicalAllocation: resolvedCanonicalAllocation,
  });
  const itemOptions = {
    selectedPlatforms,
    allocationRows: model.allocationRows,
    earnedMediaProfileUrls,
  };

  const openingSections = model.sections.filter(
    (section) => section.key === "overview" || section.key === "objective"
  );
  const budgetSection =
    model.sections.find((section) => section.key === "budgetAllocation") || null;
  const fallbackSections = model.sections.filter((section) => section.isFallback);
  const closingSections = model.sections.filter(
    (section) =>
      !section.isFallback &&
      section.key !== "overview" &&
      section.key !== "objective" &&
      section.key !== "budgetAllocation" &&
      section.key !== "channelStrategy"
  );
  // Loose Budget Allocation narrative only (with an explicit canonical
  // allocation the model has already reduced the section body to its
  // non-platform preamble). Without one, text-derived financial lines must not
  // ride into the governed canonical section — fail closed to the canonical
  // absent/summary state alone.
  const budgetLooseLines =
    budgetSection && model.allocationRows.length >= 2 ? budgetSection.lines : [];

  const renderGoldenSection = (section) => (
    <section
      key={section.key}
      className={[
        "smp-strategy-structured-section",
        section.isFallback ? "smp-strategy-structured-section--fallback" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-sherloc-strategy-section={section.key}
    >
      <h3 className="smp-strategy-structured-title">{section.label}</h3>
      {renderSherlocStrategyItems(section.lines, section.key, itemOptions)}
    </section>
  );

  return (
    <div
      className="smp-strategy-structured-output smp-canonical-strategy"
      data-sherloc-canonical-strategy={presentation.schemaVersion}
      data-sherloc-canonical-presentation={presentation.schemaVersion}
    >
      {openingSections.map(renderGoldenSection)}
      {renderSherlocCanonicalBudgetAllocationSection(
        orderedPresentation,
        budgetLooseLines.length
          ? renderSherlocStrategyItems(budgetLooseLines, "budgetAllocation", itemOptions)
          : null,
        financialsAbsentAffordance
      )}
      {fallbackSections.map(renderGoldenSection)}
      {renderSherlocCanonicalChannelStrategySection(orderedPresentation)}
      {closingSections.map(renderGoldenSection)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SHERLOC recommended allocation authority (allocation_authority_v2)
//
// The governed review → approve / reject surface for SHERLOC-recommended
// channel allocations. Client (Cortex) financial-visibility convention
// re-asserted: this surface renders integer-cents figures ONLY under the
// approved "Allocated budget" terminology — net media, target margin, and
// the accounting words gross/net NEVER render (client serialization already
// strips net/margin fields; this surface neither expects nor renders them).
// Allocation lifecycle states NEVER use strategy-regeneration language:
// developing or retrying an allocation only ever creates
// allocation_recommendation operations through the existing durable
// operations contract.
// ─────────────────────────────────────────────────────────────────────────

export const SHERLOC_ALLOCATION_AUTHORITY_SCHEMA_VERSION = "allocation_authority_v2";
export const SHERLOC_ALLOCATION_OPERATION_TYPE = "allocation_recommendation";

// Backend-governed operation failure copy (verbatim; never reworded).
export const SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE =
  "SHERLOC could not complete the recommended allocation. Retry the allocation recommendation.";
export const SHERLOC_ALLOCATION_DEVELOPING_MESSAGE =
  "SHERLOC is developing a recommended channel allocation.";
export const SHERLOC_ALLOCATION_READY_MESSAGE =
  "A reasoned SHERLOC allocation is ready for review.";
export const SHERLOC_ALLOCATION_APPROVAL_REQUIRED_NOTE =
  "This recommended allocation must be approved before deployment.";
export const SHERLOC_ALLOCATION_DEVELOP_PROMPT =
  "No governed channel allocation has been developed for this plan yet. Develop a reasoned SHERLOC allocation for review and approval.";
export const SHERLOC_ALLOCATION_REJECTED_NOTE =
  "This recommended allocation was rejected and will not be used. Request a new recommended allocation to continue.";
// Allocation decisions are Superadmin governance acts recorded in Quantum;
// the client portal presents the recommendation read-only.
export const SHERLOC_ALLOCATION_CLIENT_READ_ONLY_NOTE =
  "Allocation review and approval are completed with your Cphere team. This view is read-only.";

export const getSherlocAllocationAuthorityFromCampaign = (campaign) => {
  const payload = campaign?.strategy_payload || campaign?.strategyPayload || {};
  const authority = payload?.allocationAuthority;
  if (
    authority &&
    typeof authority === "object" &&
    !Array.isArray(authority) &&
    authority.schemaVersion === SHERLOC_ALLOCATION_AUTHORITY_SCHEMA_VERSION
  ) {
    return authority;
  }
  return null;
};

export const isSherlocRecommendedAllocationAuthority = (authority) =>
  Boolean(authority) &&
  ["SHERLOC_RECOMMENDED", "OPERATOR_RECOVERED_HISTORICAL_STRATEGY"].includes(
    String(authority.financialsSource || "").trim().toUpperCase()
  );

export const getSherlocAllocationApprovalStatus = (authority) =>
  String(authority?.allocationApprovalStatus || "")
    .trim()
    .toUpperCase();

// Integer-cents reader for authority rows: grossAmountCents is the ONLY
// authoritative amount field (never derived from percents or labels).
// Number(null) === 0, so nullish/empty values are rejected BEFORE coercion —
// a missing amount must read as absent, never as $0.
export const getSherlocAllocationAuthorityRowCents = (row) => {
  const raw = row?.grossAmountCents;
  if (raw === null || raw === undefined || raw === "") return null;
  const cents = Number(raw);
  return Number.isFinite(cents) && cents >= 0 ? Math.round(cents) : null;
};

// Descending-budget ordering for authority rows (same fail-closed contract as
// the canonical presentation ordering): the only sort key is INTEGER CENTS,
// ties keep the persisted relative order, and if any included row lacks a
// finite cents amount the persisted order is preserved unchanged. Excluded
// rows are split out and keep persisted order. Pure — returns copies.
export const orderSherlocAllocationAuthorityRows = (rows) => {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const included = [];
  const excluded = [];
  list.forEach((row, index) => {
    if (row?.excluded === true) {
      excluded.push({ row, index });
    } else {
      included.push({
        row,
        index,
        cents: getSherlocAllocationAuthorityRowCents(row),
      });
    }
  });
  if (included.length && included.every((item) => item.cents !== null)) {
    included.sort((a, b) =>
      b.cents !== a.cents ? b.cents - a.cents : a.index - b.index
    );
  }
  return {
    included: included.map((item) => item.row),
    excluded: excluded.map((item) => item.row),
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Canonical current-plan completeness resolver (ONE truth for "is the
// CURRENT plan complete?").
//
// Every surface able to claim "Sherloc returned an incomplete media plan"
// (strategy notice, action-footer banner, operation status areas) consults
// this resolver instead of echoing a historical operation snapshot.
// Precedence:
//   1. current committed strategy + allocation — prefer the
//      backend-serialized campaign.allocationCurrentState when present; else
//      derive: budget_breakdown rows in EITHER shape ({platform, grossAmount}
//      or legacy {key, budget}) or an APPROVED allocation authority ⇒
//      APPROVED. An APPROVED current allocation beats a LATER pending
//      recommendation.
//   2. approval/governance state (pending / rejected recommendation).
//   3. a genuinely ACTIVE generation operation.
//   4. historical failed operations are AUDIT HISTORY ONLY — they never make
//      a complete current plan render as incomplete.
// Client-safe: reads gross-side fields only (never net/margin/markup).
// ─────────────────────────────────────────────────────────────────────────

export const SHERLOC_CURRENT_PLAN_STATES = Object.freeze({
  APPROVED: "APPROVED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  REJECTED: "REJECTED",
  GENERATING: "GENERATING",
  INCOMPLETE: "INCOMPLETE",
});

// budget_breakdown row completeness in EITHER shape. Number(null) === 0, so
// nullish amounts are rejected BEFORE coercion — a missing amount must read
// as absent, never as $0.
export const isSherlocCompleteBudgetBreakdownRow = (row) => {
  if (!row || typeof row !== "object") return false;
  const platform = String(row.platform ?? row.key ?? "").trim();
  if (!platform) return false;
  const amountRaw = row.grossAmount ?? row.budget;
  if (amountRaw === null || amountRaw === undefined || amountRaw === "") {
    return false;
  }
  const amount = Number(amountRaw);
  return Number.isFinite(amount) && amount > 0;
};

export const getSherlocCampaignBudgetBreakdownRows = (campaign) => {
  const candidates = [
    campaign?.budget_breakdown,
    campaign?.budgetBreakdown,
    campaign?.strategy_payload?.budget_breakdown,
    campaign?.strategyPayload?.budget_breakdown,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) return candidate;
  }
  return [];
};

// Backend-serialized current-state marker (preferred whenever present):
// accepts a plain string state or an object carrying state/status. Unknown
// values are ignored (fall through to derivation) — never trusted blindly.
const getSherlocSerializedAllocationCurrentState = (campaign) => {
  const serialized =
    campaign?.allocationCurrentState ??
    campaign?.allocation_current_state ??
    null;
  const raw =
    typeof serialized === "string"
      ? serialized
      : serialized && typeof serialized === "object"
        ? serialized.state ?? serialized.status ?? ""
        : "";
  const state = String(raw || "").trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(SHERLOC_CURRENT_PLAN_STATES, state)
    ? state
    : "";
};

// Staleness verdict from the campaign's own governance stamp: the strategy
// artifact predates the campaign's current governed inputs and must not be
// presented, approved, or exported as current. Absence of a stamp is NOT
// staleness (legacy rows) — only an explicit invalidation is.
export const isSherlocStrategyStale = (campaign) => {
  const stamp = campaign?.strategy_payload?.micrositeGovernance;
  if (!stamp || typeof stamp !== "object") return false;
  return (
    stamp.requiresRegeneration === true ||
    String(stamp.status || "").trim().toUpperCase() === "REGENERATION_REQUIRED"
  );
};

export const resolveSherlocCurrentPlanState = ({
  campaign = null,
  strategyText = "",
  allocationAuthority = null,
  activeOperation = null,
} = {}) => {
  const authority =
    allocationAuthority || getSherlocAllocationAuthorityFromCampaign(campaign);
  const approvalStatus = getSherlocAllocationApprovalStatus(authority);
  const hasCommittedStrategy = Boolean(
    String(strategyText || "").trim() ||
      getUsableSherlocStrategyText(campaign || {})
  );

  // (1) Current committed allocation truth.
  const serializedState = getSherlocSerializedAllocationCurrentState(campaign);
  const breakdownRows = getSherlocCampaignBudgetBreakdownRows(campaign);
  const derivedApproved =
    (authority?.financialsSource !== "OPERATOR_RECOVERED_HISTORICAL_STRATEGY" && breakdownRows.length > 0 &&
      breakdownRows.every(isSherlocCompleteBudgetBreakdownRow)) ||
    approvalStatus === "APPROVED";
  const allocationApproved = serializedState
    ? serializedState === SHERLOC_CURRENT_PLAN_STATES.APPROVED
    : derivedApproved;

  // APPROVED beats a LATER pending recommendation: a complete committed plan
  // stays complete while a newer recommendation awaits review.
  if (hasCommittedStrategy && allocationApproved) {
    return {
      state: SHERLOC_CURRENT_PLAN_STATES.APPROVED,
      complete: true,
      hasCommittedStrategy,
      allocationApproved: true,
      source: serializedState ? "SERIALIZED_CURRENT_STATE" : "DERIVED",
    };
  }

  // (2) Approval/governance state.
  const governanceState = serializedState || approvalStatus;
  if (
    governanceState === "PENDING_APPROVAL" ||
    governanceState === "DRAFT" ||
    governanceState === "REJECTED"
  ) {
    return {
      state:
        governanceState === "REJECTED"
          ? SHERLOC_CURRENT_PLAN_STATES.REJECTED
          : SHERLOC_CURRENT_PLAN_STATES.PENDING_APPROVAL,
      complete: false,
      hasCommittedStrategy,
      allocationApproved: false,
      source: serializedState ? "SERIALIZED_CURRENT_STATE" : "DERIVED",
    };
  }

  // (3) A genuinely ACTIVE operation.
  if (isSherlocGenerationOperationActive(activeOperation)) {
    return {
      state: SHERLOC_CURRENT_PLAN_STATES.GENERATING,
      complete: false,
      hasCommittedStrategy,
      allocationApproved: false,
      source: "OPERATION",
    };
  }

  // (4) Nothing current is complete. Historical FAILED operations remain
  // audit history and never participate in this verdict.
  return {
    state: SHERLOC_CURRENT_PLAN_STATES.INCOMPLETE,
    complete: false,
    hasCommittedStrategy,
    allocationApproved: false,
    source: "DERIVED",
  };
};

// Stale-error classification: the messages that assert the CURRENT plan is
// incomplete. These are cleared whenever the resolver proves the current
// committed plan complete — a historical operation may never keep them alive.
export const isSherlocCurrentPlanCompletenessErrorMessage = (message) => {
  const text = String(message || "").trim();
  return (
    text === STRATEGY_INCOMPLETE_OUTPUT_MESSAGE ||
    text === SHERLOC_OPERATION_FAILURE_CATEGORY_COPY.incomplete_strategy
  );
};

// Edited-amount parsing: exact dollars text → integer cents (null = invalid).
export const parseSherlocAllocationEditCents = (value) => {
  const text = String(value ?? "")
    .trim()
    .replace(/[$,\s]/g, "");
  if (!text || !/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return Math.round(numeric * 100);
};

const formatSherlocAllocationEditSeedValue = (cents) => {
  if (!Number.isFinite(cents)) return "";
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
};

// Live exact reconciliation over integer cents: every edited row must parse
// as a valid dollar amount and the edited sum must equal
// totals.grossBudgetCents EXACTLY — approval is disabled otherwise.
export const reconcileSherlocAllocationEdits = ({
  rows,
  editedValues,
  totalCents,
}) => {
  const list = Array.isArray(rows) ? rows : [];
  let sumCents = 0;
  let invalid = false;
  let edited = false;
  list.forEach((row) => {
    const key = String(row?.platformKey || "");
    const originalCents = getSherlocAllocationAuthorityRowCents(row);
    const rawValue =
      editedValues && Object.prototype.hasOwnProperty.call(editedValues, key)
        ? editedValues[key]
        : formatSherlocAllocationEditSeedValue(originalCents);
    const parsedCents = parseSherlocAllocationEditCents(rawValue);
    if (parsedCents === null) {
      invalid = true;
      return;
    }
    sumCents += parsedCents;
    if (parsedCents !== originalCents) edited = true;
  });
  const total = Number.isFinite(Number(totalCents))
    ? Math.round(Number(totalCents))
    : null;
  const exact = !invalid && total !== null && sumCents === total;
  return { sumCents, totalCents: total, exact, invalid, edited };
};

// Allocation approve/reject is a Superadmin governance act recorded in
// Quantum — the client portal renders the recommendation read-only and never
// posts a decision (the backend refuses client decision requests outright).

// Durable allocation_recommendation operation lifecycle — reuses the existing
// generation-operations contract module (create / poll / terminal outcome)
// with the allocation operation type. Failure copy is the backend-governed
// retry message, never strategy-regeneration language.
const useSherlocAllocationRecommendationOperation = ({
  campaignId,
  onCampaignApplied,
  initialOperation = null,
}) => {
  const [phase, setPhase] = useState("idle"); // idle | active | ready | failed
  const [failureMessage, setFailureMessage] = useState("");
  const latchRef = useRef(false);
  const timerRef = useRef(null);
  const disposedRef = useRef(false);
  const operationRef = useRef(null);
  const adoptedInitialRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const applyCampaignResult = useCallback(
    (result) => {
      if (typeof onCampaignApplied === "function") {
        onCampaignApplied(result?.campaign || null);
      }
    },
    [onCampaignApplied]
  );

  const settleTerminalOperation = useCallback(
    (operation) => {
      const outcome = resolveSherlocGenerationPollOutcome(operation);
      if (
        outcome.kind === "apply" ||
        outcome.kind === "completed_without_result"
      ) {
        setPhase("ready");
        setFailureMessage("");
        applyCampaignResult(outcome.kind === "apply" ? outcome.result : null);
        return;
      }
      setPhase("failed");
      setFailureMessage(SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE);
    },
    [applyCampaignResult]
  );

  const scheduleAllocationPoll = useCallback(
    (operation) => {
      if (disposedRef.current || !operation?.id) return;
      timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        try {
          const payload = await getSherlocGenerationOperation({
            apiBase: safeBase(API_BASE_URL),
            token: getSherlocMediaPlannerAuthToken(),
            operationId: operation.id,
            requestId: makePlannerRequestId(),
          });
          if (disposedRef.current) return;
          const next = payload?.operation || operation;
          operationRef.current = next;
          if (isSherlocGenerationOperationActive(next)) {
            scheduleAllocationPoll(next);
          } else {
            settleTerminalOperation(next);
          }
        } catch {
          // Transient poll failure: the operation continues server-side.
          if (!disposedRef.current) scheduleAllocationPoll(operation);
        }
      }, SHERLOC_GENERATION_POLL_INTERVAL_MS);
    },
    [settleTerminalOperation]
  );

  const trackAllocationOperation = useCallback(
    (operation) => {
      operationRef.current = operation;
      if (isSherlocGenerationOperationActive(operation)) {
        setPhase("active");
        setFailureMessage("");
        scheduleAllocationPoll(operation);
      } else {
        settleTerminalOperation(operation);
      }
    },
    [scheduleAllocationPoll, settleTerminalOperation]
  );

  // Reload reconnect: an in-flight allocation operation handed down by the
  // page resumes polling here instead of the strategy-generation machinery.
  useEffect(() => {
    if (!initialOperation || adoptedInitialRef.current) return;
    adoptedInitialRef.current = true;
    if (isSherlocGenerationOperationActive(initialOperation)) {
      trackAllocationOperation(initialOperation);
    }
  }, [initialOperation, trackAllocationOperation]);

  const startAllocationOperation = useCallback(async () => {
    if (!campaignId) return;
    setFailureMessage("");
    try {
      const outcome = await startSherlocDurableGeneration({
        latchRef,
        activeOperation: operationRef.current,
        submit: () =>
          createSherlocGenerationOperation({
            apiBase: safeBase(API_BASE_URL),
            token: getSherlocMediaPlannerAuthToken(),
            requestId: makePlannerRequestId(),
            body: {
              campaignId,
              operationType: SHERLOC_ALLOCATION_OPERATION_TYPE,
              clientRequestId: makePlannerRequestId(),
            },
          }),
      });
      if (disposedRef.current) return;
      if (outcome.kind === "duplicate_suppressed") return;
      if (outcome.kind === "completed_replay") {
        setPhase("ready");
        setFailureMessage("");
        applyCampaignResult(outcome.result);
        return;
      }
      if (outcome.kind === "track") {
        trackAllocationOperation(outcome.operation);
        return;
      }
      if (outcome.kind === "terminal_rejection") {
        // Terminal refusal: adopt the prior rejected operation so this surface
        // reaches a settled failed state rather than an indefinite pending one.
        trackAllocationOperation(outcome.operation);
        return;
      }
      setPhase("failed");
      setFailureMessage(SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE);
    } catch {
      if (!disposedRef.current) {
        setPhase("failed");
        setFailureMessage(SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE);
      }
    }
  }, [campaignId, applyCampaignResult, trackAllocationOperation]);

  return { phase, failureMessage, startAllocationOperation };
};

// ABSENT-allocation affordance: develops a recommended allocation through the
// durable operations contract. Rendered inside the canonical Budget
// Allocation section in place of the static supply-the-allocation note.
export function SherlocAllocationDevelopCard({
  campaignId,
  onCampaignApplied,
  initialOperation = null,
}) {
  const { phase, failureMessage, startAllocationOperation } =
    useSherlocAllocationRecommendationOperation({
      campaignId,
      onCampaignApplied,
      initialOperation,
    });

  if (phase === "active") {
    return (
      <p
        className="smp-alloc-op-note"
        role="status"
        data-sherloc-allocation-operation="active"
      >
        {SHERLOC_ALLOCATION_DEVELOPING_MESSAGE}
      </p>
    );
  }
  if (phase === "ready") {
    return (
      <p
        className="smp-alloc-op-note smp-alloc-op-note--ready"
        role="status"
        data-sherloc-allocation-operation="ready"
      >
        {SHERLOC_ALLOCATION_READY_MESSAGE}
      </p>
    );
  }
  return (
    <div className="smp-alloc-develop" data-sherloc-allocation-develop="true">
      {phase === "failed" ? (
        <p
          className="smp-alloc-op-note smp-alloc-op-note--failed"
          role="alert"
          data-sherloc-allocation-operation="failed"
        >
          {failureMessage || SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE}
        </p>
      ) : (
        <p className="smp-alloc-op-note">{SHERLOC_ALLOCATION_DEVELOP_PROMPT}</p>
      )}
      <button
        type="button"
        className="mediaplanner-save-btn smp-alloc-btn"
        data-sherloc-allocation-develop-action={
          phase === "failed" ? "retry" : "develop"
        }
        onClick={startAllocationOperation}
      >
        {phase === "failed"
          ? "Retry Allocation Recommendation"
          : "Develop Recommended Allocation"}
      </button>
    </div>
  );
}

// SHERLOC recommended allocation review surface (PENDING_APPROVAL / DRAFT →
// review + Edit/Approve/Reject; APPROVED → approved state; REJECTED →
// rejected state + request-new-recommendation affordance). Unknown statuses
// fail closed to the awaiting-approval review — an allocation is never
// presented as approved unless the backend says APPROVED.
export function SherlocAllocationRecommendationReview({
  campaignId,
  authority,
  onAuthorityRefresh,
}) {
  // READ-ONLY by contract: allocation approve/reject (and amount edits) are
  // Superadmin governance acts recorded in Quantum. The client portal names
  // the state and shows the recommendation verbatim — it holds no decision
  // machinery at all, so there is nothing here that can drift into a
  // client-side approval.
  const {
    phase: requestPhase,
    failureMessage: requestFailureMessage,
    startAllocationOperation,
  } = useSherlocAllocationRecommendationOperation({
    campaignId,
    onCampaignApplied: onAuthorityRefresh,
  });

  const status = getSherlocAllocationApprovalStatus(authority);
  const view =
    status === "APPROVED"
      ? "approved"
      : status === "REJECTED"
        ? "rejected"
        : "pending";
  const { included: rows, excluded: excludedRows } =
    orderSherlocAllocationAuthorityRows(authority?.rows);
  const totalCents = Number(authority?.totals?.grossBudgetCents);
  const totalLabel = Number.isFinite(totalCents)
    ? formatGrossAllocationCentsAmount(Math.round(totalCents))
    : "";

  const renderAllocationRow = (row) => {
    const key = String(row?.platformKey || "");
    const displayLabel =
      normalizeSherlocPlatformDisplayLabel(key, row?.displayName) || "Platform";
    const percentLabel = formatSherlocCanonicalPercentLabel(
      row?.allocationPercent
    );
    const cents = getSherlocAllocationAuthorityRowCents(row);
    const amountLabel = cents !== null ? formatGrossAllocationCentsAmount(cents) : "";
    const rowRationale = String(row?.rationale || "").trim();
    return (
      <div
        className="smp-alloc-row"
        key={key || displayLabel}
        data-sherloc-allocation-row={key}
      >
        <div className="smp-alloc-row-head">
          <span className="smp-alloc-row-platform">{displayLabel}</span>
          {percentLabel ? (
            <span
              className="smp-alloc-row-percent"
              aria-label={"Allocation percentage: " + percentLabel}
            >
              {percentLabel}
            </span>
          ) : null}
        </div>
        <div className="smp-alloc-row-financial">
          <span className="smp-canonical-financial-label">Allocated budget</span>
          <span
            className="smp-canonical-financial-value"
            data-sherloc-allocation-amount={key}
          >
            {amountLabel}
          </span>
        </div>
        {row?.minimumApplied ? (
          <p
            className="smp-alloc-row-minimum"
            data-sherloc-allocation-minimum={key}
          >
            Platform minimum applied.
          </p>
        ) : null}
        {rowRationale ? (
          <p
            className="smp-alloc-row-rationale"
            data-sherloc-allocation-rationale={key}
          >
            {rowRationale}
          </p>
        ) : null}
      </div>
    );
  };

  const overallRationale = String(authority?.allocationRationale || "").trim();
  const recovered = authority?.financialsSource === "OPERATOR_RECOVERED_HISTORICAL_STRATEGY";
  const title =
    recovered ? "RECOVERED HISTORICAL ALLOCATION" : view === "approved" ? "APPROVED ALLOCATION" : "SHERLOC RECOMMENDED ALLOCATION";
  const chipLabel =
    view === "approved"
      ? "APPROVED"
      : view === "rejected"
        ? "REJECTED"
        : recovered ? "RECOVERED ALLOCATION · AWAITING APPROVAL" : "SHERLOC RECOMMENDATION · AWAITING APPROVAL";

  return (
    <section
      className="smp-strategy-structured-section smp-canonical-section smp-alloc-section"
      data-sherloc-allocation-recommendation={view}
      aria-label={recovered ? "Recovered historical allocation" : "Sherloc recommended allocation"}
    >
      <div className="smp-alloc-header">
        <h3 className="smp-strategy-structured-title smp-alloc-title">{title}</h3>
        <span
          className={"smp-alloc-chip smp-alloc-chip--" + view}
          data-sherloc-allocation-state={
            view === "pending" ? "awaiting-approval" : view
          }
        >
          {chipLabel}
        </span>
      </div>
      {recovered ? <p data-testid="recovered-provenance-disclosure">These financial rows were recovered from historical records. Historical approval was not reconstructed. Present-day allocation review does not establish strategy currency or authorize additional delivery.</p> : null}
      {view === "rejected" ? (
        <div className="smp-alloc-rejected" data-sherloc-allocation-rejected="true">
          {requestPhase === "active" ? (
            <p
              className="smp-alloc-op-note"
              role="status"
              data-sherloc-allocation-operation="active"
            >
              {SHERLOC_ALLOCATION_DEVELOPING_MESSAGE}
            </p>
          ) : requestPhase === "ready" ? (
            <p
              className="smp-alloc-op-note smp-alloc-op-note--ready"
              role="status"
              data-sherloc-allocation-operation="ready"
            >
              {SHERLOC_ALLOCATION_READY_MESSAGE}
            </p>
          ) : (
            <>
              {requestPhase === "failed" ? (
                <p
                  className="smp-alloc-op-note smp-alloc-op-note--failed"
                  role="alert"
                  data-sherloc-allocation-operation="failed"
                >
                  {requestFailureMessage || SHERLOC_ALLOCATION_FAILURE_RETRY_MESSAGE}
                </p>
              ) : (
                <p className="smp-alloc-op-note">
                  {SHERLOC_ALLOCATION_REJECTED_NOTE}
                </p>
              )}
              <button
                type="button"
                className="mediaplanner-save-btn smp-alloc-btn"
                data-sherloc-allocation-request-new="true"
                onClick={startAllocationOperation}
              >
                {requestPhase === "failed"
                  ? "Retry Allocation Recommendation"
                  : "Request New Recommended Allocation"}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          {overallRationale ? (
            <p
              className="smp-alloc-rationale"
              data-sherloc-allocation-rationale-overall="true"
            >
              {overallRationale}
            </p>
          ) : null}
          {totalLabel ? (
            <div
              className="smp-canonical-allocation-total smp-alloc-total"
              data-sherloc-allocation-total="true"
              aria-label={"Total allocated budget: " + totalLabel}
            >
              <span className="smp-canonical-financial-label">
                Total allocated budget
              </span>
              <span className="smp-canonical-financial-value">{totalLabel}</span>
            </div>
          ) : null}
          <div className="smp-alloc-rows">
            {rows.map((row) => renderAllocationRow(row))}
          </div>
          {excludedRows.length ? (
            <div
              className="smp-alloc-excluded"
              data-sherloc-allocation-excluded="true"
            >
              {excludedRows.map((row) => {
                const key = String(row?.platformKey || "");
                const label =
                  normalizeSherlocPlatformDisplayLabel(key, row?.displayName) ||
                  "Platform";
                const reason = String(row?.exclusionReason || "").trim();
                return (
                  <p
                    className="smp-alloc-excluded-row"
                    key={key || label}
                    data-sherloc-allocation-excluded-row={key}
                  >
                    {label} is not part of this recommended allocation
                    {reason ? ": " + reason : "."}
                  </p>
                );
              })}
            </div>
          ) : null}
          {view === "pending" ? (
            <>
              <p
                className="smp-alloc-approval-note"
                role="note"
                data-sherloc-allocation-approval-note="true"
              >
                {SHERLOC_ALLOCATION_APPROVAL_REQUIRED_NOTE}
              </p>
              <p
                className="smp-alloc-readonly-note"
                role="note"
                data-sherloc-allocation-readonly="true"
              >
                {SHERLOC_ALLOCATION_CLIENT_READ_ONLY_NOTE}
              </p>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

// Applied-revision receipt (client portal): renders the backend's
// revisionApplied summary verbatim, gross figures only — net/working-media
// amounts stay internal. Never derived optimistically from local state.
export function SherlocAppliedRevisionSummary({ summary }) {
  if (!summary) return null;
  if (summary.structuralChanges === false) {
    return (
      <div className="smp-revision-hint smp-revision-applied-summary" data-sherloc-revision-summary="narrative-only">
        Revision interpreted — no structural plan changes were requested.
        {summary.interpretationSummary ? ` ${summary.interpretationSummary}` : ""}
      </div>
    );
  }
  const money = (value) =>
    Number.isFinite(Number(value)) ? formatGrossAllocationCentsAmount(Math.round(Number(value) * 100)) : null;
  const lines = [];
  if (Array.isArray(summary.removedChannels) && summary.removedChannels.length) {
    lines.push(
      `Removed from this managed plan: ${summary.removedChannels
        .map((key) => getSherlocAllocationLabel(key, key))
        .join(", ")}.`
    );
  }
  const grossTotal = summary.totals ? money(summary.totals.grossBudget) : null;
  if (grossTotal) {
    lines.push(`Revised total budget: ${grossTotal}.`);
  }
  if (Array.isArray(summary.channelAllocations) && summary.channelAllocations.length) {
    lines.push(
      `Channel allocations updated: ${summary.channelAllocations
        .map((row) => `${getSherlocAllocationLabel(row.key, row.channel)} ${money(row.grossAllocation) || ""}`.trim())
        .join(", ")}. Percentages recalculated against the revised total.`
    );
  }
  if (summary.flightDelta?.resolvedFlightStart || summary.flightDelta?.resolvedFlightEnd) {
    const derived = String(summary.flightDelta?.derivation || "").startsWith("derived");
    lines.push(
      `Flight timing: ${summary.flightDelta.resolvedFlightStart || "unchanged"} → ${
        summary.flightDelta.resolvedFlightEnd || "unchanged"
      }${derived ? " (end date derived from the stated flight length — we'll confirm before launch)" : ""}.`
    );
  }
  if (summary.targetingIntent) {
    const radius = summary.targetingIntent.radiusMiles;
    lines.push(
      `Geo intent recorded${radius ? `: ${radius.minimum}–${radius.maximum} mile radius per location` : ""}.`
    );
  }
  const dependencies = Array.isArray(summary.unresolvedDependencies)
    ? summary.unresolvedDependencies
    : [];
  const reapproval = Boolean(summary.lifecycle?.reapprovalRequired);
  return (
    <div className="smp-revision-hint smp-revision-applied-summary" data-sherloc-revision-summary="applied">
      <strong>Revision applied to the current plan.</strong>
      <ul className="smp-revision-applied-list">
        {lines.map((line, index) => (
          <li key={`smp-revision-line-${index}`}>{line}</li>
        ))}
        {dependencies.map((dependency) => (
          <li key={`smp-revision-dependency-${dependency.key}`} className="smp-revision-dependency">
            Needs attention: {dependency.description}
          </li>
        ))}
        {reapproval ? (
          <li className="smp-revision-dependency">
            This plan was previously approved — the revision returned it to review and it requires
            renewed approval before deployment.
          </li>
        ) : null}
      </ul>
    </div>
  );
}

const stripBulletPrefix = (s) => String(s || "").replace(/^[-•]\s+/, "");
const norm = (s) => stripBulletPrefix(String(s || "").trim()).trim().toLowerCase();
const normalizePlanPlatformKey = (value) => {
  const providerKey = providerPlannerKey(value);
  if (providerKey) return providerKey;
  const compact = String(value || "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  if (
    compact === "mobile" ||
    compact === "mobileads" ||
    compact === "mobiledisplay" ||
    compact === "inappmobile" ||
    compact === "mobileinapp" ||
    compact === "inapp"
  ) {
    return "mobile";
  }

  if (
    compact === "programmaticolv" ||
    compact === "programmaticvideo" ||
    compact === "onlinevideo" ||
    compact === "olv"
  ) {
    return "video";
  }

  if (
    compact === "googlesearch" ||
    compact === "googlepaidsearch" ||
    compact === "paidsearch" ||
    compact === "search"
  ) {
    return "google_search";
  }

  if (compact.includes("chatgpt") || compact.includes("openai")) {
    return "chatgpt_search";
  }

  if (compact === "truthsocial") return "truth_social";
  if (compact === "ingame" || compact === "ingames") return "ingame";
  if (
    compact === "dooh" ||
    compact === "digitaloutofhome" ||
    compact === "ooh"
  ) {
    return "dooh";
  }
  return compact;
};
const getSelectedPlanPlatformKeys = (selectedPlatforms = []) =>
  (() => {
    const keys = new Set(
      (Array.isArray(selectedPlatforms) ? selectedPlatforms : [])
        .map(normalizePlanPlatformKey)
        .filter(Boolean)
    );
    if (keys.has("facebook") && keys.has("instagram")) keys.add("meta");
    return Array.from(keys);
  })();


// Evidence-based Risks / Dependencies findings. Renders ONLY when the
// deterministic builder surfaced a real unresolved condition — never filler,
// never a per-platform selection reminder. When the list is empty the block
// is omitted entirely (the governed no-material-findings state).
export function SherlocRiskDependencyFindings({ findings = [] }) {
  if (!Array.isArray(findings) || !findings.length) return null;
  return (
    <section
      className="smp-risk-findings"
      data-testid="sherloc-risk-findings"
      aria-label="Unresolved risks and dependencies"
    >
      <h3 className="smp-risk-findings-title">Unresolved Dependencies</h3>
      {findings.map((finding) => (
        <article
          key={finding.key}
          className="smp-risk-finding"
          data-sherloc-risk-finding={finding.key}
        >
          <h4 className="smp-risk-finding-heading">{finding.title}</h4>
          <dl className="smp-risk-finding-details">
            {[
              ["Issue", finding.issue],
              ["Impact", finding.impact],
              ["Required action", finding.requiredAction],
              ["Affected area", finding.affectedArea],
            ].map(([label, value]) =>
              value ? (
                <div key={label} className="smp-risk-finding-row">
                  <dt className="smp-risk-finding-label">{label}</dt>
                  <dd className="smp-risk-finding-value">{value}</dd>
                </div>
              ) : null
            )}
          </dl>
        </article>
      ))}
    </section>
  );
}

// The former ensureSelectedMobileStrategyCoverage / ensureSelectedVideo-
// StrategyCoverage / ensureSelectedCommercePlatformStrategyCoverage appenders
// are REMOVED. They injected "Selected Platform Coverage" boilerplate
// ("include Mobile in the platform allocation, trafficking setup, and
// delivery checks") into the rendered strategy for any selected platform the
// text parser could not find at a line start — selection reminders, not
// risks. Risks / Dependencies now carries only evidence-based findings
// (utils/sherlocRiskDependencyFindings.js) plus the provider's own
// campaign-specific watchouts; provider prompt rules already guarantee every
// selected platform (including Shopify's measurement-layer role) appears in
// the generated strategy itself.

const isSelectedPlanHero = (hero, selectedPlatforms = []) => {
  const key = normalizePlanPlatformKey(hero?.key || hero?.label);
  if (!key) return true;
  return getSelectedPlanPlatformKeys(selectedPlatforms).includes(key);
};

/**
 * Resolve which hero should render from a header-like line.
 * ✅ META priority: if both FB+IG are selected, any FB/IG/meta header resolves to META.
 */
const resolveHeroFromLine = (rawLine, selectedPlatforms) => {
  const t = norm(rawLine);
  const selectedHero = (hero) => (isSelectedPlanHero(hero, selectedPlatforms) ? hero : null);

  // Provider aliases and approved artwork already have shared authorities.
  // Keep legacy allocation cards on that same identity and selection boundary.
  const providerKey = providerPlannerKey(rawLine);
  if (providerKey) {
    const branding = resolveSherlocPlatformBranding(providerKey);
    return branding ? selectedHero({ ...branding, logo: branding.image }) : null;
  }

  const hasFacebook = Array.isArray(selectedPlatforms) && selectedPlatforms.includes("facebook");
  const hasInstagram = Array.isArray(selectedPlatforms) && selectedPlatforms.includes("instagram");
  const hasMetaPair = hasFacebook && hasInstagram;

  // helpers
  const contains = (x) => t.includes(x);

  // ✅ META FIRST (must be above facebook/instagram)
  if (
    hasMetaPair &&
    (t.startsWith("meta") ||
      t.startsWith("facebook") ||
      t.startsWith("instagram") ||
      contains("facebook + instagram") ||
      contains("facebook & instagram") ||
      contains("facebook and instagram") ||
      contains("fb + ig") ||
      contains("fb/ig") ||
      contains("fb & ig"))
  ) {
    return selectedHero({ key: "meta", label: "META (FACEBOOK + INSTAGRAM)", logo: metaLogo });
  }

  // Ordered matches (more specific first)
  if (contains("chatgpt") || contains("openai")) {
    return selectedHero({ key: "chatgpt_search", label: "CHATGPT SEARCH", logo: openaiLogo });
  }

  if (
    t.startsWith("google search") ||
    t.startsWith("paid search") ||
    t.startsWith("search") ||
    (t.startsWith("google") && contains("search"))
  ) {
    return selectedHero({ key: "google_search", label: "GOOGLE SEARCH", logo: googleLogo });
  }

  if ((contains("youtube") || contains("you tube")) && !contains("search")) {
    return selectedHero({ key: "youtube", label: "YOUTUBE", logo: youtubeLogo });
  }

  // Programmatic: only if line actually starts with programmatic/olv
  if (
    t.startsWith("programmatic video") ||
    t.startsWith("programmatic olv") ||
    t.startsWith("online video") ||
    t.startsWith("olv")
  ) {
    return selectedHero({ key: "video", label: "PROGRAMMATIC VIDEO (OLV)", logo: videoLogo });
  }

  if (t.startsWith("youtube")) return selectedHero({ key: "youtube", label: "YOUTUBE", logo: youtubeLogo });
  if (t.startsWith("tiktok")) return selectedHero({ key: "tiktok", label: "TIKTOK", logo: tiktokLogo });

  // If only FB (no IG) and header starts with facebook
  if (t.startsWith("facebook")) return selectedHero({ key: "facebook", label: "FACEBOOK", logo: facebookLogo });
  // If only IG (no FB) and header starts with instagram
  if (t.startsWith("instagram")) return selectedHero({ key: "instagram", label: "INSTAGRAM", logo: instagramLogo });

  if (t === "x" || t.startsWith("x ") || t.startsWith("twitter")) {
    return selectedHero({ key: "x", label: "X", logo: xLogo });
  }

  if (t.startsWith("reddit")) return selectedHero({ key: "reddit", label: "REDDIT", logo: redditLogo });
  if (t.startsWith("amazon")) return selectedHero({ key: "amazon", label: "AMAZON", logo: amazonLogo });
  if (t.startsWith("shopify")) return selectedHero({ key: "shopify", label: "SHOPIFY", logo: shopifyLogo });
  if (t.startsWith("linkedin") || t.startsWith("linked in")) {
    return selectedHero({ key: "linkedin", label: "LINKEDIN", logo: linkedinLogo });
  }
  if (t.startsWith("rumble")) return selectedHero({ key: "rumble", label: "RUMBLE", logo: rumbleLogo });
  if (t.startsWith("truth social")) return selectedHero({ key: "truth_social", label: "TRUTH SOCIAL", logo: truthSocialLogo });

  // programmatic stack explicit
  // "Connected TV (CTV) via Cphere Programmatic" is a valid CTV section header and should
  // resolve to the CTV hero — a word-boundary contains test (mirroring YouTube) accepts
  // qualified headings; it stays AFTER the more specific platform matches above so
  // "connected tv" mentions cannot shadow them.
  if (/\b(?:ctv|connected\s+(?:tv|television))\b/.test(t)) {
    return selectedHero({ key: "ctv", label: "CTV", logo: ctvLogo });
  }
  if (t.startsWith("video")) return selectedHero({ key: "video", label: "VIDEO", logo: videoLogo });
  if (/^viant(?:\s|$)/.test(t)) return selectedHero({ key: "viant", label: "VIANT", logo: viantLogo });
  if (t.startsWith("display")) return selectedHero({ key: "display", label: "DISPLAY", logo: displayLogo });
  if (t.startsWith("mobile")) return selectedHero({ key: "mobile", label: "MOBILE", logo: mobileLogo });
  if (t.startsWith("audio")) return selectedHero({ key: "audio", label: "AUDIO", logo: audioLogo });
  if (t.startsWith("in-game") || t.startsWith("ingame")) return selectedHero({ key: "ingame", label: "IN-GAME", logo: ingameLogo });
  if (
    t.startsWith("dooh") ||
    t.startsWith("digital out of home") ||
    t.startsWith("digital out-of-home") ||
    t === "ooh" ||
    t.startsWith("ooh ")
  ) {
    return selectedHero({ key: "dooh", label: "DOOH", logo: doohLogo });
  }
  if (t.startsWith("email")) return selectedHero({ key: "email", label: "EMAIL", logo: emailLogo });

  // If user wrote "meta" but doesn't have both selected, still show META as a single brand marker.
  if (t.startsWith("meta")) return selectedHero({ key: "meta", label: "META", logo: metaLogo });

  // If user wrote "google" but not "google search", show Google logo (rare)
  if (t.startsWith("google")) return selectedHero({ key: "google_search", label: "GOOGLE", logo: googleLogo });

  return null;
};

const createPlannerPersistence = (localStorage) => {
const plannerCopyHasPendingSave = (id) => {
  if (!id) return false;
  try { return localStorage.listPendingSaves(id).length > 0; }
  catch { return true; } // Unknown ownership/storage state never implies Saved.
};
const readGlobalIndex = () => {
  const raw = localStorage.getItem(PLANNER_INDEX_KEY);
  const parsed = safeJsonParse(raw, []);
  return Array.isArray(parsed) ? parsed : [];
};
const writeGlobalIndex = (idxArr) => {
  try {
    localStorage.setItem(PLANNER_INDEX_KEY, JSON.stringify(idxArr));
  } catch {}
};
const readPlannerBinding = (id) => {
  if (!id) return null;
  const parsed = safeJsonParse(localStorage.getItem(makePlannerStorageKey(id)), null);
  if (!parsed || typeof parsed !== "object") return null;
  return {
    serverDraftId: normalizeServerDraftId(parsed.serverDraftId ?? parsed.server_draft_id),
    unsavedChanges: typeof parsed.unsavedChanges === "boolean" ? parsed.unsavedChanges : null,
    serverSavedAt: parsed.serverSavedAt || null,
    supersededServerDraftId: normalizeServerDraftId(parsed.supersededServerDraftId),
  };
};
const readPlannerCopy = (id) => {
  if (!id) return null;
  const parsed = safeJsonParse(localStorage.getItem(makePlannerStorageKey(id)), null);
  return parsed && typeof parsed === "object" ? parsed : null;
};
const writePlannerLocalState = (id, patch) => {
  if (!id) return;
  try {
    const key = makePlannerStorageKey(id);
    const existing = safeJsonParse(localStorage.getItem(key), null);
    localStorage.setItem(
      key,
      JSON.stringify({ ...(existing && typeof existing === "object" ? existing : {}), ...patch })
    );
  } catch {}
};
const plannerLocalCopyHasContent = (id) => {
  const parsed = safeJsonParse(localStorage.getItem(makePlannerStorageKey(id)), null);
  return (
    plannerFormHasContent(parsed?.formData) ||
    Boolean(String(parsed?.strategy || "").trim()) ||
    (Array.isArray(parsed?.creativeRecommendations) && parsed.creativeRecommendations.length > 0)
  );
};

  return { readGlobalIndex, writeGlobalIndex, readPlannerBinding, readPlannerCopy, writePlannerLocalState, plannerLocalCopyHasContent, plannerCopyHasPendingSave };
};

const SherlocMediaPlanner = ({ plannerSession }) => {
  const storageFormNormalizerRef = useRef((form) => form);
  // Each mount has one freshly verified owner, advertiser and captured token.
  // Revocation denies storage and queued work even before unmount cleanup runs.
  const localStorage = useMemo(() => createPlannerBrowserStorage({
    portal: "client",
    accountId: plannerSession.accountId,
    advertiserId: plannerSession.advertiserId,
    isCurrent: plannerSession.isCurrent,
    normalizeForm: (form) => storageFormNormalizerRef.current(form),
  }), [plannerSession]);
  const { readGlobalIndex, writeGlobalIndex, readPlannerBinding, readPlannerCopy, writePlannerLocalState, plannerLocalCopyHasContent, plannerCopyHasPendingSave } = useMemo(
    () => createPlannerPersistence(localStorage), [localStorage]
  );
  const initialAuthorizedCampaignRef = useRef(plannerSession.authorizedCampaign || null);
  const fetch = useCallback(async (...args) => {
    const assertCurrent = () => {
      if (!plannerSession.isCurrent()) throw new Error("Planner session changed.");
    };
    assertCurrent();
    const initialCampaign = initialAuthorizedCampaignRef.current;
    const requestedUrl = new URL(String(args[0]), window.location.origin);
    const campaignReadPath = `/api/media-campaigns/${encodeURIComponent(plannerSession.campaignId || "")}`;
    const reuseAuthorizedCampaign = initialCampaign &&
      requestedUrl.pathname === campaignReadPath &&
      String(args[1]?.method || "GET").toUpperCase() === "GET";
    if (reuseAuthorizedCampaign) initialAuthorizedCampaignRef.current = null;
    const response = reuseAuthorizedCampaign
      ? { ok: true, status: 200, json: async () => ({ campaign: initialCampaign }), text: async () => JSON.stringify({ campaign: initialCampaign }) }
      : await window.fetch(...args);
    assertCurrent();
    return new Proxy(response, {
      get(target, property) {
        if (property === "json" || property === "text") return async (...readArgs) => {
          const value = await target[property](...readArgs);
          assertCurrent();
          return value;
        };
        const value = Reflect.get(target, property, target);
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  }, [plannerSession]);

  const getSherlocMediaPlannerAuthToken = useCallback(
    () => plannerSession.isCurrent() ? plannerSession.token : "", [plannerSession]
  );
  const navigate = useNavigate();
  const location = useLocation();
  const plannerRoutePathRef = useRef(location.pathname);
  const isMountedRef = useRef(true);

  // ✅ Route-scoped flag to help CSS isolate per page (prevents cross-page overrides)
  useEffect(() => {
    const prev = document.body.dataset.route;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.dataset.route = "/client/sherloc-media-planner";
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      isMountedRef.current = false;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      if (prev) document.body.dataset.route = prev;
      else delete document.body.dataset.route;
    };
  }, []);

  const advCtx = useAdvertiser() || {};
  const authContext = useAuth();
  const {
    selectedAdvertiserId: ctxSelectedAdvertiserId,
    selectedAdvertiserName: ctxSelectedAdvertiserName,
    // ✅ canonical setter in your updated context
    selectAdvertiser,
    selectedAdvertiserVertical,
    // ✅ legacy name-only API (still exists in your context)
    selectedAdvertiser: legacySelectedAdvertiserName,
    setSelectedAdvertiser: legacySetSelectedAdvertiserName,
  } = advCtx;
  const resolvedAdvertiserVertical = normalizeAdvertiserVertical(
    selectedAdvertiserVertical ||
      (typeof window !== "undefined"
        ? window.localStorage.getItem("selectedAdvertiserVertical")
        : "")
  );

  const ctxNameResolved = String(
    ctxSelectedAdvertiserName || legacySelectedAdvertiserName || ""
  ).trim();

  const getEmail = () => window.localStorage.getItem("userEmail");

  const plannerSearchParams = useMemo(
    () => new URLSearchParams(location?.search || ""),
    [location?.search]
  );
  const routeCampaignId =
    location?.state?.campaignId ?? plannerSearchParams.get("campaignId") ?? null;
  const routeAdvertiserId =
    location?.state?.advertiserId != null && String(location.state.advertiserId).trim()
      ? String(location.state.advertiserId).trim()
      : plannerSearchParams.get("advertiserId") || null;
  const routeAdvertiserName = String(
    location?.state?.advertiserName || plannerSearchParams.get("advertiserName") || ""
  ).trim();
  const routeCampaignName = String(
    location?.state?.campaignName || plannerSearchParams.get("campaignName") || ""
  ).trim();
  // An explicit saved-draft id (a resume link, Eden) outranks every other way
  // of choosing the plan to open — once the advertiser's own list confirms it.
  const routeDraftId = normalizeServerDraftId(
    location?.state?.draftId ??
      location?.state?.serverDraftId ??
      plannerSearchParams.get("draftId")
  );

  const [navCampaignId, setNavCampaignId] = useState(routeCampaignId);

  useEffect(() => {
    setNavCampaignId(routeCampaignId);
  }, [routeCampaignId]);

  const clearPlannerRouteState = useCallback(() => {
    if (!isMountedRef.current) return;
    if (window.location.pathname !== plannerRoutePathRef.current) return;
    try {
      navigate(plannerRoutePathRef.current, { replace: true, state: null });
    } catch {}
  }, [navigate]);

  const campaignIdFromNav = navCampaignId;

  useEffect(() => {
    if (campaignIdFromNav) {
      setSessionMode("campaign");
    }
  }, [campaignIdFromNav]);

  // CLIENT: campaign is always read-only (parity note with the superadmin
  // planner, which computes this per role).

  const BASE_BLANK_FORM = DEFAULT_SHERLOC_PLANNER_FORM_DATA;

  const normalizePlannerFormData = useCallback(
    (fd = {}) => normalizeSherlocPlannerFormData(fd),
    []
  );
  // Compare stored representations as the page loads them, without rewriting
  // preserved raw copies merely because normalization fills defaults or aliases.
  storageFormNormalizerRef.current = normalizePlannerFormData;

  // ✅ Local “page” advertiser selection
  const [pageAdvertiserId, setPageAdvertiserId] = useState(
    plannerSession.advertiserId || null
  );
  const [pageAdvertiserName, setPageAdvertiserName] = useState(
    routeCampaignId ? routeAdvertiserName || ctxNameResolved : ctxNameResolved
  );

  useEffect(() => {
    if (!routeCampaignId) return;

    if (routeAdvertiserId || routeAdvertiserName) {
      setPageAdvertiserId(routeAdvertiserId ?? null);
      setPageAdvertiserName(routeAdvertiserName);
    }

    if (typeof selectAdvertiser === "function" && (routeAdvertiserId || routeAdvertiserName)) {
      selectAdvertiser({
        id: routeAdvertiserId ?? null,
        name: routeAdvertiserName,
      });
    } else if (typeof legacySetSelectedAdvertiserName === "function" && routeAdvertiserName) {
      legacySetSelectedAdvertiserName(routeAdvertiserName);
    }
  }, [
    legacySetSelectedAdvertiserName,
    routeAdvertiserId,
    routeAdvertiserName,
    routeCampaignId,
    selectAdvertiser,
  ]);

  // keep page advertiser in sync when context changes (non-destructive)
  const lastCtxAdvRef = useRef({
    id: ctxSelectedAdvertiserId ?? null,
    name: ctxNameResolved,
  });

  const [formData, setFormData] = useState(BASE_BLANK_FORM);
  const [strategy, setStrategy] = useState("");
  const [creativeRecommendations, setCreativeRecommendations] = useState([]);
  const [creativeGenerationMeta, setCreativeGenerationMeta] = useState(null);
  const [generationLoadingType, setGenerationLoadingType] = useState("");
  const [creativeError, setCreativeError] = useState("");
  // Where the last generated creative variations were saved. Bound to that
  // exact array, so it disappears the moment other variations replace them.
  const [creativeSaveNotice, setCreativeSaveNotice] = useState(null);
  const [isBudgetFocused, setIsBudgetFocused] = useState(false);
  const [revisionPrompt, setRevisionPrompt] = useState("");
  const [lastAppliedRevisionPrompt, setLastAppliedRevisionPrompt] = useState("");
  // Canonical persisted allocation (strategy_payload.requestedChannelAllocation)
  // for the active plan. When the governed revision contract has written exact
  // amounts, these rows override notes/text-derived allocation cards.
  const [canonicalAllocation, setCanonicalAllocation] = useState(null);
  // Canonical structured strategy presentation
  // (strategy_payload.strategyPresentation, sherloc_strategy_presentation_v1).
  // When present it is the single strategy-rendering authority and every
  // legacy text-parsed strategy renderer is suppressed.
  const [strategyPresentation, setStrategyPresentation] = useState(null);
  // Governed SHERLOC allocation authority
  // (strategy_payload.allocationAuthority, allocation_authority_v2) for the
  // active campaign. Client serialization strips net/margin fields — this
  // state never carries or renders them.
  const [allocationAuthority, setAllocationAuthority] = useState(null);
  // In-flight allocation_recommendation operation found by the reload
  // reconnect probe; handed to the allocation surface (never to the
  // strategy-generation machinery).
  const [allocationReconnectOperation, setAllocationReconnectOperation] =
    useState(null);
  // Evidence-based Risks / Dependencies findings (client-safe wording).
  // Selection alone never produces a finding; the builder judges only the
  // authoritative allocation / presentation evidence it is given, and an
  // empty result omits the block entirely.
  const riskDependencyFindings = useMemo(
    () =>
      buildSherlocRiskDependencyFindings({
        selectedPlatforms: formData.platforms,
        allocationRows: canonicalAllocation,
        presentationChannelKeys: Array.isArray(strategyPresentation?.channelStrategy)
          ? strategyPresentation.channelStrategy.map((entry) => entry?.platformKey)
          : null,
        allocationApprovalStatus: "",
        creativeReadiness: null,
        viewerRole: "client",
      }),
    [formData.platforms, canonicalAllocation, strategyPresentation]
  );
  // Structured summary of the last applied governed revision, straight from
  // the backend's revisionApplied payload (never derived optimistically).
  const [appliedRevisionSummary, setAppliedRevisionSummary] = useState(null);
  // Plan version token from the last canonical hydration; sent with revisions
  // so a stale session cannot silently overwrite a newer plan.
  const planVersionRef = useRef("");
  // The strategy ARTIFACT's version — distinct from planVersionRef, which is
  // the record-concurrency token (updated_at) that ANY campaign edit
  // advances. Recency comparisons for reconnect/rehydration use the artifact
  // version so a post-generation record edit cannot outrank a newer result.
  const strategyPlanVersionRef = useRef("");
  const [strategyProvenance, setStrategyProvenance] = useState(null);
  // Eden-assisted intake draft (populated via the planner-assist event; never auto-applied)
  const [edenDraftResult, setEdenDraftResult] = useState(null);
  const [edenDraftAppliedNote, setEdenDraftAppliedNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false);
  const [error, setError] = useState("");
  const [serverDraftId, setServerDraftId] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const draftAutosaveTimerRef = useRef(null);
  const lastDraftSaveFingerprintRef = useRef("");
  // The advertiser's saved drafts as the server listed them, and where that
  // lookup stands. A failed lookup is never read as "no drafts": nothing is
  // opened, created or saved until it succeeds or the operator starts a new
  // strategy.
  const [serverDraftList, setServerDraftList] = useState({
    status: "idle",
    advertiserKey: "",
    drafts: [],
    notice: "",
  });
  const draftListReqRef = useRef(0);
  // Bootstrap runs before resumePlannerDrafts is declared; it calls through
  // this ref (assigned right after the declaration).
  const resumePlannerDraftsRef = useRef(null);
  // Identity minted for a new plan before React state carries it, so a
  // double-click or a save racing the first edit reuses it.
  const pendingPlannerIdRef = useRef("");
  // Saves run one at a time; a queued save reads the row id its plan learned
  // from the save before it, never a stale "no row yet".
  const draftSaveChainRef = useRef(new Map());
  const serverDraftIdByPlannerRef = useRef(new Map());
  // Per plan, the content its saved draft holds as far as this page knows (a
  // save it confirmed, or the saved version it opened). A browser copy has
  // unsaved changes exactly when its content differs from this.
  const savedContentByPlannerRef = useRef(new Map());
  // The plan just opened and its content as opened: autosave waits for its
  // first change, so opening a plan never saves it.
  const openedPlanContentRef = useRef(null);
  // Plans deleted from this device: a save that finishes later never brings
  // their copy back.
  const deletedPlannerIdsRef = useRef(new Set());
  // Drafts saved while a saved-plan lookup was in flight, merged into its
  // answer (the server may have read before they landed).
  const draftsSavedDuringLookupRef = useRef({ reqId: 0, drafts: [] });
  // The save that last set the on-screen save status; a save that finishes
  // after its plan left the screen hands the status back only if no other
  // save or plan has taken it since.
  const saveStatusOwnerRef = useRef(0);
  // The plan a save left showing "Unsaved changes" because the page held
  // content it did not send; autosave re-checks it once the page renders.
  const unsavedAfterSaveRef = useRef("");
  const [autosaveNudge, setAutosaveNudge] = useState(0);
  // Per plan, the content of each save whose answer has not arrived.
  const savesInFlightRef = useRef(new Map());

  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [sessionMode, setSessionMode] = useState(() =>
    getClientInitialPlannerSessionMode(campaignIdFromNav)
  );

  // Global index of ALL drafts
  const [plannerIndex, setPlannerIndex] = useState([]);
  // Active planner for current advertiser (resolved via per-adv active key)
  const [activePlannerId, setActivePlannerId] = useState("");
  // The planner a creative response belongs to: a late answer is applied only
  // while the same planner (or loaded campaign) is still open.
  const plannerIdentity = `${activePlannerId || ""}|${campaignIdFromNav || ""}`;
  const plannerIdentityRef = useRef("");
  plannerIdentityRef.current = plannerIdentity;
  // The open plan's content as of the latest render, for a save that finishes
  // later to compare with what it sent.
  const latestPlannerContentRef = useRef(null);
  latestPlannerContentRef.current = {
    formData,
    strategy,
    creatives: creativeRecommendations,
    creativeMeta: creativeGenerationMeta,
  };
  // Another plan (or campaign) on screen: a save still running for the one
  // before never takes the on-screen save status back, and an identity
  // minted for a new plan is never reused once that plan is no longer open.
  useEffect(() => {
    saveStatusOwnerRef.current += 1;
    if (pendingPlannerIdRef.current && pendingPlannerIdRef.current !== activePlannerId) {
      pendingPlannerIdRef.current = "";
    }
  }, [activePlannerId, plannerIdentity]);
  // The creative run in flight: the plan it belongs to and how to stop waiting.
  const creativeRunRef = useRef(null);
  // Opening another plan mid-run stops this page's wait (the backend still
  // finishes and saves to the plan the request named), so the plan now open
  // is never shown as generating or left locked.
  useEffect(() => {
    const run = creativeRunRef.current;
    if (run && run.identity !== plannerIdentity) run.stopWaiting();
  }, [plannerIdentity]);

  // ✅ Campaign list for currently selected advertiser
  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [campaignsLoadError, setCampaignsLoadError] = useState("");
  // True only when Load Campaign auto-selected the single available campaign,
  // so we can show a calm one-line helper. Cleared on any other selection.
  const [autoLoadedSoleCampaign, setAutoLoadedSoleCampaign] = useState(false);

  // Guards
  const seededDraftRef = useRef({ draftId: null });
  const bootstrapReqRef = useRef(0);
  const campaignLoadReqRef = useRef(0);
  // The readable strategy microsite address the backend assigned, kept with
  // the campaign id it belongs to.
  const [strategyMicrositeAddress, setStrategyMicrositeAddress] = useState({
    campaignId: "",
    path: "",
  });
  const campaignListReqRef = useRef(0);
  const manualPlannerOverrideSeqRef = useRef(0);
  const advertiserScopeGenerationRef = useRef(0);
  const creativeGenerationInFlightRef = useRef(false);
  // Bumped on every operator edit; async hydration captured before an edit
  // must not overwrite the newer local state when it resolves.
  const formEditGenerationRef = useRef(0);

  // ── Durable Sherloc generation operation (server-side; survives reloads) ──
  // The tracked operation snapshot (with __receivedAtMs stamped at adoption).
  const [generationOperation, setGenerationOperation] = useState(null);
  // Ref mirror so poll timers/listeners never read a stale closure.
  const generationOperationRef = useRef(null);
  // Advertiser scope the tracked operation belongs to (stale-scope guard).
  const generationOperationScopeRef = useRef(0);
  // Pending apply context captured at submit time (kind + revision inputs).
  const generationContextRef = useRef(null);
  // Poll timer + reentrancy guard.
  const generationPollTimerRef = useRef(null);
  const generationPollInFlightRef = useRef(false);
  // Duplicate-click guard: exactly one durable POST per user intent.
  const generationSubmitLatchRef = useRef(false);
  // Own the complete intent, including asynchronous draft save and final
  // authority read, before the existing durable-POST latch is acquired.
  const fullStrategyIntentRef = useRef(null);
  const fullStrategyContextRef = useRef(null);
  // A final-read refusal supersedes the displayed read only for that exact
  // context. A refresh/new hook result discards it; it can never grant access.
  const [fullStrategyReadRefusal, setFullStrategyReadRefusal] = useState(null);
  useEffect(() => () => {
    fullStrategyIntentRef.current = null;
    fullStrategyContextRef.current = null;
  }, []);
  // Retry grace: after a 202 retry accept, a FAILED_RETRYABLE snapshot with an
  // unchanged attemptCount is the pre-claim window, not a new failure.
  const generationRetryGraceRef = useRef(null);
  const [generationRetryPending, setGenerationRetryPending] = useState(false);
  // 1s ticker driving elapsed/last-update labels while an operation is active.
  const [generationNowMs, setGenerationNowMs] = useState(() => Date.now());
  // Latest strategy text for effects that must not re-run on every keystroke.
  const strategyTextRef = useRef("");
  // ── Current-plan completeness inputs (canonical resolver) ──
  // Latest LOADED campaign snapshot (budget_breakdown / allocationCurrentState
  // travel here) so async operation callbacks can consult the resolver against
  // server truth instead of echoing a historical operation. Reset at the start
  // of every campaign load (never reads across a campaign switch).
  const loadedCampaignSnapshotRef = useRef(null);
  // Render-published mirror of the current allocation authority (same
  // latest-render pattern as durableGenerationMachineryRef below).
  const currentPlanAuthorityRef = useRef(null);

  const beginManualPlannerOverride = useCallback(() => {
    manualPlannerOverrideSeqRef.current += 1;
    bootstrapReqRef.current += 1;
    campaignLoadReqRef.current += 1;
    return manualPlannerOverrideSeqRef.current;
  }, []);

  const clearPlannerAdvertiserScopedState = useCallback(
    (nextAdvertiser = null, { reason = "changed" } = {}) => {
      const nextId =
        nextAdvertiser?.id != null && String(nextAdvertiser.id).trim()
          ? String(nextAdvertiser.id).trim()
          : null;
      const nextName = String(
        nextAdvertiser?.name || nextAdvertiser?.advertiser || ""
      ).trim();

      advertiserScopeGenerationRef.current += 1;
      beginManualPlannerOverride();
      campaignListReqRef.current += 1;

      if (draftAutosaveTimerRef.current) {
        clearTimeout(draftAutosaveTimerRef.current);
        draftAutosaveTimerRef.current = null;
      }
      lastDraftSaveFingerprintRef.current = "";
      seededDraftRef.current = { draftId: null };
      // Another advertiser's plans: no identity, saved-plan lookup or queued
      // save carries over (a save still running finishes for its own plan).
      pendingPlannerIdRef.current = "";
      openedPlanContentRef.current = null;
      draftSaveChainRef.current = new Map();
      draftListReqRef.current += 1;
      setServerDraftList({ status: "idle", advertiserKey: "", drafts: [], notice: "" });

      setCampaigns([]);
      setCampaignsLoadError("");
      setIsLoadingCampaigns(false);
      setAutoLoadedSoleCampaign(false);
      setNavCampaignId(null);
      clearPlannerRouteState();

      setActiveCampaignId(null);
      loadedCampaignSnapshotRef.current = null;
      setActivePlannerId("");
      setServerDraftId(null);
      setSessionMode("new");
      setPlannerIndex([]);

      setFormData(
        normalizePlannerFormData({
          ...BASE_BLANK_FORM,
          advertiserId: nextId,
          advertiser: nextName,
        })
      );
      setStrategy("");
      setCreativeRecommendations([]);
      setCreativeGenerationMeta(null);
      setRevisionPrompt("");
      setLastAppliedRevisionPrompt("");
      setCanonicalAllocation(null);
      setStrategyPresentation(null);
      setAllocationAuthority(null);
      setAllocationReconnectOperation(null);
      setAppliedRevisionSummary(null);
      planVersionRef.current = "";
      strategyPlanVersionRef.current = "";
      setStrategyProvenance(null);
    strategyPlanVersionRef.current = "";
    setStrategyProvenance(null);
      setEdenDraftResult(null);
      setEdenDraftAppliedNote("");

      setGenerationLoadingType("");
      // Durable operation tracking is advertiser-scoped: drop the tracked
      // operation and stop polling (the server-side operation itself is
      // untouched — reselecting the campaign reconnects to it).
      if (generationPollTimerRef.current) {
        clearTimeout(generationPollTimerRef.current);
        generationPollTimerRef.current = null;
      }
      generationOperationRef.current = null;
      generationContextRef.current = null;
      generationRetryGraceRef.current = null;
      generationSubmitLatchRef.current = false;
      setGenerationOperation(null);
      setGenerationRetryPending(false);
      setCreativeError("");
      setIsLoading(false);
      setIsLoadingCampaign(false);
      setIsDirty(false);
      setSaveStatus("idle");
      setSaveMessage("");
      setError(
        reason === "confirmed_removed"
          ? "Select an advertiser to use Media Planner."
          : ""
      );

      setPageAdvertiserId(nextId);
      setPageAdvertiserName(nextName);
      lastCtxAdvRef.current = { id: nextId, name: nextName };
    },
    [
      BASE_BLANK_FORM,
      beginManualPlannerOverride,
      clearPlannerRouteState,
      normalizePlannerFormData,
    ]
  );

  const {
    selectedAdvertiserId: governedSelectedAdvertiserId,
    selectedAdvertiserName: governedSelectedAdvertiserName,
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    error: advertiserCatalogError,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearPlannerAdvertiserScopedState,
  });

  const activePlannerAdvertiserVertical = normalizeAdvertiserVertical(
    currentAdvertiser?.vertical || resolvedAdvertiserVertical
  );
  const plannerVerticalConfig = useMemo(
    () => getPlannerVerticalConfig(activePlannerAdvertiserVertical),
    [activePlannerAdvertiserVertical]
  );
  const plannerAdvertiserSelectionLocked =
    isLoading ||
    isLoadingCampaign ||
    isLoadingCampaigns ||
    Boolean(generationLoadingType) ||
    saveStatus === "saving";

  const handlePlannerAdvertiserSelect = useCallback(
    (advertiser) => {
      const nextId =
        advertiser?.id != null && String(advertiser.id).trim()
          ? String(advertiser.id).trim()
          : "";
      const nextName = String(
        advertiser?.name || advertiser?.advertiser || ""
      ).trim();
      const currentId =
        governedSelectedAdvertiserId != null &&
        String(governedSelectedAdvertiserId).trim()
          ? String(governedSelectedAdvertiserId).trim()
          : "";
      const currentName = String(governedSelectedAdvertiserName || "").trim();
      const isSameAdvertiser =
        (nextId && currentId && nextId === currentId) ||
        (!nextId && !currentId && nextName && nextName === currentName);

      if (isSameAdvertiser) {
        handleAdvertiserSelect(advertiser);
        return;
      }

      if (plannerAdvertiserSelectionLocked) {
        closeAdvertiserSelector();
        setError(
          "Finish the current Planner operation before changing advertisers."
        );
        return;
      }

      if (isDirty && !campaignIdFromNav) {
        const confirmed = window.confirm(
          "You have unsaved changes in Media Planner. Switching advertisers will load a different advertiser’s drafts/campaign. Continue?"
        );
        if (!confirmed) {
          closeAdvertiserSelector();
          return;
        }
      }

      handleAdvertiserSelect(advertiser);
    },
    [
      campaignIdFromNav,
      closeAdvertiserSelector,
      governedSelectedAdvertiserId,
      governedSelectedAdvertiserName,
      handleAdvertiserSelect,
      isDirty,
      plannerAdvertiserSelectionLocked,
    ]
  );

  const plannerDebug = useCallback((event, payload = {}) => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem("debug_client_media_planner") !== "1") return;
    } catch {
      return;
    }

    // eslint-disable-next-line no-console
    console.log(`[Client SherlocMediaPlanner] ${event}`, payload);
  }, []);

  const assertDateInputValue = useCallback((label, value) => {
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem("debug_client_media_planner") !== "1") return;
    } catch {
      return;
    }

    // eslint-disable-next-line no-console
    console.assert(
      !value || /^\d{4}-\d{2}-\d{2}$/.test(String(value)),
      `[Client SherlocMediaPlanner] ${label} must be YYYY-MM-DD for input[type="date"]`,
      value
    );
  }, []);

  useEffect(() => {
    const nextId = ctxSelectedAdvertiserId ?? null;
    const nextName = String(
      ctxSelectedAdvertiserName || legacySelectedAdvertiserName || ""
    ).trim();
    const prev = lastCtxAdvRef.current;
    const changed =
      String(prev?.id ?? "") !== String(nextId ?? "") ||
      String(prev?.name ?? "") !== nextName;

    if (!changed) return;

    const pageAlreadyMatches =
      String(pageAdvertiserId ?? "") === String(nextId ?? "") &&
      String(pageAdvertiserName || "").trim() === nextName;
    if (pageAlreadyMatches) {
      lastCtxAdvRef.current = { id: nextId, name: nextName };
      return;
    }

    // Preserve an in-progress local draft until the user resolves it. Keeping
    // the previous context ref lets this effect apply the pending scope once
    // the draft is no longer dirty.
    if (isDirty && !campaignIdFromNav) return;

    clearPlannerAdvertiserScopedState(
      nextId || nextName ? { id: nextId, name: nextName } : null,
      { reason: "context_changed" }
    );
  }, [
    campaignIdFromNav,
    clearPlannerAdvertiserScopedState,
    ctxSelectedAdvertiserId,
    ctxSelectedAdvertiserName,
    isDirty,
    legacySelectedAdvertiserName,
    pageAdvertiserId,
    pageAdvertiserName,
  ]);

  const advKey = useMemo(
    () => getAdvertiserKey(pageAdvertiserId, pageAdvertiserName),
    [pageAdvertiserId, pageAdvertiserName]
  );

  // The verified account plan choices are remembered for: the user the
  // server's auth check returned for this session (none until it answers).
  const plannerAccount = plannerAccountKey(plannerSession.accountId);
  const plannerAccountRef = useRef(plannerAccount);
  plannerAccountRef.current = plannerAccount;
  // Start New Strategy's plan becomes the operator's choice once it has an
  // identity (its first meaningful edit): the advertiser it was started for.
  const explicitNewPlanRef = useRef("");

  // The plan the operator chose for an advertiser, on this account. Only an
  // explicit choice is stored; a plan that opened by itself is not one.
  const rememberPlannerChoice = useCallback((advertiserKey, choice) => {
    const key = makeChosenPlanKey(plannerAccountRef.current, advertiserKey);
    const value = serializePlannerChoice(choice);
    if (!key || !value) return;
    try {
      localStorage.setItem(key, value);
    } catch {}
  }, [localStorage]);

  const readPlannerChoice = useCallback((advertiserKey) => {
    const key = makeChosenPlanKey(plannerAccountRef.current, advertiserKey);
    if (!key) return "";
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  }, [localStorage]);

  const selectedPlatforms = useMemo(
    () => orderPlannerPlatformKeys(Array.isArray(formData.platforms) ? formData.platforms : []),
    [formData.platforms]
  );

  // ---------------------------
  // Global index helpers
  // ---------------------------

  const upsertGlobalIndex = useCallback((id, fd) => {
    const now = new Date().toISOString();
    const title = getPlannerTitle(fd);
    const meta = getDraftMeta(fd);

    setPlannerIndex((prev) => {
      const copy = Array.isArray(prev) ? [...prev] : [];
      const idx = copy.findIndex((p) => p.id === id);

      const nextItem = {
        id,
        title,
        updatedAt: now,
        advertiserKey: meta.advertiserKey,
        advertiserId: meta.advertiserId,
        advertiserName: meta.advertiserName,
      };

      if (idx >= 0) copy[idx] = { ...copy[idx], ...nextItem };
      else copy.unshift(nextItem);

      writeGlobalIndex(copy);
      return copy;
    });
  }, [writeGlobalIndex]);

  const loadPlannerById = useCallback((id, { explicit = false } = {}) => {
    if (!id) return;
    if (!explicit && isDirty) {
      setSaveStatus("pending");
      setSaveMessage("Unsaved changes kept");
      return;
    }

    const raw = localStorage.getItem(makePlannerStorageKey(id));
    const parsed = safeJsonParse(raw, null);
    if (!parsed?.formData) return;

    plannerDebug("loadPlannerById.rawFormData", {
      plannerId: id,
      formData: parsed.formData,
    });

    const incoming = normalizePlannerFormData(parsed.formData || {});
    plannerDebug("loadPlannerById.normalizedFormData", {
      plannerId: id,
      flightStart: incoming.flightStart,
      flightEnd: incoming.flightEnd,
    });
    assertDateInputValue("loadPlannerById.flightStart", incoming.flightStart);
    assertDateInputValue("loadPlannerById.flightEnd", incoming.flightEnd);

    setFormData(incoming);

    const nextCreativeRecommendations = Array.isArray(parsed.creativeRecommendations)
      ? parsed.creativeRecommendations
      : Array.isArray(parsed.creativeVariations)
      ? parsed.creativeVariations
      : [];
    const nextCreativeGenerationMeta =
      parsed.creativeGenerationMeta &&
      typeof parsed.creativeGenerationMeta === "object" &&
      !Array.isArray(parsed.creativeGenerationMeta)
        ? parsed.creativeGenerationMeta
        : null;
    setStrategy(parsed.strategy || "");
    setCreativeRecommendations(nextCreativeRecommendations);
    setCreativeGenerationMeta(nextCreativeGenerationMeta);
    setRevisionPrompt("");
    setCanonicalAllocation(null);
    setStrategyPresentation(null);
    setAllocationAuthority(null);
    setAllocationReconnectOperation(null);
    setAppliedRevisionSummary(null);
    planVersionRef.current = "";
    strategyPlanVersionRef.current = "";
    setStrategyProvenance(null);
    setCreativeError("");
    setError("");
    setIsDirty(false);
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    setServerDraftId(parsed.serverDraftId || parsed.server_draft_id || null);

    seededDraftRef.current = { draftId: id };
  }, [isDirty, localStorage, plannerDebug, normalizePlannerFormData, assertDateInputValue]);

  // An intentional new plan (Copy to New Planner): its own identity now, its
  // source plan untouched.
  const createPlanner = useCallback(({ copyFromCurrent = false, seedAdvertiser = null } = {}) => {
    const id = newPlannerId();
    pendingPlannerIdRef.current = "";

    const seedId = (seedAdvertiser?.id) ?? pageAdvertiserId ?? null;
    const seedName = String((seedAdvertiser?.name) ?? pageAdvertiserName ?? "").trim();

    const nextForm = copyFromCurrent
      ? {
          ...formData,
          advertiserId: formData.advertiserId ?? seedId,
          advertiser: String(formData.advertiser || "").trim() || seedName,
          platforms: withoutClientUnselectablePlatforms(formData.platforms),
        }
      : {
          ...BASE_BLANK_FORM,
          advertiserId: seedId,
          advertiser: seedName,
        };
    const normalizedNextForm = normalizePlannerFormData(nextForm);

    const nextStrategy = copyFromCurrent ? strategy : "";

    try {
      localStorage.setItem(
        makePlannerStorageKey(id),
        JSON.stringify({
          formData: normalizedNextForm,
          strategy: nextStrategy,
          creativeRecommendations: copyFromCurrent ? creativeRecommendations : [],
          creativeVariations: copyFromCurrent ? creativeRecommendations : [],
          creativeGenerationMeta: copyFromCurrent ? creativeGenerationMeta : null,
          serverDraftId: null,
        })
      );
    } catch {}

    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    setServerDraftId(null);
    setIsDirty(false);
    setError("");

    setNavCampaignId(null);
    setActivePlannerId(id);
    setFormData(normalizedNextForm);
    setStrategy(nextStrategy);
    setCreativeRecommendations(copyFromCurrent ? creativeRecommendations : []);
    setCreativeGenerationMeta(copyFromCurrent ? creativeGenerationMeta : null);
    setRevisionPrompt("");
    setLastAppliedRevisionPrompt("");
    setCanonicalAllocation(null);
    setStrategyPresentation(null);
    setAllocationAuthority(null);
    setAllocationReconnectOperation(null);
    setAppliedRevisionSummary(null);
    planVersionRef.current = "";
    strategyPlanVersionRef.current = "";
    setStrategyProvenance(null);
    setCreativeError("");

    // per-advertiser active pointer; the new plan is the operator's choice
    const k = getAdvertiserKey(normalizedNextForm.advertiserId, normalizedNextForm.advertiser);
    explicitNewPlanRef.current = "";
    if (k && k !== "unknown") {
      try {
        localStorage.setItem(makeActiveKeyForAdvertiser(k), id);
      } catch {}
      rememberPlannerChoice(k, { localPlannerId: id });
    }

    upsertGlobalIndex(id, normalizedNextForm);
    seededDraftRef.current = { draftId: id };
    return id;
  }, [pageAdvertiserId, pageAdvertiserName, formData, BASE_BLANK_FORM, normalizePlannerFormData, strategy, creativeRecommendations, creativeGenerationMeta, upsertGlobalIndex, localStorage, rememberPlannerChoice]);

  // A new, unsaved plan: a blank form for the selected advertiser and no
  // identity yet. It gets one at its first meaningful edit
  // (ensurePlannerIdentity), so opening the planner never creates a draft.
  const startBlankPlan = useCallback(() => {
    pendingPlannerIdRef.current = "";
    explicitNewPlanRef.current = "";
    openedPlanContentRef.current = null;
    setNavCampaignId(null);
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    setActivePlannerId("");
    setServerDraftId(null);
    setFormData(
      normalizePlannerFormData({
        ...BASE_BLANK_FORM,
        advertiserId: pageAdvertiserId ?? null,
        advertiser: String(pageAdvertiserName || "").trim(),
      })
    );
    setStrategy("");
    setCreativeRecommendations([]);
    setCreativeGenerationMeta(null);
    setRevisionPrompt("");
    setLastAppliedRevisionPrompt("");
    setCanonicalAllocation(null);
    setStrategyPresentation(null);
    setAllocationAuthority(null);
    setAllocationReconnectOperation(null);
    setAppliedRevisionSummary(null);
    planVersionRef.current = "";
    strategyPlanVersionRef.current = "";
    setStrategyProvenance(null);
    setCreativeError("");
    setIsDirty(false);
    lastDraftSaveFingerprintRef.current = "";
    setSaveStatus("idle");
    setSaveMessage("");
    setSessionMode("new");
    seededDraftRef.current = { draftId: null };
  }, [BASE_BLANK_FORM, normalizePlannerFormData, pageAdvertiserId, pageAdvertiserName]);

  const deleteActiveDraft = () => {
    if (campaignIdFromNav) {
      setError(
        "You’re viewing a saved Campaign. Select Drafts to manage drafts, or use “Copy to New Planner”."
      );
      return;
    }
    if (!activePlannerId) return;
    // Only a plan that exists solely on this device can be deleted here; a
    // saved plan stays in Cphere (the button is not offered for it), and a
    // plan whose first save is running is about to be one.
    if (serverDraftId || readPlannerBinding(activePlannerId)?.serverDraftId) return;
    if (saveStatus === "saving") return;

    const active = filteredPlannerIndex.find((p) => p.id === activePlannerId);
    const label = active?.title ? `"${active.title}"` : "this draft";
    const ok = window.confirm(`Delete ${label}? This cannot be undone.`);
    if (!ok) return;

    const idToDelete = activePlannerId;
    deletedPlannerIdsRef.current.add(idToDelete);

    try {
      localStorage.removeItem(makePlannerStorageKey(idToDelete));
    } catch {}

    const nextIndex = (plannerIndex || []).filter((p) => p.id !== idToDelete);
    writeGlobalIndex(nextIndex);
    setPlannerIndex(nextIndex);

    // Reopen by the same rules as entering the planner (never a new draft).
    startBlankPlan();
    resumePlannerDraftsRef.current?.({ source: "load_draft" });
  };

  // ---------------------------
  // Campaign list fetch (multi-campaign support)
  // ---------------------------

  const fetchCampaignsForAdvertiser = useCallback(async ({ advertiserId, advertiserName, reqId }) => {
    try {
      if (!API_BASE_URL) return [];

      const authToken = getSherlocMediaPlannerAuthToken();
      if (!authToken) return [];

      const email = getEmail();
      const base = safeBase(API_BASE_URL);

      const advId =
        advertiserId != null && String(advertiserId).trim() ? String(advertiserId).trim() : "";
      const advName = String(advertiserName || "").trim();
      if (!advId && !advName) return [];

      const paramAttempts = [];
      if (advId) {
        paramAttempts.push({ advertiserId: advId });
        paramAttempts.push({ advertiser_id: advId });
      }
      if (advName) {
        paramAttempts.push({ advertiser: advName });
        paramAttempts.push({ advertiser_name: advName });
        paramAttempts.push({ advertiserName: advName });
      }

      const collected = [];

      for (const path of CAMPAIGNS_LIST_PATH_CANDIDATES) {
        for (const params of paramAttempts) {
          try {
            if (reqId !== campaignListReqRef.current) return [];

            const urlObj = new URL(`${base}${path}`);
            if (email) urlObj.searchParams.set("email", email);

            Object.entries(params).forEach(([k, v]) => urlObj.searchParams.set(k, String(v)));

            urlObj.searchParams.set("limit", "200");
            urlObj.searchParams.set("pageSize", "200");
            urlObj.searchParams.set("ts", Date.now().toString());
            urlObj.searchParams.set("sort", "updatedAt:desc");
            urlObj.searchParams.set("order", "desc");

            const url = urlObj.toString();

            const res = await fetch(url, {
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              cache: "no-store",
            });

            if (!res.ok) continue;

            const bodyText = await res.text().catch(() => "");
            let data = null;

            try {
              data = bodyText ? JSON.parse(bodyText) : null;
              if (typeof data === "string") data = JSON.parse(data);
            } catch {
              data = bodyText;
            }

            const arr = extractCampaignArray(data);
            if (!Array.isArray(arr) || arr.length === 0) continue;

            for (const c of arr) {
              const id =
                (c?.id) ?? (c?._id) ?? (c?.campaign_id) ?? (c?.campaignId) ?? null;
              if (!id) continue;
              collected.push({ ...c, id: String(id) });
            }

            if (collected.length > 0) break;
          } catch {
            // try next
          }
        }
        if (collected.length > 0) break;
      }

      const uniq = new Map();
      for (const c of collected) uniq.set(String(c.id), c);

      const list = Array.from(uniq.values()).sort((a, b) => {
        const ad = parseDateLike(campaignUpdatedAt(a));
        const bd = parseDateLike(campaignUpdatedAt(b));
        const at = ad ? ad.getTime() : 0;
        const bt = bd ? bd.getTime() : 0;
        return bt - at;
      });

      return list;
    } catch {
      return [];
    }
  }, [fetch, getSherlocMediaPlannerAuthToken]);

  const refreshCampaignsForCurrentAdvertiser = useCallback(
    async ({ allowAutoSelectLatestIfNoDrafts = false, reqId } = {}) => {
      const currentReqId = reqId ?? ++campaignListReqRef.current;

      setCampaignsLoadError("");
      setIsLoadingCampaigns(true);

      try {
        if (!advKey || advKey === "unknown") {
          setCampaigns([]);
          return [];
        }

        const list = await fetchCampaignsForAdvertiser({
          advertiserId: pageAdvertiserId,
          advertiserName: pageAdvertiserName,
          reqId: currentReqId,
        });

        if (currentReqId !== campaignListReqRef.current) return [];

        setCampaigns(Array.isArray(list) ? list : []);

        if (allowAutoSelectLatestIfNoDrafts && Array.isArray(list) && list.length > 0) {
          if (!campaignIdFromNav) {
            setNavCampaignId(String(list[0].id));
          }
        }

        return list;
      } catch (e) {
        if (currentReqId === campaignListReqRef.current) {
          setCampaignsLoadError(String(e?.message || e));
          setCampaigns([]);
        }
        return [];
      } finally {
        if (currentReqId === campaignListReqRef.current) setIsLoadingCampaigns(false);
      }
    },
    [advKey, pageAdvertiserId, pageAdvertiserName, campaignIdFromNav, fetchCampaignsForAdvertiser]
  );

  // ---------------------------
  // Durable Sherloc generation operations (server-side; no client timeout)
  // ---------------------------

  const stopGenerationOperationPolling = () => {
    if (generationPollTimerRef.current) {
      clearTimeout(generationPollTimerRef.current);
      generationPollTimerRef.current = null;
    }
  };

  // Adopt a serialized operation snapshot (stamping the receipt time so the
  // elapsed label can keep counting between polls).
  const adoptGenerationOperation = (operation) => {
    const stamped = operation ? { ...operation, __receivedAtMs: Date.now() } : null;
    generationOperationRef.current = stamped;
    if (stamped) {
      generationOperationScopeRef.current = advertiserScopeGenerationRef.current;
    }
    setGenerationOperation(stamped);
    return stamped;
  };

  const clearGenerationOperation = () => {
    stopGenerationOperationPolling();
    generationOperationRef.current = null;
    generationContextRef.current = null;
    generationRetryGraceRef.current = null;
    setGenerationOperation(null);
    setGenerationRetryPending(false);
  };

  // Shared success application: the operation result is the legacy sync
  // success envelope, so COMPLETED operations flow through the exact same
  // state updates the synchronous path used (never a second generation).
  const applyGenerationOperationSuccess = (data, context = {}) => {
    const kind = context.kind === "revision" ? "revision" : "generate";
    const nextStrategy = getUsableSherlocStrategyText(data || {});
    if (!nextStrategy) {
      // Canonical current-plan resolver: an unusable operation result must
      // never mark a COMPLETE current plan as incomplete. The committed
      // current state (loaded campaign snapshot, on-screen strategy, current
      // allocation authority — or the result's own campaign truth) takes
      // precedence; when it resolves complete, this result is audit history —
      // clear any stale completeness error and let the current plan stand.
      const resolvesComplete = (campaign) =>
        resolveSherlocCurrentPlanState({
          campaign,
          strategyText: strategyTextRef.current,
          allocationAuthority: currentPlanAuthorityRef.current,
        }).complete;
      const currentPlanComplete =
        resolvesComplete(loadedCampaignSnapshotRef.current) ||
        (data?.campaign ? resolvesComplete(data.campaign) : false);
      if (currentPlanComplete) {
        setError((previous) =>
          isSherlocCurrentPlanCompletenessErrorMessage(previous) ? "" : previous
        );
        return true;
      }
      // Genuinely incomplete current plan: the error stays accurate.
      // (Don't overwrite an existing strategy with a placeholder.)
      setError(STRATEGY_INCOMPLETE_OUTPUT_MESSAGE);
      return false;
    }
    // Always render the generated plan, even if the backend could not persist it.
    setStrategy(nextStrategy);
    setError(data?.saveFailed ? STRATEGY_SAVE_WARNING_MESSAGE : "");
    if (data?.campaign && typeof data.campaign === "object") {
      // Keep the current-plan resolver reading the freshest server truth.
      loadedCampaignSnapshotRef.current = data.campaign;
    }

    if (data.campaign?.id) {
      setActiveCampaignId(data.campaign.id);
      window.localStorage.setItem("sherloc_last_created_campaign_id", String(data.campaign.id));
    }

    if (kind === "revision") {
      // Canonical rehydration: when the backend applied a structural revision
      // (media_plan_revision_v1), the persisted campaign is the source of
      // truth — revised budgets, platforms, and dates must replace the stale
      // on-screen values instead of being "preserved" over them.
      const revisionApplied = data?.revisionApplied || null;
      const structuralRevisionApplied = Boolean(
        revisionApplied && revisionApplied.structuralChanges !== false
      );
      const baseFormData =
        context.normalizedFormData || normalizePlannerFormData(formData);
      const mergedFormData = mergePlannerStatePreservingUserFields(
        baseFormData,
        data?.campaign || {},
        structuralRevisionApplied
          ? {
              preserveCurrentNonEmpty: true,
              preferIncomingNonEmpty: true,
              allowExplicitClears: true,
            }
          : { preserveCurrentNonEmpty: true }
      );
      setFormData(mergedFormData);
      setAppliedRevisionSummary(revisionApplied);
      setCanonicalAllocation(
        Array.isArray(data?.campaign?.strategy_payload?.requestedChannelAllocation)
          ? data.campaign.strategy_payload.requestedChannelAllocation
          : null
      );
      setStrategyPresentation(
        getSherlocStrategyPresentationFromCampaign(data?.campaign)
      );
      setAllocationAuthority(
        getSherlocAllocationAuthorityFromCampaign(data?.campaign)
      );
      planVersionRef.current =
        data?.planVersion || data?.campaign?.updated_at || planVersionRef.current || "";
      strategyPlanVersionRef.current =
        data?.planVersion || strategyPlanVersionRef.current || "";
      setStrategyProvenance(
        data?.campaign?.strategy_provenance || data?.campaign?.strategyProvenance || null
      );
      if (context.revisionInstruction) {
        setLastAppliedRevisionPrompt(context.revisionInstruction);
      }
      setRevisionPrompt("");
      setIsDirty(false);
      const listReqId = ++campaignListReqRef.current;
      refreshCampaignsForCurrentAdvertiser({ reqId: listReqId });
      return true;
    }

    setRevisionPrompt("");
    setLastAppliedRevisionPrompt("");
    // Fresh generation supersedes any prior revision summary; canonical
    // allocation and plan version re-hydrate from the persisted campaign.
    setAppliedRevisionSummary(null);
    setCanonicalAllocation(
      Array.isArray(data?.campaign?.strategy_payload?.requestedChannelAllocation)
        ? data.campaign.strategy_payload.requestedChannelAllocation
        : null
    );
    setStrategyPresentation(
      getSherlocStrategyPresentationFromCampaign(data?.campaign)
    );
    setAllocationAuthority(
      getSherlocAllocationAuthorityFromCampaign(data?.campaign)
    );
    planVersionRef.current = data?.planVersion || data?.campaign?.updated_at || "";
    strategyPlanVersionRef.current = data?.planVersion || "";
    setStrategyProvenance(
      data?.campaign?.strategy_provenance || data?.campaign?.strategyProvenance || null
    );
    if (!data?.saveFailed) {
      setIsDirty(false);
      // Refresh campaign list labels
      const listReqId = ++campaignListReqRef.current;
      refreshCampaignsForCurrentAdvertiser({ reqId: listReqId });
    }
    return true;
  };

  // Terminal statuses stay on screen as labeled cards; COMPLETED applies the
  // result through the shared success path and clears the tracker.
  const handleGenerationOperationTerminal = (operation) => {
    stopGenerationOperationPolling();
    setGenerationLoadingType("");
    setIsLoading(false);
    setGenerationRetryPending(false);
    const outcome = resolveSherlocGenerationPollOutcome(operation);
    if (outcome.kind === "apply") {
      const applied = applyGenerationOperationSuccess(
        outcome.result,
        generationContextRef.current || {}
      );
      if (applied) {
        clearGenerationOperation();
        return;
      }
      // Result unusable: keep the labeled COMPLETED card + incomplete-output
      // error so the panel is never unlabeled-empty.
      return;
    }
    if (outcome.kind === "completed_without_result") {
      // COMPLETED but the result payload is missing: the persisted campaign
      // governs — surface a labeled state instead of silently doing nothing.
      setError(buildGenerationRefusalMessage(
        "the generation completed but its result payload was unavailable. Reload the campaign to see the saved plan."
      ));
      return;
    }
    // FAILED_RETRYABLE / FAILED_TERMINAL / CANCELLED / SUPERSEDED: the card
    // labels the state; the banner carries the mapped copy so the failure is
    // visible even when an older strategy is still on screen.
    if (
      outcome.status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE ||
      outcome.status === SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_TERMINAL
    ) {
      setError(mapSherlocOperationFailureToMessage(operation));
    }
  };

  const scheduleGenerationOperationPoll = () => {
    stopGenerationOperationPolling();
    generationPollTimerRef.current = setTimeout(() => {
      generationPollTimerRef.current = null;
      // Always poll with the latest render's machinery (never a stale closure).
      durableGenerationMachineryRef.current?.pollNow();
    }, SHERLOC_GENERATION_POLL_INTERVAL_MS);
  };

  const pollGenerationOperationNow = async () => {
    const tracked = generationOperationRef.current;
    if (!tracked?.id) return;
    // Pause while hidden — the visibility/focus listener resumes with an
    // immediate poll. Generation continues on the server regardless.
    if (shouldPauseSherlocGenerationPolling()) return;
    if (generationPollInFlightRef.current) return;
    generationPollInFlightRef.current = true;
    const scopeAtPoll = generationOperationScopeRef.current;
    try {
      const payload = await getSherlocGenerationOperation({
        apiBase: safeBase(API_BASE_URL),
        token: getSherlocMediaPlannerAuthToken(),
        operationId: tracked.id,
      });
      if (scopeAtPoll !== advertiserScopeGenerationRef.current) return;
      if (generationOperationRef.current?.id !== tracked.id) return;
      const next = payload?.operation;
      if (!next) {
        scheduleGenerationOperationPoll();
        return;
      }
      // Retry pre-claim window: an unchanged FAILED_RETRYABLE snapshot right
      // after a 202 retry accept means the executor hasn't claimed yet.
      const grace = generationRetryGraceRef.current;
      if (
        grace &&
        getSherlocGenerationOperationStatus(next) ===
          SHERLOC_GENERATION_OPERATION_STATUSES.FAILED_RETRYABLE &&
        Number(next.attemptCount ?? 0) <= Number(grace.attemptCount ?? 0) &&
        Date.now() < grace.untilMs
      ) {
        scheduleGenerationOperationPoll();
        return;
      }
      generationRetryGraceRef.current = null;
      adoptGenerationOperation(next);
      if (isSherlocGenerationOperationActive(next)) {
        scheduleGenerationOperationPoll();
      } else {
        handleGenerationOperationTerminal(generationOperationRef.current);
      }
    } catch {
      // Transient poll failure (network blip/deploy): the operation still
      // runs server-side, so keep polling instead of failing the UI.
      if (
        scopeAtPoll === advertiserScopeGenerationRef.current &&
        generationOperationRef.current?.id === tracked.id
      ) {
        scheduleGenerationOperationPoll();
      }
    } finally {
      generationPollInFlightRef.current = false;
    }
  };

  const beginTrackingGenerationOperation = (operation, context) => {
    generationContextRef.current = context || null;
    generationRetryGraceRef.current = null;
    setGenerationRetryPending(false);
    const stamped = adoptGenerationOperation(operation);
    if (isSherlocGenerationOperationActive(stamped)) {
      scheduleGenerationOperationPoll();
    } else {
      handleGenerationOperationTerminal(stamped);
    }
  };

  // Retry resumes the SAME server-side operation (POST /:id/retry, 202).
  // Availability is governed by the operation fields (status, retryable,
  // attempt budget, governed backoff) — never inferred from copy.
  const handleRetryGenerationOperation = async () => {
    const tracked = generationOperationRef.current;
    if (!isSherlocOperationRetryAvailable(tracked || {})) return;
    if (generationRetryPending) return;
    setGenerationRetryPending(true);
    setError("");
    try {
      const payload = await retrySherlocGenerationOperation({
        apiBase: safeBase(API_BASE_URL),
        token: getSherlocMediaPlannerAuthToken(),
        operationId: tracked.id,
        requestId: makePlannerRequestId(),
      });
      const accepted = payload?.operation || tracked;
      generationRetryGraceRef.current = {
        attemptCount: Number(accepted.attemptCount ?? tracked.attemptCount ?? 0),
        untilMs: Date.now() + 30000,
      };
      // Optimistic Queued label until the executor claims the retry.
      adoptGenerationOperation({
        ...accepted,
        status: SHERLOC_GENERATION_OPERATION_STATUSES.QUEUED,
      });
      scheduleGenerationOperationPoll();
    } catch (err) {
      const refusalCode = String(err?.code || "").trim();
      const errOperation = err?.operation || null;
      if (refusalCode === "operation_retry_backoff") {
        // 429: a governed backoff window refuses the retry. Prefer the
        // authoritative operation snapshot from the response body; otherwise
        // stamp the returned delay onto the tracked snapshot so the card
        // shows the wait state instead of the button.
        const waitSeconds =
          getSherlocOperationRetryBackoffSeconds(errOperation || {}) ||
          getSherlocOperationRetryBackoffSeconds({
            retryAfterSeconds: err?.payload?.retryAfterSeconds,
          });
        adoptGenerationOperation(
          errOperation || { ...tracked, retryAfterSeconds: waitSeconds || 1 }
        );
        setError(
          waitSeconds > 0
            ? `A governed retry delay is active. Retry available in ~${waitSeconds}s.`
            : "A governed retry delay is active — try again shortly."
        );
      } else if (
        refusalCode === "operation_stale_dependencies" ||
        refusalCode === "generation_inputs_changed_restart_required"
      ) {
        // 409: campaign inputs changed since the failed attempt — this
        // operation may not resume; a NEW generation must be started.
        adoptGenerationOperation(
          errOperation || { ...tracked, retryable: false }
        );
        setError(SHERLOC_OPERATION_STALE_DEPENDENCIES_MESSAGE);
      } else if (refusalCode === "operation_retry_budget_exhausted") {
        // 409: attempts exhausted — the server escalated the operation to
        // terminal. Adopt its snapshot and surface the terminal copy.
        const adopted = adoptGenerationOperation(
          errOperation || { ...tracked, retryable: false }
        );
        setError(resolveSherlocOperationFailureCategoryCopy(adopted || {}));
      } else if (errOperation) {
        // 409 operation_failed_terminal / operation_not_retryable: adopt the
        // authoritative snapshot so the labeled state matches the server.
        adoptGenerationOperation(errOperation);
        setError(buildGenerationRefusalMessage(err.message));
      } else if (isPlannerNetworkError(err)) {
        setError(STRATEGY_NETWORK_FAILURE_MESSAGE);
      } else {
        setError(buildGenerationRefusalMessage(err?.message));
      }
    } finally {
      setGenerationRetryPending(false);
    }
  };

  // Timers and window listeners must always call the LATEST render's
  // machinery (never a stale closure over old form state), so the current
  // functions are re-published on a ref every render.
  const durableGenerationMachineryRef = useRef(null);
  durableGenerationMachineryRef.current = {
    pollNow: pollGenerationOperationNow,
    beginTracking: beginTrackingGenerationOperation,
    applySuccess: applyGenerationOperationSuccess,
  };
  // Latest-render allocation authority for the current-plan resolver (async
  // operation callbacks must never read a stale closure).
  currentPlanAuthorityRef.current = allocationAuthority;

  useEffect(() => {
    strategyTextRef.current = strategy;
  }, [strategy]);

  // Pause on hidden, resume with an immediate poll on focus/visibility.
  useEffect(() => {
    const wakeGenerationPolling = () => {
      if (shouldPauseSherlocGenerationPolling()) return;
      if (isSherlocGenerationOperationActive(generationOperationRef.current)) {
        durableGenerationMachineryRef.current?.pollNow();
      }
    };
    document.addEventListener("visibilitychange", wakeGenerationPolling);
    window.addEventListener("focus", wakeGenerationPolling);
    return () => {
      document.removeEventListener("visibilitychange", wakeGenerationPolling);
      window.removeEventListener("focus", wakeGenerationPolling);
      if (generationPollTimerRef.current) {
        clearTimeout(generationPollTimerRef.current);
        generationPollTimerRef.current = null;
      }
    };
  }, []);

  // 1s ticker for elapsed/last-update labels while a generation is active.
  const generationOperationIsActive = isSherlocGenerationOperationActive(generationOperation);
  useEffect(() => {
    if (!generationOperationIsActive) return undefined;
    setGenerationNowMs(Date.now());
    const ticker = setInterval(() => setGenerationNowMs(Date.now()), 1000);
    return () => clearInterval(ticker);
  }, [generationOperationIsActive]);

  // Reload rehydration: with a campaign selected, reconnect to the in-flight
  // operation (resume polling) or apply a newer COMPLETED result when the
  // panel would otherwise be stale or blank.
  useEffect(() => {
    const campaignIdText = String(activeCampaignId || campaignIdFromNav || "").trim();
    if (!campaignIdText || !API_BASE_URL) return undefined;
    if (generationOperationRef.current) return undefined;
    const token = getSherlocMediaPlannerAuthToken();
    if (!token) return undefined;
    const scopeAtStart = advertiserScopeGenerationRef.current;
    let disposed = false;
    (async () => {
      try {
        const activePayload = await getSherlocGenerationOperationForCampaign({
          apiBase: safeBase(API_BASE_URL),
          token,
          campaignId: campaignIdText,
          activeOnly: true,
        });
        if (disposed || scopeAtStart !== advertiserScopeGenerationRef.current) return;
        if (generationOperationRef.current) return;
        const activeOperation = activePayload?.operation;
        if (isSherlocGenerationOperationActive(activeOperation)) {
          // Allocation operations belong to the allocation surface — never
          // to the strategy-generation machinery (an allocation result must
          // not be applied as strategy text).
          if (
            String(activeOperation.operationType || "") ===
            SHERLOC_ALLOCATION_OPERATION_TYPE
          ) {
            setAllocationReconnectOperation(activeOperation);
            return;
          }
          durableGenerationMachineryRef.current?.beginTracking(activeOperation, {
            kind:
              String(activeOperation.operationType || "") === "refresh_revision"
                ? "revision"
                : "generate",
          });
          return;
        }
        // No in-flight operation: look for a newer COMPLETED result if the
        // panel is blank or older than the operation's persisted plan version.
        const latestPayload = await getSherlocGenerationOperationForCampaign({
          apiBase: safeBase(API_BASE_URL),
          token,
          campaignId: campaignIdText,
          activeOnly: false,
        });
        if (disposed || scopeAtStart !== advertiserScopeGenerationRef.current) return;
        if (generationOperationRef.current) return;
        const latestOperation = latestPayload?.operation;
        // A completed allocation operation is not a strategy result — the
        // campaign hydration path already carries its allocationAuthority.
        if (
          String(latestOperation?.operationType || "") ===
          SHERLOC_ALLOCATION_OPERATION_TYPE
        ) {
          return;
        }
        const outcome = resolveSherlocGenerationPollOutcome(latestOperation);
        if (outcome.kind !== "apply") return;
        const panelBlank = !String(strategyTextRef.current || "").trim();
        const resultPlanVersion = String(
          outcome.result?.planVersion ||
            latestOperation?.strategyPlanVersion ||
            ""
        );
        const knownPlanVersion = String(
          strategyPlanVersionRef.current || planVersionRef.current || ""
        );
        const resultMs = Date.parse(resultPlanVersion);
        const knownMs = Date.parse(knownPlanVersion);
        const panelStale =
          Boolean(resultPlanVersion) &&
          resultPlanVersion !== knownPlanVersion &&
          Number.isFinite(resultMs) &&
          (!Number.isFinite(knownMs) || resultMs > knownMs);
        if (panelBlank || panelStale) {
          // Historical COMPLETED results WITHOUT a usable strategy payload are
          // audit history only: the campaign hydration path already renders
          // the persisted current plan, and a reconnect probe must never
          // surface a stale "incomplete media plan" error over it (canonical
          // current-plan resolver precedence: committed plan > history).
          if (!getUsableSherlocStrategyText(outcome.result || {})) return;
          durableGenerationMachineryRef.current?.applySuccess(outcome.result, {
            kind: "generate",
          });
        }
      } catch {
        // Rehydration is best-effort: the campaign hydration path already
        // rendered the persisted plan; never surface a reconnect probe error.
      }
    })();
    return () => {
      disposed = true;
    };
  }, [activeCampaignId, campaignIdFromNav, getSherlocMediaPlannerAuthToken]);

  // Ownerless storage from earlier builds stays byte-for-byte untouched.
  // It is never adopted into this verified account's browser partition.

  // ---------------------------
  // Filtered drafts (by selected advertiser)
  // ---------------------------

  const filteredPlannerIndex = useMemo(() => {
    const list = Array.isArray(plannerIndex) ? plannerIndex : [];
    if (!advKey || advKey === "unknown") return [];
    return list.filter((p) => p?.advertiserKey === advKey);
  }, [plannerIndex, advKey]);

  // The Planner Draft choices: each logical plan once (utils/
  // plannerDraftIdentity), labelled with its own saved time. The plan open
  // now is always among them, even before its first save.
  const activeDraftOptionKey = plannerDraftOptionKey({
    activePlannerId,
    serverDraftId,
    readBinding: readPlannerBinding,
  });
  const plannerDraftOptions = useMemo(() => {
    if (!advKey || advKey === "unknown") return [];
    const listReady =
      serverDraftList.status === "ready" && serverDraftList.advertiserKey === advKey;
    const options = buildPlannerDraftOptions({
      serverDrafts: listReady ? serverDraftList.drafts : [],
      plannerIndex,
      readBinding: readPlannerBinding,
      advertiserKey: advKey,
      hasLocalContent: plannerLocalCopyHasContent,
    });
    if (activeDraftOptionKey && !options.some((option) => option.key === activeDraftOptionKey)) {
      const title = String(formData.campaignName || "").trim() || "Untitled plan";
      options.unshift(
        activeDraftOptionKey.startsWith("server:")
          ? {
              key: activeDraftOptionKey,
              kind: "server",
              serverDraftId: activeDraftOptionKey.slice("server:".length),
              draft: null,
              title,
              savedAt: null,
            }
          : { key: activeDraftOptionKey, kind: "local", localPlannerId: activePlannerId, title, savedAt: null }
      );
    }
    return options;
  }, [activeDraftOptionKey, activePlannerId, advKey, formData.campaignName, plannerIndex, plannerLocalCopyHasContent, readPlannerBinding, serverDraftList.advertiserKey, serverDraftList.drafts, serverDraftList.status]);
  const activeDraftOption =
    plannerDraftOptions.find((option) => option.key === activeDraftOptionKey) || null;

  // ---------------------------
  // Bootstrap behavior
  // ---------------------------

  const bootstrapForAdvertiser = useCallback(async ({ loadDraft = false } = {}) => {
    const reqId = ++bootstrapReqRef.current;
    const manualOverrideSeqAtStart = manualPlannerOverrideSeqRef.current;
    const advertiserScopeGenerationAtStart =
      advertiserScopeGenerationRef.current;
    const shouldHonorCampaignRoute = Boolean(campaignIdFromNav && !loadDraft);

    if (!advKey || advKey === "unknown") {
      setPlannerIndex(readGlobalIndex());
      setActivePlannerId("");
      setSessionMode("new");
      setFormData(BASE_BLANK_FORM);
      setStrategy("");
      setStrategyPresentation(null);
      setAllocationAuthority(null);
      setAllocationReconnectOperation(null);
      setActiveCampaignId(null);
      loadedCampaignSnapshotRef.current = null;
      setIsLoadingCampaign(false);
      setIsDirty(false);
      setError("Select an advertiser to use Media Planner.");
      setCampaigns([]);
      return;
    }

    if (isDirty && !campaignIdFromNav) {
      const ok = window.confirm(
        "You have unsaved changes in Media Planner. Switching advertisers will load a different advertiser’s drafts/campaign. Continue?"
      );
      if (!ok) return;
    }

    setError("");
    setIsDirty(false);
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    if (!shouldHonorCampaignRoute) {
      setStrategy("");
      setStrategyPresentation(null);
      setAllocationAuthority(null);
      setAllocationReconnectOperation(null);
    }
    setIsLoadingCampaign(Boolean(shouldHonorCampaignRoute));

    const existing = readGlobalIndex();
    setPlannerIndex(existing);


    const after = readGlobalIndex();
    if (
      reqId !== bootstrapReqRef.current ||
      manualOverrideSeqAtStart !== manualPlannerOverrideSeqRef.current ||
      advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
    ) {
      return;
    }

    setPlannerIndex(after);

    // Entering the planner (and View Drafts) resumes the advertiser's plan
    // from its saved drafts; it never creates one. The lookup starts now,
    // alongside the campaign list, so the form is closed ("Loading saved
    // plans…") from the start and nothing typed meanwhile belongs to no plan.
    if (!shouldHonorCampaignRoute) {
      if (loadDraft) {
        setNavCampaignId(null);
        clearPlannerRouteState();
      }
      resumePlannerDraftsRef.current?.({ source: loadDraft ? "load_draft" : "entry" });
    }

    const listReqId = ++campaignListReqRef.current;
    await refreshCampaignsForCurrentAdvertiser({
      allowAutoSelectLatestIfNoDrafts: false,
      reqId: listReqId,
    });

    if (
      reqId !== bootstrapReqRef.current ||
      manualOverrideSeqAtStart !== manualPlannerOverrideSeqRef.current ||
      advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
    ) {
      return;
    }

    // If we're in campaign mode, don't force-switch to drafts
    if (shouldHonorCampaignRoute) {
      setSessionMode("campaign");
      setIsLoadingCampaign(false);
      return;
    }
    setIsLoadingCampaign(false);
  }, [campaignIdFromNav, advKey, isDirty, readGlobalIndex, refreshCampaignsForCurrentAdvertiser, BASE_BLANK_FORM, clearPlannerRouteState]);

  useEffect(() => {
    bootstrapForAdvertiser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advKey]);

  // ---------------------------
  // Campaign load (guard against stale fetches)
  // ---------------------------

  const loadCampaignIntoPlanner = async (campaignId) => {
    if (!campaignId) return;

    const reqId = ++campaignLoadReqRef.current;
    const manualOverrideSeqAtStart = manualPlannerOverrideSeqRef.current;
    const advertiserScopeGenerationAtStart =
      advertiserScopeGenerationRef.current;

    setIsLoadingCampaign(true);
    // Campaign switch/load clears local error state; the snapshot resets so
    // the current-plan resolver never reads across a campaign boundary.
    setError("");
    loadedCampaignSnapshotRef.current = null;

    try {
      if (!API_BASE_URL) throw new Error("API base URL is not defined.");

      const token = getSherlocMediaPlannerAuthToken();
      if (!token) throw new Error("You are not authenticated. Please log in.");

      const url = `${safeBase(API_BASE_URL)}${CAMPAIGNS_BASE_PATH}/${campaignId}`;
      const res = await fetch(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        cache: "no-store",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Failed to load campaign (${res.status}): ${text}`);
      }

      const data = await res.json().catch(() => ({}));
      const campaign = data.campaign || data;

      if (
        reqId !== campaignLoadReqRef.current ||
        manualOverrideSeqAtStart !== manualPlannerOverrideSeqRef.current ||
        advertiserScopeGenerationAtStart !==
          advertiserScopeGenerationRef.current
      ) {
        return;
      }

      if (!campaign || !(campaign.id || campaign.campaign_id || campaign.campaignId)) {
        throw new Error("Campaign response did not include a campaign object.");
      }

      const campaignScopeId = campaign.advertiser_id ?? campaign.advertiserId ?? campaign.advertiserID ?? null;
      if (!plannerSession.isCurrent() || !sameAdvertiserId(campaignScopeId, plannerSession.advertiserId)) {
        throw new Error("This campaign is not available for the verified advertiser.");
      }

      const resolvedId = campaign.id ?? campaign.campaign_id ?? campaign.campaignId;
      if (String(campaign.status || "").toUpperCase() === "DRAFT") {
        // A campaign link can name a planner draft. Open it through the same
        // owned draft/recovery path; never turn Save Changes into a second,
        // unreceipted writer. This read and opening perform no save.
        const draftResponse = await fetch(`${safeBase(API_BASE_URL)}${PLANNER_SAVE_PATH}/drafts/${encodeURIComponent(resolvedId)}?advertiserId=${encodeURIComponent(plannerSession.advertiserId)}`, {
          headers: { Authorization: `Bearer ${token}` }, cache: "no-store",
        });
        const draftResult = await draftResponse.json().catch(() => null);
        if (!plannerSession.isCurrent() || reqId !== campaignLoadReqRef.current ||
            manualOverrideSeqAtStart !== manualPlannerOverrideSeqRef.current) return;
        const draft = draftResult?.draft;
        if (!draftResponse.ok || draftResult?.ok !== true || draftResult?.protocol !== "planner-save-receipts-v1" ||
            String(draft?.serverDraftId || draft?.id) !== String(resolvedId) ||
            !sameAdvertiserId(draft?.advertiserId, plannerSession.advertiserId) ||
            !/^[1-9]\d*$/.test(String(draft?.writeVersion || "")) || !Number.isSafeInteger(Number(draft?.writeVersion))) {
          throw new Error("This draft could not be opened safely. Your browser copies are preserved. Try again when the updated save service is available.");
        }
        // The earlier campaign-list bootstrap may still be awaiting its
        // response. It cannot restore campaign mode over this chosen draft.
        bootstrapReqRef.current += 1;
        if (!applyServerDraft(draft)) throw new Error("This draft could not be opened safely.");
        setNavCampaignId(null);
        clearPlannerRouteState();
        return;
      }
      setActiveCampaignId(resolvedId);
      setStrategyMicrositeAddress({
        campaignId: String(resolvedId),
        path: readCampaignStrategyMicrositeAddress(campaign),
      });
      setSessionMode("campaign");

      // ✅ If campaign includes advertiser info, sync page + context
      const campAdvId =
        campaign.advertiser_id ?? campaign.advertiserId ?? campaign.advertiserID ?? null;
      const campAdvName = String(campaign.advertiser_name ?? campaign.advertiser ?? "").trim();

      if (campAdvId || campAdvName) {
        setPageAdvertiserId(campAdvId != null ? String(campAdvId) : null);
        setPageAdvertiserName(campAdvName || pageAdvertiserName || "");

        // ✅ Canonical context update (object)
        if (typeof selectAdvertiser === "function") {
          const campAdvVertical = normalizeAdvertiserVertical(
            campaign?.advertiser_vertical ||
              campaign?.advertiserVertical ||
              campaign?.vertical ||
              activePlannerAdvertiserVertical
          );
          selectAdvertiser({
            id: campAdvId != null ? String(campAdvId) : null,
            name: campAdvName,
            vertical: campAdvVertical,
          });
        } else if (typeof legacySetSelectedAdvertiserName === "function" && campAdvName) {
          legacySetSelectedAdvertiserName(campAdvName);
        }

        const listReqId = ++campaignListReqRef.current;
        refreshCampaignsForCurrentAdvertiser({ reqId: listReqId });
      }

      const hydrated = normalizePlannerFormData(
        buildPlannerFormDataFromCampaign(campaign, {
          advertiserId: campAdvId,
          advertiserName: campAdvName,
        })
      );

      plannerDebug("loadCampaignIntoPlanner.rawCampaignDates", {
        campaignId,
        flight_start_date: campaign?.flight_start_date,
        flight_end_date: campaign?.flight_end_date,
        flightStartDate: campaign?.flightStartDate,
        flightEndDate: campaign?.flightEndDate,
        flightStart: campaign?.flightStart,
        flightEnd: campaign?.flightEnd,
        startDate: campaign?.startDate,
        endDate: campaign?.endDate,
        strategyPayload: campaign?.strategy_payload,
        extractedFlightStart: hydrated.flightStart,
        extractedFlightEnd: hydrated.flightEnd,
      });
      assertDateInputValue("loadCampaignIntoPlanner.flightStart", hydrated.flightStart);
      assertDateInputValue("loadCampaignIntoPlanner.flightEnd", hydrated.flightEnd);

      setFormData((prev) => {
        const resolvedFlightDates = resolveLoadedCampaignFlightDates(campaign, prev);
        const nextForm = normalizePlannerFormData({
          ...prev,
          ...hydrated,
          ...resolvedFlightDates,
        });
        assertDateInputValue("loadCampaignIntoPlanner.nextFlightStart", nextForm.flightStart);
        assertDateInputValue("loadCampaignIntoPlanner.nextFlightEnd", nextForm.flightEnd);
        return nextForm;
      });
      setStrategy(getStrategyFromCampaign(campaign) || "");
      setCreativeRecommendations(getCreativeRecommendationsFromCampaign(campaign));
      setCreativeGenerationMeta(getCreativeGenerationMetaFromCampaign(campaign));
      // Canonical allocation + plan version travel with the persisted plan so
      // a reload renders the same governed revision result the backend holds.
      setCanonicalAllocation(
        Array.isArray(campaign?.strategy_payload?.requestedChannelAllocation)
          ? campaign.strategy_payload.requestedChannelAllocation
          : null
      );
      setStrategyPresentation(getSherlocStrategyPresentationFromCampaign(campaign));
      setAllocationAuthority(getSherlocAllocationAuthorityFromCampaign(campaign));
      setAllocationReconnectOperation(null);
      setAppliedRevisionSummary(null);
      planVersionRef.current = campaign?.updated_at || campaign?.updatedAt || "";
      // Strategy ARTIFACT version (strategy_provenance.planVersion from the
      // durable-operation ledger) rides in its own ref: planVersionRef stays
      // the record-concurrency token (expectedPlanVersion compares against
      // updated_at server-side), while recency decisions use the artifact.
      const loadedProvenance =
        campaign?.strategy_provenance || campaign?.strategyProvenance || null;
      setStrategyProvenance(loadedProvenance);
      strategyPlanVersionRef.current =
        loadedProvenance?.planVersion ||
        campaign?.updated_at ||
        campaign?.updatedAt ||
        "";
      setRevisionPrompt("");
      setCreativeError("");
      setIsDirty(false);
      // Successful resolution of the campaign's CURRENT state: publish the
      // snapshot for the canonical resolver and clear any stale completeness
      // error that raced in from a concurrent historical-operation probe. A
      // genuinely incomplete current plan keeps erroring accurately.
      loadedCampaignSnapshotRef.current = campaign;
      if (resolveSherlocCurrentPlanState({ campaign }).complete) {
        setError((previous) =>
          isSherlocCurrentPlanCompletenessErrorMessage(previous) ? "" : previous
        );
      }
    } catch (e) {
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[Client SherlocMediaPlanner] Error loading campaign:", e);
      setError(
        "Unable to load this campaign into Media Planner. Please check backend logs / campaign GET route."
      );
    } finally {
      if (
        reqId === campaignLoadReqRef.current &&
        advertiserScopeGenerationAtStart ===
          advertiserScopeGenerationRef.current
      ) {
        setIsLoadingCampaign(false);
      }
    }
  };

  useEffect(() => {
    if (!campaignIdFromNav) return;
    loadCampaignIntoPlanner(campaignIdFromNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignIdFromNav]);

  // ---------------------------
  // SHERLOC recommended allocation surface wiring
  // ---------------------------

  // Post-decision / post-operation hydration: the backend response campaign
  // (when present) is authoritative; otherwise the persisted campaign is
  // reloaded so the surface always reflects server truth, never optimistic
  // local state.
  const handleAllocationAuthorityRefresh = (campaign) => {
    setAllocationReconnectOperation(null);
    if (campaign && typeof campaign === "object") {
      // Server truth advanced (approval decision / completed allocation
      // operation): the current-plan resolver reads this snapshot, and a
      // now-complete plan clears any stale completeness error.
      loadedCampaignSnapshotRef.current = campaign;
      if (resolveSherlocCurrentPlanState({ campaign }).complete) {
        setError((previous) =>
          isSherlocCurrentPlanCompletenessErrorMessage(previous) ? "" : previous
        );
      }
      setAllocationAuthority(getSherlocAllocationAuthorityFromCampaign(campaign));
      const nextPresentation =
        getSherlocStrategyPresentationFromCampaign(campaign);
      if (nextPresentation) setStrategyPresentation(nextPresentation);
      planVersionRef.current =
        campaign?.updated_at || campaign?.updatedAt || planVersionRef.current || "";
      return;
    }
    const idText = String(activeCampaignId || campaignIdFromNav || "").trim();
    if (idText) loadCampaignIntoPlanner(idText);
  };

  const allocationCampaignIdText = String(
    activeCampaignId || campaignIdFromNav || ""
  ).trim();

  // Review surface for a SHERLOC-recommended allocation (awaiting approval /
  // approved / rejected states).
  const sherlocAllocationReviewPanel =
    allocationCampaignIdText &&
    isSherlocRecommendedAllocationAuthority(allocationAuthority) ? (
      <SherlocAllocationRecommendationReview
        campaignId={allocationCampaignIdText}
        authority={allocationAuthority}
        onAuthorityRefresh={handleAllocationAuthorityRefresh}
      />
    ) : null;

  // ABSENT-financials affordance: preferred over the static
  // supply-the-allocation note whenever a persisted campaign can create an
  // allocation_recommendation operation. Unsaved drafts and presentation-less
  // legacy paths keep the truthful static guidance unchanged.
  const sherlocAllocationDevelopAffordance =
    allocationCampaignIdText &&
    !isSherlocRecommendedAllocationAuthority(allocationAuthority) ? (
      <SherlocAllocationDevelopCard
        campaignId={allocationCampaignIdText}
        onCampaignApplied={handleAllocationAuthorityRefresh}
        initialOperation={allocationReconnectOperation}
      />
    ) : null;

  // ---------------------------
  // Persist current draft (ONLY when in draft mode)
  // ---------------------------

  useEffect(() => {
    if (campaignIdFromNav) return;
    if (!activePlannerId) return;

    // Merge-written: the saved version this copy was based on is kept, and
    // the copy records whether it holds edits not yet confirmed saved: its
    // content differs from what its saved draft holds, as far as this page
    // knows (a save it confirmed, or the version it opened).
    // A save of other content still writing means the plan is not saved yet.
    const savedContent = savedContentByPlannerRef.current.get(activePlannerId);
    const shownContent = plannerSaveContentFingerprint(
      { formData, strategy, creatives: creativeRecommendations, creativeMeta: creativeGenerationMeta },
      {
        advertiserId: formData.advertiserId ?? pageAdvertiserId ?? null,
        advertiser: String(formData.advertiser || pageAdvertiserName || "").trim(),
        verticalConfig: plannerVerticalConfig,
      }
    );
    writePlannerLocalState(activePlannerId, {
      formData: normalizePlannerFormData(formData),
      strategy,
      creativeRecommendations,
      creativeVariations: creativeRecommendations,
      creativeGenerationMeta,
      serverDraftId,
      unsavedChanges: Boolean(readPlannerCopy(activePlannerId)?.plannerRecoveryRequired || readPlannerCopy(activePlannerId)?.plannerExplicitSaveRequired) || plannerCopyHasPendingSave(activePlannerId) || (savedContent
        ? savedContent !== shownContent ||
          otherContentInFlight(savesInFlightRef.current, activePlannerId, shownContent)
        : Boolean(isDirty)),
    });

    upsertGlobalIndex(activePlannerId, normalizePlannerFormData(formData));

    const k = getAdvertiserKey(formData.advertiserId, formData.advertiser);
    if (k && k !== "unknown") {
      try {
        localStorage.setItem(makeActiveKeyForAdvertiser(k), activePlannerId);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    strategy,
    creativeRecommendations,
    creativeGenerationMeta,
    campaignIdFromNav,
    activePlannerId,
    serverDraftId,
    normalizePlannerFormData,
  ]);

  // ---------------------------
  // Server-backed draft persistence
  // ---------------------------

  const buildDraftSaveFingerprint = useCallback(
    (
      fd = formData,
      strat = strategy,
      creatives = creativeRecommendations,
      creativeMeta = creativeGenerationMeta
    ) =>
      JSON.stringify({
        formData: normalizePlannerFormData(fd),
        strategy: strat || "",
        creativeRecommendations: Array.isArray(creatives) ? creatives : [],
        creativeGenerationMeta: creativeMeta || null,
      }),
    [
      creativeGenerationMeta,
      creativeRecommendations,
      formData,
      normalizePlannerFormData,
      strategy,
    ]
  );

  const hasSaveablePlannerContent = useCallback(
    (fd = formData, strat = strategy) =>
      Boolean(
        plannerFormHasContent(fd) ||
          String(strat || "").trim() ||
          creativeRecommendations.length > 0
      ),
    [creativeRecommendations.length, formData, strategy]
  );

  // A new plan gets its identity at its first meaningful edit or first save —
  // never on page load — and keeps it: a double-click or a save racing that
  // first edit reuses the same id instead of starting a second plan.
  const ensurePlannerIdentity = useCallback(() => {
    if (campaignIdFromNav) return "";
    if (activePlannerId) return activePlannerId;
    // No new plan while a campaign is being chosen: the form belongs to none.
    if (sessionMode === "campaign") return "";
    if (pendingPlannerIdRef.current) return pendingPlannerIdRef.current;
    if (!advKey || advKey === "unknown") return "";
    // A form that does not carry this advertiser yet would be filed under no
    // advertiser and never listed.
    if (getAdvertiserKey(formData.advertiserId, formData.advertiser) !== advKey) return "";
    const id = newPlannerId();
    pendingPlannerIdRef.current = id;
    setActivePlannerId(id);
    if (explicitNewPlanRef.current === advKey) rememberPlannerChoice(advKey, { localPlannerId: id });
    explicitNewPlanRef.current = "";
    return id;
  }, [
    activePlannerId,
    advKey,
    campaignIdFromNav,
    formData.advertiser,
    formData.advertiserId,
    rememberPlannerChoice,
    sessionMode,
  ]);

  useEffect(() => {
    if (campaignIdFromNav || activePlannerId || sessionMode !== "new") return;
    if (!hasSaveablePlannerContent(formData, strategy)) return;
    ensurePlannerIdentity();
  }, [
    activePlannerId,
    campaignIdFromNav,
    ensurePlannerIdentity,
    formData,
    hasSaveablePlannerContent,
    sessionMode,
    strategy,
  ]);

  // UI calls for one plan wait for the bounded receipt transport. Other
  // plans have independent queues. A timeout never releases an unknown
  // operation for blind retry: its durable pending record remains a fence.
  const acquireDraftSaveSlot = useCallback((planId) => {
    let release;
    const held = new Promise((resolve) => { release = resolve; });
    const turn = draftSaveChainRef.current.get(planId) || Promise.resolve();
    const tail = turn.then(() => held);
    draftSaveChainRef.current.set(planId, tail);
    return turn.then(() => () => {
      release();
      if (draftSaveChainRef.current.get(planId) === tail) draftSaveChainRef.current.delete(planId);
    });
  }, []);

  const receiptRecovery = usePlannerReceiptRecovery({
    storage: localStorage, session: plannerSession, fetch, baseUrl: safeBase(API_BASE_URL),
    activePlanId: activePlannerId, readCopy: readPlannerCopy, writeCopy: writePlannerLocalState,
    currentContent: latestPlannerContentRef,
    contentFingerprint: (content) => plannerSaveContentFingerprint(content, {
      advertiserId: plannerSession.advertiserId,
      advertiser: String(pageAdvertiserName || content?.formData?.advertiser || "").trim(),
      verticalConfig: plannerVerticalConfig,
    }),
    onCommitted: ({ planId, result, sentContent, sameContent }) => {
      const id = String(result.receipt.serverDraftId);
      serverDraftIdByPlannerRef.current.set(planId, id);
      savedContentByPlannerRef.current.set(planId, sentContent);
      if (plannerIdentityRef.current !== `${planId}|`) return;
      setServerDraftId(id);
      setSaveStatus(sameContent ? "saved" : "pending");
      setSaveMessage(sameContent ? "Saved" : "Unsaved changes");
      setIsDirty(!sameContent);

    },
  });

  const persistPlannerDraftToServer = useCallback(
    async ({
      explicit = false,
      requireSave = false,
      formDataOverride = null,
      strategyOverride,
      creativeRecommendationsOverride,
      creativeGenerationMetaOverride,
      // A caller that reports a failed save in its own section (Generate
      // Creative Variations) receives the reason here instead of the
      // strategy panel's error line.
      onSaveError = null,
    } = {}) => {
      if (!plannerSession.isCurrent()) return requireSave ? false : null;
      const advertiserScopeGenerationAtStart =
        advertiserScopeGenerationRef.current;
      const reportSaveFailure = (message, showInStrategyPanel) => {
        if (onSaveError) onSaveError(message);
        else if (showInStrategyPanel) setError(message);
      };
      if (campaignIdFromNav) return null;
      if (!API_BASE_URL) {
        reportSaveFailure("API base URL is not defined.", explicit || requireSave);
        return requireSave ? false : null;
      }

      const sourceFormData = formDataOverride || formData;
      const sourceStrategy =
        strategyOverride === undefined ? strategy || "" : strategyOverride || "";
      const sourceCreativeRecommendations = Array.isArray(creativeRecommendationsOverride)
        ? creativeRecommendationsOverride
        : creativeRecommendations;
      const sourceCreativeGenerationMeta =
        creativeGenerationMetaOverride === undefined
          ? creativeGenerationMeta
          : creativeGenerationMetaOverride;
      const effectiveAdvertiserId = sourceFormData.advertiserId ?? pageAdvertiserId ?? null;
      if (!plannerSession.isCurrent() || !sameAdvertiserId(effectiveAdvertiserId, plannerSession.advertiserId)) return requireSave ? false : null;
      const effectiveAdvertiserName = String(
        sourceFormData.advertiser || pageAdvertiserName || ""
      ).trim();
      const normalizedDraftFormData = gatePlannerReleaseDateForVertical(
        normalizePlannerFormData({
          ...sourceFormData,
          advertiserId: effectiveAdvertiserId,
          advertiser: effectiveAdvertiserName,
        }),
        plannerVerticalConfig
      );
      const draftStrategy = sourceStrategy || "";

      if (!effectiveAdvertiserId) {
        reportSaveFailure(
          "No advertiser is selected. Use the advertiser selector above.",
          explicit || requireSave
        );
        return requireSave ? false : null;
      }

      if (!hasSaveablePlannerContent(normalizedDraftFormData, draftStrategy)) {
        if (explicit) {
          setSaveStatus("idle");
          setSaveMessage("Add planner inputs before saving.");
        }
        reportSaveFailure(
          onSaveError
            ? "Add planner inputs before generating."
            : "Add planner inputs before generating a media strategy.",
          requireSave
        );
        return requireSave ? false : null;
      }

      const urlValidationError = getPlannerUrlValidationError(normalizedDraftFormData, plannerVerticalConfig);
      if (urlValidationError) {
        reportSaveFailure(urlValidationError, explicit || requireSave);
        setSaveStatus("error");
        setSaveMessage("Draft was not saved.");
        return requireSave ? false : null;
      }

      const authToken = getSherlocMediaPlannerAuthToken();
      if (!authToken) {
        reportSaveFailure("You are not authenticated. Please log in again.", explicit || requireSave);
        return requireSave ? false : null;
      }

      const fingerprint = buildDraftSaveFingerprint(
        normalizedDraftFormData,
        draftStrategy,
        sourceCreativeRecommendations,
        sourceCreativeGenerationMeta
      );
      if (!explicit && fingerprint === lastDraftSaveFingerprintRef.current && !receiptRecovery.hasPending(activePlannerId) && !readPlannerCopy(activePlannerId)?.plannerRecoveryRequired) {
        return null;
      }

      // The plan this save belongs to. Its row id is the one that plan
      // learned (from an earlier save or from opening it), never another
      // plan's: a save that finishes after the operator opened a different
      // plan updates its own plan's copy and leaves the open plan alone.
      const plannerLocalId = ensurePlannerIdentity();
      if (!plannerLocalId) {
        // No plan is open (a campaign is being chosen, or the form does not
        // carry the advertiser yet): nothing is saved, and nothing is saved
        // onto another plan.
        reportSaveFailure("Open a plan or start a new strategy before saving.", explicit || requireSave);
        return requireSave ? false : null;
      }
      if (!explicit && !requireSave && readPlannerCopy(plannerLocalId)?.plannerExplicitSaveRequired) return null;
      const serverDraftIdAtCall =
        activePlannerId && activePlannerId === plannerLocalId ? serverDraftId : null;
      const planStillOnScreen = () => plannerSession.isCurrent() && plannerIdentityRef.current === `${plannerLocalId}|`;
      // What this save sent can be compared, when it ends, with the open page
      // or the plan's stored copy.
      const contentFingerprintFor = (content) =>
        plannerSaveContentFingerprint(content, {
          advertiserId: effectiveAdvertiserId,
          advertiser: effectiveAdvertiserName,
          verticalConfig: plannerVerticalConfig,
        });
      const sentContent = contentFingerprintFor({
        formData: sourceFormData,
        strategy: draftStrategy,
        creatives: sourceCreativeRecommendations,
        creativeMeta: sourceCreativeGenerationMeta,
      });
      // Whether the plan still holds exactly what this save sent: the open
      // page while the plan is on screen, else its stored copy. Edits made
      // while the save ran are not in it.
      const planStillHoldsSentContent = () => {
        if (planStillOnScreen()) {
          return contentFingerprintFor(latestPlannerContentRef.current) === sentContent;
        }
        const copy = readPlannerCopy(plannerLocalId);
        if (!copy?.formData) return false;
        return (
          contentFingerprintFor({
            formData: copy.formData,
            strategy: copy.strategy,
            creatives: Array.isArray(copy.creativeRecommendations)
              ? copy.creativeRecommendations
              : copy.creativeVariations,
            creativeMeta: copy.creativeGenerationMeta,
          }) === sentContent
        );
      };
      let targetServerDraftId = null;

      const statusToken = ++saveStatusOwnerRef.current;
      // A save that ends after its plan left the screen hands the status
      // back, unless another save or plan has set it since.
      const handBackStatusIfOffScreen = () => {
        if (!planStillOnScreen() && saveStatusOwnerRef.current === statusToken) {
          setSaveStatus("idle");
          setSaveMessage("");
        }
      };
      setSaveStatus("saving");
      setSaveMessage("Saving...");

      const releaseSaveSlot = await acquireDraftSaveSlot(plannerLocalId);
      let releaseInFlight = () => {};
      try {
        if (!plannerSession.isCurrent()) return requireSave ? false : null;
        if (
          advertiserScopeGenerationAtStart !==
          advertiserScopeGenerationRef.current
        ) {
          return requireSave ? false : null;
        }
        // A queued autosave whose content the save before it already stored
        // has nothing left to send.
        if (!explicit && fingerprint === lastDraftSaveFingerprintRef.current && !receiptRecovery.hasPending(activePlannerId) && !readPlannerCopy(activePlannerId)?.plannerRecoveryRequired) {
          if (!planStillOnScreen()) handBackStatusIfOffScreen();
          else if (
            planStillHoldsSentContent() &&
            !otherContentInFlight(savesInFlightRef.current, plannerLocalId, sentContent)
          ) {
            setSaveStatus("saved");
            setSaveMessage("Saved");
          }
          return null;
        }
        targetServerDraftId =
          serverDraftIdByPlannerRef.current.get(plannerLocalId) || serverDraftIdAtCall || null;
        const targetingPayload = buildPlannerTargetingPayload(normalizedDraftFormData);
        releaseInFlight = trackSaveInFlight(savesInFlightRef.current, plannerLocalId, sentContent);
        const saveResult = await receiptRecovery.run({ planId: plannerLocalId, explicit: explicit || requireSave, savedPayload: {
            formData: normalizedDraftFormData,
            localDraftId: plannerLocalId || null,
            serverDraftId: targetServerDraftId,
            advertiserId: effectiveAdvertiserId,
            advertiser_id: effectiveAdvertiserId,
            advertiser: effectiveAdvertiserName,
            advertiser_name: effectiveAdvertiserName,
            advertiserVertical: activePlannerAdvertiserVertical,
            advertiser_vertical: activePlannerAdvertiserVertical,
            budget: normalizedDraftFormData.budget,
            totalBudget: normalizedDraftFormData.budget,
            total_budget: normalizedDraftFormData.budget,
            grossBudget: normalizedDraftFormData.budget,
            gross_budget: normalizedDraftFormData.budget,
            netMediaBudget: normalizedDraftFormData.netMediaBudget,
            net_media_budget: normalizedDraftFormData.netMediaBudget,
            targetMarginPercent: normalizedDraftFormData.targetMarginPercent,
            target_margin_percent: normalizedDraftFormData.targetMarginPercent,
            targetMargin: normalizedDraftFormData.targetMarginPercent,
            target_margin: normalizedDraftFormData.targetMarginPercent,
            primaryKPI: normalizedDraftFormData.primaryKPI,
            primaryKpi: normalizedDraftFormData.primaryKPI,
            primary_kpi: normalizedDraftFormData.primaryKPI,
            notes: normalizedDraftFormData.notes,
            additionalNotes: normalizedDraftFormData.notes,
            additional_notes: normalizedDraftFormData.notes,
            targetPlatforms: normalizedDraftFormData.platforms,
            target_platforms: normalizedDraftFormData.platforms,
            destinationUrl: normalizedDraftFormData.destinationUrl,
            destination_url: normalizedDraftFormData.destinationUrl,
            imdbUrl: normalizedDraftFormData.imdbUrl,
            imdb_url: normalizedDraftFormData.imdbUrl,
            rottenTomatoesUrl: normalizedDraftFormData.rottenTomatoesUrl,
            rotten_tomatoes_url: normalizedDraftFormData.rottenTomatoesUrl,
            ageMin: targetingPayload.ageMin,
            age_min: targetingPayload.age_min,
            ageMax: targetingPayload.ageMax,
            age_max: targetingPayload.age_max,
            gender: targetingPayload.gender,
            interests: targetingPayload.interests,
            customTargeting: targetingPayload.customTargeting,
            custom_targeting: targetingPayload.custom_targeting,
            geoTargetingNotes: targetingPayload.geoTargetingNotes,
            geo_targeting_notes: targetingPayload.geo_targeting_notes,
            geoTargeting: targetingPayload.geoTargeting,
            geo_targeting: targetingPayload.geo_targeting,
            locationTargeting: targetingPayload.locationTargeting,
            location_targeting: targetingPayload.location_targeting,
            languageTargeting: targetingPayload.languageTargeting,
            language_targeting: targetingPayload.language_targeting,
            deviceTargetingNotes: targetingPayload.deviceTargetingNotes,
            device_targeting_notes: targetingPayload.device_targeting_notes,
            devicePlatformNotes: targetingPayload.devicePlatformNotes,
            device_platform_notes: targetingPayload.device_platform_notes,
            flightStart: normalizedDraftFormData.flightStart,
            flight_start_date: normalizedDraftFormData.flightStart,
            flightEnd: normalizedDraftFormData.flightEnd,
            flight_end_date: normalizedDraftFormData.flightEnd,
            releaseDate: normalizedDraftFormData.releaseDate,
            release_date: normalizedDraftFormData.releaseDate,
            targeting: targetingPayload,
            strategy: draftStrategy,
            creativeRecommendations: sourceCreativeRecommendations,
            creativeVariations: sourceCreativeRecommendations,
            creativeGenerationMeta: sourceCreativeGenerationMeta,
          } });
        if (saveResult.outcome !== "COMMITTED" || saveResult.superseded) {
          if (saveResult.outcome === "SESSION_CHANGED") return requireSave ? false : null;
          const terminal = saveResult.outcome === "REJECTED" || saveResult.outcome === "CANCELLED";
          const blocked = saveResult.outcome === "BLOCKED";
          const error = new Error(blocked ? saveResult.message || "The save was not sent. Your inputs remain on this page." : terminal ? saveResult.receipt?.message || "The save did not change this plan. Your browser edits are preserved." : "Check the save outcome before saving or generating this plan.");
          error.code = saveResult.receipt?.code || saveResult.code || saveResult.outcome;
          error.blocked = blocked;
          error.recovery = !blocked && (!terminal || saveResult.outcome === "CANCELLED" || error.code === "PLANNER_WRITE_CONFLICT");
          throw error;
        }
        const data = { draft: saveResult.draft };
        if (!plannerSession.isCurrent()) return requireSave ? false : null;
        const draft = data?.draft || null;
        const savedServerDraftId = serverDraftIdOf(draft);
        const scopeUnchanged =
          advertiserScopeGenerationAtStart === advertiserScopeGenerationRef.current;
        // Saved only if the plan still holds what this save sent and no save
        // of other content is still writing (an older one landing later
        // would overwrite this).
        const planHoldsSave =
          planStillHoldsSentContent() &&
          !otherContentInFlight(savesInFlightRef.current, plannerLocalId, sentContent);
        if (savedServerDraftId && !deletedPlannerIdsRef.current.has(plannerLocalId)) {
          // The plan's own copy learns its row and saved version whatever is
          // open now (another plan or advertiser may be). It is marked saved
          // only if it still holds what this save sent.
          serverDraftIdByPlannerRef.current.set(plannerLocalId, savedServerDraftId);
          savedContentByPlannerRef.current.set(plannerLocalId, sentContent);
          writePlannerLocalState(plannerLocalId, {
            serverDraftId: savedServerDraftId,
            unsavedChanges: Boolean(readPlannerCopy(plannerLocalId)?.formData) && !planHoldsSave,
            serverSavedAt: draft?.savedAt || draft?.updatedAt || null,
            plannerWriteVersion: saveResult.receipt.writeVersion,
            plannerRecoveryRequired: false,
          });
        }
        if (!scopeUnchanged) return null;
        if (savedServerDraftId) {
          if (draftsSavedDuringLookupRef.current.open) draftsSavedDuringLookupRef.current.drafts.push(draft);
          setServerDraftList((list) =>
            list.advertiserKey === advKey
              ? { ...list, drafts: upsertServerDraft(list.drafts, draft) }
              : list
          );
        }
        if (!planStillOnScreen()) {
          handBackStatusIfOffScreen();
          return requireSave ? false : draft;
        }
        if (savedServerDraftId) setServerDraftId(draft.serverDraftId || draft.id);

        lastDraftSaveFingerprintRef.current = fingerprint;
        // Saved once: from here autosave compares with what was saved, not
        // with the plan as it was opened.
        if (openedPlanContentRef.current?.plannerId === plannerLocalId) openedPlanContentRef.current = null;
        if (!planHoldsSave) {
          // The plan on screen holds content this save did not send (edits
          // made while it ran, or a slower earlier save that landed last):
          // it stays unsaved, and autosave looks again now rather than
          // waiting for the next edit. If the page simply had not rendered
          // what was sent yet, that look finds it saved.
          unsavedAfterSaveRef.current = plannerLocalId;
          setSaveStatus("pending");
          setSaveMessage("Unsaved changes");
          setAutosaveNudge((count) => count + 1);
          return requireSave ? false : draft;
        }
        unsavedAfterSaveRef.current = "";
        setSaveStatus("saved");
        setSaveMessage("Saved");
        setIsDirty(false);
        // A save that carries creative variations this page held as unsaved
        // settles their notice.
        setCreativeSaveNotice((notice) =>
          ["not_saved", "unknown"].includes(notice?.state) &&
          notice.recommendations === sourceCreativeRecommendations &&
          notice.identity === plannerIdentityRef.current
            ? { ...notice, state: "saved", text: CREATIVE_SAVED_TO_DRAFT_NOTICE }
            : notice
        );
        return draft;
      } catch (err) {
        if (
          advertiserScopeGenerationAtStart !==
          advertiserScopeGenerationRef.current
        ) {
          return null;
        }
        logSherlocPlannerDraftSaveFailure({
          advertiserId: effectiveAdvertiserId,
          localDraftId: plannerLocalId || null,
          serverDraftId: targetServerDraftId,
          status: err?.status,
          message: err?.message,
        });
        console.error("[Client SherlocMediaPlanner] Draft save error:", err);
        if (!planStillOnScreen()) {
          handBackStatusIfOffScreen();
          return requireSave ? false : null;
        }
        setSaveStatus(err?.recovery ? "pending" : "error");
        setSaveMessage(err?.recovery ? "Save outcome needs recovery" : err?.blocked ? "Save not sent" : "Save failed");
        reportSaveFailure(
          err?.blocked ? err.message : err?.recovery
            ? "Draft save is not confirmed. Check its outcome before generating."
            : PLANNER_DRAFT_ID_REFUSAL_CODES.has(err?.code)
            ? err.message
            : onSaveError && isPlannerNetworkError(err)
            ? "The planner service didn’t respond."
            : "Unable to save this planner draft. Please try again before generating.",
          explicit || requireSave
        );
        return requireSave ? false : null;
      } finally {
        releaseInFlight();
        releaseSaveSlot();
      }
    },
    [receiptRecovery, acquireDraftSaveSlot, activePlannerAdvertiserVertical, activePlannerId, advKey, buildDraftSaveFingerprint, campaignIdFromNav, creativeGenerationMeta, creativeRecommendations, ensurePlannerIdentity, formData, getSherlocMediaPlannerAuthToken, hasSaveablePlannerContent, normalizePlannerFormData, pageAdvertiserId, pageAdvertiserName, plannerSession, plannerVerticalConfig, readPlannerCopy, serverDraftId, strategy, writePlannerLocalState]
  );

  // The advertiser's saved drafts, newest first. A failed read is reported as
  // a failure, never as "no drafts".
  const fetchServerDraftsForAdvertiser = useCallback(async () => {
    const effectiveAdvertiserId = pageAdvertiserId ?? formData.advertiserId ?? null;
    if (!plannerSession.isCurrent() || (effectiveAdvertiserId && !sameAdvertiserId(effectiveAdvertiserId, plannerSession.advertiserId))) return { ok: false, drafts: [] };
    if (!effectiveAdvertiserId) return { ok: false, drafts: [], reason: "no_advertiser_id" };
    if (!API_BASE_URL) return { ok: false, drafts: [] };
    const authToken = getSherlocMediaPlannerAuthToken();
    if (!authToken) return { ok: false, drafts: [] };

    try {
      const url = new URL(`${safeBase(API_BASE_URL)}${PLANNER_DRAFTS_LIST_PATH}`);
      url.searchParams.set("advertiserId", String(effectiveAdvertiserId));
      url.searchParams.set("ts", Date.now().toString());

      const res = await fetch(url.toString(), {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        cache: "no-store",
      });
      if (!res.ok) return { ok: false, drafts: [] };
      const data = await res.json().catch(() => null);
      if (!data || !Array.isArray(data.drafts)) return { ok: false, drafts: [] };
      // Only this advertiser's drafts, each by its own id.
      return {
        ok: true,
        drafts: data.drafts.filter(
          (draft) =>
            serverDraftIdOf(draft) && sameAdvertiserId(draft?.advertiserId, effectiveAdvertiserId)
        ),
      };
    } catch (err) {
      console.error("[Client SherlocMediaPlanner] Draft list error:", err);
      return { ok: false, drafts: [] };
    }
  }, [fetch, formData.advertiserId, getSherlocMediaPlannerAuthToken, pageAdvertiserId, plannerSession]);

  // Keep a browser copy's never-saved edits apart from its saved plan: edits
  // the saved plan has since moved past, or edits a copy the previous planner
  // left made after the plan was last saved. They become their own
  // browser-only plan, listed as such, and are never written over the saved
  // plan or lost. Says whether they are kept (false: the copy could not be
  // stored, so the caller must not write over the original).
  const keepUnsavedCopyApart = useCallback((localId, draft, { reason = "superseded" } = {}) => {
    const copy = readPlannerCopy(localId);
    if (!copy?.formData) return true;
    const keptId = newPlannerId();
    const serverId = serverDraftIdOf(draft);
    const sourceEntry = readGlobalIndex().find((entry) => entry?.id === localId) || null;
    try {
      localStorage.setItem(
        makePlannerStorageKey(keptId),
        JSON.stringify({
          ...copy,
          serverDraftId: null,
          serverSavedAt: null,
          unsavedChanges: true,
          supersededServerDraftId: serverId || null,
            plannerWriteVersion: null, plannerReceiptId: null, plannerRecoveryRequired: false, plannerExplicitSaveRequired: false,
        })
      );
    } catch {
      return false;
    }
    // Filed under the advertiser of the copy's own entry, so it is listed
    // with that advertiser's plans even if the copy's form lacks it.
    const meta = sourceEntry?.advertiserKey
      ? {
          advertiserKey: sourceEntry.advertiserKey,
          advertiserId: sourceEntry.advertiserId,
          advertiserName: sourceEntry.advertiserName,
        }
      : getDraftMeta(copy.formData);
    // Stored now, so a lookup reading the index right after lists it.
    const keptEntry = {
      id: keptId,
      title: getPlannerTitle(copy.formData),
      updatedAt: new Date().toISOString(),
      ...meta,
    };
    writeGlobalIndex([keptEntry, ...readGlobalIndex().filter((entry) => entry?.id !== keptId)]);
      if (!readGlobalIndex().some((entry) => entry?.id === keptId) || !readPlannerCopy(keptId)) return false;
    setPlannerIndex((prev) => [
      keptEntry,
      ...(Array.isArray(prev) ? prev : []).filter((entry) => entry?.id !== keptId),
    ]);
    const planName =
      String(draft?.campaignName || draft?.formData?.campaignName || "").trim() || "this plan";
    setServerDraftList((list) => ({
      ...list,
      notice:
        reason === "legacy"
          ? `This device has changes to “${planName}” made after it was last saved. They are kept separately as “Unsaved changes to #${serverId}”.`
          : `Changes this device never saved to “${planName}” were made before the saved plan changed. They are kept separately as “Unsaved changes to #${serverId}”.`,
    }));
    return true;
  }, [localStorage, readGlobalIndex, readPlannerCopy, writeGlobalIndex]);


  // What autosave compares for a stored copy as it opens (the same inputs
  // loadPlannerById puts on the page), so opening a copy never saves it by
  // itself: only a new edit or Save Draft does.
  const storedCopyFingerprint = useCallback(
    (id) => {
      const copy = safeJsonParse(localStorage.getItem(makePlannerStorageKey(id)), null);
      if (!copy?.formData) return "";
      return buildDraftSaveFingerprint(
        normalizePlannerFormData(copy.formData),
        copy.strategy || "",
        Array.isArray(copy.creativeRecommendations)
          ? copy.creativeRecommendations
          : Array.isArray(copy.creativeVariations)
          ? copy.creativeVariations
          : [],
        copy.creativeGenerationMeta &&
          typeof copy.creativeGenerationMeta === "object" &&
          !Array.isArray(copy.creativeGenerationMeta)
          ? copy.creativeGenerationMeta
          : null
      );
    },
    [buildDraftSaveFingerprint, localStorage, normalizePlannerFormData]
  );

  // Open a saved draft into its one local entry (utils/plannerDraftIdentity):
  // the same saved draft always reopens the same entry, so opening it again
  // never lists it twice.
  const applyServerDraft = useCallback(
    (draft) => {
      const serverId = serverDraftIdOf(draft);
      if (!serverId) return false;
      let rememberedLocalId = "";
      try {
        rememberedLocalId = localStorage.getItem(makeActiveKeyForAdvertiser(advKey)) || "";
      } catch {}
      const resolvedLocalId = resolveLocalPlannerIdForServerDraft({
        draft,
        plannerIndex: readGlobalIndex(),
        readBinding: readPlannerBinding,
        advertiserKey: advKey,
        preferredLocalId: rememberedLocalId,
      });
      const binding = readPlannerBinding(resolvedLocalId);
      const recoveryRequired = Boolean(readPlannerCopy(resolvedLocalId)?.plannerRecoveryRequired || readPlannerCopy(resolvedLocalId)?.plannerExplicitSaveRequired);
      // A copy marked unsaved, based on an earlier saved version, whose inputs
      // are exactly the saved draft's holds nothing unsaved: its save
      // committed after the page closed. (Edits to this same version are
      // reopened as unsaved changes, whatever they touch.)
      const markedCopyMatchesDraft =
        !recoveryRequired && !receiptRecovery.hasPending(resolvedLocalId) &&
        binding?.unsavedChanges === true &&
        !shouldKeepUnsavedLocalCopy({ binding, draft }) &&
        String(readPlannerCopy(resolvedLocalId)?.strategy || "") === String(draft.strategy || "") &&
        JSON.stringify(readPlannerCopy(resolvedLocalId)?.creativeRecommendations || readPlannerCopy(resolvedLocalId)?.creativeVariations || []) === JSON.stringify(draft.creativeRecommendations || draft.creativeVariations || []) &&
        JSON.stringify(readPlannerCopy(resolvedLocalId)?.creativeGenerationMeta || null) === JSON.stringify(draft.creativeGenerationMeta || null) &&
        plannerFormsMatch(readPlannerCopy(resolvedLocalId)?.formData, draft.formData, {
          verticalConfig: plannerVerticalConfig,
        });
      const keepUnsavedCopy = Boolean(readPlannerCopy(resolvedLocalId)?.formData) && (recoveryRequired || receiptRecovery.hasPending(resolvedLocalId) || (!markedCopyMatchesDraft && shouldKeepUnsavedLocalCopy({ binding, draft })));
      // Edits the saved plan has moved past, or edits a copy the previous
      // planner left made after the plan was last saved, are kept as their own
      // plan first. If they cannot be stored, the saved plan opens in another
      // entry and that copy is left as it is.
      let nextLocalDraftId = resolvedLocalId;
      if (!keepUnsavedCopy) {
        const keptApart =
          binding?.unsavedChanges === true && !markedCopyMatchesDraft
            ? keepUnsavedCopyApart(resolvedLocalId, draft, { reason: "superseded" })
            : binding?.unsavedChanges === null && Boolean(readPlannerCopy(resolvedLocalId)?.formData)
            ? keepUnsavedCopyApart(resolvedLocalId, draft, { reason: "legacy" })
            : true;
        if (!keptApart) {
          // Reuse a clean server cache slot, but never overwrite another
          // distinct owned copy when preserving this one failed.
          const derivedId = localPlannerIdForServerDraft(serverId);
          let suffix = 0;
          nextLocalDraftId = derivedId;
          while (nextLocalDraftId === resolvedLocalId ||
            (readPlannerCopy(nextLocalDraftId)?.formData && readPlannerBinding(nextLocalDraftId)?.unsavedChanges !== false)) {
            suffix += 1;
            nextLocalDraftId = `${derivedId}_alt_${suffix}`;
          }
        }
      }
      const nextForm = normalizePlannerFormData(draft.formData || {});
      const nextStrategy = draft.strategy || "";
      const nextCreativeRecommendations = Array.isArray(draft.creativeRecommendations)
        ? draft.creativeRecommendations
        : Array.isArray(draft.creativeVariations)
        ? draft.creativeVariations
        : [];
      const nextCreativeGenerationMeta = draft.creativeGenerationMeta || null;
      // The saved version, as autosave and the save-first steps compare it:
      // a reopened plan with unsaved changes is saved before anything
      // generates from it.
      const savedFingerprint = buildDraftSaveFingerprint(
        nextForm,
        nextStrategy,
        nextCreativeRecommendations,
        nextCreativeGenerationMeta
      );

      pendingPlannerIdRef.current = "";
      serverDraftIdByPlannerRef.current.set(nextLocalDraftId, serverId);
      savedContentByPlannerRef.current.set(
        nextLocalDraftId,
        plannerSaveContentFingerprint(
          {
            formData: nextForm,
            strategy: nextStrategy,
            creatives: nextCreativeRecommendations,
            creativeMeta: nextCreativeGenerationMeta,
          },
          {
            advertiserId: nextForm.advertiserId ?? pageAdvertiserId ?? null,
            advertiser: String(nextForm.advertiser || pageAdvertiserName || "").trim(),
            verticalConfig: plannerVerticalConfig,
          }
        )
      );
      try {
        localStorage.setItem(makeActiveKeyForAdvertiser(advKey), nextLocalDraftId);
      } catch {}

      if (keepUnsavedCopy) {
        // This browser holds edits to exactly this saved version that were
        // never confirmed saved: reopen them as unsaved changes. Opening
        // saves nothing; the next edit or Save Draft stores them on this
        // same draft, and Generate saves them first.
        loadPlannerById(nextLocalDraftId, { explicit: true });
        setActivePlannerId(nextLocalDraftId);
        setServerDraftId(draft.serverDraftId || draft.id);
        setSessionMode("draft");
        lastDraftSaveFingerprintRef.current = savedFingerprint;
        openedPlanContentRef.current = {
          plannerId: nextLocalDraftId,
          fingerprint: storedCopyFingerprint(nextLocalDraftId),
        };
        setIsDirty(true);
        setSaveStatus("pending");
        setSaveMessage("Unsaved changes");
        return true;
      }
      writePlannerLocalState(nextLocalDraftId, {
        formData: nextForm,
        strategy: nextStrategy,
        creativeRecommendations: nextCreativeRecommendations,
        creativeVariations: nextCreativeRecommendations,
        creativeGenerationMeta: nextCreativeGenerationMeta,
        serverDraftId: draft.serverDraftId || draft.id || null,
        unsavedChanges: false,
        serverSavedAt: draft.savedAt || draft.updatedAt || null,
        plannerWriteVersion: draft.writeVersion || null,
        plannerRecoveryRequired: false,
      });

      setActivePlannerId(nextLocalDraftId);
      setServerDraftId(draft.serverDraftId || draft.id || null);
      setFormData(nextForm);
      setStrategy(nextStrategy);
      setCreativeRecommendations(nextCreativeRecommendations);
      setCreativeGenerationMeta(nextCreativeGenerationMeta);
      setRevisionPrompt("");
      setCanonicalAllocation(null);
      setStrategyPresentation(null);
      setAppliedRevisionSummary(null);
      planVersionRef.current = "";
      strategyPlanVersionRef.current = "";
      setStrategyProvenance(null);
      setCreativeError("");
      setError("");
      setSessionMode("draft");
      setActiveCampaignId(null);
      loadedCampaignSnapshotRef.current = null;
      upsertGlobalIndex(nextLocalDraftId, nextForm);
      lastDraftSaveFingerprintRef.current = savedFingerprint;
      openedPlanContentRef.current = { plannerId: nextLocalDraftId, fingerprint: savedFingerprint };
      setSaveStatus("saved");
      setSaveMessage("Saved");
      setIsDirty(false);
      seededDraftRef.current = { draftId: nextLocalDraftId };
      return true;
    },
    [receiptRecovery, advKey, buildDraftSaveFingerprint, keepUnsavedCopyApart, loadPlannerById, localStorage, normalizePlannerFormData, pageAdvertiserId, pageAdvertiserName, plannerVerticalConfig, readGlobalIndex, readPlannerBinding, readPlannerCopy, storedCopyFingerprint, upsertGlobalIndex, writePlannerLocalState]
  );

  const openPlannerOption = useCallback(
    (option) => {
      explicitNewPlanRef.current = "";
      if (option?.kind === "server") return option.draft ? applyServerDraft(option.draft) : false;
      if (option?.kind !== "local" || !option.localPlannerId) return false;
      if (!readPlannerCopy(option.localPlannerId)?.formData) {
        setServerDraftList((list) => ({
          ...list,
          notice: "That unsaved plan is no longer on this device. Choose another plan or start a new strategy.",
        }));
        return false;
      }
      pendingPlannerIdRef.current = "";
      setActiveCampaignId(null);
      loadedCampaignSnapshotRef.current = null;
      setActivePlannerId(option.localPlannerId);
      setSessionMode("draft");
      try {
        localStorage.setItem(makeActiveKeyForAdvertiser(advKey), option.localPlannerId);
      } catch {}
      loadPlannerById(option.localPlannerId, { explicit: true });
      // A plan never saved has no saved version: opening it saves nothing
      // (autosave waits for its first change), and Generate saves it first.
      lastDraftSaveFingerprintRef.current = "";
      openedPlanContentRef.current = {
        plannerId: option.localPlannerId,
        fingerprint: storedCopyFingerprint(option.localPlannerId),
      };
      setSaveStatus("pending");
      setSaveMessage("Not saved yet");
      return true;
    },
    [advKey, applyServerDraft, loadPlannerById, localStorage, readPlannerCopy, storedCopyFingerprint]
  );

  // Entering the planner and Load Draft open the plan to work on from the
  // advertiser's saved drafts (utils/plannerDraftIdentity choosePlannerResume):
  // an explicit draft id, else the plan this browser last worked on, else the
  // only plan. Several plans and nothing to prefer: the operator chooses. No
  // plans on entry: an unsaved blank plan. Nothing here creates a draft, and a
  // failed lookup opens nothing and offers Retry.
  const resumePlannerDrafts = useCallback(
    async ({ source = "entry" } = {}) => {
      if (!advKey || advKey === "unknown") return;
      const reqId = ++draftListReqRef.current;
      const advertiserScopeGenerationAtStart = advertiserScopeGenerationRef.current;
      const manualOverrideSeqAtStart = manualPlannerOverrideSeqRef.current;
      const formEditGenerationAtStart = formEditGenerationRef.current;
      draftsSavedDuringLookupRef.current = { reqId, drafts: [], open: true };
      setServerDraftList({ status: "loading", advertiserKey: advKey, drafts: [], notice: "", source });

      const result = await fetchServerDraftsForAdvertiser();
      // The drafts saved while this lookup ran; collecting stops with it.
      const savedDuringLookup =
        draftsSavedDuringLookupRef.current.reqId === reqId ? draftsSavedDuringLookupRef.current.drafts : [];
      if (draftsSavedDuringLookupRef.current.reqId === reqId) {
        draftsSavedDuringLookupRef.current = { reqId: 0, drafts: [], open: false };
      }
      if (!plannerSession.isCurrent() || reqId !== draftListReqRef.current) return;
      if (
        advertiserScopeGenerationAtStart !== advertiserScopeGenerationRef.current ||
        manualOverrideSeqAtStart !== manualPlannerOverrideSeqRef.current
      ) {
        // The operator moved on (another advertiser, plan or campaign): this
        // lookup opens nothing and stops showing as loading.
        setServerDraftList((list) => (list.status === "loading" ? { ...list, status: "idle" } : list));
        return;
      }
      if (!result.ok && result.reason === "no_advertiser_id") {
        // Saved plans are listed by advertiser id; without one there is
        // nothing to retry. A new, unsaved plan is open instead.
        setServerDraftList({ status: "unavailable", advertiserKey: advKey, drafts: [], notice: "", source });
        if (formEditGenerationRef.current === formEditGenerationAtStart) startBlankPlan();
        return;
      }
      if (!result.ok) {
        setServerDraftList({ status: "error", advertiserKey: advKey, drafts: [], notice: "", source });
        setSessionMode("draft");
        return;
      }

      // Drafts saved while this lookup ran may have landed after the server
      // read the list; the newer version of each is kept.
      let drafts = result.drafts;
      for (const saved of savedDuringLookup) {
        const listed = drafts.find((entry) => serverDraftIdOf(entry) === serverDraftIdOf(saved));
        const savedTime = Date.parse(String(saved?.savedAt || saved?.updatedAt || "")) || 0;
        const listedTime = Date.parse(String(listed?.savedAt || listed?.updatedAt || "")) || 0;
        if (sameAdvertiserId(saved?.advertiserId, pageAdvertiserId ?? formData.advertiserId) && (!listed || savedTime >= listedTime)) {
          drafts = upsertServerDraft(drafts, saved);
        }
      }

      // A saved row can have several distinct owned browser edits. Keep the
      // preferred copy attached for ordinary same-version resume; expose every
      // other variant independently before logical-plan grouping can hide it.
      for (const draft of drafts) {
        const index = readGlobalIndex();
        const preferredLocalId = localStorage.getItem(makeActiveKeyForAdvertiser(advKey)) || "";
        const attachedId = resolveLocalPlannerIdForServerDraft({
          draft, plannerIndex: index, readBinding: readPlannerBinding,
          advertiserKey: advKey, preferredLocalId,
        });
        for (const entry of index) {
          const binding = readPlannerBinding(entry?.id);
          if (readPlannerCopy(entry?.id)?.plannerRecoveryRequired || readPlannerCopy(entry?.id)?.plannerExplicitSaveRequired || receiptRecovery.hasPending(entry?.id) || entry?.id === attachedId || entry?.advertiserKey !== advKey ||
              binding?.unsavedChanges !== true ||
              normalizeServerDraftId(binding.serverDraftId) !== serverDraftIdOf(draft)) continue;
          if (keepUnsavedCopyApart(entry.id, draft)) {
            writePlannerLocalState(entry.id, { unsavedChanges: false });
          }
        }
      }

      // Preserve every owned unmarked variant. A newer copy is not proof
      // that an older, distinct copy was saved. Ownerless copies are excluded
      // by the storage adapter and are never read or changed here.
      const unmarked = unmarkedCopiesOfListedDrafts({
        serverDrafts: drafts,
        plannerIndex: readGlobalIndex(),
        readBinding: readPlannerBinding,
        advertiserKey: advKey,
      });
      for (const { localPlannerId, draft } of unmarked) {
        if (readPlannerCopy(localPlannerId)?.plannerRecoveryRequired || readPlannerCopy(localPlannerId)?.plannerExplicitSaveRequired || receiptRecovery.hasPending(localPlannerId)) continue;
        if (!keepUnsavedCopyApart(localPlannerId, draft, { reason: "legacy" })) continue;
        writePlannerLocalState(localPlannerId, { unsavedChanges: false });
      }

      const index = readGlobalIndex();
      setPlannerIndex(index);
      const options = buildPlannerDraftOptions({
        serverDrafts: drafts,
        plannerIndex: index,
        readBinding: readPlannerBinding,
        advertiserKey: advKey,
        hasLocalContent: plannerLocalCopyHasContent,
      });
      const decision = choosePlannerResume({
        options,
        explicitServerDraftId: source === "entry" ? routeDraftId : "",
        choice: readPlannerChoice(advKey),
        readBinding: readPlannerBinding,
      });
      // A notice a kept-apart copy set during this lookup stays.
      setServerDraftList((list) => ({
        status: "ready",
        advertiserKey: advKey,
        drafts,
        source,
        notice:
          decision.action === "explicit_not_found"
            ? "That saved plan isn’t available for this advertiser. Choose one of its saved plans."
            : list.advertiserKey === advKey && list.status === "loading"
            ? list.notice || ""
            : "",
      }));
      // An edit made while the list loaded is newer: never replace it.
      if (formEditGenerationRef.current !== formEditGenerationAtStart) return;

      if (decision.action === "resume" && openPlannerOption(decision.option)) {
        // A link to one saved plan is the operator's choice of it.
        if (decision.reason === "explicit") rememberPlannerChoice(advKey, plannerChoiceOfOption(decision.option));
        return;
      }
      startBlankPlan();
      // On entry with no plans: a new unsaved plan. Otherwise (several plans,
      // none on Load Draft, an unknown explicit id): the choice, nothing open.
      if (!(decision.action === "new" && source === "entry")) setSessionMode("draft");
    },
    [receiptRecovery, advKey, fetchServerDraftsForAdvertiser, formData.advertiserId, keepUnsavedCopyApart, localStorage, openPlannerOption, pageAdvertiserId, plannerLocalCopyHasContent, plannerSession, readGlobalIndex, readPlannerBinding, readPlannerChoice, readPlannerCopy, rememberPlannerChoice, routeDraftId, startBlankPlan, writePlannerLocalState]
  );
  resumePlannerDraftsRef.current = resumePlannerDrafts;

  // A creative run writes onto this draft when it finishes, and the backend
  // refuses that write if the draft moved meanwhile, so autosave waits for
  // it: the pending timer is cleared when a run starts and rescheduled after.
  const creativeRunActive =
    generationLoadingType === GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS;
  useEffect(() => {
    if (campaignIdFromNav || !activePlannerId) return undefined;
    if (creativeRunActive) return undefined;
    if (!hasSaveablePlannerContent(formData, strategy)) return undefined;

    const fingerprint = buildDraftSaveFingerprint(formData, strategy);
    if (fingerprint === lastDraftSaveFingerprintRef.current) {
      // A save left this plan "Unsaved changes" before the page had rendered
      // what it sent; the page now holds exactly that, so it is saved, unless
      // a save of other content is still writing (it answers and re-checks).
      if (unsavedAfterSaveRef.current && unsavedAfterSaveRef.current === activePlannerId) {
        const shownContent = plannerSaveContentFingerprint(latestPlannerContentRef.current, {
          advertiserId: formData.advertiserId ?? pageAdvertiserId ?? null,
          advertiser: String(formData.advertiser || pageAdvertiserName || "").trim(),
          verticalConfig: plannerVerticalConfig,
        });
        if (!otherContentInFlight(savesInFlightRef.current, activePlannerId, shownContent)) {
          unsavedAfterSaveRef.current = "";
          setSaveStatus("saved");
          setSaveMessage("Saved");
          setIsDirty(false);
        }
      }
      return undefined;
    }
    // Opening a plan saves nothing: autosave waits for its first change, and
    // from that change on saves as usual (a change back included).
    const opened = openedPlanContentRef.current;
    if (opened && opened.plannerId === activePlannerId) {
      if (opened.fingerprint === fingerprint) return undefined;
      openedPlanContentRef.current = null;
    }

    setSaveStatus("pending");
    setSaveMessage("Autosave pending");

    if (draftAutosaveTimerRef.current) clearTimeout(draftAutosaveTimerRef.current);
    draftAutosaveTimerRef.current = setTimeout(() => {
      if (!plannerSession.isCurrent() || creativeGenerationInFlightRef.current) return;
      persistPlannerDraftToServer({ explicit: false });
    }, 800);

    return () => {
      if (draftAutosaveTimerRef.current) clearTimeout(draftAutosaveTimerRef.current);
    };
  }, [activePlannerId, autosaveNudge, buildDraftSaveFingerprint, campaignIdFromNav, creativeRunActive, formData, hasSaveablePlannerContent, pageAdvertiserId, pageAdvertiserName, persistPlannerDraftToServer, plannerSession, plannerVerticalConfig, strategy]);

  // ---------------------------
  // Handlers
  // ---------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (campaignIdFromNav) {
      setError("This Campaign is read-only in the Client Portal. Use 'Copy to New Planner' to edit.");
      return;
    }

    if (name === "budget") {
      // Keystrokes carry the operator's editing representation: sanitized to
      // the permitted characters but NEVER re-normalized (no display
      // formatting, no alias fallback) — canonicalization happens at
      // hydration/persistence boundaries. Running it per keystroke made
      // cleared fields snap back to stale source-campaign values.
      formEditGenerationRef.current += 1;
      const sanitizedBudget = normalizeBudgetInputValue(value);
      setFormData((prev) => ({
        ...prev,
        budget: sanitizedBudget,
        netMediaBudget: resolveNetMediaBudgetValue(
          { grossBudget: sanitizedBudget, targetMarginPercent: prev.targetMarginPercent },
          prev.netMediaBudget
        ),
      }));
      setIsDirty(true);
      return;
    }

    if (name === "advertiser") {
      setError("Use the advertiser selector above to switch advertisers.");
      return;
    }

    formEditGenerationRef.current += 1;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "gender" ? { plannerInputIntent: { ...prev.plannerInputIntent, gender: true } } : {}),
    }));
    setIsDirty(true);
  };

  const handleEarnedMediaProfileUrlChange = (index, value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.earnedMediaProfileUrls)
        ? [...prev.earnedMediaProfileUrls]
        : ["", "", ""];
      current[index] = value;
      return { ...prev, earnedMediaProfileUrls: current };
    });
    setIsDirty(true);
  };

  const handleAddEarnedMediaProfileUrl = () => {
    setFormData((prev) => {
      const current = Array.isArray(prev.earnedMediaProfileUrls)
        ? [...prev.earnedMediaProfileUrls]
        : ["", "", ""];
      if (current.length >= EARNED_MEDIA_MAX_PROFILE_FIELDS) return prev;
      return { ...prev, earnedMediaProfileUrls: [...current, ""] };
    });
    setIsDirty(true);
  };

  // ---------------------------
  // Eden-assisted intake draft (Apply to Planner)
  // ---------------------------
  useEffect(() => {
    const handlePlannerDraft = (event) => {
      const result = event?.detail?.result;
      if (!result || typeof result !== "object") return;
      setEdenDraftAppliedNote("");
      setEdenDraftResult(result);
    };
    window.addEventListener(EDEN_PLANNER_DRAFT_EVENT, handlePlannerDraft);
    return () => window.removeEventListener(EDEN_PLANNER_DRAFT_EVENT, handlePlannerDraft);
  }, []);

  const computeEdenDraftPreview = useCallback(
    (overwriteFields = []) => {
      if (!edenDraftResult?.draft) return null;
      return mapPlannerDraftToCortexForm(edenDraftResult.draft, formData, { overwriteFields });
    },
    [edenDraftResult, formData]
  );

  const handleApplyEdenDraft = useCallback(
    ({ overwriteFields = [] } = {}) => {
      if (campaignIdFromNav) {
        setError("This Campaign is read-only in the Client Portal. Use 'Copy to New Planner' to edit.");
        return;
      }
      if (!edenDraftResult?.draft) return;
      // Applied fields belong to a plan: never to a form that is still
      // loading saved plans, offering a choice, or choosing a campaign.
      if (
        (serverDraftList.status === "loading" && serverDraftList.advertiserKey === advKey) ||
        (sessionMode === "draft" && !activePlannerId) ||
        (sessionMode === "campaign" && !activeCampaignId)
      ) {
        setEdenDraftAppliedNote("Open a plan or start a new strategy first, then Apply to Planner.");
        return;
      }

      try {
        const { nextForm, applied, conflicts } = mapPlannerDraftToCortexForm(
          edenDraftResult.draft,
          formData,
          { overwriteFields }
        );

        if (!applied.length) {
          setEdenDraftAppliedNote(
            "Nothing applied — those fields already have values. Tick a field to confirm an overwrite."
          );
          return;
        }

        // EDEN may carry historical provider labels. Use the same registry-backed
        // normalization as hydration/save before the selected chips can render.
        setFormData({ ...nextForm, platforms: orderPlannerPlatformKeys(nextForm.platforms) });
        setIsDirty(true);
        setError("");

        const remainingConflicts = conflicts.length
          ? ` ${conflicts.length} field${conflicts.length === 1 ? "" : "s"} kept your existing values — confirm to overwrite.`
          : "";
        setEdenDraftAppliedNote(
          `Applied ${applied.length} field${applied.length === 1 ? "" : "s"}. Everything is editable before you generate a strategy.${remainingConflicts}`
        );
      } catch (err) {
        console.error("[Client SherlocMediaPlanner] Eden draft apply failed:", err);
        setEdenDraftAppliedNote(
          "I couldn’t apply this to the planner yet. Your intake was captured — please try Apply to Planner again."
        );
      }
    },
    [activeCampaignId, activePlannerId, advKey, campaignIdFromNav, edenDraftResult, formData, serverDraftList, sessionMode]
  );

  const handleDismissEdenDraft = useCallback(() => {
    setEdenDraftResult(null);
    setEdenDraftAppliedNote("");
  }, []);

  const handlePlatformToggle = (platformKey) => {
    if (campaignIdFromNav) {
      setError("This Campaign is read-only in the Client Portal. Use 'Copy to New Planner' to edit.");
      return;
    }
    if (
      !isClientSelectablePlannerPlatform(platformKey) &&
      !(Array.isArray(formData.platforms) ? formData.platforms : []).includes(platformKey)
    ) {
      return;
    }

    setFormData((prev) => {
      const list = Array.isArray(prev.platforms) ? prev.platforms : [];
      const alreadySelected = list.includes(platformKey);
      if (!alreadySelected && !isClientSelectablePlannerPlatform(platformKey)) return prev;
      const nextPlatforms = alreadySelected
        ? list.filter((p) => p !== platformKey)
        : [...list, platformKey];
      return {
        ...prev,
        platforms: orderPlannerPlatformKeys(nextPlatforms),
      };
    });

    setIsDirty(true);
  };

  const exitCampaignModeToDrafts = useCallback(({ restoreDraft = true } = {}) => {
    beginManualPlannerOverride();
    setNavCampaignId(null);
    setAutoLoadedSoleCampaign(false);
    clearPlannerRouteState();
    setSessionMode("draft");
    if (restoreDraft) bootstrapForAdvertiser({ loadDraft: true });
  }, [beginManualPlannerOverride, clearPlannerRouteState, bootstrapForAdvertiser]);

  const handleNewPlanner = useCallback(() => {
    beginManualPlannerOverride();
    setAutoLoadedSoleCampaign(false);
    setError("");
    setCreativeRecommendations([]);
    setCreativeGenerationMeta(null);
    setCreativeError("");
    setRevisionPrompt("");
    setLastAppliedRevisionPrompt("");
    setCanonicalAllocation(null);
    setStrategyPresentation(null);
    setAppliedRevisionSummary(null);
    planVersionRef.current = "";
    strategyPlanVersionRef.current = "";
    setStrategyProvenance(null);
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    setIsDirty(false);

    // Leaving a campaign for a new plan does not reload the drafts first.
    if (campaignIdFromNav) exitCampaignModeToDrafts({ restoreDraft: false });

    if (!advKey || advKey === "unknown") {
      setError("Select an advertiser first.");
      return;
    }

    // A new plan is created by its first meaningful edit, not by this click.
    // A saved-plan lookup still in flight is abandoned, and a failed one's
    // message and Retry no longer apply to the new plan (Load Draft retries).
    draftListReqRef.current += 1;
    setServerDraftList((list) =>
      list.status === "loading" || list.status === "error"
        ? { ...list, status: "idle", notice: "" }
        : { ...list, notice: "" }
    );
    startBlankPlan();
    // The operator chose a new plan: it is their choice once it has an identity.
    explicitNewPlanRef.current = advKey;
  }, [
    beginManualPlannerOverride,
    campaignIdFromNav,
    exitCampaignModeToDrafts,
    advKey,
    startBlankPlan,
  ]);

  const handlePlannerModeSelect = useCallback(
    async (nextMode) => {
      if (!advKey || advKey === "unknown") {
        setError("Select an advertiser first.");
        return;
      }

      if (nextMode === "new") {
        handleNewPlanner();
        return;
      }

      beginManualPlannerOverride();
      const advertiserScopeGenerationAtStart =
        advertiserScopeGenerationRef.current;
      setError("");
      setCreativeRecommendations([]);
      setCreativeGenerationMeta(null);
      setCreativeError("");
      setActiveCampaignId(null);
      loadedCampaignSnapshotRef.current = null;
      // No plan is open now: neither its row nor an identity minted for it
      // carries over to whatever is saved next.
      setActivePlannerId("");
      setServerDraftId(null);
      pendingPlannerIdRef.current = "";
      openedPlanContentRef.current = null;
      setNavCampaignId(null);
      setAutoLoadedSoleCampaign(false);
      clearPlannerRouteState();
      setFormData(
        normalizePlannerFormData({
          ...BASE_BLANK_FORM,
          advertiserId: pageAdvertiserId ?? null,
          advertiser: String(pageAdvertiserName || "").trim(),
        })
      );
      setStrategy("");
      setStrategyPresentation(null);
      setIsDirty(false);
      setSaveStatus("idle");
      setSaveMessage("");
      setSessionMode(nextMode);

      if (nextMode === "draft") {
        // Load Draft opens a saved plan by the same rules as entering the
        // planner; with several and none to prefer, the operator chooses.
        await resumePlannerDrafts({ source: "load_draft" });
        return;
      }
      // Choosing a campaign abandons a saved-plan lookup still in flight.
      draftListReqRef.current += 1;
      setServerDraftList((list) => (list.status === "loading" ? { ...list, status: "idle" } : list));

      const reqId = ++campaignListReqRef.current;
      const list = await refreshCampaignsForCurrentAdvertiser({
        allowAutoSelectLatestIfNoDrafts: false,
        reqId,
      });
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      const { campaignId: targetCampaignId, autoLoadedSole } =
        resolvePlannerCampaignAutoSelection(list);

      if (targetCampaignId && reqId === campaignListReqRef.current) {
        setAutoLoadedSoleCampaign(autoLoadedSole);
        setNavCampaignId(targetCampaignId);
      }
    },
    [
      advKey,
      beginManualPlannerOverride,
      handleNewPlanner,
      clearPlannerRouteState,
      normalizePlannerFormData,
      BASE_BLANK_FORM,
      pageAdvertiserId,
      pageAdvertiserName,
      resumePlannerDrafts,
      refreshCampaignsForCurrentAdvertiser,
    ]
  );

  const handleCopyToNewPlanner = () => {
    beginManualPlannerOverride();
    setError("");
    setCreativeError("");
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;
    setIsDirty(false);

    if (campaignIdFromNav) {
      const seed = {
        id: formData.advertiserId ?? pageAdvertiserId ?? null,
        name: String(formData.advertiser || pageAdvertiserName || "").trim(),
      };

      // The new copy is the selected draft. Restoring the previous draft in
      // parallel can overwrite its platforms and budget when that load resolves.
      exitCampaignModeToDrafts({ restoreDraft: false });
      createPlanner({ copyFromCurrent: true, seedAdvertiser: seed });
      return;
    }

    createPlanner({ copyFromCurrent: true });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (fullStrategyIntentRef.current || generationSubmitLatchRef.current ||
      isSherlocGenerationOperationActive(generationOperationRef.current)) return;
    if (isLoadingCampaign || ((activeCampaignId || campaignIdFromNav) && !fullStrategyRequestAvailable)) {
      setError(currentCurrencyAction?.notice?.nextAction || STRATEGY_REQUEST_UNAVAILABLE);
      return;
    }
    const advertiserScopeGenerationAtStart =
      advertiserScopeGenerationRef.current;
    setError("");
    setCreativeError("");

    const effectiveAdvertiserId = formData.advertiserId ?? pageAdvertiserId ?? null;
    const effectiveAdvertiserName = String(formData.advertiser || pageAdvertiserName || "").trim();
    const normalizedFormData = gatePlannerReleaseDateForVertical(
      normalizePlannerFormData(formData),
      plannerVerticalConfig
    );
    const strategyRequestId = makePlannerRequestId();
    const strategyStartedAt = Date.now();
    const strategyContext = {
      advertiserId: effectiveAdvertiserId,
      campaignId: activeCampaignId || null,
      requestId: strategyRequestId,
    };

    if (!API_BASE_URL) {
      logSherlocStrategyRefreshFailure("missing_api_base", strategyContext);
      setError(STRATEGY_REFRESH_RECOVERY_MESSAGE);
      return;
    }

    if (!effectiveAdvertiserId) {
      setError("No advertiser is selected. Use the advertiser selector above.");
      return;
    }

    const missingFields = getMissingPlannerGenerationFields(normalizedFormData);
    if (missingFields.length) {
      logSherlocStrategyRefreshFailure("missing_required_fields", {
        ...strategyContext,
        missingFields,
      });
      // Show the exact missing fields instead of a generic message.
      setError(buildStrategyValidationMessage(missingFields));
      return;
    }

    const urlValidationError = getPlannerUrlValidationError(normalizedFormData, plannerVerticalConfig);
    if (urlValidationError) {
      setError(urlValidationError);
      return;
    }

    const authToken = getSherlocMediaPlannerAuthToken();
    if (!authToken) {
      setError("You are not authenticated. Please log in again.");
      return;
    }

    // Duplicate clicks (or an already-active durable operation) must never
    // start a second generation: exactly one POST per user intent.
    if (
      generationSubmitLatchRef.current ||
      isSherlocGenerationOperationActive(generationOperationRef.current)
    ) {
      return;
    }

    if (JSON.stringify(normalizedFormData) !== JSON.stringify(formData)) {
      setFormData(normalizedFormData);
    }
    const nextLoadingType = String(activeCampaignId || campaignIdFromNav || "").trim()
      ? GENERATION_LOADING_TYPES.REFRESH_STRATEGY
      : GENERATION_LOADING_TYPES.STRATEGY;
    setGenerationLoadingType(nextLoadingType);
    const intent = Symbol("full-strategy-request");
    fullStrategyIntentRef.current = intent;
    const contextAtStart = fullStrategyContextRef.current;
    const loadAtStart = campaignLoadReqRef.current;
    const editAtStart = formEditGenerationRef.current;
    const stillOwned = () => fullStrategyIntentRef.current === intent &&
      contextAtStart === fullStrategyContextRef.current &&
      loadAtStart === campaignLoadReqRef.current &&
      editAtStart === formEditGenerationRef.current &&
      advertiserScopeGenerationAtStart === advertiserScopeGenerationRef.current &&
      authToken === getSherlocMediaPlannerAuthToken();

    try {
      // Draft persistence stays, but OUTSIDE any generation timing: the
      // durable operations path has NO client-side generation timeout — the
      // server runs the generation and this page only polls its status.
      const savedDraft = await persistPlannerDraftToServer({
        explicit: true,
        requireSave: true,
      });
      if (!stillOwned()) return;
      if (savedDraft === false) return;
      const sourceDraft =
        !activeCampaignId && !campaignIdFromNav && savedDraft
          ? savedDraft
          : null;

      const prompt = buildMediaPlannerPrompt({
        ...normalizedFormData,
        advertiserId: effectiveAdvertiserId,
        advertiser: effectiveAdvertiserName,
        advertiserVertical: activePlannerAdvertiserVertical,
      });
      const targetingPayload = buildPlannerTargetingPayload(normalizedFormData);

      // Re-read the actual canonical route at the point of intent. A changed
      // operator decision or failed read must not be bypassed by an older UI.
      // This read does not authorize generation: the operations route and
      // execution core still enforce their independent checks on every POST.
      const targetCampaignId = activeCampaignId || campaignIdFromNav;
      if (targetCampaignId) {
        let latest = null;
        try {
          const response = await fetch(`${safeBase(API_BASE_URL)}${buildStrategyNextActionPath(targetCampaignId)}`, {
            headers: { Accept: "application/json", Authorization: `Bearer ${authToken}` },
            cache: "no-store",
          });
          latest = response.ok ? normalizeStrategyNextAction(await response.json()) : null;
          if (String(latest?.campaignId) !== String(targetCampaignId)) latest = null;
        } catch {
          // A failed status read is not a failed generation (none was sent).
        }
        if (!stillOwned()) return;
        if (!canRequestFullStrategy(latest, targetCampaignId)) {
          setFullStrategyReadRefusal({ basis: currencyRead, next: latest });
          setError(latest?.notice?.nextAction || STRATEGY_REQUEST_UNAVAILABLE);
          return;
        }
      }
      if (!stillOwned()) return;

      // POST /api/sherloc/media-planner/operations — same payload as the
      // legacy sync endpoint plus clientRequestId, then poll GET /:id.
      const outcome = await startSherlocDurableGeneration({
        latchRef: generationSubmitLatchRef,
        activeOperation: generationOperationRef.current,
        submit: () =>
          createSherlocGenerationOperation({
            apiBase: safeBase(API_BASE_URL),
            token: authToken,
            requestId: strategyRequestId,
            // An approved allocation travels as the campaign holds it — never
            // as the saved form's copy, which allocation approval does not update.
            body: applyApprovedAllocationToRefreshRequest({
              clientRequestId: strategyRequestId,
              formData: normalizedFormData,
              ...normalizedFormData,
              advertiserId: effectiveAdvertiserId,
              advertiser_id: effectiveAdvertiserId,
              advertiser: effectiveAdvertiserName,
              advertiser_name: effectiveAdvertiserName,
              advertiserVertical: activePlannerAdvertiserVertical,
              advertiser_vertical: activePlannerAdvertiserVertical,
              objective: normalizedFormData.goalType,
              goalType: normalizedFormData.goalType,
              goal_type: normalizedFormData.goalType,
              primaryKPI: normalizedFormData.primaryKPI,
              primaryKpi: normalizedFormData.primaryKPI,
              primary_kpi: normalizedFormData.primaryKPI,
              notes: normalizedFormData.notes,
              additionalNotes: normalizedFormData.notes,
              additional_notes: normalizedFormData.notes,
              budget: normalizedFormData.budget,
              totalBudget: normalizedFormData.budget,
              total_budget: normalizedFormData.budget,
              grossBudget: normalizedFormData.budget,
              gross_budget: normalizedFormData.budget,
              netMediaBudget: normalizedFormData.netMediaBudget,
              net_media_budget: normalizedFormData.netMediaBudget,
              targetMarginPercent: normalizedFormData.targetMarginPercent,
              target_margin_percent: normalizedFormData.targetMarginPercent,
              targetMargin: normalizedFormData.targetMarginPercent,
              target_margin: normalizedFormData.targetMarginPercent,
              targetPlatforms: normalizedFormData.platforms,
              target_platforms: normalizedFormData.platforms,
              destinationUrl: normalizedFormData.destinationUrl,
              destination_url: normalizedFormData.destinationUrl,
              imdbUrl: normalizedFormData.imdbUrl,
              imdb_url: normalizedFormData.imdbUrl,
              rottenTomatoesUrl: normalizedFormData.rottenTomatoesUrl,
              rotten_tomatoes_url: normalizedFormData.rottenTomatoesUrl,
              ageMin: targetingPayload.ageMin,
              age_min: targetingPayload.age_min,
              ageMax: targetingPayload.ageMax,
              age_max: targetingPayload.age_max,
              gender: targetingPayload.gender,
              interests: targetingPayload.interests,
              customTargeting: targetingPayload.customTargeting,
              custom_targeting: targetingPayload.custom_targeting,
              geoTargetingNotes: targetingPayload.geoTargetingNotes,
              geo_targeting_notes: targetingPayload.geo_targeting_notes,
              geoTargeting: targetingPayload.geoTargeting,
              geo_targeting: targetingPayload.geo_targeting,
              locationTargeting: targetingPayload.locationTargeting,
              location_targeting: targetingPayload.location_targeting,
              languageTargeting: targetingPayload.languageTargeting,
              language_targeting: targetingPayload.language_targeting,
              deviceTargetingNotes: targetingPayload.deviceTargetingNotes,
              device_targeting_notes: targetingPayload.device_targeting_notes,
              devicePlatformNotes: targetingPayload.devicePlatformNotes,
              device_platform_notes: targetingPayload.device_platform_notes,
              flightStart: normalizedFormData.flightStart,
              flight_start_date: normalizedFormData.flightStart,
              flightEnd: normalizedFormData.flightEnd,
              flight_end_date: normalizedFormData.flightEnd,
              releaseDate: normalizedFormData.releaseDate,
              release_date: normalizedFormData.releaseDate,
              targeting: targetingPayload,
              creativeRecommendations,
              creativeVariations: creativeRecommendations,
              creativeGenerationMeta,
              campaignId: activeCampaignId || campaignIdFromNav || null,
              campaign_id: activeCampaignId || campaignIdFromNav || null,
              serverDraftId: sourceDraft?.serverDraftId || sourceDraft?.id || null,
              server_draft_id: sourceDraft?.serverDraftId || sourceDraft?.id || null,
              localDraftId: sourceDraft?.localDraftId || activePlannerId || null,
              local_draft_id: sourceDraft?.localDraftId || activePlannerId || null,
              sourceDraftRevision: sourceDraft?.draftRevision || null,
              sourceDraftWriteVersion: sourceDraft?.writeVersion || loadedCampaignSnapshotRef.current?.planner_write_version || readPlannerCopy(activePlannerId)?.plannerWriteVersion || null,
              source_draft_revision: sourceDraft?.draftRevision || null,
              sourceDraftFingerprint: sourceDraft?.governedInputFingerprint || null,
              source_draft_fingerprint: sourceDraft?.governedInputFingerprint || null,
              prompt,
            }, {
              campaignId: targetCampaignId || null,
              allocationAuthority,
              canonicalAllocation,
            }),
          }),
      });

      if (!stillOwned()) return;
      if (outcome.kind === "duplicate_suppressed") return;
      if (outcome.kind === "completed_replay") {
        // Identical inputs already completed server-side: apply the stored
        // result envelope WITHOUT regenerating.
        applyGenerationOperationSuccess(outcome.result, { kind: "generate" });
        clearGenerationOperation();
        return;
      }
      if (outcome.kind === "track") {
        // 201 queued / 200 reused / 409 operation_in_progress reconnect:
        // adopt the operation and poll GET /:id every 4s (no client timeout).
        beginTrackingGenerationOperation(outcome.operation, { kind: "generate" });
        return;
      }
      if (outcome.kind === "terminal_rejection") {
        // 409 operation_terminally_rejected: no new operation exists and none
        // will. Adopt the PRIOR rejected operation so the surface shows its
        // real terminal disposition and findings; beginTracking terminalizes
        // it immediately (it is not active) instead of polling an id that was
        // never created — which is what stranded campaign 50 in UPDATING….
        logSherlocStrategyRefreshFailure(
          "operation_terminally_rejected",
          strategyContext
        );
        beginTrackingGenerationOperation(outcome.operation, { kind: "generate" });
        return;
      }
      // 2xx acknowledgement without an operation: labeled unknown failure.
      logSherlocStrategyRefreshFailure("operation_unacknowledged", strategyContext);
      setError(buildUnknownFailureMessage(strategyRequestId));
    } catch (err) {
      if (!stillOwned()) return;
      const isNetwork = isPlannerNetworkError(err);
      const isRefusal =
        !isNetwork && (err?.status === 403 || err?.status === 409);
      const failureCode = isNetwork
        ? "network_error"
        : err?.backendCode || err?.code || "unexpected_error";
      logSherlocStrategyRefreshFailure(failureCode, {
        ...strategyContext,
        status: err?.status,
      });
      logPlannerFailureDebug({
        url: SHERLOC_GENERATION_OPERATIONS_PATH,
        status: err?.status,
        code: failureCode,
        aborted: false,
        errorName: err?.name,
        errorMessage: err?.message,
        elapsedMs: Date.now() - strategyStartedAt,
        requestId: strategyRequestId,
        missingFields: err?.missingFields,
      });
      // Specific, calm copy per failure category. Existing strategy stays
      // visible. A network/no-response failure has no backend log, so it must
      // not show a backend reference id; a 403/409 refusal (e.g. a client
      // asked to regenerate past DRAFT) surfaces the backend's own refusal
      // message as a labeled state; everything else maps to its category.
      setError(
        isNetwork
          ? STRATEGY_NETWORK_FAILURE_MESSAGE
          : isRefusal
          ? buildGenerationRefusalMessage(err?.message)
          : mapStrategyFailureToMessage({
              status: err?.status,
              code: err?.backendCode || err?.code,
              category: err?.backendCategory,
              missingFields: err?.missingFields,
              requestId: strategyRequestId,
              // 422 planner_prompt_scope_review_required carries the
              // preflight findings; other failures carry nothing here.
              preflight: err?.payload?.preflight,
            })
      );
    } finally {
      if (fullStrategyIntentRef.current === intent) fullStrategyIntentRef.current = null;
      if (
        advertiserScopeGenerationAtStart ===
        advertiserScopeGenerationRef.current
      ) {
        setGenerationLoadingType("");
      }
    }
  };

  const handleApplyRevision = async () => {
    const advertiserScopeGenerationAtStart =
      advertiserScopeGenerationRef.current;
    setError("");
    setCreativeError("");

    if (!String(strategy || "").trim()) {
      setError("Generate a media strategy first, then apply a revision prompt.");
      return;
    }

    if (campaignIdFromNav) {
      setError(
        "This loaded campaign is read-only in the Client Portal. Use 'Copy to New Planner' to revise it."
      );
      return;
    }

    const revisionInstruction = String(revisionPrompt || "").trim();
    if (!revisionInstruction) {
      setError("Enter a revision prompt before applying an AI revision.");
      return;
    }

    // Duplicate clicks (or an already-active durable operation) must never
    // start a second revision generation: exactly one POST per user intent.
    if (
      generationSubmitLatchRef.current ||
      isSherlocGenerationOperationActive(generationOperationRef.current)
    ) {
      return;
    }

    setIsLoading(true);
    setGenerationLoadingType(GENERATION_LOADING_TYPES.REFRESH_STRATEGY);

    const revisionRequestId = makePlannerRequestId();
    const revisionContext = {
      advertiserId: formData.advertiserId ?? pageAdvertiserId ?? null,
      campaignId: activeCampaignId || campaignIdFromNav || null,
      requestId: revisionRequestId,
    };

    try {
      if (!API_BASE_URL) throw new Error("API base URL is not defined.");

      const effectiveAdvertiserId = formData.advertiserId ?? pageAdvertiserId ?? null;
      const effectiveAdvertiserName = String(formData.advertiser || pageAdvertiserName || "").trim();
      const normalizedFormData = gatePlannerReleaseDateForVertical(
        normalizePlannerFormData(formData),
        plannerVerticalConfig
      );

      if (!effectiveAdvertiserId) {
        setError("No advertiser is selected. Use the advertiser selector above.");
        return;
      }

      const urlValidationError = getPlannerUrlValidationError(normalizedFormData, plannerVerticalConfig);
      if (urlValidationError) {
        setError(urlValidationError);
        return;
      }

      const authToken = getSherlocMediaPlannerAuthToken();
      if (!authToken) {
        setError("You are not authenticated. Please log in again.");
        return;
      }

      const prompt = buildMediaPlannerPrompt({
        ...normalizedFormData,
        advertiserId: effectiveAdvertiserId,
        advertiser: effectiveAdvertiserName,
        advertiserVertical: activePlannerAdvertiserVertical,
      });
      const targetingPayload = buildPlannerTargetingPayload(normalizedFormData);

      // Same durable operations endpoint as full generation — the backend
      // derives refresh_revision from the revisionPrompt in the payload. An
      // approved allocation travels as the campaign holds it, never as the
      // saved form's copy.
      const outcome = await startSherlocDurableGeneration({
        latchRef: generationSubmitLatchRef,
        activeOperation: generationOperationRef.current,
        submit: () =>
          createSherlocGenerationOperation({
            apiBase: safeBase(API_BASE_URL),
            token: authToken,
            requestId: revisionRequestId,
            body: applyApprovedAllocationToRefreshRequest({
          clientRequestId: revisionRequestId,
          formData: normalizedFormData,
          ...normalizedFormData,
          advertiserId: effectiveAdvertiserId,
          advertiser_id: effectiveAdvertiserId,
          advertiser: effectiveAdvertiserName,
          advertiser_name: effectiveAdvertiserName,
          advertiserVertical: activePlannerAdvertiserVertical,
          advertiser_vertical: activePlannerAdvertiserVertical,
          budget: normalizedFormData.budget,
          totalBudget: normalizedFormData.budget,
          total_budget: normalizedFormData.budget,
          grossBudget: normalizedFormData.budget,
          gross_budget: normalizedFormData.budget,
          netMediaBudget: normalizedFormData.netMediaBudget,
          net_media_budget: normalizedFormData.netMediaBudget,
          targetMarginPercent: normalizedFormData.targetMarginPercent,
          target_margin_percent: normalizedFormData.targetMarginPercent,
          targetMargin: normalizedFormData.targetMarginPercent,
          target_margin: normalizedFormData.targetMarginPercent,
          targetPlatforms: normalizedFormData.platforms,
          target_platforms: normalizedFormData.platforms,
          primaryKPI: normalizedFormData.primaryKPI,
          primaryKpi: normalizedFormData.primaryKPI,
          primary_kpi: normalizedFormData.primaryKPI,
          notes: normalizedFormData.notes,
          additionalNotes: normalizedFormData.notes,
          additional_notes: normalizedFormData.notes,
          destinationUrl: normalizedFormData.destinationUrl,
          destination_url: normalizedFormData.destinationUrl,
          imdbUrl: normalizedFormData.imdbUrl,
          imdb_url: normalizedFormData.imdbUrl,
          rottenTomatoesUrl: normalizedFormData.rottenTomatoesUrl,
          rotten_tomatoes_url: normalizedFormData.rottenTomatoesUrl,
          ageMin: targetingPayload.ageMin,
          age_min: targetingPayload.age_min,
          ageMax: targetingPayload.ageMax,
          age_max: targetingPayload.age_max,
          gender: targetingPayload.gender,
          interests: targetingPayload.interests,
          customTargeting: targetingPayload.customTargeting,
          custom_targeting: targetingPayload.custom_targeting,
          geoTargetingNotes: targetingPayload.geoTargetingNotes,
          geo_targeting_notes: targetingPayload.geo_targeting_notes,
          geoTargeting: targetingPayload.geoTargeting,
          geo_targeting: targetingPayload.geo_targeting,
          locationTargeting: targetingPayload.locationTargeting,
          location_targeting: targetingPayload.location_targeting,
          languageTargeting: targetingPayload.languageTargeting,
          language_targeting: targetingPayload.language_targeting,
          deviceTargetingNotes: targetingPayload.deviceTargetingNotes,
          device_targeting_notes: targetingPayload.device_targeting_notes,
          devicePlatformNotes: targetingPayload.devicePlatformNotes,
          device_platform_notes: targetingPayload.device_platform_notes,
          flightStart: normalizedFormData.flightStart,
          flight_start_date: normalizedFormData.flightStart,
          flightEnd: normalizedFormData.flightEnd,
          flight_end_date: normalizedFormData.flightEnd,
          releaseDate: normalizedFormData.releaseDate,
          release_date: normalizedFormData.releaseDate,
          targeting: targetingPayload,
          creativeRecommendations,
          creativeVariations: creativeRecommendations,
          creativeGenerationMeta,
          campaignId: activeCampaignId || null,
          campaign_id: activeCampaignId || null,
          currentStrategy: strategy,
          current_strategy: strategy,
          revisionPrompt: revisionInstruction,
          revision_prompt: revisionInstruction,
          // Stale-session guard: the backend refuses (409 revision_stale_version)
          // when the plan changed since this session last hydrated it.
          ...(planVersionRef.current
            ? {
                expectedPlanVersion: planVersionRef.current,
                expected_plan_version: planVersionRef.current,
              }
            : {}),
          prompt,
            }, {
              campaignId: activeCampaignId || null,
              allocationAuthority,
              canonicalAllocation,
            }),
          }),
      });

      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      if (outcome.kind === "duplicate_suppressed") return;
      if (outcome.kind === "completed_replay") {
        // Identical revision already completed server-side: apply the stored
        // result envelope WITHOUT regenerating.
        applyGenerationOperationSuccess(outcome.result, {
          kind: "revision",
          normalizedFormData,
          revisionInstruction,
        });
        clearGenerationOperation();
        return;
      }
      if (outcome.kind === "track") {
        // 201 queued / 200 reused / 409 operation_in_progress reconnect:
        // adopt the operation and poll GET /:id every 4s (no client timeout).
        beginTrackingGenerationOperation(outcome.operation, {
          kind: "revision",
          normalizedFormData,
          revisionInstruction,
        });
        return;
      }
      if (outcome.kind === "terminal_rejection") {
        // See the generate path: adopt the prior rejected operation so the
        // surface terminalizes instead of polling an operation that the
        // backend deliberately did not create.
        logSherlocStrategyRefreshFailure(
          "operation_terminally_rejected",
          revisionContext
        );
        beginTrackingGenerationOperation(outcome.operation, {
          kind: "revision",
          normalizedFormData,
          revisionInstruction,
        });
        return;
      }
      // 2xx acknowledgement without an operation: labeled unknown failure.
      logSherlocStrategyRefreshFailure("operation_unacknowledged", revisionContext);
      setError(buildUnknownFailureMessage(revisionRequestId));
    } catch (err) {
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      // Network failure: keep existing output, show calm copy only. Governed
      // revision refusal categories keep their distinct, actionable copy;
      // other 403/409 refusals surface the backend's message as a labeled
      // state (clients may be refused regeneration past DRAFT).
      const isNetwork = isPlannerNetworkError(err);
      const backendCode = String(err?.backendCode || err?.code || "");
      const isRefusal =
        !isNetwork &&
        (err?.status === 403 || err?.status === 409) &&
        !backendCode.startsWith("revision_");
      logSherlocStrategyRefreshFailure(
        isNetwork ? "network_error" : backendCode || "unexpected_error",
        {
          ...revisionContext,
          status: err?.status,
        }
      );
      logPlannerFailureDebug({
        url: SHERLOC_GENERATION_OPERATIONS_PATH,
        status: err?.status,
        code: backendCode || null,
        requestId: revisionRequestId,
        missingFields: err?.missingFields,
      });
      setError(
        isNetwork
          ? STRATEGY_NETWORK_FAILURE_MESSAGE
          : isRefusal
          ? buildGenerationRefusalMessage(err?.message)
          : backendCode
          ? mapStrategyFailureToMessage({
              status: err?.status,
              code: backendCode,
              missingFields: err?.missingFields,
              requestId: revisionRequestId,
            })
          : buildUnknownFailureMessage(revisionRequestId)
      );
    } finally {
      if (
        advertiserScopeGenerationAtStart ===
        advertiserScopeGenerationRef.current
      ) {
        setIsLoading(false);
        setGenerationLoadingType("");
      }
    }
  };

  const handleReset = () => {
    if (campaignIdFromNav) {
      // In campaign mode, reset leaves the campaign for a new, unsaved plan.
      exitCampaignModeToDrafts({ restoreDraft: false });
      startBlankPlan();
      return;
    }

    const keepAdvId = formData.advertiserId ?? pageAdvertiserId ?? null;
    const keepAdvName = String(formData.advertiser || pageAdvertiserName || "").trim();

    const resetForm = {
      ...BASE_BLANK_FORM,
      advertiserId: keepAdvId != null ? String(keepAdvId) : null,
      advertiser: keepAdvName,
    };
    const normalizedResetForm = normalizePlannerFormData(resetForm);

    setFormData(normalizedResetForm);
    setStrategy("");
    setStrategyPresentation(null);
    setCreativeRecommendations([]);
    setCreativeGenerationMeta(null);
    setCreativeError("");
    setError("");
    setIsDirty(false);
    setActiveCampaignId(null);
    loadedCampaignSnapshotRef.current = null;

    if (!activePlannerId) return;

    // The copy keeps which saved draft it belongs to.
    writePlannerLocalState(activePlannerId, {
      formData: normalizedResetForm,
      strategy: "",
      creativeRecommendations: [],
      creativeVariations: [],
      creativeGenerationMeta: null,
    });

    upsertGlobalIndex(activePlannerId, normalizedResetForm);
  };

  const handleGenerateCreativeVariations = async () => {
    // One creative request at a time: a second click while one is in flight
    // never starts a second paid run.
    if (creativeGenerationInFlightRef.current) return;
    const advertiserScopeGenerationAtStart =
      advertiserScopeGenerationRef.current;
    setCreativeError("");

    if (!API_BASE_URL) {
      setCreativeError(CREATIVE_CONFIGURATION_MESSAGE);
      return;
    }

    const token = getSherlocMediaPlannerAuthToken();
    if (!token) {
      setCreativeError("You are not authenticated. Please log in again.");
      return;
    }

    const urlValidationError = getPlannerUrlValidationError(formData, plannerVerticalConfig);
    if (urlValidationError) {
      setCreativeError(urlValidationError);
      return;
    }

    const abortController = typeof AbortController !== "undefined" ? new AbortController() : null;
    // A late answer is applied only to the planner that asked for it.
    const plannerIdentityAtStart = plannerIdentityRef.current;
    creativeGenerationInFlightRef.current = true;
    // Set when the operator opens another plan mid-run: the run then stops
    // quietly — it is no longer this page's to report.
    let stoppedByLeaving = false;
    creativeRunRef.current = {
      identity: plannerIdentityAtStart,
      stopWaiting: () => {
        stoppedByLeaving = true;
        abortController?.abort?.();
      },
    };
    setGenerationLoadingType(GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS);

    let creativeCampaignId = null;
    try {
      const effectiveAdvertiserId = formData.advertiserId ?? pageAdvertiserId ?? null;
      const effectiveAdvertiserName = String(formData.advertiser || pageAdvertiserName || "").trim();
      const normalizedFormData = gatePlannerReleaseDateForVertical(
        normalizePlannerFormData(formData),
        plannerVerticalConfig
      );
      const targetingPayload = buildPlannerTargetingPayload(normalizedFormData);
      // The request names the saved draft, so the draft is saved first, also
      // after a first run (the active campaign is then this draft) so an edit
      // still waiting for autosave is what the backend generates from. It is
      // a no-op when nothing changed. When it fails nothing is sent.
      const activeCampaignIsThisDraft =
        Boolean(activeCampaignId) && String(activeCampaignId) === String(serverDraftId || "");
      let draftSaveFailure = "";
      const savedDraftBeforeCreative =
        !campaignIdFromNav && (!activeCampaignId || activeCampaignIsThisDraft)
          ? await persistPlannerDraftToServer({
              explicit: false,
              requireSave: true,
              formDataOverride: normalizedFormData,
              onSaveError: (message) => {
                draftSaveFailure = message;
              },
            })
          : null;
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      if (stoppedByLeaving || plannerIdentityRef.current !== plannerIdentityAtStart) return;
      if (savedDraftBeforeCreative === false) {
        setCreativeError(
          receiptRecovery.hasPending(activePlannerId) ? "Draft save is not confirmed. Check its outcome before generating." : [CREATIVE_DRAFT_NOT_SAVED_MESSAGE, draftSaveFailure].filter(Boolean).join(" ")
        );
        return;
      }
      creativeCampaignId =
        activeCampaignId ||
        campaignIdFromNav ||
        savedDraftBeforeCreative?.serverDraftId ||
        savedDraftBeforeCreative?.id ||
        serverDraftId ||
        null;

      const result = await runWithSherlocStrategyRefreshTimeout(
        async () => {
          const res = await fetch(
            `${safeBase(API_BASE_URL)}/api/sherloc/media-planner/creative-variations`,
            {
              method: "POST",
              signal: abortController?.signal,
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                formData: normalizedFormData,
                campaignId: creativeCampaignId,
                campaign_id: creativeCampaignId,
                sourceDraftWriteVersion: savedDraftBeforeCreative?.writeVersion || loadedCampaignSnapshotRef.current?.planner_write_version || readPlannerCopy(activePlannerId)?.plannerWriteVersion || null,
                advertiserId: effectiveAdvertiserId,
                advertiser_id: effectiveAdvertiserId,
                advertiser: effectiveAdvertiserName,
                advertiser_name: effectiveAdvertiserName,
                advertiserVertical: activePlannerAdvertiserVertical,
                advertiser_vertical: activePlannerAdvertiserVertical,
                campaignName: normalizedFormData.campaignName,
                campaign_name: normalizedFormData.campaignName,
                budget: normalizedFormData.budget,
                totalBudget: normalizedFormData.budget,
                total_budget: normalizedFormData.budget,
                grossBudget: normalizedFormData.budget,
                gross_budget: normalizedFormData.budget,
                netMediaBudget: normalizedFormData.netMediaBudget,
                net_media_budget: normalizedFormData.netMediaBudget,
                targetMarginPercent: normalizedFormData.targetMarginPercent,
                target_margin_percent: normalizedFormData.targetMarginPercent,
                targetMargin: normalizedFormData.targetMarginPercent,
                target_margin: normalizedFormData.targetMarginPercent,
                goalType: normalizedFormData.goalType,
                goal_type: normalizedFormData.goalType,
                primaryKPI: normalizedFormData.primaryKPI,
                primaryKpi: normalizedFormData.primaryKPI,
                primary_kpi: normalizedFormData.primaryKPI,
                notes: normalizedFormData.notes,
                additionalNotes: normalizedFormData.notes,
                additional_notes: normalizedFormData.notes,
                destinationUrl: normalizedFormData.destinationUrl,
                destination_url: normalizedFormData.destinationUrl,
                imdbUrl: normalizedFormData.imdbUrl,
                imdb_url: normalizedFormData.imdbUrl,
                rottenTomatoesUrl: normalizedFormData.rottenTomatoesUrl,
                rotten_tomatoes_url: normalizedFormData.rottenTomatoesUrl,
                flightStart: normalizedFormData.flightStart,
                flight_start_date: normalizedFormData.flightStart,
                flightEnd: normalizedFormData.flightEnd,
                flight_end_date: normalizedFormData.flightEnd,
                releaseDate: normalizedFormData.releaseDate,
                release_date: normalizedFormData.releaseDate,
                targetPlatforms: normalizedFormData.platforms,
                target_platforms: normalizedFormData.platforms,
                ...targetingPayload,
                targeting: targetingPayload,
              }),
            }
          );

          // Kept: the status, the backend's code and stage, and the server
          // request id. The body's text never reaches the page or the console.
          const { requestId } = readTraceHeadersFromResponse(res);
          const data = await readSherlocStrategyResponse(res);
          if (!res.ok) {
            const err = new Error(`Creative generation failed (${res.status})`);
            err.status = res.status;
            err.code = typeof data?.code === "string" ? data.code : "";
            err.stage = typeof data?.stage === "string" ? data.stage : "";
            // true, false, or null when the backend could not confirm it.
            err.persisted = data?.persisted === null ? null : data?.persisted === true;
            err.backendError = typeof data?.error === "string" ? data.error : "";
            err.requestId = requestId;
            throw err;
          }

          const nextCreativeRecommendations = Array.isArray(data?.creativeRecommendations)
            ? data.creativeRecommendations.filter(
                (recommendation) =>
                  recommendation &&
                  typeof recommendation === "object" &&
                  !Array.isArray(recommendation)
              )
            : [];
          if (data?.__malformed || !nextCreativeRecommendations.length) {
            const err = new Error(CREATIVE_EMPTY_RESPONSE_MESSAGE);
            err.code = "malformed_response";
            // The route answers 200 for a named plan only after its write
            // committed, so whatever it generated is already on that plan.
            err.kind = creativeCampaignId ? "saved_unreadable" : "empty_response";
            err.status = res.status;
            err.requestId = requestId;
            throw err;
          }

          return { data, nextCreativeRecommendations };
        },
        {
          timeoutMs: CREATIVE_GENERATION_TIMEOUT_MS,
          onTimeout: () => abortController?.abort?.(),
        }
      );

      const { data, nextCreativeRecommendations } = result;
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      // Another planner or campaign is open now: its view is left alone. The
      // route has already saved the variations to the plan the request named.
      if (plannerIdentityRef.current !== plannerIdentityAtStart) return;
      const nextCreativeGenerationMeta =
        data.creativeGenerationMeta &&
        typeof data.creativeGenerationMeta === "object" &&
        !Array.isArray(data.creativeGenerationMeta)
          ? data.creativeGenerationMeta
          : null;
      setCreativeRecommendations(nextCreativeRecommendations);
      setCreativeGenerationMeta(nextCreativeGenerationMeta);
      // The route writes the variations onto the plan the request named, and
      // its answer carries that plan once the write has committed.
      const savedByRoute =
        Boolean(creativeCampaignId) &&
        data.campaign?.id != null &&
        String(data.campaign.id) === String(creativeCampaignId);

      if (data.campaign?.id) {
        setActiveCampaignId(data.campaign.id);
        try {
          window.localStorage.setItem("sherloc_last_created_campaign_id", String(data.campaign.id));
        } catch {}
      }

      // The creative route's committed response is an authorized writer
      // result, not a matching read. Advance only the same draft's causal
      // base, never a newer local version or a plan with a pending save.
      const creativeWriteVersion = String(data.campaign?.planner_write_version || "");
      const creativeCopy = readPlannerCopy(activePlannerId);
      if (savedByRoute && activePlannerId && !receiptRecovery.hasPending(activePlannerId) &&
          String(creativeCopy?.serverDraftId || "") === String(creativeCampaignId) &&
          /^[1-9]\d*$/.test(creativeWriteVersion) && Number.isSafeInteger(Number(creativeWriteVersion)) &&
          Number(creativeWriteVersion) > Number(creativeCopy?.plannerWriteVersion || 0)) {
        writePlannerLocalState(activePlannerId, { plannerWriteVersion: creativeWriteVersion });
      }

      let followUpSaveFailure = null;
      if (!campaignIdFromNav) {
        await persistPlannerDraftToServer({
          explicit: false,
          formDataOverride: normalizedFormData,
          creativeRecommendationsOverride: nextCreativeRecommendations,
          creativeGenerationMetaOverride: nextCreativeGenerationMeta,
          onSaveError: (message) => {
            followUpSaveFailure = message || "";
          },
        });
      }

      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      if (plannerIdentityRef.current !== plannerIdentityAtStart) return;
      setIsDirty(Boolean(campaignIdFromNav));
      // Generated variations stay on the page either way; the notice says
      // whether they are saved, and a later save that carries them settles it.
      // The notice belongs to these variations on this plan: a copy of them
      // in a new, unsaved planner does not inherit "saved".
      setCreativeSaveNotice(
        savedByRoute || followUpSaveFailure === null
          ? {
              recommendations: nextCreativeRecommendations,
              identity: plannerIdentityAtStart,
              state: "saved",
              text: campaignIdFromNav
                ? CREATIVE_SAVED_TO_CAMPAIGN_NOTICE
                : CREATIVE_SAVED_TO_DRAFT_NOTICE,
            }
          : {
              recommendations: nextCreativeRecommendations,
              identity: plannerIdentityAtStart,
              state: receiptRecovery.hasPending(activePlannerId) ? "unknown" : "not_saved",
              text: receiptRecovery.hasPending(activePlannerId)
                ? "Generated; save outcome not yet known. These variations are preserved in this browser. Check the save outcome before saving or generating this plan."
                : [CREATIVE_NOT_SAVED_NOTICE, followUpSaveFailure].filter(Boolean).join(" "),
            }
      );
    } catch (err) {
      if (
        advertiserScopeGenerationAtStart !==
        advertiserScopeGenerationRef.current
      ) {
        return;
      }
      if (stoppedByLeaving || plannerIdentityRef.current !== plannerIdentityAtStart) return;
      const kind = isSherlocStrategyTimeoutError(err)
        ? "timeout"
        : isPlannerNetworkError(err)
          ? "network"
          : err?.kind || "";
      // eslint-disable-next-line no-console
      console.error("[Client SherlocMediaPlanner] Creative generation error:", {
        kind: kind || null,
        // A timeout is this page's own; it has no HTTP status.
        status: kind === "timeout" ? null : err?.status ?? null,
        code: err?.code || null,
        stage: err?.stage || null,
        requestId: err?.requestId || null,
      });
      setCreativeError(
        mapCreativeFailureToMessage({
          kind,
          status: err?.status,
          code: err?.code,
          stage: err?.stage,
          persisted: err?.persisted,
          backendError: err?.backendError,
          requestId: err?.requestId,
          targetSent: Boolean(creativeCampaignId),
        })
      );
    } finally {
      creativeGenerationInFlightRef.current = false;
      creativeRunRef.current = null;
      // Always released, even when the scope moved on mid-run: the run locks
      // editing, so a stale loading state would leave the planner locked.
      setGenerationLoadingType((current) =>
        current === GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS ? "" : current
      );
    }
  };

  const formattedBudgetValue =
    isBudgetFocused ? String(formData.budget || "") : formatBudgetForDisplay(formData.budget);

  // The stored stamp is not a current dependency verdict. A failed read is
  // explicit unavailability, never proof that a strategy is current.
  const currencyCampaignId = String(activeCampaignId || campaignIdFromNav || "");
  const currencyRead = useStrategyCurrency({
    baseUrl: safeBase(API_BASE_URL), campaignId: currencyCampaignId, advertiserId: ctxSelectedAdvertiserId,
    token: getSherlocMediaPlannerAuthToken(),
    authScope: { id: authContext?.user?.id, role: authContext?.role, advertisers: authContext?.advertisers, advertiserIds: authContext?.user?.advertiserIds },
    recordVersion: planVersionRef.current, provenance: strategyProvenance, loading: isLoadingCampaign,
    // The read-only campaign serializer does not require updated_at. Wait for
    // its actual hydrated identity, not for an optional write-concurrency pin.
    ready: !!currencyCampaignId && String(loadedCampaignSnapshotRef.current?.id || loadedCampaignSnapshotRef.current?.campaign_id || loadedCampaignSnapshotRef.current?.campaignId || "") === currencyCampaignId,
  });
  const currentReadRefusal = fullStrategyReadRefusal?.basis === currencyRead ? fullStrategyReadRefusal : null;
  const currentCurrencyAction = currentReadRefusal ? currentReadRefusal.next : currencyRead.next;
  const fullStrategyRequestAvailable = currencyRead.status === "available" &&
    canRequestFullStrategy(currentCurrencyAction, currencyCampaignId);
  // The backend names the request it advertised: a campaign with nothing
  // saved is offered its FIRST strategy, never a "refresh" of nothing.
  const fullStrategyRequest = fullStrategyRequestAvailable
    ? resolveFullStrategyRequest(currentCurrencyAction, currencyCampaignId)
    : null;
  const fullStrategyRequestIsCreation = fullStrategyRequest?.kind === "CREATE_STRATEGY";
  // Render-published identity withdraws stale authority immediately on token,
  // advertiser, campaign, loaded-version or canonical-read changes. Comparing
  // identities here is not caching a generation permission.
  fullStrategyContextRef.current = JSON.stringify({
    campaignId: currencyCampaignId, advertiserId: ctxSelectedAdvertiserId,
    token: getSherlocMediaPlannerAuthToken(),
    role: authContext?.role, user: authContext?.user?.id,
    readKey: currencyRead.key, readStatus: currencyRead.status,
    decisionState: currentCurrencyAction?.state,
  });
  // The backend's client-safe notice, except when the canonical envelope
  // itself advertises the campaign's first strategy: then the notice explains
  // that affordance instead of telling the client to wait for their team.
  const currentStrategyNotice = resolveClientStrategyNotice(
    resolveStrategyNextActionNotice(currentCurrencyAction),
    fullStrategyRequest
  );
  const currencyUnavailable = currencyRead.status === "unavailable";

  // Latest-attempt row for the stale banner. The campaign-GET provenance
  // snapshot is the reload truth, but while this page tracks a live durable
  // operation the polled state is newer — a terminal failure must relabel the
  // banner WITHOUT a reload, and a reload must then produce the same label
  // from the snapshot (one canonical lifecycle, shared with the operation
  // card).
  const bannerLatestOperation = useMemo(() => {
    const snapshot = strategyProvenance?.latestOperation || null;
    const live = generationOperation;
    if (!live?.id) return snapshot;
    // A tracked operation for a different campaign never relabels this
    // campaign's banner (compare only when both sides carry an id).
    const liveCampaignId = String(live.campaignId ?? "").trim();
    const loadedCampaignId = String(
      activeCampaignId || campaignIdFromNav || ""
    ).trim();
    if (liveCampaignId && loadedCampaignId && liveCampaignId !== loadedCampaignId) {
      return snapshot;
    }
    // Distinct attempts: the newer createdAt wins (a rehydrated snapshot can
    // postdate a lingering tracked operation).
    if (snapshot?.id && String(snapshot.id) !== String(live.id)) {
      const snapshotCreatedMs = Date.parse(String(snapshot.createdAt ?? ""));
      const liveCreatedMs = Date.parse(String(live.createdAt ?? ""));
      if (
        Number.isFinite(snapshotCreatedMs) &&
        Number.isFinite(liveCreatedMs) &&
        snapshotCreatedMs > liveCreatedMs
      ) {
        return snapshot;
      }
    }
    return {
      id: live.id,
      supportReference: String(live.id).slice(0, 8).toUpperCase(),
      status: live.status,
      disposition: live.disposition,
      failureClass: live.failureClass,
      failureMessage: live.failureMessage,
      endedAt: live.failedAt || live.completedAt || null,
      createdAt: live.createdAt,
    };
  }, [strategyProvenance, generationOperation, activeCampaignId, campaignIdFromNav]);

  const headerSubtitle = useMemo(() => {
    if (isLoadingCampaign) return "Loading selected campaign strategy…";
    if (campaignIdFromNav) {
      return "Saved strategy — status shown below.";
    }
    if (sessionMode === "draft") {
      return "Select one of this advertiser's drafts to resume a saved planner session.";
    }
    if (sessionMode === "campaign") {
      return "Select one of this advertiser's campaigns to load its saved strategy and settings.";
    }
    return "Sherloc will break your budget into platforms, phases, tactics, and optimizations across the stack.";
  }, [
    campaignIdFromNav,
    isLoadingCampaign,
    sessionMode,
  ]);

  const subtitleClassName = useMemo(() => {
    const base = "mediaplanner-output-subtitle";
    const isDefaultIdleSubtitle = !isLoadingCampaign && !campaignIdFromNav;
    return [base, isDefaultIdleSubtitle ? "mediaplanner-output-subtitle--pending" : ""]
      .filter(Boolean)
      .join(" ");
  }, [campaignIdFromNav, isLoadingCampaign]);

  const plannerUiLocked = isLoading || isLoadingCampaign;
  const isStrategyGenerationLoading =
    generationLoadingType === GENERATION_LOADING_TYPES.STRATEGY ||
    generationLoadingType === GENERATION_LOADING_TYPES.REFRESH_STRATEGY;
  const isCreativeGenerationLoading =
    generationLoadingType === GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS;
  const creativeRuleBasedLabel = describeRuleBasedCreativeVariations(
    creativeGenerationMeta,
    creativeRecommendations.length
  );
  // Durable server-side generation counts as "generating": the buttons stay
  // disabled while the tracked operation is QUEUED/GENERATING/VALIDATING/
  // PERSISTING, even though the page itself is free to navigate away.
  const isAnyGenerationLoading =
    Boolean(generationLoadingType) || generationOperationIsActive;
  // Creative generation writes onto the saved draft when it finishes, and the
  // backend refuses that write if the draft moved meanwhile (on Oct 5 2026 an
  // edit autosaved mid-run). So editing and saving pause until it answers.
  // Nothing is edited while the advertiser's saved plans load, while they
  // offer a choice and no plan is open, or while a campaign is being chosen
  // and none is loaded: an edit then would belong to no plan (and could start
  // a new one, or land on another plan, by accident).
  const noPlanOpenToEdit =
    !campaignIdFromNav &&
    ((serverDraftList.status === "loading" && serverDraftList.advertiserKey === advKey) ||
      (sessionMode === "draft" && !activePlannerId) ||
      (sessionMode === "campaign" && !activeCampaignId));
  const fieldsDisabled =
    plannerUiLocked || !!campaignIdFromNav || isCreativeGenerationLoading || noPlanOpenToEdit;
  const platformDisabled =
    plannerUiLocked || !!campaignIdFromNav || isCreativeGenerationLoading || noPlanOpenToEdit;
  const referenceUrlsDisabled = plannerUiLocked || isCreativeGenerationLoading || noPlanOpenToEdit;
  // Where the advertiser's saved plans stand, in one line (nothing once a
  // plan is open and there is nothing to add).
  const plannerDraftStatusText = (() => {
    if (campaignIdFromNav || !advKey || advKey === "unknown") return "";
    if (serverDraftList.advertiserKey !== advKey) return "";
    if (serverDraftList.status === "loading") return "Loading saved plans…";
    if (serverDraftList.status === "error") {
      return "Saved plans couldn’t be loaded, so nothing was opened or created.";
    }
    if (serverDraftList.status === "unavailable") {
      return "Saved plans can’t be listed for this advertiser here. A new, unsaved plan is open.";
    }
    if (serverDraftList.notice) return serverDraftList.notice;
    if (serverDraftList.status === "ready" && sessionMode === "draft" && !activePlannerId) {
      return plannerDraftOptions.length
        ? "Choose a saved plan to continue, or start a new strategy."
        : "No saved plans for this advertiser yet. Start a new strategy to create one.";
    }
    return "";
  })();

  const currentCampaignId = activeCampaignId || campaignIdFromNav || "";
  const currentCampaignIdText = String(currentCampaignId || "").trim();
  const canRefreshCurrentStrategy = Boolean(currentCampaignIdText);
  const currentAdvertiserIdText = String(
    formData.advertiserId ?? pageAdvertiserId ?? routeAdvertiserId ?? ""
  ).trim();
  const currentAdvertiserNameText = String(
    formData.advertiser || pageAdvertiserName || routeAdvertiserName || ""
  ).trim();
  const currentCampaignNameText = String(
    formData.campaignName || routeCampaignName || ""
  ).trim();
  const strategyMicrositeSlug = useMemo(
    () =>
      buildCampaignStrategyMicrositeSlug({
        advertiserName: currentAdvertiserNameText,
        campaignName: currentCampaignNameText,
      }),
    [currentAdvertiserNameText, currentCampaignNameText]
  );
  // The strategy page opens only by the readable address the backend assigned
  // (/campaign-strategy/{advertiser}/{campaign}): never by the campaign id,
  // and never by a name slug computed here, which two campaigns can share.
  const strategyMicrositePath =
    currentCampaignIdText && strategyMicrositeAddress.campaignId === currentCampaignIdText
      ? strategyMicrositeAddress.path
      : "";
  const plannerMicrositeReturnPath = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    params.delete("from");
    params.delete("advertiserName");
    params.delete("campaignName");
    if (currentCampaignIdText) params.set("campaignId", currentCampaignIdText);
    if (currentAdvertiserIdText) params.set("advertiserId", currentAdvertiserIdText);
    params.set("returnSource", SHERLOC_MICROSITE_RETURN_SOURCE);
    params.set("restoredFromMicrosite", "1");
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ""}`;
  }, [
    currentAdvertiserIdText,
    currentCampaignIdText,
    location.pathname,
    location.search,
  ]);
  const strategyMicrositeOpenUrl = useMemo(() => {
    if (!strategyMicrositePath || !currentCampaignIdText) return "";
    return strategyMicrositePath;
  }, [currentCampaignIdText, strategyMicrositePath]);
  const micrositeReturnState = useMemo(
    () => ({
      source: SHERLOC_MICROSITE_RETURN_SOURCE,
      returnSource: SHERLOC_MICROSITE_RETURN_SOURCE,
      returnTo: plannerMicrositeReturnPath,
      campaignId: currentCampaignIdText,
      micrositePath: strategyMicrositePath,
      micrositeSlug: strategyMicrositeSlug,
      publicSlug: strategyMicrositeSlug,
      advertiserId: currentAdvertiserIdText || null,
      advertiserName: currentAdvertiserNameText,
      campaignName: currentCampaignNameText,
    }),
    [
      currentAdvertiserIdText,
      currentAdvertiserNameText,
      currentCampaignIdText,
      currentCampaignNameText,
      plannerMicrositeReturnPath,
      strategyMicrositePath,
      strategyMicrositeSlug,
    ]
  );
  // "Refresh Full Strategy" is deliberately explicit: this action regenerates the
  // ENTIRE strategy narrative. Targeted actions (Generate Creative Variations,
  // Apply AI Revision) patch only their own section and are the right choice when
  // the user only added/changed one module.
  const primaryStrategyActionLabel = fullStrategyRequestIsCreation
    ? fullStrategyRequest.label || "Generate Media Strategy"
    : canRefreshCurrentStrategy
      ? "Refresh Full Strategy"
      : "Generate Media Strategy";
  const primaryStrategyActionBusyLabel =
    canRefreshCurrentStrategy && !fullStrategyRequestIsCreation
      ? "Refreshing Full Strategy..."
      : "Generating Strategy...";
  const strategyRefreshPanelMessage = !String(strategy || "").trim()
    ? getStrategyRefreshPanelMessage(error)
    : "";
  const campaignSelectValue =
    sessionMode === "campaign" ? String(currentCampaignId) : "";

  // The strategy page opens by the campaign's readable address. A campaign
  // adopted without it (the first generation creates the campaign) reads it
  // from the campaign first; "" when there is none to open.
  const resolveStrategyMicrositeOpenUrl = useCallback(async () => {
    if (strategyMicrositeOpenUrl) return strategyMicrositeOpenUrl;
    if (!currentCampaignIdText) return "";
    const path = await fetchStrategyMicrositeAddress(currentCampaignIdText, {
      token: getSherlocMediaPlannerAuthToken(),
    });
    if (path) setStrategyMicrositeAddress({ campaignId: currentCampaignIdText, path });
    return path;
  }, [currentCampaignIdText, getSherlocMediaPlannerAuthToken, strategyMicrositeOpenUrl]);

  const handleOpenStrategyMicrosite = useCallback(async () => {
    if (!currentCampaignIdText) return;
    // The microsite renders the saved campaign strategy. If the planner has
    // unsaved edits, opening now would show stale data — warn and stop instead of
    // silently publishing or auto-saving.
    if (isDirty) {
      setError(STRATEGY_MICROSITE_UNSAVED_CHANGES_MESSAGE);
      return;
    }
    const openUrl = await resolveStrategyMicrositeOpenUrl();
    if (!openUrl) {
      setError(STRATEGY_MICROSITE_ADDRESS_UNAVAILABLE_MESSAGE);
      return;
    }
    const returnState = { ...micrositeReturnState, micrositePath: openUrl };
    setError("");
    try {
      sessionStorage.setItem(SHERLOC_MICROSITE_RETURN_STATE_KEY, JSON.stringify(returnState));
    } catch {}
    navigate(openUrl, {
      state: returnState,
    });
  }, [
    currentCampaignIdText,
    isDirty,
    micrositeReturnState,
    navigate,
    resolveStrategyMicrositeOpenUrl,
  ]);
  const plannerActionButtons = [
    {
      key: "reset",
      className: "mediaplanner-reset-btn",
      onClick: handleReset,
      disabled: plannerUiLocked || isCreativeGenerationLoading,
      label: "Reset",
    },
    {
      key: "new-planner",
      className: "mediaplanner-save-btn",
      onClick: campaignIdFromNav ? handleCopyToNewPlanner : handleNewPlanner,
      disabled: plannerUiLocked || isCreativeGenerationLoading,
      title: campaignIdFromNav
        ? "Copy this read-only campaign into a new editable draft"
        : undefined,
      label: campaignIdFromNav ? "Copy to New Planner" : "New Planner",
    },
    {
      key: "save-draft",
      className: "mediaplanner-save-btn",
      onClick: () => persistPlannerDraftToServer({ explicit: true }),
      disabled:
        plannerUiLocked ||
        saveStatus === "saving" ||
        !!campaignIdFromNav ||
        isCreativeGenerationLoading ||
        noPlanOpenToEdit,
      title: "Save this planner draft so it can be recovered after refresh.",
      label: saveStatus === "saving" ? "Saving..." : "Save Draft",
    },
    {
      key: "creative-variations",
      className: "mediaplanner-save-btn",
      onClick: handleGenerateCreativeVariations,
      disabled: plannerUiLocked || isAnyGenerationLoading,
      label: isCreativeGenerationLoading
        ? "Generating Creatives..."
        : "Generate Creative Variations",
    },
    {
      key: "primary-strategy",
      className: "mediaplanner-generate-btn",
      onClick: handleSubmit,
      disabled: plannerUiLocked || isAnyGenerationLoading || (!!currencyCampaignId && !fullStrategyRequestAvailable),
      title: fullStrategyRequestIsCreation
        ? "Generate this campaign's first Sherloc media strategy from the saved inputs."
        : campaignIdFromNav
        ? "Regenerate the entire strategy narrative from the latest saved inputs (including updated film reference URLs). Existing creative variations, targeting, and earned-media inputs are preserved. To update just one section, use Generate Creative Variations or Apply AI Revision instead."
        : canRefreshCurrentStrategy
        ? "Regenerate the entire strategy narrative from the latest saved inputs. Existing creative variations, targeting, and earned-media inputs are preserved. To update just one section, use Generate Creative Variations or Apply AI Revision instead."
        : "Generate a new Sherloc media strategy.",
      label: generationOperationIsActive
        ? "Generating…"
        : isStrategyGenerationLoading
        ? primaryStrategyActionBusyLabel
        : primaryStrategyActionLabel,
    },
    {
      key: "strategy-microsite",
      className: "mediaplanner-save-btn",
      fullWidth: true,
      onClick: handleOpenStrategyMicrosite,
      disabled: plannerUiLocked || !canRefreshCurrentStrategy,
      title: canRefreshCurrentStrategy
        ? "Open the hosted Cphere strategy microsite for this campaign."
        : "Save or load a campaign first to open its hosted strategy page.",
      label: "Open Strategy Microsite",
    },
  ];
  const visiblePlannerActions = plannerActionButtons.filter(({ visible = true }) => visible);

  // ---------------------------
  // Existing UI render (unchanged)
  // ---------------------------

  return (
    <div className="smp-page-container">
      <div className="smp-page-content smp-dashboard-content">
        <CphereHeaderLogo
          alt="Powered by Cphere"
          className="smp-poweredby-logo"
          onClick={() => navigate("/dashboard")}
        />

        <div className="cphere-governed-hero-selector-stack smp-governed-advertiser-shell">
          <div className="smp-hero-container">
            <div className="smp-hero-pill">
              <span className="smp-hero-label cphere-hero-pill-wordmark">Media</span>
              <span className="smp-hero-tag cphere-hero-pill-tag">Planner</span>
            </div>
            <div className="smp-hero-shimmer" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={currentAdvertiser?.name || ""}
              apiBase={API_BASE_URL}
              disabled={
                selectorDisabled || plannerAdvertiserSelectionLocked
              }
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={governedSelectedAdvertiserId}
            selectedAdvertiserName={governedSelectedAdvertiserName}
            apiBase={API_BASE_URL}
            onSelect={handlePlannerAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        {advertiserCatalogError ? (
          <div className="smp-governed-advertiser-error" role="alert">
            Advertiser selection is temporarily unavailable.
          </div>
        ) : null}

        <div className="mediaplanner-inner">
          {process.env.REACT_APP_AISR_FILM_INTELLIGENCE_ENABLED === "true" && (
            <PredictiveFilmIntelligence
              baseUrl={safeBase(API_BASE_URL)}
              campaignId={currencyCampaignId}
              advertiserId={ctxSelectedAdvertiserId}
              token={getSherlocMediaPlannerAuthToken()}
              authScope={{ id: authContext?.user?.id, role: authContext?.role, advertisers: authContext?.advertisers, advertiserIds: authContext?.user?.advertiserIds }}
              loading={isLoadingCampaign}
              ready={isFilmIntelligenceScopeReady(loadedCampaignSnapshotRef.current, currencyCampaignId, ctxSelectedAdvertiserId)}
            />
          )}
          <div className="mediaplanner-main-row">
            <div className="planner-card mediaplanner-inputs-card">
              <div className="section-pill-header">
                <span className="badge-pill section-pill">Campaign Inputs</span>
              </div>

              {edenDraftResult ? (
                <EdenPlannerDraftReview
                  result={edenDraftResult}
                  appliedNote={edenDraftAppliedNote}
                  computePreview={computeEdenDraftPreview}
                  onApply={handleApplyEdenDraft}
                  onDismiss={handleDismissEdenDraft}
                />
              ) : null}

              <form className="mediaplanner-form" onSubmit={handleSubmit} noValidate>
                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label>Planner Flow</label>

                    <div className="smp-draft-row">
                      <button
                        type="button"
                        className={
                          sessionMode === "new"
                            ? "mediaplanner-save-btn"
                            : "mediaplanner-reset-btn"
                        }
                        onClick={() => handlePlannerModeSelect("new")}
                        disabled={isPlannerFlowActionDisabled({
                          plannerUiLocked: plannerUiLocked || isCreativeGenerationLoading,
                          advKey,
                        })}
                        title="Start a brand new strategy for the selected advertiser."
                      >
                        Start New Strategy
                      </button>

                      <button
                        type="button"
                        className={
                          sessionMode === "draft"
                            ? "mediaplanner-save-btn"
                            : "mediaplanner-reset-btn"
                        }
                        onClick={() => handlePlannerModeSelect("draft")}
                        disabled={isPlannerFlowActionDisabled({
                          plannerUiLocked: plannerUiLocked || isCreativeGenerationLoading,
                          advKey,
                        })}
                        title="Resume one of this advertiser's planner drafts."
                      >
                        Load Draft
                      </button>

                      <button
                        type="button"
                        className={
                          sessionMode === "campaign"
                            ? "mediaplanner-save-btn"
                            : "mediaplanner-reset-btn"
                        }
                        onClick={() => handlePlannerModeSelect("campaign")}
                        disabled={isPlannerFlowActionDisabled({
                          plannerUiLocked: plannerUiLocked || isCreativeGenerationLoading,
                          advKey,
                        })}
                        title="Load an existing media campaign for this advertiser."
                      >
                        Load Campaign
                      </button>
                    </div>
                  </div>
                </div>

                {sessionMode === "campaign" ? (
                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="campaignSelect">Media Campaign</label>

                    <div className="smp-draft-row">
                      <select
                        id="campaignSelect"
                        name="campaignSelect"
                        value={campaignSelectValue}
                        onChange={(e) => {
                          const nextCampaignId = e.target.value ? String(e.target.value) : "";

                          setError("");

                          if (!nextCampaignId) {
                            handlePlannerModeSelect("campaign");
                            return;
                          }

                          beginManualPlannerOverride();
                          setAutoLoadedSoleCampaign(false);
                          setSessionMode("campaign");
                          setNavCampaignId(nextCampaignId);
                        }}
                        disabled={
                          isLoading ||
                          isLoadingCampaign ||
                          isLoadingCampaigns ||
                          isCreativeGenerationLoading ||
                          !advKey ||
                          advKey === "unknown"
                        }
                        title="Select a saved Media Campaign or select Drafts to work from drafts."
                      >
                        <option value="">
                          {advKey && advKey !== "unknown"
                            ? "Drafts (no campaign selected)"
                            : "Select an advertiser first"}
                        </option>

                        {isLoadingCampaigns ? (
                          <option value="" disabled>
                            Loading campaigns...
                          </option>
                        ) : campaigns.length === 0 ? (
                          <option value="" disabled>
                            No campaigns found for this advertiser
                          </option>
                        ) : (
                          campaigns.map((c) => {
                            const ts = campaignUpdatedAt(c);
                            const labelTs = ts ? formatDraftTimestamp(ts) : "";
                            const title = campaignTitle(c);
                            const label = labelTs ? `${title} — ${labelTs}` : title;
                            return (
                              <option key={String(c.id)} value={String(c.id)}>
                                {label}
                              </option>
                            );
                          })
                        )}
                      </select>

                      <button
                        type="button"
                        className="mediaplanner-reset-btn"
                        onClick={() => {
                          const reqId = ++campaignListReqRef.current;
                          refreshCampaignsForCurrentAdvertiser({ reqId });
                        }}
                        disabled={
                          isLoading ||
                          isLoadingCampaign ||
                          isLoadingCampaigns ||
                          !advKey ||
                          advKey === "unknown"
                        }
                        title="Refresh campaigns for this advertiser"
                      >
                        Refresh Campaigns
                      </button>

                      {campaignIdFromNav ? (
                        <button
                          type="button"
                          className="mediaplanner-reset-btn"
                          onClick={exitCampaignModeToDrafts}
                          disabled={isLoading || isLoadingCampaign || isCreativeGenerationLoading}
                          title="Exit campaign view and return to drafts for this advertiser"
                        >
                          View Drafts
                        </button>
                      ) : null}
                    </div>

                    {campaignsLoadError ? (
                      <div className="smp-adv-error">{campaignsLoadError}</div>
                    ) : null}

                    {autoLoadedSoleCampaign && !campaignsLoadError ? (
                      <div className="smp-revision-hint smp-campaign-status-hint">
                        Loaded the only campaign for this advertiser.
                      </div>
                    ) : null}
                  </div>
                </div>
                ) : null}

                {!campaignIdFromNav && plannerDraftStatusText ? (
                  <div className="mediaplanner-form-row smp-wide-row">
                    <div
                      className={`smp-revision-hint smp-draft-resume-status is-${serverDraftList.status}`}
                      role="status"
                      aria-live="polite"
                    >
                      <span>{plannerDraftStatusText}</span>
                      {serverDraftList.status === "error" ? (
                        <button
                          type="button"
                          className="mediaplanner-reset-btn"
                          onClick={() => {
                            beginManualPlannerOverride();
                            // The same lookup again, so an entry's explicit
                            // draft id still counts.
                            resumePlannerDrafts({ source: serverDraftList.source || "load_draft" });
                          }}
                          disabled={plannerUiLocked || isCreativeGenerationLoading}
                        >
                          Retry
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {/* Drafts row only shown in draft mode */}
                {sessionMode === "draft" && !campaignIdFromNav && (
                  <div className="mediaplanner-form-row smp-wide-row">
                    <div className="mediaplanner-field">
                      <label htmlFor="plannerDraft">Planner Draft</label>

                      <div className="smp-draft-row">
                        <select
                          id="plannerDraft"
                          name="plannerDraft"
                          value={activeDraftOption ? activeDraftOptionKey : ""}
                          onChange={(e) => {
                            const option = plannerDraftOptions.find(
                              (entry) => entry.key === e.target.value
                            );
                            if (!option || option.key === activeDraftOptionKey) return;

                            beginManualPlannerOverride();
                            setActiveCampaignId(null);
                            loadedCampaignSnapshotRef.current = null;
                            setError("");
                            setServerDraftList((list) => (list.notice ? { ...list, notice: "" } : list));
                            if (openPlannerOption(option)) rememberPlannerChoice(advKey, plannerChoiceOfOption(option));
                          }}
                          disabled={isLoading || isLoadingCampaign || isCreativeGenerationLoading}
                        >
                          {activeDraftOption ? null : (
                            <option value="">
                              {plannerDraftOptions.length
                                ? "Select a draft"
                                : serverDraftList.status === "loading"
                                ? "Loading saved plans…"
                                : serverDraftList.status === "error"
                                ? "Saved plans unavailable"
                                : "No drafts yet"}
                            </option>
                          )}
                          {plannerDraftOptions.map((option) => (
                            <option key={option.key} value={option.key}>
                              {formatPlannerDraftOptionLabel(option)}
                            </option>
                          ))}
                        </select>

                        {activeDraftOption?.kind === "local" ? (
                          <button
                            type="button"
                            className="mediaplanner-reset-btn"
                            onClick={deleteActiveDraft}
                            disabled={
                              isLoading ||
                              isLoadingCampaign ||
                              isCreativeGenerationLoading ||
                              !activePlannerId ||
                              saveStatus === "saving"
                            }
                            title="Delete this plan from this device. It was never saved."
                          >
                            Delete Draft
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="campaignName">Campaign Name</label>
                    <input
                      id="campaignName"
                      name="campaignName"
                      type="text"
                      value={formData.campaignName}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.campaignNamePlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="budget">Budget (Total)</label>
                    <input
                      id="budget"
                      name="budget"
                      type="text"
                      inputMode="numeric"
                      value={formattedBudgetValue}
                      onChange={handleChange}
                      onFocus={() => setIsBudgetFocused(true)}
                      onBlur={() => setIsBudgetFocused(false)}
                      placeholder="e.g. $50,000"
                      required
                      disabled={fieldsDisabled}
                    />
                  </div>

                  <div className="mediaplanner-field">
                    <label htmlFor="goalType">Goal Type</label>
                    <select
                      id="goalType"
                      name="goalType"
                      value={formData.goalType}
                      onChange={handleChange}
                      required
                      disabled={fieldsDisabled}
                    >
                      <option value="">Select goal</option>
                      {plannerVerticalConfig.goalOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="primaryKPI">Primary KPI</label>
                    <input
                      id="primaryKPI"
                      name="primaryKPI"
                      type="text"
                      value={formData.primaryKPI}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.primaryKpiPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="notes">Additional Notes</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.notesPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row mediaplanner-reference-urls-row">
                  {plannerVerticalConfig.referenceUrlFields.map((field) => (
                    <div className="mediaplanner-field" key={field.name}>
                      <label htmlFor={field.name}>{field.label}</label>
                      <input
                        id={field.name}
                        name={field.name}
                        type="url"
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        title={formData[field.name] || field.placeholder}
                        disabled={referenceUrlsDisabled}
                      />
                    </div>
                  ))}
                </div>

                {plannerVerticalConfig.earnedMediaProfilesEnabled ? (
                  <div
                    className="mediaplanner-form-row smp-wide-row mediaplanner-earned-media-row"
                    data-testid="sherloc-earned-media-profiles"
                  >
                    <div className="mediaplanner-field mediaplanner-earned-media-field">
                      <label>Earned Media Profile URLs</label>
                      <p className="mediaplanner-field-help">
                        Add social/media profile URLs (Instagram, Facebook, YouTube,
                        TikTok, X, and more) for talent, cast, creators, or influencer
                        partners. Submitted profiles are queued for earned media analysis.
                        Leave blank to skip — these never block strategy creation.
                      </p>
                      {(Array.isArray(formData.earnedMediaProfileUrls)
                        ? formData.earnedMediaProfileUrls
                        : ["", "", ""]
                      ).map((profileUrl, index) => (
                        <input
                          key={`earned-media-profile-${index}`}
                          className="mediaplanner-earned-media-input"
                          name={`earnedMediaProfileUrl${index + 1}`}
                          aria-label={`Earned Media Profile URL ${index + 1}`}
                          type="url"
                          value={profileUrl || ""}
                          onChange={(e) =>
                            handleEarnedMediaProfileUrlChange(index, e.target.value)
                          }
                          placeholder={getEarnedMediaProfilePlaceholder(index)}
                          disabled={fieldsDisabled}
                        />
                      ))}
                      {(Array.isArray(formData.earnedMediaProfileUrls)
                        ? formData.earnedMediaProfileUrls.length
                        : EARNED_MEDIA_DEFAULT_PROFILE_FIELDS) <
                      EARNED_MEDIA_MAX_PROFILE_FIELDS ? (
                        <button
                          type="button"
                          className="mediaplanner-earned-media-add-btn"
                          onClick={handleAddEarnedMediaProfileUrl}
                          disabled={fieldsDisabled}
                          data-testid="sherloc-earned-media-add"
                        >
                          + Add another profile
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="ageMin">Age Min</label>
                    <input
                      id="ageMin"
                      name="ageMin"
                      type="number"
                      min="13"
                      max="99"
                      value={formData.ageMin}
                      onChange={handleChange}
                      placeholder="18"
                      disabled={fieldsDisabled}
                    />
                  </div>

                  <div className="mediaplanner-field">
                    <label htmlFor="ageMax">Age Max</label>
                    <input
                      id="ageMax"
                      name="ageMax"
                      type="number"
                      min="13"
                      max="99"
                      value={formData.ageMax}
                      onChange={handleChange}
                      placeholder="54"
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="gender">Gender</label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      disabled={fieldsDisabled}
                    >
                      <option value="all">All</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                  </div>

                  <div className="mediaplanner-field">
                    <label htmlFor="languageTargeting">Language Targeting</label>
                    <input
                      id="languageTargeting"
                      name="languageTargeting"
                      type="text"
                      value={formData.languageTargeting}
                      onChange={handleChange}
                      placeholder="English, Spanish"
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="interests">Interests</label>
                    <textarea
                      id="interests"
                      name="interests"
                      value={formData.interests}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.interestsPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="customTargeting">Custom Targeting</label>
                    <textarea
                      id="customTargeting"
                      name="customTargeting"
                      value={formData.customTargeting}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.customTargetingPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="geoTargetingNotes">Geo / Location Targeting</label>
                    <textarea
                      id="geoTargetingNotes"
                      name="geoTargetingNotes"
                      value={formData.geoTargetingNotes}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.geoTargetingPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="deviceTargetingNotes">Device / Platform Notes</label>
                    <textarea
                      id="deviceTargetingNotes"
                      name="deviceTargetingNotes"
                      value={formData.deviceTargetingNotes}
                      onChange={handleChange}
                      placeholder={plannerVerticalConfig.deviceTargetingPlaceholder}
                      disabled={fieldsDisabled}
                    />
                  </div>
                </div>

                <div className="mediaplanner-form-row smp-wide-row">
                  <div className="mediaplanner-field">
                    <label htmlFor="flightStart">Flight Start</label>
                    <input
                      id="flightStart"
                      name="flightStart"
                      type="date"
                      value={formData.flightStart}
                      onChange={handleChange}
                      required
                      disabled={fieldsDisabled}
                    />
                  </div>

                  <div className="mediaplanner-field">
                    <label htmlFor="flightEnd">Flight End</label>
                    <input
                      id="flightEnd"
                      name="flightEnd"
                      type="date"
                      value={formData.flightEnd}
                      onChange={handleChange}
                      required
                      disabled={fieldsDisabled}
                    />
                  </div>

                  {plannerVerticalConfig.releaseDateEnabled ? (
                    <div
                      className="mediaplanner-field"
                      data-testid="sherloc-release-date-field"
                    >
                      <label htmlFor="releaseDate">Release Date</label>
                      <input
                        id="releaseDate"
                        name="releaseDate"
                        type="date"
                        value={formData.releaseDate}
                        onChange={handleChange}
                        disabled={fieldsDisabled}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="mediaplanner-field mediaplanner-platforms-field">
                  <div className="creative-platform-toggles mediaplanner-platforms-card">
                    <div className="section-pill-header">
                      <span className="badge-pill section-pill">Platforms</span>
                    </div>

                    <div className="platform-toggle-row">
                      {clientVisiblePlannerPlatformOptions(formData.platforms).map((platform) => {
                        const isActive = (formData.platforms || []).includes(platform.key);
                        const legacyOnly = !isClientSelectablePlannerPlatform(platform.key);
                        return (
                          <button
                            key={platform.key}
                            type="button"
                            className={`platform-toggle ${isActive ? "active" : ""}`}
                            data-legacy-platform={legacyOnly ? "true" : undefined}
                            title={legacyOnly ? "Saved on this plan. Not available for new selection." : undefined}
                            onClick={() => handlePlatformToggle(platform.key)}
                            disabled={platformDisabled}
                            aria-disabled={platformDisabled}
                          >
                            <img
                              src={platform.logo}
                              alt={platform.label}
                              className={
                                platform.logoShape === "wordmark"
                                  ? "platform-logo platform-logo--wordmark"
                                  : "platform-logo"
                              }
                            />
                            <span className="platform-label-text">{platform.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="mediaplanner-actions">
                  <SherlocPlannerActionButtons actions={visiblePlannerActions} />

                  <PlannerSaveRecoveryNotice recovery={receiptRecovery} planId={activePlannerId} />
                  {saveMessage ? (
                    <div className={`mediaplanner-save-status is-${saveStatus}`} aria-live="polite">
                      {saveMessage}
                    </div>
                  ) : null}
                </div>

                {error && <div className="mediaplanner-error">{error}</div>}
              </form>
            </div>

            <div className="planner-card mediaplanner-output-card">
              <div className="section-pill-header">
                <span className="badge-pill section-pill">Campaign Strategy</span>
              </div>

              <p className={subtitleClassName}>{headerSubtitle}</p>

              {currencyCampaignId && !isLoadingCampaign ? (
                <div className="smp-stale-strategy-banner" role="status" data-testid="smp-strategy-status">
                  <div className="smp-stale-strategy-banner__title">
                    {currentStrategyNotice?.headline || (currencyUnavailable ? "Strategy status unavailable" : "Checking strategy status…")}
                  </div>
                  {currentStrategyNotice?.detail ? <p className="smp-stale-strategy-banner__body">{currentStrategyNotice.detail}</p> : null}
                  <p className="smp-stale-strategy-banner__body">
                    {currentStrategyNotice?.nextAction || (currencyUnavailable ? "We could not load the strategy and approval records. Try refreshing before making changes." : "Checking the saved strategy and approval records…")}
                  </p>
                  {strategyProvenance ? <details><summary>Saved strategy details</summary>
                  <dl className="smp-stale-strategy-banner__provenance">
                    {strategyProvenance?.planVersion ? (
                      <>
                        <dt>Strategy generated</dt>
                        <dd>{strategyProvenance.planVersion}</dd>
                      </>
                    ) : null}
                    {strategyProvenance?.governanceStamp?.invalidatedAt ? (
                      <>
                        <dt>Invalidated</dt>
                        <dd>
                          {strategyProvenance.governanceStamp.invalidatedAt}
                          {Array.isArray(strategyProvenance.governanceStamp.changedFields) &&
                          strategyProvenance.governanceStamp.changedFields.length
                            ? ` — changed: ${strategyProvenance.governanceStamp.changedFields.join(", ")}`
                            : ""}
                        </dd>
                      </>
                    ) : null}
                    {bannerLatestOperation?.supportReference ? (
                      <>
                        <dt>Latest generation attempt</dt>
                        <dd>
                          {/* The formatter owns the failure-class parenthetical. */}
                          {`${bannerLatestOperation.supportReference} — ${formatSherlocProvenanceAttemptStatus(
                            bannerLatestOperation
                          )}`}
                        </dd>
                      </>
                    ) : null}
                  </dl>
                  </details> : null}
                </div>
              ) : null}

              <div className="smp-strategy-block optimization-notes-container smp-strategy-canvas">
                <div className="smp-strategy-line">
                  <span className="smp-strategy-label optimization-notes-label">
                    Sherloc Recommendations:
                  </span>
                </div>

                <div className="smp-strategy-shell optimization-notes-textarea">
                  <div className="smp-strategy-hero-row mediaplanner-output-hero-row">
                    <div className="sherloc-card-icon-wrapper">
                      <img
                        src={aiOptimizationsImage}
                        alt="Sherloc AI Media Plan Strategy"
                        className="sherloc-card-icon"
                      />
                    </div>

                    <div className="smp-output-overview-hero">
                      <div className="smp-output-overview-text">Sherloc Media Plan Overview</div>
                    </div>
                  </div>

                  {isLoadingCampaign ? (
                    <p className="mediaplanner-output-placeholder">Pulling campaign strategy…</p>
                  ) : (
                    <>
                      {/* Every durable operation status renders an explicit
                          labeled state — the panel is never unlabeled-empty,
                          and a failure card stays visible even when an older
                          strategy remains on screen below it. */}
                      {generationOperation ? (
                        <SherlocDurableGenerationStatusCard
                          operation={generationOperation}
                          nowMs={generationNowMs}
                          onRetry={handleRetryGenerationOperation}
                          retryPending={generationRetryPending}
                        />
                      ) : isStrategyGenerationLoading ? (
                        <SherlocGenerationLoadingCard type={generationLoadingType} />
                      ) : null}
                      {/* Governed SHERLOC recommended allocation surface:
                          review → approve/reject for SHERLOC_RECOMMENDED
                          authorities. Renders above the strategy output so
                          the approval decision is unmistakable; legacy
                          USER_PROVIDED paths render nothing here. */}
                      {sherlocAllocationReviewPanel}
                      {/* Canonical presentation gate: when the backend has
                          written strategy_payload.strategyPresentation
                          (sherloc_strategy_presentation_v1), the golden
                          composed renderer owns the panel — the canonical
                          payload stays the ONLY financial authority while the
                          strategy text's narrative sections are restored
                          around it. Legacy FINANCIAL surfaces (text-derived
                          allocation tiles, platform hero rows, repair-block
                          bullets, mediaPlanResult strategy text) stay
                          suppressed. Campaigns without a presentation keep
                          the legacy renderer path unchanged. */}
                      {isCanonicalSherlocStrategyPresentation(strategyPresentation) ? (
                        <div className="smp-strategy-body mediaplanner-output-text">
                          <SherlocGoldenCanonicalPresentation
                            presentation={strategyPresentation}
                            text={strategy}
                            emptyMessage="No recommendations yet."
                            selectedPlatforms={selectedPlatforms}
                            budget={formData.budget}
                            netMediaBudget={formData.netMediaBudget}
                            notes={formData.notes}
                            canonicalAllocation={canonicalAllocation}
                            earnedMediaProfileUrls={formData.earnedMediaProfileUrls}
                            financialsAbsentAffordance={sherlocAllocationDevelopAffordance}
                          />
                          <SherlocRiskDependencyFindings
                            findings={riskDependencyFindings}
                          />
                        </div>
                      ) : strategy ? (
                        <div className="smp-strategy-body mediaplanner-output-text">
                          <SherlocRichOutputWithHeroes
                            text={strategy}
                            emptyMessage="No recommendations yet."
                            showChannelHeroes={true}
                            forceFirstLineAsH1={false}
                            suppressOverallHero={false}
                            selectedPlatforms={selectedPlatforms}
                            budget={formData.budget}
                            netMediaBudget={formData.netMediaBudget}
                            notes={formData.notes}
                            canonicalAllocation={canonicalAllocation}
                            allocationAuthority={allocationAuthority}
                            />
                          <SherlocRiskDependencyFindings
                            findings={riskDependencyFindings}
                          />
                        </div>
                      ) : generationOperation || isStrategyGenerationLoading ? (
                        null
                      ) : strategyRefreshPanelMessage ? (
                        <p className="mediaplanner-output-placeholder">
                          {strategyRefreshPanelMessage}
                        </p>
                      ) : (
                        <p className="mediaplanner-output-placeholder">
                          Fill out the campaign inputs and click{" "}
                          <strong>
                            &quot;
                            {primaryStrategyActionLabel}
                            &quot;
                          </strong>{" "}
                          to see Sherloc&apos;s recommended media
                          plan here.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="smp-strategy-block optimization-notes-container smp-revision-block">
                <div className="smp-strategy-line">
                  <span className="smp-strategy-label optimization-notes-label">
                    Refine Current Plan:
                  </span>
                </div>
                <div className="smp-revision-shell">
                  <textarea
                    className="smp-revision-textarea"
                    value={revisionPrompt}
                    onChange={(e) => setRevisionPrompt(e.target.value)}
                    placeholder={
                      campaignIdFromNav
                        ? "Copy to New Planner for broader revisions. Use Refresh Strategy to update film reference URLs in place."
                        : SHERLOC_REVISION_REFINE_PLACEHOLDER
                    }
                    disabled={isLoading || isLoadingCampaign || campaignIdFromNav}
                  />
                  <div className="smp-revision-actions">
                    <button
                      type="button"
                      className="mediaplanner-save-btn"
                      onClick={handleApplyRevision}
                      disabled={
                        isLoading ||
                        isLoadingCampaign ||
                        isAnyGenerationLoading ||
                        campaignIdFromNav ||
                        !String(strategy || "").trim() ||
                        !String(revisionPrompt || "").trim()
                      }
                    >
                      {isLoading ? "Applying Revision..." : "Apply AI Revision"}
                    </button>
                    {lastAppliedRevisionPrompt ? (
                      <div className="smp-revision-hint">
                        Last revision: {lastAppliedRevisionPrompt}
                      </div>
                    ) : (
                      <div className="smp-revision-hint">
                        Revisions update the current working plan instead of creating a duplicate.
                      </div>
                    )}
                  </div>

                  <SherlocAppliedRevisionSummary summary={appliedRevisionSummary} />
                </div>
              </div>

              <div className="smp-strategy-block optimization-notes-container smp-creative-output-container">
                <div className="smp-strategy-line">
                  <span className="smp-strategy-label optimization-notes-label">
                    Creative Variations:
                  </span>
                </div>

                {creativeError ? <div className="mediaplanner-error">{creativeError}</div> : null}

                {isCreativeGenerationLoading ? (
                  <SherlocGenerationLoadingCard
                    type={GENERATION_LOADING_TYPES.CREATIVE_VARIATIONS}
                    compact
                  />
                ) : creativeRecommendations.length > 0 ? (
                  <>
                  {creativeSaveNotice?.recommendations === creativeRecommendations &&
                  creativeSaveNotice.identity === plannerIdentity ? (
                    <p
                      className="smp-creative-meta"
                      data-creative-save-state={creativeSaveNotice.state}
                    >
                      {creativeSaveNotice.text}
                    </p>
                  ) : null}
                  {creativeRuleBasedLabel ? (
                    <p className="smp-creative-meta" data-creative-provenance="rule_based">
                      {creativeRuleBasedLabel}
                    </p>
                  ) : null}
                  <div className="smp-creative-grid">
                    {creativeRecommendations.map((recommendation, index) => {
                      const adCopy = mapCreativeVariationForDisplay(recommendation, index);
                      return (
                      <div key={`${adCopy.headline}-${index}`} className="smp-creative-card">
                        <div className="smp-creative-card-header">
                          <span className="badge-pill section-pill">{adCopy.variantLabel}</span>
                          {adCopy.cta ? (
                            <span className="smp-creative-cta-pill">{adCopy.cta}</span>
                          ) : null}
                        </div>
                        {adCopy.headline ? (
                          <div className="smp-creative-line">
                            <strong>Headline:</strong> {adCopy.headline}
                          </div>
                        ) : null}
                        {adCopy.primaryText ? (
                          <div className="smp-creative-line">
                            <strong>Primary Text:</strong> {adCopy.primaryText}
                          </div>
                        ) : null}
                        {adCopy.description ? (
                          <div className="smp-creative-line">
                            <strong>Description:</strong> {adCopy.description}
                          </div>
                        ) : null}
                        {adCopy.platformFit ? (
                          <div className="smp-creative-line smp-creative-line-muted">
                            <strong>Platform Fit:</strong> {adCopy.platformFit}
                          </div>
                        ) : null}
                        {adCopy.internalNotes ? (
                          <details className="smp-creative-internal-notes">
                            <summary>Creative Strategy Notes (internal)</summary>
                            {adCopy.internalNotes.uniqueDescription ? (
                              <div className="smp-creative-line">
                                <strong>Unique Angle:</strong>{" "}
                                {adCopy.internalNotes.uniqueDescription}
                              </div>
                            ) : null}
                            {adCopy.internalNotes.designDirection ? (
                              <div className="smp-creative-line">
                                <strong>Design Direction:</strong>{" "}
                                {adCopy.internalNotes.designDirection}
                              </div>
                            ) : null}
                            {adCopy.internalNotes.audienceAngle ? (
                              <div className="smp-creative-line">
                                <strong>Audience Angle:</strong>{" "}
                                {adCopy.internalNotes.audienceAngle}
                              </div>
                            ) : null}
                            {adCopy.internalNotes.platformHints.length > 0 ? (
                              <div className="smp-creative-line">
                                <strong>Platform Hints:</strong>{" "}
                                {orderPlannerPlatformLabels(
                                  adCopy.internalNotes.platformHints
                                ).join(", ")}
                              </div>
                            ) : null}
                            {adCopy.internalNotes.rationale ? (
                              <div className="smp-creative-line">
                                <strong>Rationale:</strong> {adCopy.internalNotes.rationale}
                              </div>
                            ) : null}
                          </details>
                        ) : null}
                      </div>
                      );
                    })}
                  </div>
                  </>
                ) : (
                  <p className="mediaplanner-output-placeholder">
                    Use <strong>&quot;Generate Creative Variations&quot;</strong> to produce
                    ready-to-run ad copy — headlines, primary text, and CTAs — from the planner
                    inputs.
                  </p>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* No Previous Page button */}
      </div>
    </div>
  );
};

const VerifiedSherlocMediaPlanner = () => {
  const location = useLocation();
  const advertiser = useAdvertiser() || {};
  const params = new URLSearchParams(location?.search || "");
  const advertiserId = location?.state?.advertiserId || params.get("advertiserId") || advertiser.selectedAdvertiserId || "";
  const campaignId = location?.state?.campaignId || params.get("campaignId") || "";
  return (
    <PlannerSessionBoundary portal="client" advertiserId={advertiserId} campaignId={advertiserId ? "" : campaignId} apiBase={API_BASE_URL}>
      {(session) => <SherlocMediaPlanner key={session.key} plannerSession={session} />}
    </PlannerSessionBoundary>
  );
};

export default VerifiedSherlocMediaPlanner;
